import React, { useEffect, useMemo, useRef, useState } from "react";
import { exportFractalAsTIFF, readTIFFMetadata, parseToolkitTIFFDescription } from "./tiffExport";
import { useUserTier, can, tierRequiredFor, TIERS } from "./userTier";
import { FractalMusicEngine, makeDefaultTableLookup, makeSoundPaletteLookup, DEFAULT_SOUND_STOPS, makeLoudnessLookup, DEFAULT_LOUDNESS_PALETTE, instrumentsInLoudnessPalette, GM_INSTRUMENTS, notesAtBand, SCALES, realize } from "./fractalMusic";
import { logExport } from "./logExport";
import { analyzeScan, inspectColumn, VOICE_CAP, SMPLR_RELEASE_SEC } from "./scanDiagnostics";
import { extractBands } from "./bandExtractor";
import { traceContour } from "./contourTracer";
import AuthWidget from "./AuthWidget";
import UpgradePanel from "./UpgradePanel";
import { getPieceById } from "./playerLibrary";
import { getFunctions, httpsCallable } from "firebase/functions";

// Single source of truth for the Spoken labels setting. Module scope, so the
// Help dialog and other code outside the component can read it too. Screen
// reader users turn this off to hear their own reader rather than both at once.
function spokenLabelsOn() {
  try { return window.localStorage.getItem("fmSpokenLabels") !== "off"; } catch (_) { return true; }
}

// ── Firebase Storage config ───────────────────────────────────────────────────
// Files are stored in the "palettes" folder inside this public bucket.
// The bucket has read-open rules so no API key or auth token is needed.
const FIREBASE_BUCKET = "plato-s-playground-591ed.firebasestorage.app";
const STORAGE_BASE    = `https://firebasestorage.googleapis.com/v0/b/${FIREBASE_BUCKET}/o`;

// Standard high-res TIFF export sizes. Picked so the long edge corresponds
// to a real print size at 300 DPI:
//   2K   → roughly 17 × 11 cm at 300 DPI; A4 at 240 DPI. Web/share size.
//   4K   → roughly 33 × 22 cm at 300 DPI; medium prints.
//   8K   → roughly 67 × 44 cm at 300 DPI; gallery-scale (matches your
//          existing 70×50 cm work almost exactly).
//   12K  → roughly 100 × 67 cm at 300 DPI; large gallery / installation.
// Aspect ratio is 3:2, matching your existing canvas (750 × 500).
const TIFF_EXPORT_PRESETS = [
  { label: "2K  (2000 × 1333)",   width: 2000,  height: 1333  },
  { label: "4K  (4000 × 2667)",   width: 4000,  height: 2667  },
  { label: "8K  (8000 × 5333)",   width: 8000,  height: 5333  },
  { label: "12K (12000 × 8000)",  width: 12000, height: 8000  },
];
// PNG export presets.  The "Canvas" entry exports at the on-screen render
// resolution (free tier, watermarked).  The 2K/4K/8K/12K entries trigger
// a fresh high-resolution render before encoding, just like TIFF.  The
// width/height for "Canvas" are filled in at export time from canvasRef.
const PNG_EXPORT_PRESETS = [
  { label: "Canvas (current view)",  width: 0,     height: 0,    isCanvas: true },
  { label: "2K  (2000 × 1333)",      width: 2000,  height: 1333 },
  { label: "4K  (4000 × 2667)",      width: 4000,  height: 2667 },
  { label: "8K  (8000 × 5333)",      width: 8000,  height: 5333 },
  { label: "12K (12000 × 8000)",     width: 12000, height: 8000 },
];
const WIDTH = 750;   // logical pixel dimensions — used for all math/coordinates
const HEIGHT = 500;
// Named tempi (Italian) -> per-column time budget in ms. Slow = large ms.
// Used by the movements tempo-map (Stage 3). feelFor() maps ms -> nearest name.
const TEMPO_OPTIONS = [
  { name: "Largo", ms: 1500 },
  { name: "Larghetto", ms: 1100 },
  { name: "Adagio", ms: 800 },
  { name: "Andante", ms: 600 },
  { name: "Moderato", ms: 450 },
  { name: "Allegro", ms: 300 },
  { name: "Presto", ms: 200 },
];
function feelFor(ms) {
  let best = "", bd = Infinity;
  for (const t of TEMPO_OPTIONS) { const d = Math.abs(ms - t.ms); if (d < bd) { bd = d; best = t.name; } }
  return best;
}
// Physical pixel multiplier for HiDPI/Retina displays (2× on most modern screens).
// The canvas buffer is rendered at this higher resolution so it's crisp when
// the browser scales it up to fill the CSS display size.
const DPR = typeof window !== "undefined" ? (window.devicePixelRatio || 1) : 1;
const PHYS_WIDTH  = WIDTH  * DPR;
const PHYS_HEIGHT = HEIGHT * DPR;
// eslint-disable-next-line
const DEFAULT_LINE_DRAW_MS = 1000;


const PAGE_SIZE = 3100;
const COLORS_PER_PAGE = 256;
const MAXCOLRS = 65536;
const ESCAPE_RADIUS_SQUARED = 100.0;
// Number of smooth-iteration units per full palette cycle (controls band density)
const PALETTE_CYCLE = 64;

// Developer logging. Off by default so a published build has a quiet console.
// Enable by flipping this to true, or by loading the app with ?debug=1 in the URL.
// Only gates console.log noise — console.warn / console.error still fire so real
// problems remain visible in production.
const DEBUG = (() => {
  try { return new URLSearchParams(window.location.search).get('debug') === '1'; }
  catch (_) { return false; }
})();
const dlog = (...args) => { if (DEBUG) console.log(...args); };

// 'idle' | 'listening' | 'speaking'

const DEFAULT_PRESETS = {
  mandelbrotFull: {
    label: "Mandelbrot Full",
    fractalType: "mandelbrot",
    // x-range 3.5 / y-range 2.3333 = 1.5, matching the 750x500 canvas's 3:2
    // aspect ratio. The Mandelbrot set extends from about y = -1.13 to +1.13,
    // so y:[-1.1667, 1.1667] still shows the whole set with a tiny margin.
    view: { xMin: -2.5, xMax: 1.0, yMin: -1.1666666666666667, yMax: 1.1666666666666667 },
    maxIter: 500,
    juliaC: { re: 0, im: 0 },
  },
  seahorseValley: {
    label: "Seahorse Valley",
    fractalType: "mandelbrot",
    view: { xMin: -0.8700, xMax: -0.6500, yMin: 0.2267, yMax: 0.3733 },
    maxIter: 1024,
    juliaC: { re: 0, im: 0 },
  },
  elephantValley: {
    label: "Elephant Valley",
    fractalType: "mandelbrot",
    view: { xMin: 0.1875, xMax: 0.5625, yMin: -0.1250, yMax: 0.1250 },
    maxIter: 1000,
    juliaC: { re: 0, im: 0 },
  },
  juliaClassic: {
    label: "Julia c = -0.8 + 0.156i",
    fractalType: "julia",
    view: { xMin: -1.875, xMax: 1.875, yMin: -1.25, yMax: 1.25 },
    maxIter: 500,
    juliaC: { re: -0.8, im: 0.156 },
  },
  juliaSpiral: {
    label: "Julia c = 0.285 + 0.01i",
    fractalType: "julia",
    view: { xMin: -1.875, xMax: 1.875, yMin: -1.25, yMax: 1.25 },
    maxIter: 500,
    juliaC: { re: 0.285, im: 0.01 },
  },
  // ── IBM / Wollschläger c-plane Mandelbrot maps (Hehl, Wollschläger) ──────
  // Progressive deep-zoom into the boundary near c ≈ -0.7454295, 0.1130082.
  // maxIter scales roughly with zoom depth so deep maps actually resolve.
  map27: {
    label: "Map 27 — Northern bay",
    fractalType: "mandelbrot",
    view: { xMin: -0.19920, xMax: -0.12954, yMin: 1.01480, yMax: 1.06707 },
    maxIter: 1024,
    juliaC: { re: 0, im: 0 },
  },
  map29: {
    label: "Map 29 — Western bulb",
    fractalType: "mandelbrot",
    view: { xMin: -0.95, xMax: -0.88333, yMin: 0.23333, yMax: 0.3 },
    maxIter: 1024,
    juliaC: { re: 0, im: 0 },
  },
  map30: {
    label: "Map 30 — Upper-left lobe",
    fractalType: "mandelbrot",
    view: { xMin: -0.713, xMax: -0.4082, yMin: 0.49216, yMax: 0.71429 },
    maxIter: 1024,
    juliaC: { re: 0, im: 0 },
  },
  map33: {
    label: "Map 33 — Far-left antenna",
    fractalType: "mandelbrot",
    view: { xMin: -1.781, xMax: -1.764, yMin: 0, yMax: 0.013 },
    maxIter: 1024,
    juliaC: { re: 0, im: 0 },
  },
  map36: {
    label: "Map 36 — Approach",
    fractalType: "mandelbrot",
    view: { xMin: -0.75104, xMax: -0.7408, yMin: 0.10511, yMax: 0.11536 },
    maxIter: 1500,
    juliaC: { re: 0, im: 0 },
  },
  map38: {
    label: "Map 38 — Closer",
    fractalType: "mandelbrot",
    view: { xMin: -0.74758, xMax: -0.74624, yMin: 0.10671, yMax: 0.10779 },
    maxIter: 2000,
    juliaC: { re: 0, im: 0 },
  },
  map40: {
    label: "Map 40 — Detail",
    fractalType: "mandelbrot",
    view: { xMin: -0.746541, xMax: -0.746378, yMin: 0.107574, yMax: 0.107678 },
    maxIter: 2500,
    juliaC: { re: 0, im: 0 },
  },
  map42: {
    label: "Map 42 — Mini Mandelbrot",
    fractalType: "mandelbrot",
    view: { xMin: -0.74591, xMax: -0.74448, yMin: 0.11196, yMax: 0.11339 },
    maxIter: 2000,
    juliaC: { re: 0, im: 0 },
  },
  map44: {
    label: "Map 44 — Spiral arms",
    fractalType: "mandelbrot",
    view: { xMin: -0.745538, xMax: -0.745054, yMin: 0.112881, yMax: 0.113236 },
    maxIter: 2500,
    juliaC: { re: 0, im: 0 },
  },
  map45: {
    label: "Map 45 — Tighter spiral",
    fractalType: "mandelbrot",
    view: { xMin: -0.745468, xMax: -0.745385, yMin: 0.112979, yMax: 0.113039 },
    maxIter: 3000,
    juliaC: { re: 0, im: 0 },
  },
  map48: {
    label: "Map 48 — Deep filaments",
    fractalType: "mandelbrot",
    view: { xMin: -0.7454356, xMax: -0.7454215, yMin: 0.1130037, yMax: 0.1130139 },
    maxIter: 3500,
    juliaC: { re: 0, im: 0 },
  },
  map49: {
    label: "Map 49 — Deepest detail",
    fractalType: "mandelbrot",
    view: { xMin: -0.7454301, xMax: -0.7454289, yMin: 0.1130076, yMax: 0.1130085 },
    maxIter: 4000,
    juliaC: { re: 0, im: 0 },
  },
};

const DEFAULT_PALETTES = {
  classic: {
    label: "Classic",
    stops: [
      { t: 0.0, r: 0, g: 7, b: 100 },
      { t: 0.16, r: 32, g: 107, b: 203 },
      { t: 0.42, r: 237, g: 255, b: 255 },
      { t: 0.64, r: 255, g: 170, b: 0 },
      { t: 0.86, r: 120, g: 20, b: 20 },
      { t: 1.0, r: 0, g: 2, b: 0 },
    ],
  },
  grayscale: {
    label: "Grayscale",
    stops: [
      { t: 0.0, r: 0, g: 0, b: 0 },
      { t: 1.0, r: 255, g: 255, b: 255 },
    ],
  },
  fire: {
    label: "Fire",
    stops: [
      { t: 0.0, r: 0, g: 0, b: 0 },
      { t: 0.25, r: 120, g: 0, b: 0 },
      { t: 0.5, r: 220, g: 60, b: 0 },
      { t: 0.75, r: 255, g: 180, b: 0 },
      { t: 1.0, r: 255, g: 255, b: 180 },
    ],
  },
  ocean: {
    label: "Ocean",
    stops: [
      { t: 0.0, r: 0, g: 10, b: 30 },
      { t: 0.3, r: 0, g: 70, b: 140 },
      { t: 0.6, r: 40, g: 180, b: 220 },
      { t: 0.85, r: 180, g: 240, b: 255 },
      { t: 1.0, r: 255, g: 255, b: 255 },
    ],
  },
  rainbow: {
    label: "Rainbow",
    stops: [
      { t: 0.0, r: 148, g: 0, b: 211 },
      { t: 0.17, r: 75, g: 0, b: 130 },
      { t: 0.34, r: 0, g: 0, b: 255 },
      { t: 0.51, r: 0, g: 255, b: 0 },
      { t: 0.68, r: 255, g: 255, b: 0 },
      { t: 0.85, r: 255, g: 127, b: 0 },
      { t: 1.0, r: 255, g: 0, b: 0 },
    ],
  },
};

const DEFAULT_SPEC = DEFAULT_PRESETS.mandelbrotFull;

// SOUND_MAP[k] gives synth parameters for the 256 palette indices. The pitch
// spans 7 octaves from 120 Hz (k=0) to ~15360 Hz (k=255), which puts adjacent
// steps about a third of a semitone (~33 cents) apart — distinguishable to
// most listeners while still smooth when sweeping. The filter brightness and
// roughness scale linearly across the same range.
const SOUND_MAP = Array.from({ length: 256 }, (_, k) => {
  const t = k / 255;
  return {
    pitchHz: 120 * Math.pow(2, 7 * t),
    filterHz: 300 + 7000 * t,
    roughness: 0.02 + 0.9 * t,
  };
});

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function mapRange(value, inMin, inMax, outMin, outMax) {
  if (inMax === inMin) return outMin;
  const t = clamp((value - inMin) / (inMax - inMin), 0, 1);
  return lerp(outMin, outMax, t);
}

function formatDuration(ms) {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

// Parse "item N" / "number N" / standalone ordinal into a 1-based index, or
// null if the text doesn't look like a positional reference. Used by
// browseList and the post-list command handler so the user can pick an entry
// by position ("item 12") instead of by name. Number words are recognised up
// to twenty as singles, and 21-99 via compound ("twenty one"). Beyond that
// the user has to dictate digits ("item 127"), which Web Speech API and
// Whisper both transcribe as numerals.
const ITEM_WORD_NUMBERS = {
  one:1, two:2, three:3, four:4, five:5, six:6, seven:7, eight:8, nine:9, ten:10,
  eleven:11, twelve:12, thirteen:13, fourteen:14, fifteen:15,
  sixteen:16, seventeen:17, eighteen:18, nineteen:19, twenty:20,
};
const ITEM_ORDINALS = {
  first:1, second:2, third:3, fourth:4, fifth:5, sixth:6, seventh:7, eighth:8, ninth:9, tenth:10,
  eleventh:11, twelfth:12, thirteenth:13, fourteenth:14, fifteenth:15,
  sixteenth:16, seventeenth:17, eighteenth:18, nineteenth:19, twentieth:20,
};
function parseItemNumber(text) {
  const t = String(text || '').toLowerCase().trim();
  if (!t) return null;
  // "item 5" / "number 5"
  let m = t.match(/\b(?:item|number)\s*(\d+)\b/);
  if (m) return parseInt(m[1], 10);
  // "item twenty one" / "number thirty five" — compound 21-99 first, since
  // a greedy single-word match would otherwise stop at "twenty" and yield 20.
  const tens = { twenty:20, thirty:30, forty:40, fifty:50, sixty:60, seventy:70, eighty:80, ninety:90 };
  m = t.match(/\b(?:item|number)\s+(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)[\s-]+([a-z]+)\b/);
  if (m && ITEM_WORD_NUMBERS[m[2]] !== undefined && ITEM_WORD_NUMBERS[m[2]] < 10) {
    return tens[m[1]] + ITEM_WORD_NUMBERS[m[2]];
  }
  // "item five" / "number twelve" / "item twenty"
  m = t.match(/\b(?:item|number)\s+([a-z]+)\b/);
  if (m) {
    if (ITEM_WORD_NUMBERS[m[1]] !== undefined) return ITEM_WORD_NUMBERS[m[1]];
    if (tens[m[1]] !== undefined) return tens[m[1]];
  }
  // Standalone ordinal: "first", "twelfth", etc.
  for (const w in ITEM_ORDINALS) {
    if (new RegExp('\\b' + w + '\\b').test(t)) return ITEM_ORDINALS[w];
  }
  return null;
}

function soundMapIndexFromEscape(meanValue, maxIter) {
  // Map escape-iteration count to a 0-255 SOUND_MAP index. Escape counts in
  // a typical Mandelbrot view are distributed log-like — most exterior
  // pixels escape in the first few dozen iterations, with only a thin
  // boundary band approaching maxIter. A linear mapping (the obvious
  // choice) crams ~95% of pixels into the bottom 5% of SOUND_MAP, so
  // adjacent visual color bands end up just a few Hz apart and sound
  // nearly identical even with a 7-octave pitch range. Taking log(value)
  // normalised against log(maxIter) spreads the typical escape range
  // (~10–200 iters at maxIter=1024) across the full SOUND_MAP, giving
  // each visible band a clearly distinct pitch.
  if (meanValue <= 0 || !Number.isFinite(meanValue)) return 0;
  const logV = Math.log(meanValue + 1);
  const logMax = Math.log(Math.max(maxIter, 1) + 1);
  const t = clamp(logV / logMax, 0, 1);
  return Math.max(0, Math.min(255, Math.round(255 * t)));
}

function samplePalette(stops, t) {
  const tt = clamp(t, 0, 1);

  if (tt <= stops[0].t) {
    return { r: stops[0].r, g: stops[0].g, b: stops[0].b };
  }

  for (let i = 0; i < stops.length - 1; i += 1) {
    const a = stops[i];
    const b = stops[i + 1];
    if (tt >= a.t && tt <= b.t) {
      const localT = (tt - a.t) / (b.t - a.t);
      return {
        r: Math.round(lerp(a.r, b.r, localT)),
        g: Math.round(lerp(a.g, b.g, localT)),
        b: Math.round(lerp(a.b, b.b, localT)),
      };
    }
  }

  const last = stops[stops.length - 1];
  return { r: last.r, g: last.g, b: last.b };
}

// Represent a gradient (its colour stops) as a full palette bank, so every
// palette — built-in gradients and imported banks alike — is the same kind of
// object. fullColors is filled using the formula renderer's cyclic period, so
// computed fractals look the same as the old gradient path; page 0 gives the
// colour editor real, editable colours instead of a grey fallback. Cached per
// stops array (they're stable module constants).
const _gradientBankCache = new WeakMap();
function gradientToBank(stops) {
  const cached = _gradientBankCache.get(stops);
  if (cached) return cached;
  const fullColors = new Uint8Array(MAXCOLRS * 3);
  for (let g = 0; g < MAXCOLRS; g += 1) {
    const c = samplePalette(stops, (g % PALETTE_CYCLE) / PALETTE_CYCLE);
    const o = g * 3;
    fullColors[o] = c.r; fullColors[o + 1] = c.g; fullColors[o + 2] = c.b;
  }
  const pages = [];
  for (let page = 0; page < 256; page += 1) {
    const start = page * COLORS_PER_PAGE * 3;
    const pageColors = new Uint8Array(fullColors.subarray(start, start + COLORS_PER_PAGE * 3));
    pages.push({ pageIndex: page, controls: null, colors: pageColors });
  }
  const bank = { pages, fullColors };
  _gradientBankCache.set(stops, bank);
  return bank;
}

function rgbToHsv(r, g, b) {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === rr) {
      h = ((gg - bb) / delta) % 6;
    } else if (max === gg) {
      h = (bb - rr) / delta + 2;
    } else {
      h = (rr - gg) / delta + 4;
    }
    h /= 6;
    if (h < 0) h += 1;
  }

  const s = max === 0 ? 0 : delta / max;
  const v = max;

  return { h, s, v };
}

function buildFallbackPaletteBank() {
  const fullColors = new Uint8Array(MAXCOLRS * 3);
  const pages = [];

  for (let page = 0; page < 256; page += 1) {
    const pageColors = new Uint8Array(COLORS_PER_PAGE * 3);

    for (let i = 0; i < COLORS_PER_PAGE; i += 1) {
      const v = i;
      pageColors[i * 3] = v;
      pageColors[i * 3 + 1] = v;
      pageColors[i * 3 + 2] = v;

      const globalIndex = page * COLORS_PER_PAGE + i;
      fullColors[globalIndex * 3] = v;
      fullColors[globalIndex * 3 + 1] = v;
      fullColors[globalIndex * 3 + 2] = v;
    }

    pages.push({
      pageIndex: page,
      controls: null,
      colors: pageColors,
    });
  }

  return { pages, fullColors };
}

function parsePaletteBank(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  const numPages = Math.floor(bytes.length / PAGE_SIZE);
  const pages = [];
  const fullColors = new Uint8Array(Math.max(numPages * COLORS_PER_PAGE * 3, MAXCOLRS * 3));

  for (let page = 0; page < numPages; page += 1) {
    const base = page * PAGE_SIZE;
    const view = new DataView(arrayBuffer, base, PAGE_SIZE);

    const redSlider = view.getInt32(0, false);
    const redStep = view.getInt32(4, false);
    const redDirection = view.getUint8(8);

    const greenSlider = view.getInt32(9, false);
    const greenStep = view.getInt32(13, false);
    const greenDirection = view.getUint8(17);

    const blueSlider = view.getInt32(18, false);
    const blueStep = view.getInt32(22, false);
    const blueDirection = view.getUint8(26);

    const unknown27 = view.getUint8(27); // smoothing boolean (0x01 = true)

    const pageColors = new Uint8Array(COLORS_PER_PAGE * 3);
    let offset = 28;

    for (let i = 0; i < COLORS_PER_PAGE; i += 1) {
      const r = view.getInt32(offset, false) & 0xff;
      const g = view.getInt32(offset + 4, false) & 0xff;
      const b = view.getInt32(offset + 8, false) & 0xff;
      offset += 12;

      pageColors[i * 3] = r;
      pageColors[i * 3 + 1] = g;
      pageColors[i * 3 + 2] = b;

      const globalIndex = page * COLORS_PER_PAGE + i;
      fullColors[globalIndex * 3] = r;
      fullColors[globalIndex * 3 + 1] = g;
      fullColors[globalIndex * 3 + 2] = b;
    }

    pages.push({
      pageIndex: page,
      controls: {
        redSlider,
        redStep,
        redDirection,      // 0x01 = lighter (increasing), 0x00 = darker (decreasing)
        greenSlider,
        greenStep,
        greenDirection,
        blueSlider,
        blueStep,
        blueDirection,
        smoothing: unknown27, // 0x01 = smoothing on
      },
      colors: pageColors,
    });
  }

  return { pages, fullColors };
}

// Derive approximate Color-Editor slider values from a page's actual colours,
// for palettes that carry no stored slider controls (e.g. ones built by hand
// or converted from other tools). The editor's model is a linear ramp per
// channel (start value + per-slot step + direction), so this fits:
//   start     = the channel value at slot 0,
//   step      = average per-slot change across the page (rounded),
//   direction = increasing if the page ends higher than it starts.
// It is an approximation — a single ramp can't reproduce a multi-stop palette
// exactly — but it gives the sliders a meaningful starting point that reflects
// the palette instead of opening at all zeros.
function deriveControls(pageColors) {
  function fit(ch) {
    const first = pageColors[ch] & 0xff;
    const last = pageColors[255 * 3 + ch] & 0xff;
    let totalAbsDelta = 0;
    for (let i = 1; i < 256; i += 1) {
      totalAbsDelta += Math.abs(
        (pageColors[i * 3 + ch] & 0xff) - (pageColors[(i - 1) * 3 + ch] & 0xff)
      );
    }
    const step = Math.max(0, Math.min(255, Math.round(totalAbsDelta / 255)));
    return { start: first, step, lighter: last >= first };
  }
  const r = fit(0), g = fit(1), b = fit(2);
  return {
    rS: r.start, rT: r.step, rL: r.lighter,
    gS: g.start, gT: g.step, gL: g.lighter,
    bS: b.start, bT: b.step, bL: b.lighter,
    sm: false,
  };
}

function controlsAreMeaningful(c) {
  return !!c && (
    c.redSlider || c.redStep || c.greenSlider || c.greenStep ||
    c.blueSlider || c.blueStep || c.smoothing
  );
}

// Build the per-page slider controls array the Color Editor consumes: use a
// page's stored controls when present, otherwise derive them from its colours.
function buildAllPageControls(bank) {
  return bank?.pages?.map(pg => {
    const c = pg?.controls;
    if (controlsAreMeaningful(c)) {
      return {
        rS: Math.max(0, Math.min(255, c.redSlider   & 0xff)),
        rT: Math.max(0, Math.min(255, c.redStep     & 0xff)),
        rL: c.redDirection   === 1,
        gS: Math.max(0, Math.min(255, c.greenSlider & 0xff)),
        gT: Math.max(0, Math.min(255, c.greenStep   & 0xff)),
        gL: c.greenDirection === 1,
        bS: Math.max(0, Math.min(255, c.blueSlider  & 0xff)),
        bT: Math.max(0, Math.min(255, c.blueStep    & 0xff)),
        bL: c.blueDirection  === 1,
        sm: c.smoothing === 1,
      };
    }
    if (pg?.colors && pg.colors.length >= 256 * 3) return deriveControls(pg.colors);
    return null;
  }) || [];
}

// Identify common image formats by magic bytes, so an import failure can say
// "that's a PNG, not a fractal .img" instead of a generic error. Returns a
// format name string, or null if the bytes aren't a recognised picture format.
function detectImageFormat(buffer) {
  const b = new Uint8Array(buffer);
  const has = (sig, off = 0) => sig.every((v, i) => b[off + i] === v);
  if (has([0x89, 0x50, 0x4e, 0x47])) return "PNG";
  if (has([0xff, 0xd8, 0xff])) return "JPEG";
  if (has([0x47, 0x49, 0x46, 0x38])) return "GIF";
  if (has([0x42, 0x4d])) return "BMP";
  if (has([0x49, 0x49, 0x2a, 0x00]) || has([0x4d, 0x4d, 0x00, 0x2a])) return "TIFF";
  if (has([0x52, 0x49, 0x46, 0x46]) && has([0x57, 0x45, 0x42, 0x50], 8)) return "WebP";
  if (has([0x25, 0x50, 0x44, 0x46])) return "PDF";
  return null;
}

// Auto-detects and imports BOTH .img formats:
//   • React/native format   – text `paletteId` line after height, then an
//                             int32-per-pixel raster (indices 0..65535).
//   • Legacy "hiperf" format – big-endian int32 page-count after height,
//                             NO paletteId line, ONE byte per pixel raster
//                             (indices 0..255, i.e. page 0 of the palette).
// Both return `raster` as a Uint32Array so every downstream consumer
// (renderImportedRaster, palette-bank coloring, the Color Editor) is identical.
function parseNativeImg(buffer) {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  let offset = 0;

  const sourceWidth = view.getInt32(offset, false);
  offset += 4;
  const sourceHeight = view.getInt32(offset, false);
  offset += 4;

  // Dimensions guard — palette files / junk have nonsense here.
  if (
    !Number.isInteger(sourceWidth) ||
    !Number.isInteger(sourceHeight) ||
    sourceWidth < 1 || sourceWidth > 65536 ||
    sourceHeight < 1 || sourceHeight > 65536
  ) {
    throw new Error("Not a valid .img file: bad dimensions");
  }

  function readLine() {
    const chars = [];
    while (offset < bytes.length) {
      const b = bytes[offset++];
      if (b === 10) break;
      if (b !== 13) chars.push(b);
    }
    return new TextDecoder().decode(new Uint8Array(chars)).trim();
  }

  // ── Format detection ──────────────────────────────────────────────────────
  // Native: byte[8] begins a text paletteId line -> ASCII (non-zero).
  // Legacy: byte[8] is the high byte of a big-endian int32 page-count
  //         (e.g. 4 -> 00 00 00 04), so byte[8] === 0.
  const isLegacy = bytes[8] === 0;

  let paletteId = "";
  if (isLegacy) {
    offset += 4; // skip int32 page-count field (legacy has no paletteId line)
  } else {
    paletteId = readLine();
  }

  const maxIterText = readLine();
  const xfactorText = readLine();
  const yfactorText = readLine();

  const drawingMode = view.getInt32(offset, false);
  offset += 4;

  const p1Text = readLine();
  const q1Text = readLine();
  const pminText = readLine();
  const pmaxText = readLine();
  const qminText = readLine();
  const qmaxText = readLine();

  const imgTypeByte = view.getUint8(offset);
  offset += 1;
  if (imgTypeByte !== 74 && imgTypeByte !== 77) { // 'J' or 'M'
    throw new Error("Not a valid .img file: unknown fractal-type byte");
  }

  const xMin = Number(pminText);
  const xMax = Number(pmaxText);
  const yMin = Number(qminText);
  const yMax = Number(qmaxText);
  if (![xMin, xMax, yMin, yMax].every(Number.isFinite)) {
    throw new Error("Not a valid .img file: non-finite view coordinates");
  }

  const pixelCount = sourceWidth * sourceHeight;
  const bytesPerPixel = isLegacy ? 1 : 4;
  if (offset + pixelCount * bytesPerPixel > buffer.byteLength) {
    throw new Error(
      `Not a valid .img file: buffer too small for ${sourceWidth}x${sourceHeight} raster`
    );
  }

  const raster = new Uint32Array(pixelCount);
  if (isLegacy) {
    for (let i = 0; i < pixelCount; i += 1) {
      raster[i] = bytes[offset]; // one byte per pixel -> 0..255 (page 0)
      offset += 1;
    }
  } else {
    for (let i = 0; i < pixelCount; i += 1) {
      raster[i] = view.getInt32(offset, false);
      offset += 4;
    }
  }

  return {
    fractalType: imgTypeByte === 74 ? "julia" : "mandelbrot",
    maxIter: Math.max(10, Number(maxIterText) || 1024),
    juliaC: {
      re: Number(p1Text) || 0,
      im: Number(q1Text) || 0,
    },
    view: { xMin, xMax, yMin, yMax },
    sourceWidth,
    sourceHeight,
    paletteId,
    xfactorText,
    yfactorText,
    drawingMode,
    raster,
    legacyFormat: isLegacy,
  };
}

// ── .img encoder ─────────────────────────────────────────────────────────────
// Mirror image of parseNativeImg — writes the same binary format that the
// original Java app produced, so files are fully interchangeable.
//
// The format in byte order:
//   [0..3]   int32 BE   sourceWidth
//   [4..7]   int32 BE   sourceHeight
//   text\n              paletteId
//   text\n              maxIter
//   text\n              xfactorText
//   text\n              yfactorText
//   int32 BE            drawingMode
//   text\n              p1  (Julia Re, or "0" for Mandelbrot)
//   text\n              q1  (Julia Im, or "0" for Mandelbrot)
//   text\n              pmin (xMin)
//   text\n              pmax (xMax)
//   text\n              qmin (yMin)
//   text\n              qmax (yMax)
//   uint8               imgType (74 = 'J' for Julia, 77 = 'M' for Mandelbrot)
//   int32 BE × pixels   raster data (one entry per pixel)
//
// Arguments:
//   raster      — Uint32Array of raw iteration indices, one per pixel
//   width       — logical pixel width (always WIDTH = 750)
//   height      — logical pixel height (always HEIGHT = 500)
//   params      — object with: fractalType, maxIter, view, juliaC,
//                 paletteId, xfactorText, yfactorText, drawingMode
function writeNativeImg(raster, width, height, params) {
  const {
    fractalType,
    maxIter,
    view,
    juliaC,
    paletteId      = "0",
    xfactorText    = "1",
    yfactorText    = "1",
    drawingMode    = 0,
  } = params;

  // Build the text lines first so we know their byte lengths.
  // Each line is encoded as UTF-8 bytes followed by a newline (0x0A).
  const enc = new TextEncoder();
  function encodeLine(value) {
    const bytes = enc.encode(String(value));
    const line = new Uint8Array(bytes.length + 1);
    line.set(bytes);
    line[bytes.length] = 10; // newline
    return line;
  }

  const lines = [
    encodeLine(paletteId),
    encodeLine(maxIter),
    encodeLine(xfactorText),
    encodeLine(yfactorText),
    // Julia params — the original Java app wrote "0.0" for Mandelbrot
    encodeLine(fractalType === "julia" ? juliaC.re : "0.0"),
    encodeLine(fractalType === "julia" ? juliaC.im : "0.0"),
    encodeLine(view.xMin),
    encodeLine(view.xMax),
    encodeLine(view.yMin),
    encodeLine(view.yMax),
  ];
// eslint-disable-next-line
  const linesLength = lines.reduce((sum, l) => sum + l.length, 0);

  // Total buffer size:
  //   8 bytes  (sourceWidth + sourceHeight as int32 BE each)
  //   paletteId\n + maxIter\n + xfactor\n + yfactor\n  (4 lines before drawingMode)
  //   4 bytes  (drawingMode int32 BE)
  //   6 lines  (p1, q1, pmin, pmax, qmin, qmax)
  //   1 byte   (imgType)
  //   width × height × 4 bytes  (raster as int32 BE each)
  const firstFourLines = lines.slice(0, 4).reduce((s, l) => s + l.length, 0);
  const lastSixLines   = lines.slice(4).reduce((s, l) => s + l.length, 0);
  const totalSize = 8 + firstFourLines + 4 + lastSixLines + 1 + (width * height * 4);

  const buf = new ArrayBuffer(totalSize);
  const dv  = new DataView(buf);
  const u8  = new Uint8Array(buf);
  let offset = 0;

  // sourceWidth, sourceHeight
  dv.setInt32(offset, width,  false); offset += 4;
  dv.setInt32(offset, height, false); offset += 4;

  // First four text lines (paletteId, maxIter, xfactor, yfactor)
  for (const line of lines.slice(0, 4)) {
    u8.set(line, offset);
    offset += line.length;
  }

  // drawingMode
  dv.setInt32(offset, drawingMode, false); offset += 4;

  // Remaining six text lines (p1, q1, pmin, pmax, qmin, qmax)
  for (const line of lines.slice(4)) {
    u8.set(line, offset);
    offset += line.length;
  }

  // imgType byte: 74 = 'J' (Julia), 77 = 'M' (Mandelbrot)
  u8[offset] = fractalType === "julia" ? 74 : 77;
  offset += 1;

  // Raster pixels as big-endian int32
  for (let i = 0; i < raster.length; i++) {
    dv.setInt32(offset, raster[i], false);
    offset += 4;
  }

  return buf;
}

function fractalSmooth(cx, cy, maxIter, fractalType, juliaC) {
  let x;
  let y;
  let cRe;
  let cIm;

  if (fractalType === "julia") {
    x = cx;
    y = cy;
    cRe = juliaC.re;
    cIm = juliaC.im;
  } else {
    x = 0;
    y = 0;
    cRe = cx;
    cIm = cy;
  }

  let xx = x * x;
  let yy = y * y;
  let iter = 0;

  while (xx + yy <= ESCAPE_RADIUS_SQUARED && iter < maxIter) {
    y = 2 * x * y + cIm;
    x = xx - yy + cRe;
    xx = x * x;
    yy = y * y;
    iter += 1;
  }

  if (iter >= maxIter) {
    return { inside: true, smooth: maxIter, raw: 0 };
  }

  const modulus = Math.sqrt(xx + yy);
  // Standard smooth iteration count (continuous, avoids log2(log2) domain issues)
  const logLog = Math.log(Math.log(Math.max(modulus, 1.0001))) / Math.log(2);
  const smooth = Math.max(0, iter + 1 - logLog);

  let colorIndex = iter % MAXCOLRS;
  if (colorIndex === 0) colorIndex = 1;

  return { inside: false, smooth, raw: colorIndex };
}

function computeGradients(image, width, height) {
  const gradients = new Float32Array(width * height);

  for (let py = 1; py < height - 1; py += 1) {
    for (let px = 1; px < width - 1; px += 1) {
      const idx = py * width + px;
      const dx = image[idx + 1] - image[idx - 1];
      const dy = image[idx + width] - image[idx - width];
      gradients[idx] = Math.sqrt(dx * dx + dy * dy);
    }
  }

  let globalMaxGradient = 0;
  for (let i = 0; i < gradients.length; i += 1) {
    globalMaxGradient = Math.max(globalMaxGradient, gradients[i]);
  }

  return { gradients, globalMaxGradient };
}

function classifyPixel(imageValue, inside, gradient, maxIter, globalMaxGradient) {
  if (inside) return "inside";

  const gradientNorm = globalMaxGradient > 0 ? gradient / globalMaxGradient : 0;

  // High gradient = detail/boundary zone; high smooth value = near-boundary (barely escaped)
  if (gradientNorm > 0.18 || imageValue > 0.85 * maxIter) {
    return "boundary";
  }

  return "exterior";
}

// Compute per-cell statistics for the 15×10 navigation grid. Single pass
// over the image+interior arrays; returns one array of 150 cells indexed
// row-major (matching the focusable <div> ordering). Each cell entry has:
//   meanIter       — average iteration count over the cell's pixels
//   cellMaxIter    — single highest iter value in the cell
//   interiorFrac   — fraction of pixels that are inside the set
// Cost: one pass over WIDTH*HEIGHT pixels = ~0.4ms at 750×500 on modern
// hardware. Cheap enough to recompute on every fractalData change.
function buildGridStats(fractalData, width, height, maxIter, gridCols = 15, gridRows = 10) {
  if (!fractalData?.image || !fractalData?.interior) return null;
  const { image, interior } = fractalData;
  const cellW = width / gridCols;
  const cellH = height / gridRows;
  const total = gridCols * gridRows;
  const stats = new Array(total);
  for (let i = 0; i < total; i++) {
    stats[i] = { sum: 0, count: 0, maxV: 0, interiorCount: 0 };
  }
  for (let y = 0; y < height; y++) {
    const row = Math.min(gridRows - 1, Math.floor(y / cellH));
    for (let x = 0; x < width; x++) {
      const col = Math.min(gridCols - 1, Math.floor(x / cellW));
      const cellIdx = row * gridCols + col;
      const pxIdx = y * width + x;
      const v = image[pxIdx];
      const inside = interior[pxIdx] === 1;
      const s = stats[cellIdx];
      s.count++;
      if (inside) s.interiorCount++;
      else {
        s.sum += v;
        if (v > s.maxV) s.maxV = v;
      }
    }
  }
  // Finalize: replace accumulators with derived metrics.
  return stats.map(s => {
    const exteriorCount = s.count - s.interiorCount;
    return {
      meanIter: exteriorCount > 0 ? s.sum / exteriorCount : 0,
      cellMaxIter: s.maxV,
      interiorFrac: s.interiorCount / s.count,
    };
  });
}

// Build a compass-style description of where the detail lies relative to
// a focused cell. Compares the focused cell's cellMaxIter to its (up to 8)
// neighbours. A neighbour is "more interesting" if its cellMaxIter exceeds
// the focused cell's by a meaningful margin AND isn't dominated by interior.
// The margin scales with the focused cell's own maxIter so the threshold is
// proportional rather than absolute.
function describeCellBearing(stats, cellIdx, gridCols = 15, gridRows = 10, maxIter) {
  if (!stats) return null;
  const me = stats[cellIdx];
  if (!me) return null;
  const row = Math.floor(cellIdx / gridCols);
  const col = cellIdx % gridCols;

  // Self-classification first — the user wants to know what they're ON
  // before they hear which direction to go. Thresholds expressed as
  // fractions of the global maxIter so they scale across zoom levels:
  // at maxIter=500, "deep" means cellMaxIter > 250; at maxIter=8000,
  // "deep" means cellMaxIter > 4000.
  let selfLabel;
  if (me.interiorFrac > 0.95) selfLabel = "Solid interior";
  else if (me.interiorFrac > 0.5) selfLabel = "Half inside the set";
  else if (me.cellMaxIter > maxIter * 0.5) selfLabel = "Very interesting";
  else if (me.cellMaxIter > maxIter * 0.2) selfLabel = "Interesting";
  else if (me.cellMaxIter < 20) selfLabel = "Boring";
  else selfLabel = "Some detail";

  // Direction-of-detail: collect any neighbour whose cellMaxIter is
  // substantially higher AND isn't itself solid interior. "Substantially
  // higher" = at least 30% more iters than current cell, or 50+ iters
  // absolute if current cell is near-empty. The 8 bearings map to
  // compass-style names that are short and unambiguous.
  const BEARINGS = [
    { dr: -1, dc:  0, name: "north" },
    { dr: -1, dc:  1, name: "northeast" },
    { dr:  0, dc:  1, name: "east" },
    { dr:  1, dc:  1, name: "southeast" },
    { dr:  1, dc:  0, name: "south" },
    { dr:  1, dc: -1, name: "southwest" },
    { dr:  0, dc: -1, name: "west" },
    { dr: -1, dc: -1, name: "northwest" },
  ];
  const threshold = Math.max(50, me.cellMaxIter * 1.3);
  const interesting = [];
  for (const b of BEARINGS) {
    const nr = row + b.dr;
    const nc = col + b.dc;
    if (nr < 0 || nr >= gridRows || nc < 0 || nc >= gridCols) continue;
    const n = stats[nr * gridCols + nc];
    if (!n) continue;
    if (n.interiorFrac > 0.95) continue;  // solid interior isn't "detail to zoom into"
    if (n.cellMaxIter > threshold) {
      interesting.push({ name: b.name, score: n.cellMaxIter });
    }
  }
  // Sort by score descending so the most interesting direction comes first.
  interesting.sort((a, b) => b.score - a.score);

  if (interesting.length === 0) {
    return selfLabel + ".";
  }
  // Combine top 2-3 bearings into a single short phrase. More than 3
  // becomes "in all directions" because listing 4+ compass points is
  // verbose and not useful.
  if (interesting.length >= 5) {
    return `${selfLabel}. Detail in most directions.`;
  }
  const top = interesting.slice(0, 3).map(x => x.name);
  if (top.length === 1) return `${selfLabel}. Detail to the ${top[0]}.`;
  if (top.length === 2) return `${selfLabel}. Detail to the ${top[0]} and ${top[1]}.`;
  return `${selfLabel}. Detail to the ${top[0]}, ${top[1]}, and ${top[2]}.`;
}

function extractLineRegions(fractalData, linePos, width, height, maxIter, orientation = "vertical") {
  // orientation "vertical": scan a COLUMN — walk y 0..height at x=linePos, idx = y*width + linePos
  // orientation "horizontal": scan a ROW — walk x 0..width  at y=linePos, idx = linePos*width + x
  const isVertical = orientation !== "horizontal";
  const scanLen = isVertical ? height : width;       // how far the scan walks
  const idxAt = isVertical
    ? (i) => i * width + linePos
    : (i) => linePos * width + i;
  // Number of within-region samples to capture for audio glissando. The
  // synth uses these to schedule a pitch ramp through the region's
  // iteration values rather than playing a single flat tone derived from
  // the mean — without this, two regions with similar means sound
  // identical even when their internal gradients are opposite (e.g. a
  // region whose iter values rise top-to-bottom vs one that falls).
  // 8 captures typical band gradients well; regions shorter than 8 px get
  // one sample per pixel.
  const SAMPLE_COUNT = 8;

  const regions = [];
  const { image, interior, gradients, globalMaxGradient } = fractalData;

  let currentType = null;
  let startY = 0;
  let sumValue = 0;
  let count = 0;
  let allValues = [];

  const flushRegion = (yEndInclusive) => {
    // Pick SAMPLE_COUNT evenly-spaced values from allValues. For short
    // regions, that's effectively every pixel; for longer ones, we
    // downsample uniformly across the height.
    const N = Math.min(SAMPLE_COUNT, allValues.length);
    const samples = new Array(N);
    for (let i = 0; i < N; i += 1) {
      const t = N === 1 ? 0 : i / (N - 1);
      const k = Math.round(t * (allValues.length - 1));
      samples[i] = allValues[k];
    }
    regions.push({
      type: currentType,
      yStart: startY,
      yEnd: yEndInclusive,
      meanValue: sumValue / count, // kept for any callers still using it
      samples,
      height: count,
    });
  };

  for (let y = 0; y < scanLen; y += 1) {
    const idx = idxAt(y);
    const pixelType = classifyPixel(
      image[idx],
      interior[idx] === 1,
      gradients[idx],
      maxIter,
      globalMaxGradient
    );
    const v = image[idx];

    if (currentType === null) {
      currentType = pixelType;
      startY = y;
      sumValue = v;
      count = 1;
      allValues = [v];
      continue;
    }

    if (pixelType === currentType) {
      sumValue += v;
      count += 1;
      allValues.push(v);
    } else {
      flushRegion(y - 1);

      currentType = pixelType;
      startY = y;
      sumValue = v;
      count = 1;
      allValues = [v];
    }
  }

  if (currentType !== null) {
    flushRegion(scanLen - 1);
  }

  // Coalesce spurious short regions. A 1-3 pixel "boundary" run between
  // two large exterior runs is almost always a gradient spike at a
  // color-ring crossing, not a meaningful structural feature — and it
  // makes the audio scan produce multiple notes where the user expects
  // one. The fix is a post-pass that merges any region shorter than
  // MIN_REGION_HEIGHT into the adjacent region with the same type if
  // possible, or into whichever neighbour is larger if both neighbours
  // differ. 8 pixels is 1.6% of column height at HEIGHT=500 — well below
  // the threshold where the user could perceive a distinct audio event
  // at typical column speeds (200ms-1s per column).
  const MIN_REGION_HEIGHT = 8;
  let i = 0;
  while (i < regions.length) {
    if (regions[i].height >= MIN_REGION_HEIGHT) { i += 1; continue; }
    const prev = i > 0 ? regions[i - 1] : null;
    const next = i + 1 < regions.length ? regions[i + 1] : null;
    // Boundary case: tiny region at the very top or bottom of the column.
    // No neighbour on one side — just merge into whichever side exists.
    let mergeWith = null;
    if (prev && next) {
      // Prefer merging into a same-type neighbour. If both differ, merge
      // into the larger one — its statistics are more authoritative and
      // its glide samples carry more information than the short region's.
      if (prev.type === regions[i].type) mergeWith = prev;
      else if (next.type === regions[i].type) mergeWith = next;
      else mergeWith = prev.height >= next.height ? prev : next;
    } else {
      mergeWith = prev || next;
    }
    if (!mergeWith) { i += 1; continue; }
    // Extend mergeWith to include the short region's span and accumulate
    // its iter values into the samples array. Then drop the short region.
    if (mergeWith === prev) {
      mergeWith.yEnd = regions[i].yEnd;
    } else {
      mergeWith.yStart = regions[i].yStart;
    }
    mergeWith.height += regions[i].height;
    // Re-derive samples by interleaving the two source samples arrays in
    // order. Order matters: yStart-to-yEnd direction must be preserved
    // so the audio glissando matches the visual gradient.
    const combined = mergeWith === prev
      ? [...mergeWith.samples, ...regions[i].samples]
      : [...regions[i].samples, ...mergeWith.samples];
    const N = Math.min(SAMPLE_COUNT, combined.length);
    mergeWith.samples = new Array(N).fill(0).map((_, k) => {
      const t = N === 1 ? 0 : k / (N - 1);
      return combined[Math.round(t * (combined.length - 1))];
    });
    // meanValue recompute (still kept for legacy callers).
    mergeWith.meanValue =
      mergeWith.samples.reduce((s, v) => s + v, 0) / mergeWith.samples.length;
    regions.splice(i, 1);
    // Don't advance i — the now-merged region may itself need re-checking
    // against ITS neighbour if it was already short.
  }

  // Second pass: coalesce adjacent same-type regions. After the short-
  // region merge above, a column that originally had three regions
  // exterior|tiny-boundary|exterior collapses to exterior|exterior — two
  // adjacent same-type regions that should actually be one. Without this
  // pass, the audio plays them as two separate notes (same pitch, but
  // with an envelope re-attack between them, producing an audible click).
  let j = 0;
  while (j < regions.length - 1) {
    const a = regions[j], b = regions[j + 1];
    if (a.type !== b.type) { j += 1; continue; }
    // Merge b into a.
    a.yEnd = b.yEnd;
    a.height += b.height;
    const combined = [...a.samples, ...b.samples];
    const N = Math.min(SAMPLE_COUNT, combined.length);
    a.samples = new Array(N).fill(0).map((_, k) => {
      const t = N === 1 ? 0 : k / (N - 1);
      return combined[Math.round(t * (combined.length - 1))];
    });
    a.meanValue = a.samples.reduce((s, v) => s + v, 0) / a.samples.length;
    regions.splice(j + 1, 1);
    // Stay at j — there might be a third same-type region beyond.
  }

  return regions;
}

// Back-compat wrapper: existing callers scan a vertical column.
function extractColumnRegions(fractalData, column, width, height, maxIter) {
  return extractLineRegions(fractalData, column, width, height, maxIter, "vertical");
}

function renderFormulaFractal(
  width,
  height,
  maxIter,
  view,
  fractalType,
  juliaC,
  paletteName,
  palettes,
  usePaletteBank,
  fullColors,
  aa = 1
) {
  const image = new Float32Array(width * height);
  const rawIndex = new Uint32Array(width * height);
  const interior = new Uint8Array(width * height);
  const colors = new Uint8ClampedArray(width * height * 3);
  const palette = (palettes[paletteName]?.stops ? palettes[paletteName] : null) || DEFAULT_PALETTES.classic;

  // Anti-aliasing via supersampling: S samples per axis (S*S per output pixel).
  // S===1 is the original single-sample path, left byte-for-byte unchanged so
  // existing renders and exact preset coordinates never shift. S>1 samples a
  // finer grid that spans the SAME [min,max] window (so the framing is
  // identical) and box-averages each S*S block down to one output pixel.
  const S = Math.max(1, Math.min(4, aa | 0));

  if (S === 1) {
    for (let py = 0; py < height; py += 1) {
      const cy = view.yMax - (py / (height - 1)) * (view.yMax - view.yMin);

      for (let px = 0; px < width; px += 1) {
        const cx = view.xMin + (px / (width - 1)) * (view.xMax - view.xMin);
        const result = fractalSmooth(cx, cy, maxIter, fractalType, juliaC);
        const idx = py * width + px;
        image[idx] = result.inside ? maxIter : result.smooth;
        rawIndex[idx] = result.inside ? 0 : result.raw;
        interior[idx] = result.inside ? 1 : 0;
      }
    }

    for (let i = 0; i < width * height; i += 1) {
      let rgb;

      if (interior[i] === 1) {
        rgb = { r: 0, g: 0, b: 0 };
      } else if (fullColors) {
        // rawIndex is iter % MAXCOLRS — a direct flat index into the 65536-entry palette
        const safeIndex = Math.max(0, Math.min(MAXCOLRS - 1, rawIndex[i]));
        const c = safeIndex * 3;
        rgb = {
          r: fullColors[c],
          g: fullColors[c + 1],
          b: fullColors[c + 2],
        };
      } else {
        // Cyclic coloring: repeating color bands reveal structure at all zoom levels
        const t = (image[i] % PALETTE_CYCLE) / PALETTE_CYCLE;
        rgb = samplePalette(palette.stops, t);
      }

      const base = i * 3;
      colors[base] = rgb.r;
      colors[base + 1] = rgb.g;
      colors[base + 2] = rgb.b;
    }
  } else {
    // ── Supersampled path ──────────────────────────────────────────────────
    // Fine grid is width*S × height*S over the same view rectangle, so when
    // S===1 the fx/(SW-1) mapping reduces exactly to the single-sample mapping
    // above. Each subsample is colored individually and the colors are averaged
    // — averaging the final RGB (not the iteration counts) is what removes the
    // salt-and-pepper aliasing along high-frequency boundaries.
    const SW = width * S, SH = height * S;
    const fxDen = (SW - 1) || 1;
    const fyDen = (SH - 1) || 1;
    const dx = view.xMax - view.xMin;
    const dy = view.yMax - view.yMin;
    const n = S * S;

    for (let py = 0; py < height; py += 1) {
      for (let px = 0; px < width; px += 1) {
        let rAcc = 0, gAcc = 0, bAcc = 0, svAcc = 0;
        // Representative subsample (top-left of the block) defines the display-
        // resolution rawIndex/interior, which the color editor and orbit probe
        // read. For S===1 this is exactly the original per-pixel sample.
        let repRaw = 0, repInside = 0;

        for (let sy = 0; sy < S; sy += 1) {
          const cy = view.yMax - ((py * S + sy) / fyDen) * dy;
          for (let sx = 0; sx < S; sx += 1) {
            const cx = view.xMin + ((px * S + sx) / fxDen) * dx;
            const result = fractalSmooth(cx, cy, maxIter, fractalType, juliaC);
            const inside = result.inside ? 1 : 0;
            const sv = result.inside ? maxIter : result.smooth;
            const raw = result.inside ? 0 : result.raw;
            if (sx === 0 && sy === 0) { repRaw = raw; repInside = inside; }
            svAcc += sv;

            let cr, cg, cb;
            if (inside) {
              cr = 0; cg = 0; cb = 0;
            } else if (fullColors) {
              const c = Math.max(0, Math.min(MAXCOLRS - 1, raw)) * 3;
              cr = fullColors[c]; cg = fullColors[c + 1]; cb = fullColors[c + 2];
            } else {
              const t = (sv % PALETTE_CYCLE) / PALETTE_CYCLE;
              const rgb = samplePalette(palette.stops, t);
              cr = rgb.r; cg = rgb.g; cb = rgb.b;
            }
            rAcc += cr; gAcc += cg; bAcc += cb;
          }
        }

        const oi = py * width + px;
        image[oi] = repInside ? maxIter : (svAcc / n);
        rawIndex[oi] = repRaw;
        interior[oi] = repInside;
        const base = oi * 3;
        colors[base] = rAcc / n;       // Uint8ClampedArray rounds + clamps
        colors[base + 1] = gAcc / n;
        colors[base + 2] = bAcc / n;
      }
    }
  }

  const { gradients, globalMaxGradient } = computeGradients(image, width, height);
  const columnStats = buildColumnStats(image, interior, gradients, colors, width, height);

  return {
    image,
    interior,
    gradients,
    colors,
    rawIndex,
    columnStats,
    globalMaxGradient,
  };
}

function resampleRasterToDisplay(srcRaster, srcWidth, srcHeight, dstWidth, dstHeight) {
  const dst = new Uint32Array(dstWidth * dstHeight);

  for (let y = 0; y < dstHeight; y += 1) {
    const sy = Math.min(srcHeight - 1, Math.floor((y * srcHeight) / dstHeight));
    for (let x = 0; x < dstWidth; x += 1) {
      const sx = Math.min(srcWidth - 1, Math.floor((x * srcWidth) / dstWidth));
      dst[y * dstWidth + x] = srcRaster[sy * srcWidth + sx];
    }
  }

  return dst;
}

function renderImportedRaster(
  width,
  height,
  importedRaster,
  importedWidth,
  importedHeight,
  maxIter,
  paletteName,
  palettes,
  usePaletteBank,
  fullColors
) {
  const raster = resampleRasterToDisplay(
    importedRaster,
    importedWidth,
    importedHeight,
    width,
    height
  );

  const image = new Float32Array(width * height);
  const interior = new Uint8Array(width * height);
  const colors = new Uint8ClampedArray(width * height * 3);
  const palette = (palettes[paletteName]?.stops ? palettes[paletteName] : null) || DEFAULT_PALETTES.classic;

  for (let i = 0; i < raster.length; i += 1) {
    image[i] = raster[i];
    interior[i] = raster[i] === 0 ? 1 : 0;
  }

  const { gradients, globalMaxGradient } = computeGradients(image, width, height);

  for (let i = 0; i < width * height; i += 1) {
    let rgb;

    if (interior[i] === 1) {
      rgb = { r: 0, g: 0, b: 0 };
    } else if (fullColors) {
      // raster[i] is the original currcolr value — a direct flat index into the 65536-entry palette
      const safeIndex = Math.max(0, Math.min(MAXCOLRS - 1, raster[i]));
      const c = safeIndex * 3;
      rgb = {
        r: fullColors[c],
        g: fullColors[c + 1],
        b: fullColors[c + 2],
      };
    } else {
      // Cyclic coloring: use raster value mod PALETTE_CYCLE for built-in palettes
      const t = (raster[i] % PALETTE_CYCLE) / PALETTE_CYCLE;
      rgb = samplePalette(palette.stops, t);
    }

    const base = i * 3;
    colors[base] = rgb.r;
    colors[base + 1] = rgb.g;
    colors[base + 2] = rgb.b;
  }

  const columnStats = buildColumnStats(image, interior, gradients, colors, width, height);

  return {
    image,
    interior,
    gradients,
    colors,
    rawIndex: raster,   // per-display-pixel palette indices, so the Color
                        // Editor can recolor its preview live as sliders move
    columnStats,
    globalMaxGradient,
  };
}

function buildColumnStats(image, interior, gradients, colors, width, height) {
  const columnStats = [];

  for (let x = 0; x < width; x += 1) {
    let sum = 0;
    let sumSq = 0;
    let edgeCrossings = 0;
    let insideCount = 0;
    let strongestGradient = 0;
    let sumR = 0;
    let sumG = 0;
    let sumB = 0;

    for (let y = 0; y < height; y += 1) {
      const idx = y * width + x;
      const v = image[idx];
      const g = gradients[idx];
      const inside = interior[idx] === 1;

      sum += v;
      sumSq += v * v;
      if (inside) insideCount += 1;
      if (g > strongestGradient) strongestGradient = g;

      const base = idx * 3;
      sumR += colors[base];
      sumG += colors[base + 1];
      sumB += colors[base + 2];

      if (y > 0) {
        const prevIdx = (y - 1) * width + x;
        if (interior[idx] !== interior[prevIdx]) {
          edgeCrossings += 1;
        }
      }
    }

    const mean = sum / height;
    const variance = Math.max(sumSq / height - mean * mean, 0);
    const stdDev = Math.sqrt(variance);

    const avgR = sumR / height;
    const avgG = sumG / height;
    const avgB = sumB / height;
    const hsv = rgbToHsv(avgR, avgG, avgB);

    columnStats.push({
      mean,
      stdDev,
      edgeCrossings,
      strongestGradient,
      insideRatio: insideCount / height,
      avgColor: { r: avgR, g: avgG, b: avgB },
      avgHue: hsv.h,
      avgSaturation: hsv.s,
      avgBrightness: hsv.v,
    });
  }

  return columnStats;
}

function drawFractal(canvas, width, height, data, playheadPos, orbitPath = [], playheadOrientation = "vertical", movementBounds = [], contourMarker = null) {
  if (!canvas || !data?.colors) return; // guard against calls during re-render transitions
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Create an offscreen canvas at logical size to build the pixel data,
  // then scale it up to the physical canvas.  This gives crisp integer-scaled
  // pixels on HiDPI screens without needing to compute DPR times more pixels.
  const offscreen = document.createElement("canvas");
  offscreen.width  = width;
  offscreen.height = height;
  const offCtx = offscreen.getContext("2d");
  const img = offCtx.createImageData(width, height);

  for (let i = 0; i < width * height; i += 1) {
    const baseColor = i * 3;
    const baseImg   = i * 4;
    img.data[baseImg]     = data.colors[baseColor];
    img.data[baseImg + 1] = data.colors[baseColor + 1];
    img.data[baseImg + 2] = data.colors[baseColor + 2];
    img.data[baseImg + 3] = 255;
  }
  offCtx.putImageData(img, 0, 0);

  // Draw overlays (playhead, orbit dots) onto the offscreen canvas at logical
  // coordinates, then scale the whole thing up to the physical canvas in one
  // drawImage call with smoothing disabled — this preserves crisp pixel edges.
  if (typeof playheadPos === "number") {
    offCtx.save();
    offCtx.strokeStyle = "rgba(255,255,255,0.95)";
    offCtx.lineWidth = 2;
    offCtx.beginPath();
    if (playheadOrientation === "horizontal") {
      offCtx.moveTo(0, playheadPos + 0.5);
      offCtx.lineTo(width, playheadPos + 0.5);
    } else {
      offCtx.moveTo(playheadPos + 0.5, 0);
      offCtx.lineTo(playheadPos + 0.5, height);
    }
    offCtx.stroke();
    offCtx.restore();
  }
  // Contour glide marker: a bright dot at the current point on the traced
  // contour, synced to the gliding pitch, so eye and ear follow together.
  if (contourMarker && typeof contourMarker.x === "number") {
    offCtx.save();
    offCtx.beginPath();
    offCtx.arc(contourMarker.x + 0.5, contourMarker.y + 0.5, 4, 0, 2 * Math.PI);
    offCtx.fillStyle = "rgba(255,0,200,0.95)";
    offCtx.fill();
    offCtx.lineWidth = 1.5;
    offCtx.strokeStyle = "rgba(0,0,0,0.85)";
    offCtx.stroke();
    offCtx.restore();
  }
  // Movement boundary markers: small filled amber triangles at the canvas edge,
  // pointing up (vertical scan, from the bottom) or right (horizontal scan, from
  // the left), at each internal movement start. Small, distinct, unobtrusive.
  if (movementBounds && movementBounds.length) {
    offCtx.save();
    offCtx.fillStyle = "rgba(255,176,0,0.95)";
    var _h = 7;   // triangle height (px)
    var _w = 5;   // half-width of the base (px)
    movementBounds.forEach((b) => {
      if (typeof b !== "number") return;
      offCtx.beginPath();
      if (playheadOrientation === "horizontal") {
        // boundary row b: triangle on the LEFT edge, apex pointing right
        offCtx.moveTo(0, b + 0.5 - _w);
        offCtx.lineTo(0, b + 0.5 + _w);
        offCtx.lineTo(_h, b + 0.5);
      } else {
        // boundary column b: triangle on the BOTTOM edge, apex pointing up
        offCtx.moveTo(b + 0.5 - _w, height);
        offCtx.lineTo(b + 0.5 + _w, height);
        offCtx.lineTo(b + 0.5, height - _h);
      }
      offCtx.closePath();
      offCtx.fill();
    });
    offCtx.restore();
  }

  if (orbitPath.length > 0) {
    offCtx.save();
    offCtx.fillStyle = "white";
    orbitPath.forEach((pt) => {
      offCtx.fillRect(Math.round(pt.x - 1), Math.round(pt.y - 1), 3, 3);
    });
    offCtx.restore();
  }

  // Scale up to physical canvas size without blurring
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(offscreen, 0, 0, canvas.width, canvas.height);
}

function createSynthGraph(audioContext) {
  const master = audioContext.createGain();
  master.gain.value = 1.0;
  master.connect(audioContext.destination);

  const filter = audioContext.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 1000;
  filter.Q.value = 1.0;

  const panner = audioContext.createStereoPanner();

  filter.connect(panner);
  panner.connect(master);

  const osc1 = audioContext.createOscillator();
  const osc2 = audioContext.createOscillator();
  const drone = audioContext.createOscillator();

  osc1.type = "sine";
  osc2.type = "triangle";
  drone.type = "sawtooth";

  const gain1 = audioContext.createGain();
  const gain2 = audioContext.createGain();
  const droneGain = audioContext.createGain();

  gain1.gain.value = 0;
  gain2.gain.value = 0;
  droneGain.gain.value = 0;

  osc1.connect(gain1);
  osc2.connect(gain2);
  drone.connect(droneGain);

  gain1.connect(filter);
  gain2.connect(filter);
  droneGain.connect(filter);

  osc1.start();
  osc2.start();
  drone.start();

  return {
    audioContext,
    master,
    filter,
    panner,
    osc1,
    osc2,
    drone,
    gain1,
    gain2,
    droneGain,
  };
}

function silenceSynth(synth) {
  const t = synth.audioContext.currentTime;
  synth.gain1.gain.cancelScheduledValues(t);
  synth.gain2.gain.cancelScheduledValues(t);
  synth.droneGain.gain.cancelScheduledValues(t);

  // Set current value explicitly before ramping — cancelScheduledValues doesn't set the value
  synth.gain1.gain.setValueAtTime(synth.gain1.gain.value, t);
  synth.gain2.gain.setValueAtTime(synth.gain2.gain.value, t);
  synth.droneGain.gain.setValueAtTime(synth.droneGain.gain.value, t);

  synth.gain1.gain.setTargetAtTime(0, t, 0.03);
  synth.gain2.gain.setTargetAtTime(0, t, 0.03);
  synth.droneGain.gain.setTargetAtTime(0, t, 0.05);
}

function MetricCard({ label, value }) {
  return (
    <div
      style={{
        background: "#020617",
        border: "1px solid #1f2937",
        borderRadius: "12px",
        padding: "14px",
      }}
    >
      <div
        style={{
          fontSize: "11px",
          color: "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          marginBottom: "8px",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "22px", color: "#f8fafc" }}>{value}</div>
    </div>
  );
}

function parseNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

// ── Persistence layer ─────────────────────────────────────────────────────────
//
// Strategy:
//   • Small JSON-serialisable state  → localStorage  (synchronous, convenient
//     for useState initialisers on first render)
//   • Large binary data              → IndexedDB     (no size limit, async)
//       - "paletteBank"  : the full Uint8Array of palette colours AS LAST EDITED
//                          (not the original imported file — so a day's worth of
//                          colour editing is preserved exactly)
//       - "paletteMeta"  : JSON array of per-page controls (slider values etc.)
//       - "editedColors" : the editedFullColors Uint8Array if active
//       - "raster"       : the imported .img raster Uint32Array
//
// IndexedDB is opened once.  Writes are fire-and-forget (we don't block the UI
// on them).  Reads happen once on startup inside a useEffect.

const LS_STATE_KEY = "fractalMusicState";

// localStorage helpers — only used for the small JSON blob.
function lsSet(key, value) {
  try { localStorage.setItem(key, value); } catch (_) {}
}
function lsGet(key) {
  try { return localStorage.getItem(key); } catch (_) { return null; }
}
function saveJsonState(_state) {
  // Session persistence is disabled — the app always opens from scratch, so we
  // never write session parameters (view, palette selection, editor sliders,
  // imported image, etc.) to storage.
}
function loadSavedState() {
  // Session persistence is disabled — return no saved state so every visit
  // starts identically. Also clear any blob written by an earlier build so it
  // can't resurface and reintroduce the old "restored last session" behaviour.
  try { lsSet(LS_STATE_KEY, null); } catch (_) {}
  return null;
}

// Open (or create) the IndexedDB database.  Returns a Promise<IDBDatabase>.
// The object store uses string keys so we can store multiple named blobs.
function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open("fractalMusic", 1);
    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore("blobs");
    };
    req.onsuccess  = (e) => resolve(e.target.result);
    req.onerror    = ()  => reject(req.error);
  });
}

// Write an ArrayBuffer (or Uint8Array) under `key`.  Fire-and-forget.
async function idbSet(key, data) {
  try {
    const db = await openDB();
    const tx = db.transaction("blobs", "readwrite");
    tx.objectStore("blobs").put(data instanceof Uint8Array ? data.buffer : data, key);
  } catch (_) {}
}

// Write a plain JS value (string, object, etc.) serialised to JSON under `key`.
async function idbSetJson(key, value) {
  await idbSet(key, new TextEncoder().encode(JSON.stringify(value)).buffer);
}

// Read an ArrayBuffer from IndexedDB.  Returns null if absent.
async function idbGet(key) {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction("blobs", "readonly");
      const req = tx.objectStore("blobs").get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror   = () => reject(req.error);
    });
  } catch (_) { return null; }
}

// Read a JSON-encoded value from IndexedDB.  Returns null if absent.
async function idbGetJson(key) {
  try {
    const buf = await idbGet(key);
    if (!buf) return null;
    return JSON.parse(new TextDecoder().decode(buf));
  } catch (_) { return null; }
}

// Delete a key from IndexedDB.
async function idbDelete(key) {
  try {
    const db = await openDB();
    const tx = db.transaction("blobs", "readwrite");
    tx.objectStore("blobs").delete(key);
  } catch (_) {}
}

// List all keys in IndexedDB that start with a given prefix.
async function idbKeys(prefix = "") {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction("blobs", "readonly");
      const req = tx.objectStore("blobs").getAllKeys();
      req.onsuccess = () => resolve((req.result || []).filter(k => k.startsWith(prefix)));
      req.onerror   = () => reject(req.error);
    });
  } catch (_) { return []; }
}

// ── Firebase Storage helpers ──────────────────────────────────────────────────

// List all files inside the "palettes/" folder in Firebase Storage.
// Returns an array of objects: { name, fullPath, downloadUrl }
// Results are cached in memory so repeated modal opens don't re-fetch.
let _libraryCache = null;
async function fetchLibraryFiles() {
  if (_libraryCache && _libraryCache.length > 0) return _libraryCache;
  const url = `${STORAGE_BASE}?prefix=palettes%2F&delimiter=%2F`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Firebase Storage listing failed: ${res.status}`);
  const json = await res.json();
  const items = (json.items || []).map(item => {
    const encoded = encodeURIComponent(item.name);
    return {
      name: item.name.replace("palettes/", ""),
      fullPath: item.name,
      downloadUrl: `${STORAGE_BASE}/${encoded}?alt=media`,
    };
  });
  _libraryCache = items;
  return items;
}

// Download a file from Firebase Storage as an ArrayBuffer.
async function downloadLibraryFile(downloadUrl) {
  const res = await fetch(downloadUrl);
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  return res.arrayBuffer();
}
// List curated example compositions in the "compositions/" folder of Firebase
// Storage. Mirrors fetchLibraryFiles() but for .fmc.json composition files.
// Returns [{ name, fullPath, downloadUrl }]. Cached in memory.
let _compositionCache = null;
async function fetchCompositionFiles() {
  if (_compositionCache && _compositionCache.length > 0) return _compositionCache;
  const url = `${STORAGE_BASE}?prefix=compositions%2F&delimiter=%2F`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Composition listing failed: ${res.status}`);
  const json = await res.json();
  const items = (json.items || [])
    .filter(item => item.name && item.name !== "compositions/")
    .map(item => {
      const encoded = encodeURIComponent(item.name);
      return {
        name: item.name.replace("compositions/", ""),
        fullPath: item.name,
        downloadUrl: `${STORAGE_BASE}/${encoded}?alt=media`,
      };
    });
  _compositionCache = items;
  return items;
}

// Upload a file to the "palettes/" folder in Firebase Storage. Same name
// overwrites the existing file. Throws on failure (caller decides whether
// to swallow). Invalidates the in-memory library cache so the next
// fetchLibraryFiles() call returns fresh data including the new file.
//
// Requires Firebase Storage rules to allow unauthenticated writes to the
// palettes/ path. If your rules are read-only, calls will 403 — catch and
// degrade gracefully.
async function uploadLibraryFile(name, arrayBuffer) {
  const fullPath = `palettes/${name}`;
  const url = `${STORAGE_BASE}?uploadType=media&name=${encodeURIComponent(fullPath)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: arrayBuffer,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Upload failed: ${res.status}${detail ? ' — ' + detail.slice(0, 200) : ''}`);
  }
  _libraryCache = null;
  return res.json();
}

// ── ImportModal component ─────────────────────────────────────────────────────
// Shows a two-tab modal: "From library" lists files from Firebase Storage,
// "From local file" triggers the existing file picker.
// Props:
//   type        — "palette" | "img"  (controls which files are shown)
//   onClose     — called when the modal is dismissed
//   onLocalFile — called when the user wants to pick a local file
//   onImport    — called with an ArrayBuffer when a library file is selected
function ImportModal({ type, onClose, onLocalFile, onImport, onSpeak, cachedNames }) {
  const [tab, setTab] = useState("library"); // "library" | "local"
  const [files, setFiles] = useState(null);  // null = loading, [] = loaded
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const modalRef = React.useRef(null);

  // Focus the first focusable element ONCE when the modal mounts.
  // Don't depend on onClose (which is recreated each parent render) — that
  // would re-run this on every render and yank focus back to the first
  // element, breaking voice "tab forward" navigation.
  React.useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    const focusable = Array.from(el.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    if (focusable[0]) focusable[0].focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Trap focus inside the modal — handles Tab cycling at edges and Escape.
  React.useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    const getFocusable = () => Array.from(el.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    function onKeyDown(e) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = getFocusable();
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  async function handleBatchImport() {
    if (selected.size === 0) return;
    const toImport = files.filter(f => selected.has(f.name));
    for (const file of toImport) {
      setDownloading(file.name);
      try {
        const buf = await downloadLibraryFile(file.downloadUrl);
        await onImport(buf, file.name);
        // Small delay to let React process the state update before next import
        await new Promise(res => setTimeout(res, 100));
      }
      catch (err) { setError(`Failed: ${err.message}`); break; }
    }
    setDownloading(null); onClose();
  }
  function toggleSelect(name) { setSelected(prev => { const n=new Set(prev); n.has(name)?n.delete(name):n.add(name); return n; }); }
  function toggleSelectAll() { if (!files) return; setSelected(prev => prev.size===files.length ? new Set() : new Set(files.map(f=>f.name))); }
  
  // Fetch the file listing when the modal first opens.
  useEffect(() => {
    fetchLibraryFiles()
      .then(all => {
        // Filter by type: palette files have no .img extension,
        // .img files end in .img.
        const filtered = type === "img"
          ? all.filter(f => f.name.toLowerCase().endsWith(".img"))
          : all.filter(f => !f.name.toLowerCase().endsWith(".img"));
        setFiles(filtered);
      })
      .catch(err => setError(err.message));
  }, [type]);

  async function handleLibrarySelect(file) {
    setDownloading(file.name);
    try {
      const buf = await downloadLibraryFile(file.downloadUrl);
      onImport(buf, file.name);
      onClose();
    } catch (err) {
      setError(`Failed to download ${file.name}: ${err.message}`);
    } finally {
      setDownloading(null);
    }
  }

  async function handleLoadFromCache(file) {
    setDownloading(file.name);
    try {
      const buf = await idbGet(`paletteBank:${file.name}`);
      if (!buf) {
        // Cache miss despite cachedNames saying it's there — fall back to Firebase.
        return handleLibrarySelect(file);
      }
      onImport(buf, file.name);
      onClose();
    } catch (err) {
      setError(`Failed to load ${file.name} from cache: ${err.message}`);
    } finally {
      setDownloading(null);
    }
  }

  async function handleRefresh(file) {
    setDownloading(file.name);
    try {
      const buf = await downloadLibraryFile(file.downloadUrl);
      // onImport calls importPaletteFromBuffer which caches to IDB, overwriting
      // any previously-cached copy under the same filename. That's exactly the
      // "refresh from library" semantic.
      onImport(buf, file.name);
      onClose();
    } catch (err) {
      setError(`Failed to refresh ${file.name}: ${err.message}`);
    } finally {
      setDownloading(null);
    }
  }

  const overlayStyle = {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
  };
  const modalStyle = {
    background: "#0f172a",
    border: "1px solid #334155",
    borderRadius: "14px",
    padding: "24px",
    width: "480px",
    maxWidth: "92vw",
    maxHeight: "70vh",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  };
  const tabBtnStyle = (active) => ({
    padding: "8px 18px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: active ? 700 : 400,
    background: active ? "#38bdf8" : "#1e293b",
    color: active ? "#082f49" : "#94a3b8",
  });
  const fileRowStyle = {
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid #1e293b",
    cursor: "pointer",
    fontSize: "14px",
    color: "#e2e8f0",
    background: "#0f172a",
    textAlign: "left",
    width: "100%",
  };

  const speak = (text) => onSpeak && onSpeak(text);

  return (
    <div style={overlayStyle} onClick={onClose} role="dialog" aria-modal="true" aria-label={type === "img" ? "Import IMG file" : "Import palette bank"}>
      <div style={modalStyle} onClick={e => e.stopPropagation()} ref={modalRef}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: "16px", fontWeight: 700, color: "#f8fafc" }}>
            Import {type === "img" ? ".img file" : "palette bank"}
          </div>
          <button onClick={onClose} onFocus={() => speak("Close dialog")} style={{ background: "none", border: "none", color: "#64748b", fontSize: "20px", cursor: "pointer", lineHeight: 1 }} aria-label="Close dialog">×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "8px" }}>
          <button style={tabBtnStyle(tab === "library")} onClick={() => setTab("library")} onFocus={() => speak("From library")}>
            From library
          </button>
          <button style={tabBtnStyle(tab === "local")} onClick={() => setTab("local")} onFocus={() => speak("From local file")}>
            From local file
          </button>
        </div>

        {/* Tab content */}
        {tab === "library" ? (
          <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                style={{ background: "none", border: "none", color: "#38bdf8", fontSize: "12px", cursor: "pointer", padding: 0 }}
                onFocus={() => speak("Refresh library")}
                onClick={() => { _libraryCache = null; setFiles(null); setError(null);
                  fetchLibraryFiles()
                    .then(all => {
                      const filtered = type === "img"
                        ? all.filter(f => f.name.toLowerCase().endsWith(".img"))
                        : all.filter(f => !f.name.toLowerCase().endsWith(".img"));
                      setFiles(filtered);
                    })
                    .catch(err => setError(err.message));
                }}
              >
                ↻ Refresh library
              </button>
            </div>
            {error && (
              <div style={{ color: "#f87171", fontSize: "13px" }}>{error}</div>
            )}
            {!files && !error && (
              <div style={{ color: "#94a3b8", fontSize: "14px" }}>Loading library…</div>
            )}
            {files && files.length === 0 && (
              <div style={{ color: "#94a3b8", fontSize: "14px" }}>
                No {type === "img" ? ".img" : "palette"} files in the library yet.
              </div>
            )}
            {files && files.length > 0 && type !== "img" && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <button style={{ background: "none", border: "none", color: "#94a3b8", fontSize: "12px", cursor: "pointer", padding: 0 }} onClick={toggleSelectAll} onFocus={() => speak(selected.size === files.length ? "Deselect all" : "Select all")}>
                  {selected.size === files.length ? "Deselect all" : "Select all"}
                </button>
                {selected.size > 0 && (
                  <button style={{ padding: "6px 14px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "13px", background: "#38bdf8", color: "#082f49", fontWeight: 700 }} onClick={handleBatchImport} onFocus={() => speak(downloading ? "Downloading" : `Import ${selected.size} selected`)} disabled={!!downloading}>
                    {downloading ? `Downloading…` : `Import ${selected.size} selected`}
                  </button>
                )}
              </div>
            )}
            {files && files.map(file => {
              const isCached = type !== "img" && cachedNames && cachedNames.has(file.name);
              if (type === "img") {
                return (
                  <button key={file.fullPath} style={{ ...fileRowStyle, opacity: downloading === file.name ? 0.5 : 1 }} onClick={() => handleLibrarySelect(file)} disabled={!!downloading} aria-label={`Load ${file.name}`} onFocus={() => onSpeak && onSpeak(file.name)}>
                    {downloading === file.name ? `Downloading ${file.name}…` : file.name}
                  </button>
                );
              }
              return (
                <div key={file.fullPath} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${file.name}`}
                    checked={selected.has(file.name)}
                    onChange={() => toggleSelect(file.name)}
                    onFocus={() => onSpeak && onSpeak(`Checkbox for ${file.name}, ${selected.has(file.name) ? 'selected' : 'not selected'}. Press space to toggle.`)}
                    disabled={!!downloading}
                    style={{ cursor: "pointer", flexShrink: 0 }}
                  />
                  <button
                    style={{ ...fileRowStyle, opacity: downloading === file.name ? 0.5 : 1, flex: 1 }}
                    onClick={() => isCached ? handleLoadFromCache(file) : handleLibrarySelect(file)}
                    disabled={!!downloading}
                    aria-label={isCached ? `Load ${file.name} from local cache` : `Load ${file.name} from library`}
                    onFocus={() => onSpeak && onSpeak(isCached ? `${file.name}, cached locally. Press Enter to load.` : file.name)}
                  >
                    {downloading === file.name ? `Loading ${file.name}…` : (isCached ? `${file.name} (cached)` : file.name)}
                  </button>
                  {isCached && (
                    <button
                      style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid rgba(56, 189, 248, 0.4)", background: "transparent", color: "#cbd5e1", cursor: "pointer", fontSize: "13px", whiteSpace: "nowrap", opacity: downloading === file.name ? 0.5 : 1 }}
                      onClick={() => handleRefresh(file)}
                      disabled={!!downloading}
                      aria-label={`Refresh ${file.name} from library, overwriting local cached copy`}
                      onFocus={() => onSpeak && onSpeak(`Refresh ${file.name} from library. This overwrites your local copy.`)}
                    >
                      Refresh from library
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ color: "#94a3b8", fontSize: "14px" }}>
              Select a {type === "img" ? ".img" : "palette bank"} file from your computer.
            </div>
            <button
              style={{ padding: "10px 20px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "14px", background: "#38bdf8", color: "#082f49", fontWeight: 700 }}
              onClick={() => { onLocalFile(); onClose(); }}
              onFocus={() => speak("Choose file from computer")}
              aria-label="Choose file from computer"
            >
              Choose file…
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

// Accessible Help dialog — each topic is a focusable item that announces its
// content on focus.  Tab through topics, Escape to close.
function SetupDialog({ initialValue, onSave, onCancel }) {
  // Caregiver-facing accessibility configuration. Plain form controls, no
  // voice prompts and no focus-speech announcements — those would be
  // distracting while a sighted helper is configuring the app. The dialog
  // does not call any AT APIs itself; it just edits the settings blob.
  // The end-user-facing app reads the saved blob on startup and behaves
  // accordingly.
  const [value, setValue] = React.useState(initialValue);
  const update = (patch) => setValue(v => ({ ...v, ...patch }));

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Accessibility setup"
    >
      <div
        style={{
          background: "#0f172a", border: "1px solid #334155", borderRadius: "14px",
          padding: "32px", width: "560px", maxWidth: "92vw", maxHeight: "85vh",
          overflowY: "auto", color: "#cbd5e1", fontSize: "14px", lineHeight: 1.6,
        }}
      >
        <div style={{ fontSize: "20px", fontWeight: 600, color: "#f8fafc", marginBottom: "8px" }}>
          Accessibility setup
        </div>
        <div style={{ color: "#94a3b8", marginBottom: "20px", lineHeight: 1.5 }}>
          For caregivers, clinicians, or family members configuring the app on
          behalf of an end user. The end user will see only the features you
          enable here.
        </div>

        <fieldset style={{ border: "1px solid #1e293b", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
          <legend style={{ padding: "0 8px", color: "#cbd5e1", fontWeight: 600 }}>Voice control</legend>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer", marginBottom: "10px" }}>
            <input
              type="checkbox"
              checked={value.voiceAutoStart}
              onChange={(e) => update({ voiceAutoStart: e.target.checked })}
              style={{ marginTop: "3px" }}
            />
            <span>
              <strong>Auto-focus the microphone button</strong>
              <div style={{ color: "#94a3b8", fontSize: "13px" }}>
                When enabled, the microphone button receives focus on page load
                so the end user can start voice control with a single keypress.
                (Browser security prevents truly automatic activation — some user
                gesture is always required.)
              </div>
            </span>
          </label>
        </fieldset>

        <fieldset style={{ border: "1px solid #1e293b", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
          <legend style={{ padding: "0 8px", color: "#cbd5e1", fontWeight: 600 }}>Webcam-based eye tracking</legend>
          <div style={{ color: "#94a3b8", marginBottom: "12px", fontSize: "13px" }}>
            Enable only if the user does not already have a dedicated eye-tracker
            (Tobii, EyeTech, etc.). External hardware trackers work automatically
            as a mouse — no configuration needed in this app.
          </div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer", marginBottom: "12px" }}>
            <input
              type="checkbox"
              checked={value.eyeTrackingAutoStart}
              onChange={(e) => update({ eyeTrackingAutoStart: e.target.checked })}
              style={{ marginTop: "3px" }}
            />
            <span>
              <strong>Start webcam eye tracking automatically</strong>
              <div style={{ color: "#94a3b8", fontSize: "13px" }}>
                After voice activates, load WebGazer and run the 9-dot calibration.
                Help an end user who cannot speak by doing the calibration clicks
                yourself before handing the session over.
              </div>
            </span>
          </label>
          <label style={{ display: "block", marginTop: "8px" }}>
            <span style={{ display: "block", marginBottom: "4px", color: "#cbd5e1" }}>Dwell time before click</span>
            <select
              value={value.dwellMs}
              onChange={(e) => update({ dwellMs: parseInt(e.target.value, 10) })}
              style={{ background: "#020617", color: "#f8fafc", border: "1px solid #334155", borderRadius: "6px", padding: "6px 10px" }}
            >
              <option value={800}>800 ms (responsive)</option>
              <option value={1000}>1000 ms (default)</option>
              <option value={1500}>1500 ms (forgiving)</option>
              <option value={2000}>2000 ms (very forgiving)</option>
            </select>
          </label>
        </fieldset>

        <fieldset style={{ border: "1px solid #1e293b", borderRadius: "8px", padding: "16px", marginBottom: "20px" }}>
          <legend style={{ padding: "0 8px", color: "#cbd5e1", fontWeight: 600 }}>Interface</legend>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={value.minimalToolbar}
              onChange={(e) => update({ minimalToolbar: e.target.checked })}
              style={{ marginTop: "3px" }}
            />
            <span>
              <strong>Minimal toolbar</strong>
              <div style={{ color: "#94a3b8", fontSize: "13px" }}>
                Hide the toolbar buttons (Color Editor, Help, Eye, Microphone).
                Voice commands still work for everything; reduces visual clutter
                for users who rely entirely on voice or eye tracking.
              </div>
            </span>
          </label>
        </fieldset>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button
            onClick={onCancel}
            style={{ background: "#1e293b", border: "1px solid #334155", color: "#f8fafc", fontSize: "14px", padding: "8px 16px", borderRadius: "6px", cursor: "pointer" }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ ...value, configured: true })}
            style={{ background: "#0ea5e9", border: "1px solid #0284c7", color: "white", fontSize: "14px", padding: "8px 20px", borderRadius: "6px", cursor: "pointer", fontWeight: 600 }}
          >
            Done
          </button>
        </div>

        <div style={{ color: "#475569", fontSize: "12px", marginTop: "16px", textAlign: "center" }}>
          Re-open this dialog any time by visiting the URL with{" "}
          <code style={{ color: "#94a3b8", background: "#020617", padding: "1px 4px", borderRadius: "3px" }}>?setup=1</code>
          {" "}appended, or by Shift-clicking the Help button.
        </div>
      </div>
    </div>
  );
}

function HelpDialog({ onClose }) {
  const dialogRef = React.useRef(null);
  const closeRef = React.useRef(null);

  // Topic list — each entry is { title, body } displayed as one focusable item.
  const TOPICS = [
    { title: "Spoken labels, and using a screen reader", body: "The app announces each control as you reach it. If you use a screen reader, that means hearing everything twice, since your reader announces the same controls itself. The Spoken labels checkbox, beside Voice volume in the transport section, turns the app's own voice off. Every control keeps its full label either way, so with the app quiet your reader announces the interface in your own voice and at your own rate. The setting is remembered between visits. Two popups — the Colour Editor and the Sound Editor — read the setting when they open, so change it before opening them rather than while they are up. Voice navigation and voice commands still speak when you use them, since those are things you asked for out loud rather than the app narrating itself." },
    { title: "Navigating the Fractal — Zoom in", body: "Drag to draw a selection box, then right-click inside it to zoom into that region." },
    { title: "Cancel selection", body: "Press Escape or right-click outside the selection box." },
    { title: "Pan", body: "Use the arrow keys to pan left, right, up, or down. By voice, say pan up, pan down, pan left, or pan right." },
    { title: "Zoom with scroll", body: "Hold Control and scroll the mouse wheel to zoom in or out at the cursor position. By voice, say zoom in or zoom out." },
    { title: "Back and Forward", body: "Use the Back and Forward buttons to navigate through your zoom and pan history." },
    { title: "Show grid", body: "The Show grid checkbox, in the Fractal Definition section, lays a grid of 150 cells, 15 across and 10 down, over the fractal image so you can explore the picture from the keyboard. With the grid on, the Focus first cell button beside the checkbox takes you to the top-left cell. The arrow keys then move one cell at a time and wrap around at the edges; Home and End jump to the start or end of the row, and Control with Home or End to the first or last cell. As you land on each cell, the app's voice gives its number, row and column, says what the cell holds, from boring through some detail to interesting and very interesting, or solid interior and half inside the set, and names the compass directions where there is more detail, so you can follow the detail across the image. With Spoken labels off, a screen reader hears the cell number, row and column. Press Enter on a cell to zoom into it; this also stops any music that is playing. By voice, say show grid, hide grid, or toggle grid." },
    { title: "Presets", body: "Select a preset from the dropdown and press Enter to load it. Built-in presets include Mandelbrot Full, Seahorse Valley, Elephant Valley, and several Julia views." },
    { title: "Save as PNG", body: "Saves the current canvas as a PNG image file in your downloads folder." },
    { title: "Save as IMG", body: "Saves the current fractal in the app's native dot-IMG format. Preserves all parameters for later import." },
    { title: "Save as TIFF", body: "The Save as menu also offers TIFF at four print sizes: 2K, 4K, 8K and 12K. The fractal is drawn afresh at the size you choose rather than enlarged from the screen, and saved as an uncompressed, full-colour TIFF marked at 300 dots per inch, the kind of file print labs expect. 2K and 4K require the Artist tier; 8K and 12K require Studio. Every TIFF also records how it was made. The fractal type, the exact view coordinates, the Julia parameter, the iteration count and the palette name are stored inside the file, together with the app's name and the date and time it was saved. Because these details travel inside the file, they survive renaming and copying, and the coordinates keep their full precision even though the file name rounds them. Open TIFF uses them to bring the view back. TIFFs saved before September 11, 2026 do not carry them." },
    { title: "Import IMG file", body: "Loads a previously saved dot-IMG file and restores its fractal view and parameters." },
    { title: "Open TIFF", body: "The Open TIFF button, beside Import IMG, reopens a TIFF saved by the app. It reads only the details stored inside the file, not the picture itself, so even a very large print file opens at once. It restores the fractal type, view, iteration count and Julia parameter, and redraws the image from them. The saved palette is looked for in your palette list and then in the online library; if it cannot be found, the view still comes back in the current palette and the status line says so. Your previous view stays in the history, so Back returns you to where you were. Band composer regions belonging to the previous image are cleared. A TIFF from another program, or one saved before September 11, 2026, has no stored details, and the app says so rather than guessing." },
    { title: "Example compositions", body: "The Example compositions menu, next to Open composition, loads curated pieces from the online library. In the Hall of the Fractal King is provided in two versions, Fractal and Chordal, so you can hear the same fractal sonified purely and then harmonized, and compare the two. Choosing one restores its fractal view, key, instruments, and movements, ready to play." },
    { title: "Import palette bank", body: "Opens a dialog to load custom palette files from a library or your local computer. Imported palettes appear in the palette selector." },
    { title: "Scan mode", body: "Plays the fractal as a left-to-right audio scan. Each column produces a chord from the colors in that vertical line." },
    { title: "Orbit mode", body: "Traces a Mandelbrot orbit path from a chosen point and plays it as music." },
    { title: "Play full scan", body: "Starts playback from the leftmost column of the fractal." },
    { title: "Audio — use a wired connection", body: "For uninterrupted playback, use wired headphones or speakers rather than Bluetooth. Bluetooth audio can drop out during long scans — especially dense, fast passages — because the wireless link may not sustain a steady data rate, producing gaps or brief silences. A wired connection (the headphone jack or a USB audio adapter) gives smooth, reliable playback." },
    { title: "Resume here", body: "Resumes playback from the current playhead position instead of restarting from the beginning." },
    { title: "Pause", body: "Stops the music and freezes the playhead at its current position. The Escape key does the same from anywhere, and stops playback immediately in every mode — scan, band composer, contour glide, layered and drawn path alike." },
    { title: "While instruments load", body: "The first time you play a piece that uses instruments, there is a short pause while the sounds load; the cursor shows the busy, in-progress shape until the first note is ready. Later plays in the same session start faster, because the instruments are already loaded." },
    { title: "The open composition's name", body: "When you open a composition — from a file or from the Example compositions menu — its name appears just below the Color influences sound checkbox, so you can always see which piece is loaded. The name is taken from the file's name." },
    { title: "Opening a composition", body: "Opening a composition restores its fractal view, key, instruments and sound mode together, so a piece comes back in the same mode it was saved in: a Band composer piece opens ready in Band composer, a Drawn path piece in Drawn path, and so on. Press Play to hear it." },
    { title: "Band composer — what it is", body: "Band composer mode turns each escape band — all the points of one escape value, forming a concentric ring on the image — into a separate voice you can select and sequence. Instead of scanning the whole image at once, you choose which regions play, in what order, each with its own instrument, pitch, and speed. In this mode the image is silent until you add regions." },
    { title: "Band composer — selecting regions", body: "In Band composer mode, right-click any coloured region of the fractal to add it to your composition; a numbered label marks it in play order. Right-click another region to add the next, and so on. Control-right-click a region to remove it. Right-clicking inside the black set, or on a region you have already chosen, does nothing." },
    { title: "Band composer — the Regions panel", body: "Below the image, the Regions panel lists your regions in play order. For each one you set the instrument, the pitch as a scale degree and octave within the current key, and the sweep speed in seconds per column. The up and down arrows reorder a region in the sequence; the trash button removes it. Every change is reflected in the numbered labels on the image." },
    { title: "Band composer — how it plays", body: "Press Play full scan and the regions play one after another, in the order you chose. Each region sweeps across only the columns where its band appears, at its own speed, sounding only that band on its chosen instrument — so there is no bleed between voices. When one region's sweep finishes, the next begins. Loudness follows the band's thickness: where the ring is thick the voice is louder." },
    { title: "Band composer — tips", body: "Thick outer bands — low escape values, the large outer rings — span many columns and make good sustained foundations; thin inner bands are sparser and sparkle as fragmented detail. Give each region a different instrument and pitch to build harmony across the sequence, and vary the sweep speed to change the pace from region to region. A band composition is tied to its specific image, so it is saved and loaded together with that fractal." },
    { title: "Contour glide — what it is", body: "Contour glide traces an equipotential — the curve joining all the points of one escape value — and sings it as one continuous gliding tone. A single oscillator slides in pitch as it follows the curve, so instead of separate notes you hear one unbroken line that rises and falls with the shape of the boundary. Smooth outer curves sing simple arcs; the intricate curves near the set sing long, complex melodic lines. A magenta dot travels the curve in time with the sound, so you can watch what you are hearing. Contour glide requires the Studio tier." },
    { title: "Contour glide — selecting contours", body: "Left-click anywhere on the coloured part of the image to preview the contour through that point on its own. Right-click to add that contour to the sequence instead; each addition gets a numbered row. Clicking inside the black set does nothing, since interior points have no escape value and therefore no contour. A curve that fits within the frame is traced as a closed loop; one that runs off the edge is traced as an open arc from edge to edge. Either way the path is anchored at its leftmost point, so the same ring always gives the same phrase no matter where you clicked on it." },
    { title: "Contour glide — the Contours panel", body: "Below the image, the Contours panel lists your contours in play order, each showing its escape value and whether it is a closed loop or an open arc, with its point count. Per row you choose the waveform — sine, triangle, sawtooth or square — and the speed, either Global or one of Slow, Medium and Fast. The up and down arrows reorder, the trash button removes, and Clear all empties the sequence. The row currently sounding is highlighted in amber as the piece plays. Unlike bands, the same contour may appear more than once in a sequence." },
    { title: "Contour glide — pitch, speed and gap", body: "Pitch follows offers two readings of the curve. Distance from axis maps how far each point lies from the real axis, which makes the set's mirror symmetry audible: points equally above and below the axis sound the same pitch. Arc height instead maps each contour to its own vertical range, so even a shallow, nearly horizontal curve arches across the full pitch range rather than sounding flat. The Slow-to-Fast slider sets the global reading speed for contours left on Global, and Contour gap sets the silence between one contour and the next, from none up to a little over a second." },
    { title: "Contour glide — column windows", body: "Each contour can be limited to a horizontal slice of the image rather than its whole length. Press Set cols on a contour row, then drag left to right across the canvas; a cyan band shows the range, and on release those columns are stored for that contour. A very short drag clears the limit back to full width, pressing Set cols again cancels, and so does Escape. On an arch, a column window keeps both the rising and the falling stroke through those columns, giving you two fragments rather than one — a way to cut a short motif out of a long curve." },
    { title: "Layered — glide and triplets together", body: "Layered mode plays a contour two ways at once. The gliding tone traces the curve as in Contour glide, and at the same time a flute sounds an even stream of triplets read from the escape banding just off that same curve, sampled along the boundary's local normal — one shape generating both a slow arching line and fast local motion, the two-timescale texture of a Bach chorale prelude. Both layers start together and stay locked to one clock. The Tempo control sets the beat for the triplets. Layered mode shares all of the Contour glide editing tools: the same clicking, the same panel, the same speed and gap controls. It requires the Studio tier." },
    { title: "Drawn path — what it is", body: "Drawn path plays a line you draw yourself, rather than one the fractal's geometry dictates. Every pixel already has an escape value and therefore a pitch, and a contour follows a curve of constant escape value — which is exactly why it holds one pitch. Draw across those bands instead and the escape values change under your line, giving a melody read straight from the image. Where your line crosses the black set the note becomes a rest, keeping its place in time, so the interior reads as silence rather than being skipped. A magenta marker steps from vertex to vertex as it plays, so you can see which note is sounding. Drawn path requires the Studio tier." },
    { title: "Drawn path — drawing and playing", body: "The Draw control offers two ways to make a line. In Vertices, each click drops a point and straight segments join them, with Notes per segment setting how many notes are sampled along each one — so you set the rhythm by where you click; at 1 you get exactly one note per vertex. In Freehand, press and sweep across the image and the line is sampled evenly by length, so a longer sweep gives more notes; Resolution sets how closely. Backspace removes the last vertex and Escape clears the line. Choose any of the 128 instruments, set Notes per second for the pace, and press Play. The path and every path setting are saved with the composition." },
    { title: "Drawn path — typing coordinates", body: "Clicking is not the only way to place a path. The Vertices box takes pairs of pixel coordinates separated by spaces or newlines — 516,144 489,172 473,188 — and Apply places them exactly. Read does the reverse, filling the box from the path currently drawn, so you can copy the numbers out and keep them. This matters when you are aiming at particular escape values rather than a particular shape: a few pixels either way can cross a band boundary and change the note, which is hard to control by eye. Each vertex is numbered on the image with its coordinates, listed down the opposite edge with a leader line to its point; the Show coordinates box turns that off." },
    { title: "Drawn path — pitch mappings", body: "Pitch decides how an escape value becomes a note. Raw uses the value directly as a scale degree, which is exact but climbs out of hearing once the values are large. Shift subtracts the path's lowest value and nothing else, so the intervals are exactly the escape field's own, just moved into a playable register — the most literal reading, and the one to use when you want the numbers to speak for themselves. Linear rescales the path's range across two octaves, keeping the relative interval sizes but stretching them. Rank spreads the distinct values evenly, which flattens the intervals but guarantees the melodic shape survives however lopsided the values are. On a chromatic scale one escape step is one semitone, which makes Shift especially direct." },
    { title: "Drawn path — note lengths", body: "By default every note occupies the same slot. To hold one longer, add a colon and a multiplier to its coordinate pair: 487,224:3 holds that note three slots, and 0.5 halves it. Omit the suffix for a normal-length note. The length belongs to the segment starting at that vertex, so with Notes per segment above 1 it stretches all of that segment's notes together. Read writes the suffixes back out along with the coordinates, so an existing path can be edited without retyping it." },
    { title: "Drawn path — passes", body: "The Passes panel builds a form out of one path. Each row plays the whole path a number of times, with its own transposition, speed, loudness, instrument and trailing pause, and the rows play in order. That is most of what a piece does with a theme: state it, then restate it changed. A row set quiet and slow on a bassoon, followed by rows climbing in pitch, quickening, growing louder and moving to brass, is the shape of a whole movement written as a short table. Notes per second or velocity of 0, and a blank instrument, inherit the settings above the panel, so you only fill in what differs. With no rows at all the simpler Repeats, Transpose, per repeat and Pause fields are used instead, where per repeat adds its step to every repetition. Notes transposed past the top of the MIDI range are dropped rather than folded back, so a climb that goes too far thins out audibly — lower the transposition when that happens." },
    { title: "Delay before playing", body: "The Delay box next to the transport buttons sets how many seconds the app waits after you press Play before any sound starts, from none up to two minutes. It exists mainly for recording: start a screen capture, press Play, and use the delay to move the recorder out of shot or switch windows before the music begins. A countdown appears while it runs, and Escape cancels it. Left at zero, playback starts immediately." },
    { title: "Show boundaries", body: "Movements divide a scan into sections, each with its own instrument and pace. Show boundaries draws the markers where those sections begin and end — triangles and vertical lines over the image. They are hidden by default so a finished piece can be watched against a clean picture, and showing them changes nothing about the music. Placing a mark turns them on automatically, since you can hardly position a boundary you cannot see. The button toggles back to Hide boundaries while they are showing." },
    { title: "Sound Editor — opening it", body: "Click the Sound Editor button to open the Fractal Sound Editor in its own window. Here you set the musical key and scale and build the loudness palette — the set of instruments that turn the fractal's escape values into music, the audible analog of the color palette. Changes update the main app live, so play a scan to hear them." },
    { title: "Sound Editor — Key and Scale", body: "The Key dropdown chooses the root note, from C, D flat, D, E flat, E, F, G flat, G, A flat, A, B flat, or B. The Scale dropdown chooses major, minor, pentatonic, blues, or whole-tone. Together they set which notes the music is built from." },
    { title: "Sound Editor — loudness palette", body: "The loudness palette is a table of instruments. Each instrument is a channel with four settings: initial, step, direction, and octave. For each escape band, that instrument's loudness is its initial value plus or minus step times the band number. Instruments whose loudness is above zero sound together, each playing in its own octave." },
    { title: "Sound Editor — initial, step, and direction", body: "Initial sets an instrument's loudness at the lowest band. Step sets how much its loudness changes from band to band. Direction is the plus or minus sign — whether that instrument grows louder or softer as the escape band rises. Octave sets how high or low it plays. Loudness is clamped to 0-100, so if initial is low and step is negative and direction is minus, low bands play softly or silently; if the sum stays at 0 for a range of bands, those bands are silent. If your click-to-play test is silent, raise initial or increase step in the audible direction." },
    { title: "Sound Editor — adding instruments", body: "Choose one of the 128 General MIDI instruments from the Add dropdown, then press Add instrument to place it in the loudness palette. Combine several instruments, each with its own loudness curve, to shape the overall sound." },
    { title: "Sound Editor — live preview", body: "You have two ways to audition a band. First, click anywhere on the fractal preview at the top of the editor to hear the chord for the band value at that pixel — a one-second sample, then silence. Second, press Start preview to sustain a chord at a fixed band; the Band slider from 0 to 255 chooses which band you are auditioning, and while preview is on, editing one instrument changes only that instrument in the sustained chord — the audible analog of the color editor's live recolor. If you hear nothing at a low band, that instrument's loudness at that band may be zero — see the initial, step, and direction help." },
    { title: "Sound Editor — reordering instruments", body: "Use the up and down arrows on each palette row to move an instrument earlier or later in the list. Order is only for your own organisation — every instrument sounds according to its own initial, step, direction, and octave regardless of where it sits in the list." },
    { title: "Sound Editor — saving sound maps", body: "A sound map is the whole instrument set you have built. Save to library stores it by name so you can recall it later; you are warned before replacing an existing map of the same name, and Load brings a saved map back. This is the sound-map counterpart of the colour library. Save map to file and Load map from file instead export or import a sound map as a file on your computer, which you can back up, share, or send to others." },
    { title: "Movements — what they are", body: "A movement is a section of the scan with its own tempo. By dividing a scan into movements, you can make different parts of the fractal play faster or slower, like sections of a musical composition." },
    { title: "Mark a movement boundary", body: "While the scan is playing, press Enter to mark a boundary at the current playhead position. This splits the current movement in two at that point. A small amber triangle appears at the bottom edge to mark it." },
    { title: "Mark a boundary by voice", body: "While the scan is playing, say mark here, mark, new movement, or new section to mark a boundary at the current position — the same as pressing Enter. This lets you compose sections hands-free by ear." },
    { title: "Movements table", body: "The movements table at the top of the transport section lists each movement with its start column, end column, tempo, and end-of-section rest. Add a movement with the Add movement button, choose a tempo from the dropdown, or delete a movement with its delete button." },
    { title: "Movement tempos", body: "Each movement can play at its own tempo, from Largo (slowest) through Larghetto, Adagio, Andante, Moderato, up to Allegro (fastest). During playback the scan speeds up or slows down as it enters each movement." },
    { title: "End-of-section rests", body: "Each movement can have a rest at its end — a pause before the next movement begins, measured in beats. This gives a musical breath between sections." },
    { title: "Column speed and live tempo", body: "When there is a single movement, the Column speed slider sets the playback tempo, and you can drag it during playback to change the speed live. When there are several movements, each movement's own tempo takes over." },
    { title: "Boundary markers", body: "Movement boundaries appear as small amber triangles along the bottom edge of the canvas, at the column where each new movement begins. They mark the structure without covering the fractal image." },
    { title: "Current movement", body: "During playback the movements table highlights the movement being played, in amber, with a triangle beside its number. The scan also announces each movement aloud as it enters it, giving its number and tempo, so you can follow the structure of the piece by ear. When playback is stopped, the highlight instead follows the selected column, which makes it easy to see which movement a column belongs to while you are placing boundaries." },
    { title: "Saving movements", body: "Saving a composition in the app's native format stores all your movements, their tempos and rests, and the current playhead position, so you can reopen a work in progress and resume exactly where you left off." },
    { title: "Voice load", body: "Every note keeps sounding for a fraction of a second after its region ends, so notes from neighbouring regions and neighbouring columns overlap and pile up. Columns with a lot of fine detail, played fast, stack up the most voices, and past a certain number the sound begins to stutter. Detail and tempo are the same lever: doubling the detail in a column costs the same as playing it twice as fast." },
    { title: "Key and Harmony", body: "Key and Scale apply wherever pitches are drawn from a musical scale: Instrumental fractal music, Layered mode's triplets, and Drawn path. Harmony is narrower — it snaps notes to a chord progression during the instrumental scan only, so it stays greyed out elsewhere. With Simple sine waves the fractal is sonified directly as tones, so none of the three applies and all are greyed out." },
    { title: "Harmony — Fractal and Chordal", body: "The Harmony setting chooses how the fractal's notes are pitched. Fractal is pure sonification: each note is the fractal's own escape value, sounding freely within the chosen key and scale. Chordal snaps those same notes to a chord progression that advances across the scan, so the piece takes on a tonal, harmonic shape while still being driven by the fractal. Both play the same fractal data — one modal and unmediated, the other harmonized. Try a scan each way to compare." },
    { title: "Diagnostics panel", body: "The Diagnostics section sits below the movements table and is closed until you open it, so it stays out of the way while you compose. Inside are two tools, Analyse scan and Inspect column. Both only measure. Neither plays a note nor changes your music in any way." },
    { title: "Analyse scan", body: "The Analyse scan button walks the whole scan and reports the greatest number of voices sounding at once, which column that happens in, the eight busiest columns, and any stretches that are silent. It only measures. It never plays a note, and it never changes your music. Play the piece, note where it first stutters, then look that column up: its voice count is the ceiling your computer can manage." },
    { title: "Inspect column", body: "The Inspect column button describes the selected column region by region: the size of each region, whether it lies inside the set, its escape value and the loudness band that value maps to, and exactly which instruments sound and at what velocity. A region showing nothing fired means every instrument in the loudness palette is at zero loudness for that band, which is why some stretches of a scan are silent." },
    { title: "If the music stutters", body: "Stuttering means too many voices are sounding at once for your computer to keep up. It is not a fault in the image, and there is no kind of fractal you should avoid. The cause is always the same: a stretch where the scan crosses fine detail, so each column breaks into many regions, played at a tempo fast enough that the notes pile up on top of one another. There are three cures, and all of them are musical choices rather than technical ones. Slow the movement that covers the passage, which gives the same notes more room. Thin the loudness palette there, so fewer instruments sound in each region. Or move a movement boundary, so the detailed passage is played at a gentler tempo than the smooth passage beside it. Use Analyse scan to find which columns are the busiest, and Inspect column to see exactly which instruments are sounding in them." },
    { title: "Finding your computer's limit", body: "The number of voices a computer can sustain varies from machine to machine, so there is no universal figure. To find yours, play the piece and note the column where the sound first begins to break up, then run Analyse scan and look that column up in the list of busiest columns. The voice count beside it is your ceiling. Keep the rest of the composition below that number and it will play cleanly." },
    { title: "Why a stretch is silent", body: "There are three reasons a stretch of the scan makes no sound. The scan may be crossing the interior of the set, where there is nothing to sound and the silence is intended. A movement may have a rest at its end. Or, most often, the escape values there fall in a band where every instrument in the loudness palette computes a loudness of zero, so no note is fired at all. Inspect column will tell you which of the three it is. To put a floor under the silence, give one instrument a nonzero initial loudness so that something always sounds." },
    { title: "Voice control activation", body: "Press Enter or click the microphone button to start voice control. Say goodbye to stop." },
    { title: "Voice navigation commands", body: "Say go to fractal, go to palette, or go to transport to jump to a section. Say tab forward or tab backward to move between controls. Say jump to followed by a control name to focus it directly." },
    { title: "Voice describe", body: "When focused on any control, say describe to hear a longer explanation of what it does." },
    { title: "Voice list items", body: "Say list items or what's in this list to hear all controls in the current section or dialog." },
    { title: "Voice playback", body: "Say play full scan, resume here, pause, scan mode, or orbit mode to control playback." },
    { title: "Voice audio", body: "Say set music volume to a number from 0 to 100. Set column speed to a number of seconds. Set stereo width to a value between 0 and 1. Or say louder, quieter, faster, slower." },
    { title: "Voice presets", body: "Say mandelbrot, seahorse, elephant, julia classic, or julia spiral to load that preset directly." },
    { title: "Voice color editor", body: "Say open color editor to launch it. Once open, say set red to a number, set green to a number, set blue to a number, set red step to a number, red up, red down, next page, previous page, smoothing on, smoothing off, reset page, apply to all, save palette, or close color editor." },
    { title: "Keyboard navigation", body: "Tab moves to the next control. Shift Tab moves backwards. Control plus arrow keys jump between sections. Within a section, up and down arrows move between controls. On a slider, left and right adjust the value." },
    { title: "Eye tracking", body: "Click the eye button or say start eye tracking to enable webcam-based gaze control. Calibrate by looking at each of nine dots and clicking five times. Dwell on a control for one second to focus it." },
    { title: "Color influences sound", body: "When enabled in the palette section, the colors in the palette modulate the harmonic content of the music. When disabled, only the iteration depth determines pitch and timbre." },
  ];

  // Auto-focus the close button when the dialog opens.
  React.useEffect(() => {
    setTimeout(() => closeRef.current?.focus(), 0);
  }, []);

  // Keyboard handler: Escape closes; Tab is handled by the browser naturally.
  React.useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Help"
      ref={dialogRef}
    >
      <div
        style={{
          background: "#0f172a", border: "1px solid #334155", borderRadius: "14px",
          padding: "32px", width: "640px", maxWidth: "92vw", maxHeight: "80vh",
          overflowY: "auto", color: "#cbd5e1", fontSize: "14px", lineHeight: 1.7,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div style={{ fontSize: "18px", fontWeight: 600, color: "#f8fafc" }}>User Guide</div>
          <button
            ref={closeRef}
            id="help-close-btn"
            aria-label="Close help"
            data-description="Close help. Closes the help dialog and returns focus to the Help button."
            onClick={onClose}
            onFocus={() => {
              if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
                if (!spokenLabelsOn()) return;
                const utt = new SpeechSynthesisUtterance("Help dialog. Tab to browse topics, or press Enter to close.");
                utt.rate = 0.95;
                window.speechSynthesis.speak(utt);
              }
            }}
            style={{ background: "#1e293b", border: "1px solid #334155", color: "#f8fafc", fontSize: "14px", padding: "6px 14px", borderRadius: "6px", cursor: "pointer" }}
          >
            Close
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {TOPICS.map((topic, i) => (
            <button
              key={i}
              tabIndex={0}
              aria-label={topic.title} aria-describedby={`help-topic-body-${i}`}
              data-description={topic.body}
              onFocus={() => {
                if ('speechSynthesis' in window) {
                  window.speechSynthesis.cancel();
                  if (!spokenLabelsOn()) return;
                  const utt = new SpeechSynthesisUtterance(`${topic.title}. ${topic.body}`);
                  utt.rate = 0.95;
                  window.speechSynthesis.speak(utt);
                }
              }}
              onClick={(e) => { e.stopPropagation(); /* clicking a topic does nothing — it's just info */ }}
              style={{
                textAlign: "left",
                background: "#1e293b",
                border: "1px solid #334155",
                borderRadius: "6px",
                padding: "10px 14px",
                color: "#cbd5e1",
                fontSize: "14px",
                lineHeight: 1.5,
                cursor: "default",
              }}
            >
              <strong style={{ color: "#f8fafc" }}>{topic.title}</strong>: <span id={`help-topic-body-${i}`}>{topic.body}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function buildColorEditorHtml() {
  return `<!DOCTYPE html><html><head><title>Color Editor</title><style>
*{box-sizing:border-box;margin:0;padding:0;}
body{background:#0f172a;color:#cbd5e1;font-family:system-ui,sans-serif;font-size:13px;padding:16px;}
h2{font-size:12px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.15em;margin-bottom:12px;}
.row{display:flex;align-items:center;gap:8px;margin-bottom:8px;}
.lbl{width:40px;font-weight:700;font-size:12px;}
.val{width:26px;text-align:right;color:#64748b;font-size:11px;}
.sval{width:34px;text-align:right;color:#64748b;font-size:11px;}
input[type=range]{flex:1;cursor:pointer;}
input[type=number]{width:58px;background:#1e293b;border:1px solid #334155;color:#f8fafc;border-radius:6px;padding:4px 6px;font-size:13px;}
.btn{background:#1e293b;border:1px solid #334155;color:#cbd5e1;border-radius:6px;padding:4px 10px;cursor:pointer;font-size:12px;}
.btn:hover{background:#334155;}
.btn.active{background:#38bdf8;color:#082f49;border-color:#38bdf8;}
.btn.accent{background:#3b82f6;color:white;border-color:#3b82f6;}
.page-row{display:flex;align-items:center;gap:8px;margin-bottom:12px;}
.info{color:#64748b;font-size:11px;}
.grid-wrap{display:flex;gap:8px;margin:10px 0;}
#colorGrid{display:grid;grid-template-columns:repeat(16,12px);gap:1px;border:1px solid #374151;}
.cell{width:12px;height:12px;cursor:pointer;}
.cell.sel{outline:2px solid #38bdf8;outline-offset:-1px;position:relative;z-index:1;}
#selInfo{color:#94a3b8;font-size:11px;margin:2px 0 8px 0;}
#solidPreview{flex:1;border:1px solid #374151;border-radius:4px;min-height:80px;}
.btn-row{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:8px;}
canvas{display:block;width:100%;border-radius:8px;margin-bottom:14px;background:#000;image-rendering:pixelated;}
#status{font-size:11px;color:#64748b;margin-top:6px;}
</style></head><body>
<canvas id="fractalCanvas" width="750" height="500"></canvas>
<div id="pixelInfo" class="info" style="margin:-8px 0 10px 0;font-size:12px;color:#94a3b8">Click anywhere on the preview to see the palette index at that point.</div>
<div class="page-row">
  <label class="info">Page:</label>
  <input type="number" id="pageInput" min="0" max="255" value="0"/>
  <span class="info" id="pageInfo">(indexes 0\u20130255)</span>
</div>
<div id="channelRows"></div>
<div class="grid-wrap"><div id="colorGrid"></div><div id="solidPreview"></div></div>
<div id="selInfo">Editing whole page \u2014 drag across the swatches to select a range.</div>
<div class="btn-row">
  <label style="display:flex;align-items:center;gap:6px;cursor:pointer"><input type="checkbox" id="smoothCb"/> Smoothing</label>
  <button class="btn" id="selectAllBtn">Select all</button>
  <button class="btn" id="resetBtn">Reset page</button>
  <button class="btn" id="applyAllBtn">Apply to all pages</button>
  <button class="btn accent" id="saveBtn">Save</button>
  <button class="btn" id="saveAsBtn">Save as...</button>
</div>
<div id="status">Connecting...</div>
<script>
const ch=new BroadcastChannel('fractal_color_editor');
// Speak a brief description on focus. Uses the popup's own speechSynthesis
// (the main app and popup are separate windows with separate speech engines).
// Each call cancels any in-flight popup speech so rapid Tab navigation doesn't
// queue up a backlog.
function spokenRoot(r){ return ({ C:"C", Db:"D flat", D:"D", Eb:"E flat", E:"E", F:"F", Gb:"G flat", G:"G", Ab:"A flat", A:"A", Bb:"B flat", B:"B" })[r] || r; }
function spokenScale(s){ return ({ major:"major", minor:"minor", pentatonic:"pentatonic", blues:"blues", wholetone:"whole tone", chromatic:"chromatic" })[s] || s; }
function speakLabel(text){
  if(!('speechSynthesis' in window))return;
  if(!text)return;
  // Injected when this popup's HTML is built (separate window, separate engine).
  if(!${spokenLabelsOn() ? "true" : "false"})return;
  try{window.speechSynthesis.cancel();}catch(_){}
  const utt=new SpeechSynthesisUtterance(String(text));
  utt.rate=0.95;
  window.speechSynthesis.speak(utt);
}
const fc=document.getElementById('fractalCanvas'),fctx=fc.getContext('2d');
let S={page:0,pages:256,rS:0,rT:0,rL:true,gS:0,gT:0,gL:true,bS:0,bT:0,bL:true,sm:false};
let allPageControls=[];
// Write the popup's current S state back to allPageControls[S.page] so
// pageInput.onchange's lookup picks the user's latest edits up when the
// user navigates back to this page. Without this, allPageControls is
// only populated once at editor open from paletteBank.pages, so edits
// to sliders/smoothing get clobbered by the original stored values
// every time the user revisits a page.
function syncCurrentPageToCache(){
  allPageControls[S.page]={
    rS:S.rS,rT:S.rT,rL:S.rL,
    gS:S.gS,gT:S.gT,gL:S.gL,
    bS:S.bS,bT:S.bT,bL:S.bL,
    sm:S.sm,
  };
}
let baseColors=null,localColors=null,frameW=750,frameH=500;
let rawPixelIndex=null,interiorPixels=null;
// Highest frame version painted so far. Frames stamped with an older seq are
// ignored, so a late stale frame can never overwrite the current preview.
let lastFrameSeq=-1;

// Click on the preview to display the palette index of the clicked pixel.
// fc is CSS-stretched to 100% width but its pixel buffer stays at frameW x
// frameH (default 750x500), so we have to scale event coords by the ratio
// between the bounding rect and the canvas's intrinsic resolution.
fc.addEventListener('click', e => {
  if (!rawPixelIndex || !frameW || !frameH) {
    const info = document.getElementById('pixelInfo');
    info.textContent = 'Click ignored \u2014 no fractal data yet. Render a fractal in the main window first.';
    return;
  }
  const info = document.getElementById('pixelInfo');
  const rect = fc.getBoundingClientRect();
  const x = Math.floor((e.clientX - rect.left) * (frameW / rect.width));
  const y = Math.floor((e.clientY - rect.top)  * (frameH / rect.height));
  if (x < 0 || x >= frameW || y < 0 || y >= frameH) return;
  const i = y * frameW + x;
  if (interiorPixels && interiorPixels[i]) {
    info.textContent = 'Pixel (' + x + ', ' + y + '): interior of set (no palette index)';
    return;
  }
  const idx = rawPixelIndex[i] >>> 0; // raw palette index, 0..MAXCOLRS-1
  const page = Math.floor(idx / 256);
  const slot = idx % 256;
  const src = localColors || baseColors;
  let rgbStr = '';
  if (src) {
    const k = idx * 3;
    rgbStr = ' \u2014 RGB(' + (src[k] || 0) + ', ' + (src[k+1] || 0) + ', ' + (src[k+2] || 0) + ')';
  }
  info.textContent = 'Pixel (' + x + ', ' + y + '): palette index ' + idx +
    ' (page ' + page + ', slot ' + slot + ')' + rgbStr;
});

function applyPageLocally(){
  if(!baseColors)return;
  if(!localColors)localColors=new Uint8Array(baseColors);
  const po=S.page*256;
  let r=S.rS,g=S.gS,b=S.bS,revR=!S.rL,revG=!S.gL,revB=!S.bL;
  for(let i=selStart;i<=selEnd;i++){
    const idx=(po+i)*3;
    localColors[idx]=Math.max(0,Math.min(255,Math.abs(r%256)));
    localColors[idx+1]=Math.max(0,Math.min(255,Math.abs(g%256)));
    localColors[idx+2]=Math.max(0,Math.min(255,Math.abs(b%256)));
    if(!revR){r+=S.rT;if(r>=256&&S.sm){r-=S.rT;revR=true;}}else{r-=S.rT;if(r<0){if(S.sm){r+=S.rT;revR=false;}else r+=256;}}
    if(!revG){g+=S.gT;if(g>=256&&S.sm){g-=S.gT;revG=true;}}else{g-=S.gT;if(g<0){if(S.sm){g+=S.gT;revG=false;}else g+=256;}}
    if(!revB){b+=S.bT;if(b>=256&&S.sm){b-=S.bT;revB=true;}}else{b-=S.bT;if(b<0){if(S.sm){b+=S.bT;revB=false;}else b+=256;}}
  }
  updateColorGrid();
  // Redraw the preview canvas using localColors so changes are visible immediately
  recolorPreview();
}

let originalFrameColors=null; // original rendered colors from main app

function recolorPreview(){
  if(!rawPixelIndex||frameW===0||frameH===0)return;
  const img=fctx.createImageData(frameW,frameH);
  const src=localColors||baseColors;
  for(let i=0;i<frameW*frameH;i++){
    let r=0,g=0,b=0;
    if(!interiorPixels||!interiorPixels[i]){
      if(src&&src.length>0){
        // Direct rawIndex lookup — same as main app
        const c=rawPixelIndex[i]*3;
        if(c+2<src.length){r=src[c];g=src[c+1];b=src[c+2];}
        else if(originalFrameColors){
          // rawIndex out of range — use original rendered color
          r=originalFrameColors[i*3];g=originalFrameColors[i*3+1];b=originalFrameColors[i*3+2];
        }
      } else if(originalFrameColors){
        // No palette bank — use original rendered colors unchanged
        r=originalFrameColors[i*3];g=originalFrameColors[i*3+1];b=originalFrameColors[i*3+2];
      }
    }
    img.data[i*4]=r;img.data[i*4+1]=g;img.data[i*4+2]=b;img.data[i*4+3]=255;
  }
  fctx.putImageData(img,0,0);
}

function drawFrame(colors,w,h,ri,interior){
  frameW=w;frameH=h;fc.width=w;
  if(ri)rawPixelIndex=new Uint32Array(ri);
  if(interior)interiorPixels=new Uint8Array(interior);
  // Store original rendered colors so recolorPreview can fall back to them
  originalFrameColors=new Uint8Array(colors.length);
  for(let i=0;i<colors.length;i++)originalFrameColors[i]=colors[i];
  const img=fctx.createImageData(w,h);
  for(let i=0;i<w*h;i++){img.data[i*4]=colors[i*3];img.data[i*4+1]=colors[i*3+1];img.data[i*4+2]=colors[i*3+2];img.data[i*4+3]=255;}
  fctx.putImageData(img,0,0);
}

function updateColorGrid(){
  const grid=document.getElementById('colorGrid');grid.innerHTML='';
  const src=localColors||baseColors;if(!src)return;
  for(let i=0;i<256;i++){
    const base2=(S.page*256+i)*3;
    const r2=src[base2]||0,g2=src[base2+1]||0,b2=src[base2+2]||0;
    const cell=document.createElement('div');
    cell.className='cell'+((i>=selStart&&i<=selEnd)?' sel':'');
    cell.style.background='rgb('+r2+','+g2+','+b2+')';
    const me=i;
    cell.title='index '+i;
    cell.addEventListener('mousedown',ev=>{ev.preventDefault();selecting=true;selAnchor=me;setSelection(me,me);});
    cell.addEventListener('mouseenter',()=>{if(selecting)setSelection(Math.min(selAnchor,me),Math.max(selAnchor,me));});
    grid.appendChild(cell);
  }
}

function updateSolidPreview(){document.getElementById('solidPreview').style.background='rgb('+S.rS+','+S.gS+','+S.bS+')';}

// ── Range selection ───────────────────────────────────────────────────────
// selStart..selEnd (inclusive) is the slice of the current page that slider
// edits affect. Default is the whole page (0..255) so behaviour is unchanged
// unless the user drags a selection across the swatch grid.
let selStart=0,selEnd=255,selecting=false,selAnchor=0;
function updateSelInfo(){
  const el=document.getElementById('selInfo');if(!el)return;
  el.textContent=(selStart<=0&&selEnd>=255)
    ? 'Editing whole page \u2014 drag across the swatches to select a range.'
    : ('Editing indexes '+selStart+'\u2013'+selEnd+' ('+(selEnd-selStart+1)+' entries). Sliders affect only this range.');
}
function highlightSelection(){
  const grid=document.getElementById('colorGrid');if(!grid)return;
  const cells=grid.children;
  for(let i=0;i<cells.length;i++){
    if(i>=selStart&&i<=selEnd)cells[i].classList.add('sel');else cells[i].classList.remove('sel');
  }
}
function setSelection(a,b){
  selStart=Math.max(0,Math.min(255,a));
  selEnd=Math.max(0,Math.min(255,b));
  highlightSelection();updateSelInfo();
}
// Seed the sliders from the colours currently in the selected range so the
// user is adjusting that segment rather than starting from zero. Pure read —
// does not modify any colours.
function deriveFromRange(lo,hi){
  const src=localColors||baseColors;if(!src)return;
  const po=S.page*256;
  function fit(ch){
    const first=src[(po+lo)*3+ch]||0;
    const last=src[(po+hi)*3+ch]||0;
    let tot=0;
    for(let i=lo+1;i<=hi;i++){tot+=Math.abs((src[(po+i)*3+ch]||0)-(src[(po+i-1)*3+ch]||0));}
    const n=Math.max(1,hi-lo);
    return {start:first,step:Math.max(0,Math.min(255,Math.round(tot/n))),lighter:last>=first};
  }
  const r=fit(0),g=fit(1),bl=fit(2);
  S.rS=r.start;S.rT=r.step;S.rL=r.lighter;
  S.gS=g.start;S.gT=g.step;S.gL=g.lighter;
  S.bS=bl.start;S.bT=bl.step;S.bL=bl.lighter;
  syncCurrentPageToCache();syncUI();
}
// Finish a drag anywhere on the page; seed sliders from the new range.
document.addEventListener('mouseup',()=>{
  if(selecting){selecting=false;deriveFromRange(selStart,selEnd);}
});

const defs=[
  {id:'r',label:'Red',color:'#ef4444',sK:'rS',tK:'rT',lK:'rL'},
  {id:'g',label:'Green',color:'#22c55e',sK:'gS',tK:'gT',lK:'gL'},
  {id:'b',label:'Blue',color:'#60a5fa',sK:'bS',tK:'bT',lK:'bL'},
];
const rowsEl=document.getElementById('channelRows');
defs.forEach(d=>{
  const row=document.createElement('div');row.className='row';
  row.innerHTML='<span class="lbl" style="color:'+d.color+'">'+d.label+'</span>'+
    '<span class="val" id="'+d.id+'sv">0</span><input type="range" min="0" max="255" value="0" id="'+d.id+'ss"/>'+
    '<span class="sval" id="'+d.id+'tv">Step: 0</span><input type="range" min="0" max="255" value="0" id="'+d.id+'ts"/>'+
    '<button class="btn active" id="'+d.id+'up">\u2191</button><button class="btn" id="'+d.id+'dn">\u2193</button>';
  rowsEl.appendChild(row);
  document.getElementById(d.id+'ss').oninput=e=>{const v=+e.target.value;S[d.sK]=v;document.getElementById(d.id+'sv').textContent=v;updateSolidPreview();syncCurrentPageToCache();applyPageLocally();setDirty();try{ch.postMessage({type:'set'+d.label,start:v});}catch(_){}};
  document.getElementById(d.id+'ss').onfocus=()=>speakLabel(d.label+' start. Current value '+S[d.sK]+'. Sets the starting intensity for this page. Use arrow keys to adjust.');
  document.getElementById(d.id+'ts').oninput=e=>{const v=+e.target.value;S[d.tK]=v;document.getElementById(d.id+'tv').textContent='Step: '+v;syncCurrentPageToCache();applyPageLocally();setDirty();try{ch.postMessage({type:'set'+d.label,step:v});}catch(_){}};
  document.getElementById(d.id+'ts').onfocus=()=>speakLabel(d.label+' step. Current value '+S[d.tK]+'. Controls how fast the intensity changes between palette entries.');
  document.getElementById(d.id+'up').onclick=()=>{S[d.lK]=true;document.getElementById(d.id+'up').className='btn active';document.getElementById(d.id+'dn').className='btn';syncCurrentPageToCache();applyPageLocally();setDirty();try{ch.postMessage({type:'set'+d.label,lighter:true});}catch(_){}};
  document.getElementById(d.id+'up').onfocus=()=>speakLabel(d.label+' lighter direction. '+(S[d.lK]?'Selected. ':'Not selected. ')+'Press Enter to make the channel get lighter across the page.');
  document.getElementById(d.id+'dn').onclick=()=>{S[d.lK]=false;document.getElementById(d.id+'up').className='btn';document.getElementById(d.id+'dn').className='btn active';syncCurrentPageToCache();applyPageLocally();setDirty();try{ch.postMessage({type:'set'+d.label,lighter:false});}catch(_){}};
  document.getElementById(d.id+'dn').onfocus=()=>speakLabel(d.label+' darker direction. '+(!S[d.lK]?'Selected. ':'Not selected. ')+'Press Enter to make the channel get darker across the page.');
});

document.getElementById('pageInput').onfocus=()=>speakLabel('Page input. Current page '+S.page+' of '+(S.pages-1)+'. Each page is 256 palette entries. Type a number to jump.');
document.getElementById('pageInput').onchange=e=>{
  const p=Math.max(0,Math.min(S.pages-1,+e.target.value||0));
  S.page=p;e.target.value=p;
  document.getElementById('pageInfo').textContent='(indexes '+(p*256)+'\u2013'+(p*256+255)+')';
  // Always reset sliders/smoothing for the new page — fall back to defaults
  // if this page has no stored controls (e.g. fallback palette bank). Without
  // this, slider values and smoothing would "stick" from the previous page.
  const pc=allPageControls[p]||{};
  S.rS=pc.rS??0;S.rT=pc.rT??0;S.rL=pc.rL??true;
  S.gS=pc.gS??0;S.gT=pc.gT??0;S.gL=pc.gL??true;
  S.bS=pc.bS??0;S.bT=pc.bT??0;S.bL=pc.bL??true;
  S.sm=pc.sm??false;
  syncUI();
  try{ch.postMessage({type:'setPage',page:p});}catch(_){}
  // Reset the range selection to the whole page when navigating pages.
  selStart=0;selEnd=255;
  // Just refresh display from existing localColors. Do NOT regenerate the
  // new page from the formula on plain navigation. With default controls
  // (rS=0, rT=0, etc.) the formula produces 256 entries of pure black, so
  // visiting a default-controls page would silently overwrite that page's
  // stored colors with all-black in localColors — and after a 0-1-2-0
  // navigation tour, every page is destroyed. Slider edits still call
  // applyPageLocally for the current page (user-initiated, expected to
  // overwrite). The Apply to all pages button still does an explicit
  // batch-regenerate for the case where the user wants formula output
  // applied across the whole bank.
  updateColorGrid();
  recolorPreview();
  updateSelInfo();
};
// Smoothing toggle is a flag-only operation: it does NOT regenerate the
// page from the slider formula. Regenerating here would silently overwrite
// localColors using whatever the sliders are currently at — and if those
// are still at defaults (R=0, G=0, B=0, all Steps=0), the formula produces
// 256 entries of pure black RGB(0,0,0), wiping the loaded baseColors with
// no way to recover by toggling the box back. Subsequent slider edits will
// pick up the new smoothing value when they regenerate.
document.getElementById('smoothCb').onchange=e=>{S.sm=e.target.checked;syncCurrentPageToCache();setDirty();try{ch.postMessage({type:'setSmoothing',value:S.sm});}catch(_){}};
document.getElementById('smoothCb').onfocus=()=>speakLabel('Smoothing. '+(S.sm?'On.':'Off.')+' When on, palette entries blend smoothly between values. When off, hard steps. Press space to toggle.');
document.getElementById('selectAllBtn').onfocus=()=>speakLabel('Select all. Selects the whole page so sliders affect all 256 entries.');
document.getElementById('selectAllBtn').onclick=()=>{setSelection(0,255);deriveFromRange(0,255);};
document.getElementById('resetBtn').onfocus=()=>speakLabel('Reset page. Sets every slider on the current page back to zero and discards this page\\u0027s edits.');
document.getElementById('resetBtn').onclick=()=>{
  try{ch.postMessage({type:'resetPage'});}catch(_){}
  if(baseColors){localColors=new Uint8Array(baseColors);updateColorGrid();recolorPreview();}
  else if(originalFrameColors){
    // No palette bank — restore original rendered frame
    const img=fctx.createImageData(frameW,frameH);
    for(let i=0;i<frameW*frameH;i++){img.data[i*4]=originalFrameColors[i*3];img.data[i*4+1]=originalFrameColors[i*3+1];img.data[i*4+2]=originalFrameColors[i*3+2];img.data[i*4+3]=255;}
    fctx.putImageData(img,0,0);
  }
};
document.getElementById('applyAllBtn').onfocus=()=>speakLabel('Apply to all pages. Copies the current page\\u0027s slider values to every other page in the palette.');
document.getElementById('applyAllBtn').onclick=()=>{try{ch.postMessage({type:'applyAll',rStart:S.rS,rStep:S.rT,rL:S.rL,gStart:S.gS,gStep:S.gT,gL:S.gL,bStart:S.bS,bStep:S.bT,bL:S.bL,smoothing:S.sm});}catch(_){}};
document.getElementById('saveBtn').onfocus=()=>speakLabel('Save. Persists current edits to local storage. No upload, no download.');
document.getElementById('saveBtn').onclick=()=>{
  // Save = persist current edits to local IDB cache. No Firebase upload, no file download.
  if(localColors){try{ch.postMessage({type:'saveWithColors',colors:Array.from(localColors),allPageControls:allPageControls,page:S.page,rStart:S.rS,rStep:S.rT,rL:S.rL,gStart:S.gS,gStep:S.gT,gL:S.gL,bStart:S.bS,bStep:S.bT,bL:S.bL,smoothing:S.sm});}catch(_){}}
  else{try{ch.postMessage({type:'savePalette'});}catch(_){}}
};
document.getElementById('saveAsBtn').onfocus=()=>speakLabel('Save as. Prompts for a new name and uploads to the library, with confirmation if a palette of that name already exists.');
document.getElementById('saveAsBtn').onclick=()=>{
  // Save as = ask user for a new name, then upload to Firebase library AND cache locally.
  // The main app handles Firebase-collision confirmation via window.confirm in this popup.
  const defaultName=(window.__paletteFilename||'palette').replace(/\.tmp$/,'');
  const newName=window.prompt('Save palette as:',defaultName);
  if(!newName){window.__lastSaveAsCommitted=false;return;}
  window.__lastSaveAsCommitted=true;
  if(localColors){try{ch.postMessage({type:'saveAsWithColors',newName:newName,colors:Array.from(localColors),allPageControls:allPageControls,page:S.page,rStart:S.rS,rStep:S.rT,rL:S.rL,gStart:S.gS,gStep:S.gT,gL:S.gL,bStart:S.bS,bStep:S.bT,bL:S.bL,smoothing:S.sm});}catch(_){}}
  else{try{ch.postMessage({type:'saveAsPalette',newName:newName});}catch(_){}}
};

function syncUI(){
  defs.forEach(d=>{
    document.getElementById(d.id+'ss').value=S[d.sK];document.getElementById(d.id+'sv').textContent=S[d.sK];
    document.getElementById(d.id+'ts').value=S[d.tK];document.getElementById(d.id+'tv').textContent='Step: '+S[d.tK];
    document.getElementById(d.id+'up').className='btn'+(S[d.lK]?' active':'');
    document.getElementById(d.id+'dn').className='btn'+(!S[d.lK]?' active':'');
  });
  document.getElementById('smoothCb').checked=S.sm;
  document.getElementById('pageInput').value=S.page;
  document.getElementById('pageInfo').textContent='(indexes '+(S.page*256)+'\u2013'+(S.page*256+255)+')';
  updateSolidPreview();
}

function setPaletteLabel(name){
  const safe=String(name||'').trim();
  document.title=safe?('Color Editor \u2014 '+safe):'Color Editor';
}

ch.onmessage=e=>{
  const d=e.data;
  if(d.type==='frame'){
    if(d.seq!=null&&d.seq<lastFrameSeq)return; // stale frame — ignore
    if(d.seq!=null)lastFrameSeq=d.seq;
    drawFrame(d.colors,d.width,d.height,d.rawIndex,d.interior);
    // The palette colours ride along on the frame only when they changed. Update
    // the editable swatch grid to match — unless the user has unsaved edits.
    if(d.fullColors&&d.fullColors.length>0&&!window.__editorDirty){
      baseColors=new Uint8Array(d.fullColors);
      localColors=new Uint8Array(baseColors);
      if(d.allPageControls)allPageControls=d.allPageControls;
      selStart=0;selEnd=255;
      updateColorGrid();updateSelInfo();
    }
    document.getElementById('status').textContent='';
  }
  else if(d.type==='init'||d.type==='state'){
    // A live 'state' push reflects the app's current palette. Apply it only
    // when the editor has no unsaved edits — otherwise it would clobber the
    // user's in-progress work, and echo the app's own edit back into a loop.
    // 'init' (fresh open) always applies.
    if(d.type==='state'&&window.__editorDirty)return;
    // Reject messages from a stale app instance. A hot-reloaded or duplicate
    // app can stay subscribed to this channel and answer 'ready' with its own
    // init carrying an old image + palette and a low seq. The live app seeds
    // its counter from a timestamp, so its seq always exceeds a ghost's; drop
    // anything older than the newest seq we've accepted.
    if(d.seq!=null&&d.seq<lastFrameSeq)return;
    if(d.paletteLabel!==undefined)setPaletteLabel(d.paletteLabel);
    if(d.paletteFilename!==undefined)window.__paletteFilename=d.paletteFilename;
    if(d.palettePage!==undefined)S.page=d.palettePage;
    if(d.numPages)S.pages=d.numPages;
    if(d.editorRedStart!==undefined)S.rS=d.editorRedStart;
    if(d.editorRedStep!==undefined)S.rT=d.editorRedStep;
    if(d.editorRedLighter!==undefined)S.rL=d.editorRedLighter;
    if(d.editorGreenStart!==undefined)S.gS=d.editorGreenStart;
    if(d.editorGreenStep!==undefined)S.gT=d.editorGreenStep;
    if(d.editorGreenLighter!==undefined)S.gL=d.editorGreenLighter;
    if(d.editorBlueStart!==undefined)S.bS=d.editorBlueStart;
    if(d.editorBlueStep!==undefined)S.bT=d.editorBlueStep;
    if(d.editorBlueLighter!==undefined)S.bL=d.editorBlueLighter;
    if(d.editorSmoothing!==undefined)S.sm=d.editorSmoothing;
    if(d.allPageControls)allPageControls=d.allPageControls;
    if(d.fullColors&&d.fullColors.length>0){baseColors=new Uint8Array(d.fullColors);localColors=new Uint8Array(baseColors);updateColorGrid();}
    if(d.colors&&d.colors.length>0){
      if(d.seq!=null)lastFrameSeq=d.seq;
      drawFrame(d.colors,d.width,d.height,d.rawIndex,d.interior);
    }
    selStart=0;selEnd=255;
    syncUI();updateSelInfo();document.getElementById('status').textContent='';
  }
};
try{ch.postMessage({type:'ready'});}catch(_){}

// Track unsaved edits
let dirty=false;
function setDirty(){dirty=true;window.__editorDirty=true;document.getElementById('status').textContent='Unsaved edits \u2014 press Save to keep them';}
// Beforeunload prompt for unsaved edits. Gated on window.__forceClose so
// the parent app can suppress the prompt before calling win.close() — the
// historical freeze on Linux/Wayland happened specifically when a
// beforeunload native confirm fired during a programmatic win.close(), so
// we only let the prompt run on user-initiated closes (e.g. clicking X).
window.addEventListener('beforeunload', e => {
  if (window.__forceClose) return;
  if (!window.__editorDirty) return;
  e.preventDefault();
  e.returnValue = '';
});
document.getElementById('saveBtn').addEventListener('click',()=>{dirty=false;window.__editorDirty=false;},true);
// Save As: clear dirty flag too, but only when the user actually proceeded
// with the save (didn't cancel the prompt). The button's onclick has already
// returned early on cancel before this fires, so we check if the most recent
// click resulted in a postMessage send by inspecting whether newName was
// committed. Simpler: hook the button's onclick to set a flag after a
// successful prompt+postMessage, then clear dirty here. We do this by
// chaining: after the existing onclick runs, if window.__lastSaveAsCommitted
// is true, clear dirty.
document.getElementById('saveAsBtn').addEventListener('click',()=>{
  // Slight delay so this runs after the button's onclick finishes its prompt
  // and postMessage. If the prompt was cancelled, __lastSaveAsCommitted stays
  // false and dirty is preserved.
  setTimeout(()=>{
    if(window.__lastSaveAsCommitted){dirty=false;window.__editorDirty=false;window.__lastSaveAsCommitted=false;}
  },0);
},false);
// eslint-disable-next-line
// eslint-disable-next-line
<\/script></body></html>`;
}

function buildSoundEditorHtml(voiceVol) {
  var h = "";
  var _vv = (typeof voiceVol === 'number' && voiceVol >= 0 && voiceVol <= 1) ? voiceVol : 0.8;
  h += "<!DOCTYPE html><html><head><title>Sound Editor</title><style>";
  h += "*{box-sizing:border-box;margin:0;padding:0;}";
  h += "body{background:#0f172a;color:#cbd5e1;font-family:system-ui,sans-serif;font-size:13px;padding:16px;}";
  h += "h2{font-size:12px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.15em;margin-bottom:12px;}";
  h += "h3{font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.12em;margin:18px 0 8px 0;}";
  h += ".row{display:flex;align-items:center;gap:10px;margin-bottom:12px;}";
  h += ".lbl{font-weight:700;font-size:12px;min-width:46px;}";
  h += "select{background:#1e293b;border:1px solid #334155;color:#f8fafc;border-radius:6px;padding:5px 8px;font-size:13px;}";
  h += "input[type=number]{background:#1e293b;border:1px solid #334155;color:#f8fafc;border-radius:6px;padding:4px 6px;font-size:13px;width:64px;}";
  h += "input[type=range]{width:96px;vertical-align:middle;}";
  h += "td input[type=range]{width:90px;}";
  h += ".rdout{font-variant-numeric:tabular-nums;color:#38bdf8;min-width:26px;display:inline-block;text-align:right;margin-left:6px;font-size:11px;}";
  h += "button{background:#243449;border:1px solid #334155;color:#f8fafc;border-radius:6px;padding:5px 10px;font-size:12px;font-weight:600;cursor:pointer;}";
  h += "button:hover{background:#2d4159;}";
  h += "button.del{padding:3px 8px;color:#fca5a5;}";
  h += "button.mv{padding:3px 6px;color:#93c5fd;margin-right:2px;}";
  h += "button.mv:disabled{opacity:0.3;cursor:default;}";
  h += "button.prev{background:#b45309;color:#fff;}";
  h += "button.prev.on{background:#f59e0b;color:#3b1d00;}";
  h += ".info{color:#64748b;font-size:11px;}";
  h += "#status{font-size:11px;color:#64748b;margin-top:10px;}";
  h += ".hint{color:#64748b;font-size:11px;margin:4px 0 14px 0;line-height:1.5;}";
  h += "table{border-collapse:collapse;width:100%;font-size:12px;margin-bottom:8px;}";
  h += "th,td{border:1px solid #334155;padding:4px 6px;text-align:center;}";
  h += "th{color:#94a3b8;font-weight:600;font-size:11px;}";
  h += "td.iname{text-align:left;font-weight:600;}";
  h += ".val{font-variant-numeric:tabular-nums;color:#38bdf8;min-width:30px;display:inline-block;}";
  h += "</style></head><body>";
  h += "<h2>Fractal Sound Editor</h2>";
  h += "<canvas id='fractalPreview' width='375' height='250' style='display:block;background:#000;border:1px solid #334155;border-radius:6px;margin-bottom:8px;cursor:pointer;'></canvas>";
  h += "<div id='previewHint' class='info' style='margin-bottom:14px;font-size:11px;'>Click anywhere on the preview to hear the band value at that point.</div>";
  h += "<div class='hint'>Global key and scale, plus the loudness palette. Each instrument is a channel: for each escape band its loudness = clamp(initial &plusmn; step&times;band). Instruments with loudness &gt; 0 sound together, each on its octave. Changes update the main app live &mdash; play a scan to hear them.</div>";
  h += "<div class='row'>";
  h += "<span class='lbl'>Key</span>";
  h += "<select id='rootSel'>";
  h += "<option>C</option><option>Db</option><option>D</option><option>Eb</option><option>E</option><option>F</option>";
  h += "<option>Gb</option><option>G</option><option>Ab</option><option>A</option><option>Bb</option><option>B</option>";
  h += "</select>";
  h += "<span class='lbl'>Scale</span>";
  h += "<select id='scaleSel'>";
  h += "<option value='major'>major</option><option value='minor'>minor</option>";
  h += "<option value='pentatonic'>pentatonic</option><option value='blues'>blues</option>";
  h += "<option value='wholetone'>whole-tone</option>";
  h += "</select>";
  h += "</div>";
  h += "<h3>Loudness palette</h3>";
  h += "<table id='instTable'><thead><tr>";
  h += "<th class='iname'>Instrument</th><th>initial</th><th>step</th><th>dir</th><th>octave</th><th></th>";
  h += "</tr></thead><tbody id='instBody'></tbody></table>";
  h += "<div class='row'>";
  h += "<span class='lbl'>Add</span>";
  h += "<select id='addSel'></select>";
  h += "<button id='addBtn'>+ Add instrument</button>";
  h += "</div>";
  h += "<h3>Sound maps</h3>";
  h += "<div class='hint'>Save the current instrument set to a file you can reload, share, or back up.</div>";
  h += "<div class='row'>";
  h += "<button id='saveMapBtn'>Save map to file</button>";
  h += "<button id='loadMapBtn'>Load map from file</button>";
  h += "<input id='loadMapFile' type='file' accept='.json' style='display:none' />";
  h += "</div>";
  h += "<div class='hint'>Or save to your local library by name (recall it later; you'll be warned before replacing one).</div>";
  h += "<div class='row'>";
  h += "<input id='mapNameInput' type='text' placeholder='map name' style='flex:1;min-width:80px;' />";
  h += "<button id='saveLibBtn'>Save to library</button>";
  h += "</div>";
  h += "<div class='row'>";
  h += "<select id='libSel'><option value=''>Load from library...</option></select>";
  h += "<button id='loadLibBtn'>Load</button>";
  h += "</div>";
  h += "<h3>Live preview</h3>";
  h += "<div class='hint'>Hear the active instruments together at one escape band. While preview is on, editing an instrument changes only that one in the sustained chord &mdash; the audible analog of the colour editor's live recolour.</div>";
  h += "<div class='row'>";
  h += "<button id='prevBtn' class='prev'>&#9654; Start preview</button>";
  h += "<span class='lbl'>Band</span>";
  h += "<input type='range' id='bandScrub' min='0' max='255' value='128'>";
  h += "<span class='val' id='bandVal'>128</span>";
  h += "</div>";
  h += "<div id='status'>Connecting...</div>";
  h += "<scr" + "ipt>";
  h += "var ch=new BroadcastChannel('fractal_sound_editor');";
  h += "var _sl=" + (spokenLabelsOn() ? "true" : "false") + ";";
  h += "function speakLabel(t){if(!_sl)return;if(!('speechSynthesis' in window))return;if(!t)return;try{window.speechSynthesis.cancel();}catch(e){}var u=new SpeechSynthesisUtterance(String(t));u.rate=0.95;u.volume=" + _vv + ";window.speechSynthesis.speak(u);}";
  h += "function sr(r){return ({C:'C',Db:'D flat',D:'D',Eb:'E flat',E:'E',F:'F',Gb:'G flat',G:'G',Ab:'A flat',A:'A',Bb:'B flat',B:'B'})[r]||r;}";
  h += "function ss(s){return ({major:'major',minor:'minor',pentatonic:'pentatonic',blues:'blues',wholetone:'whole tone'})[s]||s;}";
  h += "var rootSel=document.getElementById('rootSel');";
  h += "var scaleSel=document.getElementById('scaleSel');";
  h += "var statusEl=document.getElementById('status');";
  h += "var addSel=document.getElementById('addSel');";
  h += "var addBtn=document.getElementById('addBtn');";
  h += "var instBody=document.getElementById('instBody');";
  h += "var prevBtn=document.getElementById('prevBtn');";
  h += "var bandScrub=document.getElementById('bandScrub');";
  h += "var bandVal=document.getElementById('bandVal');";
  h += "var palette=[];";
  h += "var instList=[];";
  h += "var previewActive=false;";
  h += "var previewBand=128;";
  h += "function updateStatus(){statusEl.textContent='Connected. Key '+sr(rootSel.value)+' '+ss(scaleSel.value)+'. '+palette.length+' instrument(s).';}";
  h += "function sendKey(){ch.postMessage({type:'key',root:rootSel.value,scale:scaleSel.value});updateStatus();}";
  h += "function sendPalette(){ch.postMessage({type:'palette',palette:palette});updateStatus();}";
  h += "function sendPreviewUpdate(inst){if(previewActive){ch.postMessage({type:'previewUpdate',band:previewBand,instrument:inst});}}";
  h += "function sendPreviewStart(){ch.postMessage({type:'previewStart',band:previewBand});}";
  h += "function sendPreviewStop(){ch.postMessage({type:'previewStop'});}";
  h += "function nice(n){return n.replace(/_/g,' ');}";
  h += "function fillPicker(){addSel.innerHTML='';for(var i=0;i<instList.length;i++){var o=document.createElement('option');o.value=instList[i];o.textContent=nice(instList[i]);addSel.appendChild(o);}}";
  h += "function renderTable(){";
  h += "instBody.innerHTML='';";
  h += "for(var i=0;i<palette.length;i++){";
  h += "var p=palette[i];";
  h += "var tr=document.createElement('tr');";
  h += "var td0=document.createElement('td');td0.className='iname';td0.textContent=nice(p.instrument);tr.appendChild(td0);";
  h += "tr.appendChild(sliderCell(i,'initial',p.initial,-127,127,1));";
  h += "tr.appendChild(sliderCell(i,'step',p.step,0,32,0.5));";
  h += "tr.appendChild(dirCell(i,p.direction));";
  h += "tr.appendChild(numCell(i,'octave',p.octave,-3,3,1));";
  h += "var tdd=document.createElement('td');";
  h += "var ub=document.createElement('button');ub.className='mv';ub.textContent='\u25b2';ub.title='Move up';ub.setAttribute('data-i',i);if(i===0){ub.disabled=true;}ub.onclick=function(){var idx=parseInt(this.getAttribute('data-i'),10);if(idx<=0)return;var t=palette[idx-1];palette[idx-1]=palette[idx];palette[idx]=t;renderTable();sendPalette();};tdd.appendChild(ub);";
  h += "var wb=document.createElement('button');wb.className='mv';wb.textContent='\u25bc';wb.title='Move down';wb.setAttribute('data-i',i);if(i===palette.length-1){wb.disabled=true;}wb.onclick=function(){var idx=parseInt(this.getAttribute('data-i'),10);if(idx>=palette.length-1)return;var t=palette[idx+1];palette[idx+1]=palette[idx];palette[idx]=t;renderTable();sendPalette();};tdd.appendChild(wb);";
  h += "var db=document.createElement('button');db.className='del';db.textContent='\u00d7';db.setAttribute('data-i',i);db.onclick=function(){var idx=parseInt(this.getAttribute('data-i'),10);var inst=palette[idx].instrument;palette.splice(idx,1);renderTable();sendPalette();sendPreviewUpdate(inst);};tdd.appendChild(db);tr.appendChild(tdd);";
  h += "instBody.appendChild(tr);";
  h += "}";
  h += "}";
  h += "function sliderCell(i,field,val,mn,mx,stp){";
  h += "var td=document.createElement('td');";
  h += "var wrap=document.createElement('div');wrap.style.display='flex';wrap.style.alignItems='center';wrap.style.justifyContent='center';";
  h += "var inp=document.createElement('input');inp.type='range';inp.value=val;inp.min=mn;inp.max=mx;inp.step=stp;";
  h += "inp.setAttribute('data-i',i);inp.setAttribute('data-f',field);";
  h += "var out=document.createElement('input');out.type='number';out.className='rdout';out.value=(Math.round(val*100)/100);out.min=mn;out.max=mx;out.step=stp;out.style.width='58px';out.style.marginLeft='6px';";
  h += "inp.oninput=function(){var idx=parseInt(this.getAttribute('data-i'),10);var f=this.getAttribute('data-f');var v=parseFloat(this.value);if(isNaN(v))v=0;palette[idx][f]=v;out.value=(Math.round(v*100)/100);sendPalette();sendPreviewUpdate(palette[idx].instrument);};";
  h += "out.oninput=function(){var idx=parseInt(inp.getAttribute('data-i'),10);var f=inp.getAttribute('data-f');var v=parseFloat(this.value);if(isNaN(v))return;palette[idx][f]=v;inp.value=v;sendPalette();sendPreviewUpdate(palette[idx].instrument);};";
  h += "wrap.appendChild(inp);wrap.appendChild(out);td.appendChild(wrap);return td;";
  h += "}";
  h += "function numCell(i,field,val,mn,mx,stp){";
  h += "var td=document.createElement('td');";
  h += "var inp=document.createElement('input');inp.type='number';inp.value=val;inp.min=mn;inp.max=mx;inp.step=stp;";
  h += "inp.setAttribute('data-i',i);inp.setAttribute('data-f',field);";
  h += "inp.oninput=function(){var idx=parseInt(this.getAttribute('data-i'),10);var f=this.getAttribute('data-f');var v=parseFloat(this.value);if(isNaN(v))v=0;palette[idx][f]=v;sendPalette();sendPreviewUpdate(palette[idx].instrument);};";
  h += "td.appendChild(inp);return td;";
  h += "}";
  h += "function dirCell(i,val){";
  h += "var td=document.createElement('td');";
  h += "var sel=document.createElement('select');";
  h += "var o1=document.createElement('option');o1.value='1';o1.textContent='+';";
  h += "var o2=document.createElement('option');o2.value='-1';o2.textContent='\u2212';";
  h += "sel.appendChild(o1);sel.appendChild(o2);sel.value=(val===-1?'-1':'1');";
  h += "sel.setAttribute('data-i',i);";
  h += "sel.onchange=function(){var idx=parseInt(this.getAttribute('data-i'),10);palette[idx].direction=(this.value==='-1'?-1:1);sendPalette();sendPreviewUpdate(palette[idx].instrument);};";
  h += "td.appendChild(sel);return td;";
  h += "}";
  h += "addBtn.onclick=function(){var id=addSel.value;if(!id)return;for(var i=0;i<palette.length;i++){if(palette[i].instrument===id){speakLabel(nice(id)+' already added.');return;}}palette.push({instrument:id,initial:0,step:1,direction:1,octave:0});renderTable();sendPalette();sendPreviewUpdate(id);speakLabel('Added '+nice(id)+'.');};";
  h += "prevBtn.onclick=function(){previewActive=!previewActive;if(previewActive){prevBtn.className='prev on';prevBtn.innerHTML='&#9632; Stop preview';sendPreviewStart();speakLabel('Preview on.');}else{prevBtn.className='prev';prevBtn.innerHTML='&#9654; Start preview';sendPreviewStop();speakLabel('Preview off.');}};";
  h += "bandScrub.oninput=function(){previewBand=parseInt(bandScrub.value,10)||0;bandVal.textContent=previewBand;if(previewActive){sendPreviewStart();}};";
  h += "rootSel.addEventListener('change',function(){sendKey();speakLabel('Key '+sr(rootSel.value)+'.');if(previewActive){sendPreviewStart();}});";
  h += "scaleSel.addEventListener('change',function(){sendKey();speakLabel('Scale '+ss(scaleSel.value)+'.');if(previewActive){sendPreviewStart();}});";
  h += "rootSel.addEventListener('focus',function(){speakLabel('Key. Currently '+sr(rootSel.value)+'. Use arrow keys to change.');});";
  h += "scaleSel.addEventListener('focus',function(){speakLabel('Scale. Currently '+ss(scaleSel.value)+'. Use arrow keys to change.');});";
  h += "var saveMapBtn=document.getElementById('saveMapBtn');";
  h += "var loadMapBtn=document.getElementById('loadMapBtn');";
  h += "saveMapBtn.onclick=function(){var nm=window.prompt('Save sound map as (file name):','sound-map');if(!nm)return;ch.postMessage({type:'downloadSoundMap',name:nm,palette:palette});speakLabel('Saving map '+nm+'.');};";
  h += "var loadMapFile=document.getElementById('loadMapFile');";
  h += "loadMapBtn.onclick=function(){loadMapFile.click();};";
  h += "loadMapFile.onchange=function(ev){var f=ev.target.files&&ev.target.files[0];if(!f){return;}var rd=new FileReader();rd.onload=function(){try{var obj=JSON.parse(rd.result);var pal=Array.isArray(obj)?obj:((obj&&Array.isArray(obj.palette))?obj.palette:null);if(pal){ch.postMessage({type:'applySoundMap',palette:pal});speakLabel('Loaded sound map from file.');}else{speakLabel('That file is not a sound map.');}}catch(err){speakLabel('Could not read that file.');}loadMapFile.value='';};rd.readAsText(f);};";
  h += "var mapNameInput=document.getElementById('mapNameInput');";
  h += "var saveLibBtn=document.getElementById('saveLibBtn');";
  h += "var libSel=document.getElementById('libSel');";
  h += "var loadLibBtn=document.getElementById('loadLibBtn');";
  h += "function refreshLib(){ch.postMessage({type:'listSoundMapsLib'});}";
  h += "saveLibBtn.onclick=function(){var nm=(mapNameInput.value||'').trim();if(!nm){speakLabel('Enter a name first.');return;}ch.postMessage({type:'saveSoundMapLib',name:nm,palette:palette});};";
  h += "loadLibBtn.onclick=function(){var nm=libSel.value;if(!nm){speakLabel('No map selected.');return;}ch.postMessage({type:'loadSoundMapLib',name:nm});speakLabel('Loading '+nm+'.');};";
  h += "refreshLib();";
  h += "ch.onmessage=function(e){var m=e.data||{};";
  h += "if((m.type==='init'||m.type==='frame')&&m.colors&&m.rawIndex&&m.width&&m.height){";
  h += "  var pc=document.getElementById('fractalPreview');";
  h += "  var pctx=pc.getContext('2d');";
  h += "  pc._frameColors=m.colors;pc._frameRawIndex=m.rawIndex;pc._frameW=m.width;pc._frameH=m.height;";
  h += "  var src=pctx.createImageData(m.width,m.height);";
  h += "  var srcData=src.data;var col=m.colors;var srcLen=m.width*m.height;";
  h += "  for(var i=0;i<srcLen;i++){var idx=i*3;var di=i*4;srcData[di]=col[idx];srcData[di+1]=col[idx+1];srcData[di+2]=col[idx+2];srcData[di+3]=255;}";
  h += "  var oc=document.createElement('canvas');oc.width=m.width;oc.height=m.height;oc.getContext('2d').putImageData(src,0,0);";
  h += "  pctx.clearRect(0,0,pc.width,pc.height);pctx.drawImage(oc,0,0,pc.width,pc.height);";
  h += "}";
  h += "if(m.type==='init'||m.type==='state'){";
  h += "if(m.root){rootSel.value=m.root;}if(m.scale){scaleSel.value=m.scale;}";
  h += "if(m.instruments&&m.instruments.length){instList=m.instruments.slice().sort(function(a,b){var na=nice(a).toLowerCase(),nb=nice(b).toLowerCase();return na<nb?-1:(na>nb?1:0);});fillPicker();}";
  h += "if(m.palette){palette=JSON.parse(JSON.stringify(m.palette));renderTable();}";
  h += "updateStatus();";
  h += "}";
  h += "else if(m.type==='soundMapLibList'){var cur=libSel.value;libSel.innerHTML='';var o0=document.createElement('option');o0.value='';o0.textContent='Load from library...';libSel.appendChild(o0);var names=m.names||[];for(var li=0;li<names.length;li++){var lo=document.createElement('option');lo.value=names[li];lo.textContent=names[li];libSel.appendChild(lo);}libSel.value=cur;}";
  h += "else if(m.type==='soundMapSaved'){speakLabel('Saved '+m.name+' to library.');if(mapNameInput)mapNameInput.value='';}";
  h += "};";
  h += "ch.postMessage({type:'ready'});";
  h += "statusEl.textContent='Ready - waiting for app...';";
  h += "var clickPreviewTimer=null;";
  h += "document.getElementById('fractalPreview').addEventListener('click',function(e){";
  h += "  var pc=document.getElementById('fractalPreview');";
  h += "  if(!pc._frameRawIndex||!pc._frameW||!pc._frameH){";
  h += "    document.getElementById('previewHint').textContent='No fractal data yet. Render a fractal in the main window first.';";
  h += "    return;";
  h += "  }";
  h += "  var rect=pc.getBoundingClientRect();";
  h += "  var x=Math.floor((e.clientX-rect.left)*(pc._frameW/rect.width));";
  h += "  var y=Math.floor((e.clientY-rect.top)*(pc._frameH/rect.height));";
  h += "  if(x<0||x>=pc._frameW||y<0||y>=pc._frameH)return;";
  h += "  var band=pc._frameRawIndex[y*pc._frameW+x];";
  h += "  band=Math.max(0,Math.min(255,band));";
  h += "  document.getElementById('previewHint').textContent='Playing band '+band+' (pixel '+x+','+y+').';";
  h += "  ch.postMessage({type:'previewStart',band:band});";
  h += "  if(clickPreviewTimer)clearTimeout(clickPreviewTimer);";
  h += "  clickPreviewTimer=setTimeout(function(){";
  h += "    ch.postMessage({type:'previewStop'});";
  h += "    clickPreviewTimer=null;";
  h += "    document.getElementById('previewHint').textContent='Click anywhere on the preview to hear the band value at that point.';";
  h += "  },1000);";
  h += "});";
  h += "window.addEventListener('beforeunload',function(){try{if(previewActive){sendPreviewStop();}ch.postMessage({type:'closed'});}catch(e){}});";
  h += "</scr" + "ipt>";
  h += "</body></html>";
  return h;
}
// ── Voice Control ─────────────────────────────────────────────────────────────
// AI calls go through Firebase Cloud Functions so the provider keys live
// server-side (defineSecret), never in the client bundle. getFunctions() uses
// the default app already initialized via ./userTier -> ./firebase on import.
const aiFns = getFunctions();
const aiParseCommand = httpsCallable(aiFns, 'aiParseCommand');
const aiTranscribe = httpsCallable(aiFns, 'aiTranscribe');
// Convert a Blob to a bare base64 string (no "data:...;base64," prefix) for the
// transcribe proxy, which expects request.data.audioBase64.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function interpretVoiceCommand(transcript, context) {
  dlog('[Voice] Interpreting:', transcript);
  const system = `You are the voice interface for a fractal music app. Understand what the user WANTS. Always return a single valid JSON object. No markdown. Always include "spokenResponse" in the same language the user spoke.

USER LANGUAGE: ${context.userLanguage || 'en-US'}. Only accept commands in this language. If the transcript is in a different language, return {"action":"idle","spokenResponse":""}.

CURRENT STATE: fractal=${context.fractalType}, user=${context.userName}, palettes=${context.paletteNames}, view center=(${((context.xMin+context.xMax)/2).toFixed(3)}, ${((context.yMin+context.yMax)/2).toFixed(3)}).

FRACTAL SETTINGS (voice only):
"set max iterations to N","N iterations","max iter N" → {"action":"setMaxIter","value":N,"spokenResponse":"Max iterations set to N."}
Navigation is done with keyboard arrow keys — no voice nav commands needed.

PRESETS: "mandelbrot"(or "nando broad") → {"action":"loadPreset","presetKey":"mandelbrotFull","spokenResponse":"Loading Mandelbrot."}
"seahorse" → {"action":"loadPreset","presetKey":"seahorseValley","spokenResponse":"Loading seahorse valley."}
"elephant" → {"action":"loadPreset","presetKey":"elephantValley","spokenResponse":"Loading elephant valley."}
"julia classic" → {"action":"loadPreset","presetKey":"juliaClassic","spokenResponse":"Loading Julia set."}
"julia spiral" → {"action":"loadPreset","presetKey":"juliaSpiral","spokenResponse":"Loading Julia spiral."}
"list presets","presets" → {"action":"listPresets","spokenResponse":"Opening preset list."}
NAVIGATION: "back","go back" → {"action":"back","spokenResponse":"Going back."} "forward" → {"action":"forward","spokenResponse":"Going forward."}
FRACTAL TYPE: "list fractal types" → {"action":"listFractalTypes","spokenResponse":"Opening fractal type list."} "switch to mandelbrot" → {"action":"setFractalType","fractalType":"mandelbrot","spokenResponse":"Switched."} "switch to julia" → {"action":"setFractalType","fractalType":"julia","spokenResponse":"Switched."} "render","draw" → {"action":"renderFractal","spokenResponse":"Rendering."}
PLAYBACK: "play","start","play music","play full scan","play full screen","start scan","start playback","full scan" → {"action":"startScan","spokenResponse":"Starting scan."} "resume","resume here","resume playback","continue","continue playback" → {"action":"resume","spokenResponse":"Resuming."} "pause","stop","stop playback","stop the music","stop music","pause playback","pause the music","pause music","halt","hold on","freeze","paws","pose","pause it" → {"action":"pause","spokenResponse":"Paused."} "reset" → {"action":"reset","spokenResponse":"Reset."} "list modes","modes" → {"action":"listModes","spokenResponse":"Opening mode list."} "orbit mode","switch to orbit","switch to orbit mode" → {"action":"setMode","mode":"orbit","spokenResponse":"Orbit mode."} "scan mode","switch to scan","switch to scan mode" → {"action":"setMode","mode":"scan","spokenResponse":"Scan mode."}
MOVEMENTS (during playback, mark a section boundary at the current scan position — same as the Enter key; lets blind users compose sections by ear): "mark here","mark","mark boundary","mark this","mark this point","new movement","new section","new movement here","section boundary","drop a marker" → {"action":"markBoundary","spokenResponse":""}
ORBIT: "orbit the line","orbit halfway" → {"action":"orbitLineAt","position":0.5,"spokenResponse":"Midpoint."} "orbit a third up" → {"action":"orbitLineAt","position":0.333,"spokenResponse":"A third up."} "orbit two thirds up" → {"action":"orbitLineAt","position":0.667,"spokenResponse":"Two thirds up."} "orbit a quarter up" → {"action":"orbitLineAt","position":0.25,"spokenResponse":"Quarter up."} "orbit three quarters" → {"action":"orbitLineAt","position":0.75,"spokenResponse":"Three quarters."} "orbit at the top" → {"action":"orbitLineAt","position":0.9,"spokenResponse":"Near top."} "orbit at the bottom" → {"action":"orbitLineAt","position":0.1,"spokenResponse":"Near bottom."} "orbit at N percent" → {"action":"orbitLineAt","position":N/100,"spokenResponse":"N percent."}
PREVIEW (describes the structural location of an orbit candidate point WITHOUT playing audio — blind users can probe before committing): "preview halfway","describe orbit halfway","what is at halfway","what's halfway" → {"action":"previewOrbit","position":0.5,"spokenResponse":""} "preview at the top","describe orbit at the top","what is at the top" → {"action":"previewOrbit","position":0.9,"spokenResponse":""} "preview at the bottom","describe orbit at the bottom","what is at the bottom" → {"action":"previewOrbit","position":0.1,"spokenResponse":""} "preview a quarter up","describe orbit a quarter up" → {"action":"previewOrbit","position":0.25,"spokenResponse":""} "preview three quarters","describe orbit three quarters" → {"action":"previewOrbit","position":0.75,"spokenResponse":""} "preview a third up","describe orbit a third up" → {"action":"previewOrbit","position":0.333,"spokenResponse":""} "preview two thirds up","describe orbit two thirds up" → {"action":"previewOrbit","position":0.667,"spokenResponse":""} "preview at N percent","describe orbit at N percent","what is at N percent" → {"action":"previewOrbit","position":N/100,"spokenResponse":""} "preview here","describe orbit here","what is here" → {"action":"previewOrbit","spokenResponse":""}
"play orbit here","orbit here" → {"action":"playOrbitHere","spokenResponse":"Playing orbit at view center."} "list orbit points","orbit points" → {"action":"listOrbitPoints","spokenResponse":"Listing orbit points."} "orbit" no point → {"action":"listOrbitPoints","spokenResponse":"Listing orbit points."} "orbit at RE IM" → {"action":"playOrbit","re":RE,"im":IM,"spokenResponse":"Playing orbit."} "where am I" → {"action":"whereAmI","spokenResponse":"..."}
SECTIONS: "open the drop-down","open the list","show the list","open menu","open the dropdown" → {"action":"openDropdown","spokenResponse":""}
"go to [control name]","focus [control name]" → {"action":"focusControl","controlName":"name","spokenResponse":""} where control names include: column speed, stereo width, music volume, voice volume, max iterations, x min, x max, y min, y max, preset, fractal type, mode, palette
"go to palette","go to pallet","go to polly" → {"action":"goToSection","section":"palette","spokenResponse":"Palette section."}
"go to transport","transport section","transport and audio" → {"action":"goToSection","section":"transport","spokenResponse":"Transport and audio section."}
"go to fractal","fractal section","fractal definition" → {"action":"goToSection","section":"fractal","spokenResponse":"Fractal definition section."}
PALETTE: "list palettes","list palets","list pallets","palettes","palets","colours","show palettes","show palette list","palette list","open palette list" → {"action":"listPalettes","spokenResponse":"Opening palette list."} "select [name] palette" → {"action":"selectPalette","paletteName":"name","spokenResponse":"Palette changed."} "turn on palette bank","enable palette bank","use palette bank" → {"action":"togglePaletteBank","value":true,"spokenResponse":"Palette bank on."} "turn off palette bank","disable palette bank" → {"action":"togglePaletteBank","value":false,"spokenResponse":"Palette bank off."} "turn on color sound","enable color sound","color influences sound on" → {"action":"toggleColorSound","value":true,"spokenResponse":"Color influences sound on."} "turn off color sound","disable color sound" → {"action":"toggleColorSound","value":false,"spokenResponse":"Color influences sound off."}
AUDIO: "louder","volume up" → {"action":"setMusicVolume","value":80,"spokenResponse":"Volume up."} "quieter","volume down" → {"action":"setMusicVolume","value":40,"spokenResponse":"Volume down."} "mute" → {"action":"setMusicVolume","value":0,"spokenResponse":"Muted."} "set volume to N","set music volume to N" → {"action":"setMusicVolume","value":N,"spokenResponse":"Volume set to N."} "faster" → {"action":"setColumnSpeed","seconds":0.5,"spokenResponse":"Speed increased."} "slower" → {"action":"setColumnSpeed","seconds":2.0,"spokenResponse":"Speed decreased."} "set column speed to N seconds","set speed to N" → {"action":"setColumnSpeed","seconds":N,"spokenResponse":"Column speed set to N seconds."} "set stereo width to N","stereo width N" → {"action":"setStereoWidth","value":N,"spokenResponse":"Stereo width set to N."} "set voice volume to N" → {"action":"setVoiceVolume","value":N,"spokenResponse":"Voice volume set to N."} "scan horizontally","horizontal scan","scan by rows","scan rows" → {"action":"setScanOrientation","value":"horizontal","spokenResponse":"Horizontal scan. Takes effect on next play."} "scan vertically","vertical scan","scan by columns","scan columns" → {"action":"setScanOrientation","value":"vertical","spokenResponse":"Vertical scan. Takes effect on next play."}
SAVING: "save image","save PNG" → {"action":"savePNG","spokenResponse":"Saving image."} "save file","save IMG" → {"action":"saveIMG","spokenResponse":"Saving file."} "import image","import img" → {"action":"importImg","spokenResponse":"Opening import."} "import palette","import palette bank" → {"action":"importPalette","spokenResponse":"Opening palette import."} "open [filename]" → {"action":"openImgFromLibrary","filename":"best match","spokenResponse":"Loading file."}
KEYBOARD: Map verbal commands to keypresses. All commands are at least two words to avoid Whisper mis-transcription. CRITICAL: Whisper frequently transcribes the word "tab" as "tap" — these are the SAME word for our purposes. Any command containing "tab" OR "tap" followed by a direction (forward/back/backward/next/previous) is a Tab keypress. NEVER route "tap forward" or "tap backward" to anything else; they are NEVER navigation history commands. "tab key forward","tab key next","tab forward","tap forward","tap key forward","tab next","tap next" → {"action":"pressKey","key":"Tab","spokenResponse":""} "tab key back","tab key backward","tab key previous","tab back","tab backward","tap backward","tap back","tap previous","shift tab" → {"action":"pressKey","key":"Tab","shift":true,"spokenResponse":""} "enter key","press enter","activate button" → {"action":"pressKey","key":"Enter","spokenResponse":""} "space key","space bar","press space" → {"action":"pressKey","key":" ","spokenResponse":""} "escape key","press escape","cancel that" → {"action":"pressKey","key":"Escape","spokenResponse":""} "up arrow","arrow up" → {"action":"pressKey","key":"ArrowUp","spokenResponse":""} "down arrow","arrow down" → {"action":"pressKey","key":"ArrowDown","spokenResponse":""} "left arrow","arrow left" → {"action":"pressKey","key":"ArrowLeft","spokenResponse":""} "right arrow","arrow right" → {"action":"pressKey","key":"ArrowRight","spokenResponse":""} REPEAT COUNT: When the user appends a number to an arrow command, attach a count field. Accept integer ("right arrow 3"), number-word ("right arrow three"), "N times" ("down five times"), and "twice"/"thrice" (=2/=3). Cap at 50; if asked for more, clamp to 50 and mention the cap in spokenResponse. Examples: "right arrow 3" → {"action":"pressKey","key":"ArrowRight","count":3,"spokenResponse":"Right arrow three times."} "down five times" → {"action":"pressKey","key":"ArrowDown","count":5,"spokenResponse":"Down arrow five times."} "up arrow twice" → {"action":"pressKey","key":"ArrowUp","count":2,"spokenResponse":"Up arrow twice."} "left arrow 100" → {"action":"pressKey","key":"ArrowLeft","count":50,"spokenResponse":"Left arrow 50 times. That is the maximum per command."} Without an explicit count, omit the field and behave as a single press. "control left arrow","ctrl left arrow" → {"action":"pressKey","key":"ArrowLeft","ctrl":true,"spokenResponse":""} "control right arrow","ctrl right arrow" → {"action":"pressKey","key":"ArrowRight","ctrl":true,"spokenResponse":""} "control up arrow","ctrl up arrow" → {"action":"pressKey","key":"ArrowUp","ctrl":true,"spokenResponse":""} "control down arrow","ctrl down arrow" → {"action":"pressKey","key":"ArrowDown","ctrl":true,"spokenResponse":""}
LIST: "open list","open the list","open dropdown","expand list" → {"action":"openList","spokenResponse":""} (Opens whichever dropdown the user is currently focused on, OR the most relevant one for the section they're in. Returns a verbal description of how to navigate it and announces the currently selected item.)
JUMP: "jump to [name]","go to [name] button","focus [name]","select [name] button" → {"action":"jumpTo","target":"name as spoken","spokenResponse":""} where [name] is the visible label or button text the user wants to focus (e.g. "save as PNG", "help", "color editor", "load preset", "import palette bank", "music volume", "stereo width", "play full scan", "resume here", "pause", "preset", "fractal type", "mode"). Distinct from "go to [section]" which jumps to a SECTION; "jump to" jumps to a specific button or control. If the user says just "go to save" or "go to PNG" without "section", treat as jumpTo. SYNONYMS: "save as image"/"save as the image"/"save image file"/"save IMG file" → target should be "Save as IMG". "save as PNG"/"save as the PNG"/"save PNG file"/"save picture" → target should be "Save as PNG".
"focus canvas","focus the canvas","focus image","focus the image","focus fractal","go to canvas","go to image","go to fractal canvas","jump to canvas","jump to image","focus grid","focus the grid","go to grid","go to the grid","jump to grid","go to first cell","focus first cell","focus cell one","first cell" → {"action":"focusCanvas","spokenResponse":"Canvas focused. Use arrow keys to pan, plus or minus to zoom."}
"show grid","show the grid","turn on grid","turn the grid on","grid on","enable grid","display grid","display the grid" → {"action":"setShowGrid","value":true,"spokenResponse":"Grid shown."}
"hide grid","hide the grid","turn off grid","turn the grid off","grid off","disable grid","remove grid","remove the grid" → {"action":"setShowGrid","value":false,"spokenResponse":"Grid hidden."}
"toggle grid","toggle the grid","switch grid","flip grid" → {"action":"setShowGrid","value":"toggle"}
DESCRIBE: "describe","describe this","describe control","what does this do","what does this control do","what is this","explain this","help on this","tell me about this control" → {"action":"describe","spokenResponse":""} (Reads aloud a longer description of the currently focused control. If the control has no description, the app says so.)
LIST ITEMS: "list items","list all items","read items","read all items","what's in this list","read the list","what are my choices" → {"action":"listItems","spokenResponse":""} (Reads aloud the names of items inside the currently-open dialog or focused section. Useful for browsing dialog contents like the palette bank.)
PAN: "pan up","pan upward","move up" → {"action":"pan","direction":"up","spokenResponse":"Panning up."} "pan down","pan downward","move down" → {"action":"pan","direction":"down","spokenResponse":"Panning down."} "pan left","move left" → {"action":"pan","direction":"left","spokenResponse":"Panning left."} "pan right","move right" → {"action":"pan","direction":"right","spokenResponse":"Panning right."}
ZOOM: "zoom in","zoom closer" → {"action":"zoom","direction":"in","spokenResponse":"Zooming in."} "zoom out","zoom away" → {"action":"zoom","direction":"out","spokenResponse":"Zooming out."}
"start eye tracking","enable eye tracking" → {"action":"startEyeTracking","spokenResponse":"Starting eye tracking."}
"stop eye tracking","disable eye tracking" → {"action":"stopEyeTracking","spokenResponse":"Eye tracking stopped."}
"recalibrate","recalibrate eye tracking","calibrate again","recalibrate eyes" → {"action":"recalibrateEyeTracking","spokenResponse":""}
"where am i","what's the current state","describe state","state","status","où suis-je","dónde estoy","wo bin ich","dove sono" → {"action":"whereAmI","spokenResponse":""}
SYSTEM: "help","what can I say" → {"action":"help","spokenResponse":"Say: go to fractal, go to palette, go to transport, list presets, list palettes, list modes, play, pause, save image, back, forward, set max iterations to N, orbit."} "goodbye","stop listening","escape","quit","bye" → {"action":"stopListening","spokenResponse":"Goodbye."} "open color editor","open colour editor","launch color editor","launch colour editor" → {"action":"openColorEditor","spokenResponse":"Opening color editor."} "close color editor","close colour editor","close the color editor","close the colour editor","close editor" → {"action":"closeColorEditor","spokenResponse":"Closing color editor."} "close help","close the help","exit help","close help dialog","close user guide" → {"action":"closeHelp","spokenResponse":"Closing help."} "go to color editor","go to colour editor","jump to color editor","jump to colour editor","focus color editor","focus colour editor" → {"action":"jumpTo","target":"Color editor","spokenResponse":""} Questions → {"action":"question","answer":"answer","spokenResponse":"answer"} Unclear/background noise/non-command → {"action":"idle","spokenResponse":""}
COLOR EDITOR (only when editor is open): "set red to N","red to N" → {"action":"colorEditor","sub":"set","channel":"r","field":"value","value":N,"spokenResponse":"Red set to N."} "set green to N","green to N" → {"action":"colorEditor","sub":"set","channel":"g","field":"value","value":N,"spokenResponse":"Green set to N."} "set blue to N","blue to N" → {"action":"colorEditor","sub":"set","channel":"b","field":"value","value":N,"spokenResponse":"Blue set to N."} "set red step to N","red step N" → {"action":"colorEditor","sub":"set","channel":"r","field":"step","value":N,"spokenResponse":"Red step N."} "set green step to N" → {"action":"colorEditor","sub":"set","channel":"g","field":"step","value":N,"spokenResponse":"Green step N."} "set blue step to N" → {"action":"colorEditor","sub":"set","channel":"b","field":"step","value":N,"spokenResponse":"Blue step N."} "red up","red increasing" → {"action":"colorEditor","sub":"dir","channel":"r","up":true,"spokenResponse":"Red increasing."} "red down","red decreasing" → {"action":"colorEditor","sub":"dir","channel":"r","up":false,"spokenResponse":"Red decreasing."} "green up","green increasing" → {"action":"colorEditor","sub":"dir","channel":"g","up":true,"spokenResponse":"Green increasing."} "green down","green decreasing" → {"action":"colorEditor","sub":"dir","channel":"g","up":false,"spokenResponse":"Green decreasing."} "blue up","blue increasing" → {"action":"colorEditor","sub":"dir","channel":"b","up":true,"spokenResponse":"Blue increasing."} "blue down","blue decreasing" → {"action":"colorEditor","sub":"dir","channel":"b","up":false,"spokenResponse":"Blue decreasing."} "next page","page forward" → {"action":"colorEditor","sub":"page","delta":1,"spokenResponse":""} "previous page","page back" → {"action":"colorEditor","sub":"page","delta":-1,"spokenResponse":""} "page N","go to page N","jump to page N" → {"action":"colorEditor","sub":"page","absolute":N,"spokenResponse":"Page N."} "smoothing on","enable smoothing","turn on smoothing" → {"action":"colorEditor","sub":"smoothing","value":true,"spokenResponse":"Smoothing on."} "smoothing off","disable smoothing","turn off smoothing" → {"action":"colorEditor","sub":"smoothing","value":false,"spokenResponse":"Smoothing off."} "reset page","reset this page" → {"action":"colorEditor","sub":"reset","spokenResponse":"Page reset."} "apply to all","apply to all pages" → {"action":"colorEditor","sub":"applyAll","spokenResponse":"Applied to all pages."} "save palette","save as tmp","save tmp" → {"action":"colorEditor","sub":"save","spokenResponse":"Saving palette."}
COMPOUND: For sentences combining multiple settings — e.g. "set red to 100 and green to 50", "change green to 4 and blue to 13", "red up and green down", "red to 200, green to 100, blue to 50" — return a list of actions: {"action":"actions","list":[ACTION1, ACTION2, ...],"spokenResponse":"concise summary"} where each item in 'list' is one of the single-action shapes above with empty spokenResponse. Example: "set red to 100 and green to 50" → {"action":"actions","list":[{"action":"colorEditor","sub":"set","channel":"r","field":"value","value":100,"spokenResponse":""},{"action":"colorEditor","sub":"set","channel":"g","field":"value","value":50,"spokenResponse":""}],"spokenResponse":"Red 100, green 50."}
Speech in a different language than the user's language → {"action":"idle","spokenResponse":""}
Recognisable words that don't match any command → {"action":"unknown","spokenResponse":""}
RULES: 1) Guess intent generously. 2) ONLY "goodbye"/"bye"/"stop listening"/"escape"/"quit" → stopListening. NEVER return stopListening for navigation phrases like "open", "show", "go to", "list". "stop" alone = pause. 3) Respond in user language. 4) Navigation is keyboard-only.`;

  try {
    const { data } = await aiParseCommand({ system, transcript });
    const raw = (data.text || '').trim();
    const start = raw.indexOf('{');
    const end = raw.lastIndexOf('}');
    if (start === -1 || end === -1) {
      console.warn('[Voice] No JSON in response:', raw.substring(0, 100));
      return { action: 'idle', spokenResponse: '' };
    }
    const text = raw.substring(start, end + 1);
    dlog('[Voice] Action:', text);
    return JSON.parse(text);
  } catch (err) {
    console.error('Voice interpret error:', err);
    return { action: 'unknown', spokenResponse: '' };
  }
}

// Voice help is now generated dynamically by Claude in the user's language.

export default function App() {
  const saved = useMemo(() => loadSavedState(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Read preset + kiosk flags from URL. The Ambient Player uses these to
  // load specific pieces and hide the Toolkit's creation UI for studios.
  const playerPreset = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const id = params.get("preset");
      return id ? getPieceById(id)?.spec ?? null : null;
    } catch (_) { return null; }
  }, []);
  const kioskMode = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("kiosk") === "true";
    } catch (_) { return false; }
  }, []);
  // Per-showing presentation flags (URL params), so one composition can be shown
  // different ways without re-editing: full visualization for a talk, sound bar
  // only for a science exhibit, or nothing for cafe/museum ambient. Both default
  // to SHOW; only "off" hides. E.g. ?kiosk=true&labels=off&soundbar=off
  const showBandLabels = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get("labels") !== "off"; }
    catch (_) { return true; }
  }, []);
  const showSoundbar = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get("soundbar") !== "off"; }
    catch (_) { return true; }
  }, []);
  const showSoundbarRef = useRef(showSoundbar); showSoundbarRef.current = showSoundbar;

  // In kiosk mode (Ambient Player), auto-start playback after first paint.
  // The user's click on the Player's "Play" card serves as the Web Audio
  // user gesture; this dispatch happens within the same event task so the
  // gesture context is preserved.
  useEffect(() => {
    if (!kioskMode) return;
    const t = setTimeout(() => {
      const btn = document.getElementById("fractal-play-button");
      if (btn) btn.click();
    }, 800);
    return () => clearTimeout(t);
  }, [kioskMode]);

  const voiceStateRef = useRef('idle'); 
  function beginListening() {
    if (voiceStateRef.current !== 'listening') return;

    dlog('[Voice] BEGIN listening');

    recognitionRef.current?.abort();  // clean slate
    startListening(userLanguageRef.current);
  }
  const [lineDrawMs, setLineDrawMs] = useState(() => saved?.lineDrawMs ?? 1000);
  const lineDrawMsRef = useRef(lineDrawMs); lineDrawMsRef.current = lineDrawMs;
  const [mode, setMode] = useState(() => saved?.mode ?? "scan");
  const [playhead, setPlayhead] = useState(() => saved?.playhead ?? null);
  // Live playhead position as a ref, so keydown/voice closures read the current
  // scan column without a stale-closure problem (mirrors musicKeyRef pattern).
  const playheadRef = useRef(playhead); playheadRef.current = playhead;
  const liveColumnRef = useRef(null);
  // ── Movements: mark a boundary at the current scan position ─────────────────
  // Called live during playback (Enter key or "mark here" voice command). Splits
  // the movement currently under the playhead at that column, so users can define
  // sections by ear without looking away from the canvas. The new (right) segment
  // inherits the split movement's tempo/rest, so nothing sounds different until
  // the user edits it. Audible confirmation via speakLabel (essential for blind
  // users). Guards against zero-width segments and duplicate boundaries.
  function markBoundary() {
    const col = liveColumnRef.current ?? playheadRef.current;
    if (col == null) { speakLabel("Play the scan first, then mark a boundary."); return; }
    const ms = movementsRef.current || [];
    const idx = ms.findIndex((m) => col > m.startCol && col <= m.endCol);
    if (idx === -1) { speakLabel("Cannot mark a boundary here."); return; }
    const m = ms[idx];
    if (col - m.startCol < 2 || m.endCol - col < 1) {
      speakLabel("Too close to an existing boundary.");
      return;
    }
    const left = { ...m, endCol: col - 1 };
    const right = { ...m, startCol: col };
    // Pure updater (no side effects — React may double-invoke it in StrictMode).
    setMovements((prev) => [...prev.slice(0, idx), left, right, ...prev.slice(idx + 1)]);
    // Reveal the boundary markers so the composer can see what they just marked
    // (you should not have to mark blind). Does not alter the movements data.
    setShowBoundaries(true);
    // Announce ONCE, outside the updater, so it isn't spoken twice.
    speakLabel("Marked at column " + col + ".");
  }
  const markBoundaryRef = useRef(markBoundary); markBoundaryRef.current = markBoundary;
  // Per-column play time (ms) from the movements tempo map: the tempoMs of the
  // movement containing this column, falling back to the Column-speed slider
  // (lineDrawMs) for any column no movement covers.
  function tempoForColumn(col) {
    // Sine mode has no movements — always honour the Column-speed slider so
    // slowing the scan actually slows it. Movements only govern tempo in
    // instrumental mode (where Key, Harmony, and movements all apply).
    if (soundModeRef.current === "sine") return lineDrawMsRef.current;
    const ms = movementsRef.current || [];
    if (ms.length <= 1) return lineDrawMsRef.current;
    for (let i = 0; i < ms.length; i++) {
      const m = ms[i];
      if (col >= m.startCol && col <= m.endCol) return m.tempoMs || lineDrawMsRef.current;
    }
    return lineDrawMsRef.current;
  }
  // ── Current movement ────────────────────────────────────────────────────────
  // Which movement covers a given column. Deliberately mirrors tempoForColumn's
  // containment test above, so the highlighted row is always the movement whose
  // tempo is actually being played. Returns -1 for a column no movement covers
  // (tempoForColumn falls back to the Column-speed slider there, and there is no
  // row to point at).
  function movementIndexForColumn(col, ms) {
    if (col == null || !ms || !ms.length) return -1;
    for (let i = 0; i < ms.length; i++) {
      if (col >= ms[i].startCol && col <= ms[i].endCol) return i;
    }
    return -1;
  }

  const [selectedColumn, setSelectedColumn] = useState(() => saved?.selectedColumn ?? null);
  const [scanOrientation, setScanOrientation] = useState(() => saved?.scanOrientation ?? "vertical"); // "vertical" | "horizontal"
  const [soundMode, setSoundMode] = useState(() => saved?.soundMode ?? "sine"); // "sine" | "instrumental" | "band" | "contour"
  const [contourSpeed, setContourSpeed] = useState(() => saved?.contourSpeed ?? 130); // px/sec for contour glide; lower = slower
  const contourSpeedRef = useRef(contourSpeed); contourSpeedRef.current = contourSpeed;
  const [contourGap, setContourGap] = useState(() => saved?.contourGap ?? 0.18); // seconds of silence between contours
  const contourGapRef = useRef(contourGap); contourGapRef.current = contourGap;
  const [contourPitchMode, setContourPitchMode] = useState(() => saved?.contourPitchMode ?? "axis"); // "axis" | "archHeight"
  const [colDragFor, setColDragFor] = useState(null); // contour id currently being column-set by canvas drag, or null
  const colDragForRef = useRef(null); colDragForRef.current = colDragFor;
  const [pathVertices, setPathVertices] = useState([]); // drawn-path vertices {x,y} in image px (path mode)
  const pathVerticesRef = useRef([]); pathVerticesRef.current = pathVertices;
  // Two-voice support. pathVertices above is always the voice BEING EDITED;
  // the other voice is parked here and swapped in when you switch. That keeps
  // every existing editing route working on the active voice unchanged.
  const [pathActiveVoice, setPathActiveVoice] = useState("A"); // "A" | "B"
  const pathActiveVoiceRef = useRef("A"); pathActiveVoiceRef.current = pathActiveVoice;
  const [pathOther, setPathOther] = useState({ verts: [], lengths: [], instrument: "flute", notesPerSeg: 1 });
  const pathOtherRef = useRef({ verts: [], lengths: [], instrument: "flute", notesPerSeg: 1 });
  pathOtherRef.current = pathOther;
  const [pathRateRatio, setPathRateRatio] = useState(3); // voice B speed = voice A speed x this
  const pathRateRatioRef = useRef(3); pathRateRatioRef.current = pathRateRatio;
  const pathMarkerRef = useRef(null);   // voice A: index of the vertex sounding
  const pathMarkerBRef = useRef(null);  // voice B: same
  const pathStopsRef = useRef([]);      // smplr stop-fns for scheduled path notes, cancelled on stop
  const pathTimelineRef = useRef([]);   // [{t, v}] note start times and their vertex
  const pathTimelineBRef = useRef([]);  // voice B's timeline
  const pathRafRef = useRef(null);      // requestAnimationFrame id for the marker
  const [pathInstrument, setPathInstrument] = useState("flute"); // path-mode instrument
  const pathInstrumentRef = useRef("flute"); pathInstrumentRef.current = pathInstrument;
  const [pathNotesPerSeg, setPathNotesPerSeg] = useState(4); // notes read per straight segment
  const pathNotesPerSegRef = useRef(4); pathNotesPerSegRef.current = pathNotesPerSeg;
  const [pathRate, setPathRate] = useState(4); // notes per second (path mode)
  const pathRateRef = useRef(4); pathRateRef.current = pathRate;
  const [pathDrawMode, setPathDrawMode] = useState("vertices"); // "vertices" | "freehand"
  const pathDrawModeRef = useRef("vertices"); pathDrawModeRef.current = pathDrawMode;
  const [pathPitchFit, setPathPitchFit] = useState("rank"); // "raw" | "linear" | "rank"
  const pathPitchFitRef = useRef("rank"); pathPitchFitRef.current = pathPitchFit;
  const [pathVertsText, setPathVertsText] = useState(""); // coordinate entry box (path mode)
  const [pathShowCoords, setPathShowCoords] = useState(true); // label vertices on the canvas
  const pathShowCoordsRef = useRef(true); pathShowCoordsRef.current = pathShowCoords;
  const [pathLengths, setPathLengths] = useState([]); // per-vertex note-length multipliers
  const pathLengthsRef = useRef([]); pathLengthsRef.current = pathLengths;
  const [pathRepeats, setPathRepeats] = useState(1);
  const pathRepeatsRef = useRef(1); pathRepeatsRef.current = pathRepeats;
  const [pathTranspose, setPathTranspose] = useState(0); // semitones
  const pathTransposeRef = useRef(0); pathTransposeRef.current = pathTranspose;
  const [pathTransposeStep, setPathTransposeStep] = useState(0); // semitones added per repeat
  const pathTransposeStepRef = useRef(0); pathTransposeStepRef.current = pathTransposeStep;
  const [pathPasses, setPathPasses] = useState([]); // [{id, repeats, transpose, pause}] — empty = use the single-pass fields
  const pathPassesRef = useRef([]); pathPassesRef.current = pathPasses;
  const [pathPause, setPathPause] = useState(0); // seconds after each repetition
  const pathPauseRef = useRef(0); pathPauseRef.current = pathPause;
  const [pathResolution, setPathResolution] = useState(12); // px between resampled points (freehand)
  const pathResolutionRef = useRef(12); pathResolutionRef.current = pathResolution;
  const freehandRawRef = useRef(null); // array of raw {x,y} during a freehand sweep, or null
  const [tripletRate, setTripletRate] = useState(() => saved?.tripletRate ?? 6); // flute triplets per second (layered mode)
  const tripletRateRef = useRef(tripletRate); tripletRateRef.current = tripletRate;
  // Dotted-quarter tempo for the layered triplet figuration. The score is 52.
  // Supersedes tripletRate (groups/sec), which is kept only so older saved
  // compositions still load without complaint.
  const [tripletBpm, setTripletBpm] = useState(() => {
    const v = saved?.tripletBpm;
    return (typeof v === "number" && v >= 20) ? v : 52;
  });
  const tripletBpmRef = useRef(tripletBpm); tripletBpmRef.current = tripletBpm;
  const contourPitchModeRef = useRef(contourPitchMode); contourPitchModeRef.current = contourPitchMode;
  const soundModeRef = useRef(soundMode); soundModeRef.current = soundMode;
  const [harmonyMode, setHarmonyMode] = useState(() => saved?.harmonyMode ?? "fractal"); // "fractal" | "chordal"
  const harmonyModeRef = useRef(harmonyMode); harmonyModeRef.current = harmonyMode;
  // ── BAND MODE (Stage 4) ─────────────────────────────────────────────────────
  // Band mode: the composer selects REGIONS (each = all points of one escape
  // value / band across the image) and orders them into a SEQUENCE. On playback
  // each region sweeps the image in turn, at its own column speed, sounding only
  // its band. Band mode is SILENT until regions are selected (no auto-play).
  //
  // viewBands: the extracted bands for the CURRENT view — Map<escapeValue,
  //   Map<col,{top,bottom,thickness}>>. Recomputed when the fractal changes.
  //   Bands are view-specific, so this is tied to the current image.
  const [viewBands, setViewBands] = useState(null);
  const viewBandsRef = useRef(null); viewBandsRef.current = viewBands;
  // bandSequence: the composition — an ORDERED array of regions. Each region:
  //   { id, escapeValue, instrument, scaleDegree, octaveOffset, columnSpeedMs,
  //     soundMap? }  (soundMap optional: a loudness-palette for in-region harmony)
  // Stored by escapeValue (stable for a given view); on load we recompute bands
  // for the saved image and match by escapeValue.
  const [bandSequence, setBandSequence] = useState(() => saved?.bandSequence ?? []);
  const bandSequenceRef = useRef(bandSequence); bandSequenceRef.current = bandSequence;
  // bandSections: optional playback sections that wrap the whole bandSequence.
  // Each: { id, instrument, repeats, octaveOffset }. Empty = play once as-is.
  // When present, playback runs the whole sequence `repeats` times per section,
  // in order, each section on its own instrument at its octave offset.
  const [bandSections, setBandSections] = useState(() => saved?.bandSections ?? []);
  const bandSectionsRef = useRef(bandSections); bandSectionsRef.current = bandSections;
  // Recompute the current view's bands from the fractal. Call when entering band
  // mode or after the image changes. Pure data (bandExtractor), no audio.
  function computeViewBands() {
    const d = fractalDataRef.current;
    if (!d?.rawIndex || !d?.interior) { setViewBands(null); return null; }
    const bands = extractBands(d.rawIndex, d.interior, WIDTH, HEIGHT, scanOrientation);
    setViewBands(bands);
    return bands;
  }
  const [exampleComps, setExampleComps] = useState([]); // curated example compositions from Firebase Storage
  const [soundStops, setSoundStops] = useState(() => saved?.soundStops ?? DEFAULT_SOUND_STOPS);
  const [loudnessPalette, setLoudnessPalette] = useState(() => saved?.loudnessPalette ?? DEFAULT_LOUDNESS_PALETTE);
  const [musicKey, setMusicKey] = useState(() => saved?.musicKey ?? { root: "Eb", scale: "major" });
  const [movements, setMovements] = useState(() => saved?.movements ?? [
    { startCol: 0, endCol: WIDTH - 1, tempoMs: 600, endRestBeats: 0 },
  ]);
  const movementsRef = useRef(movements); movementsRef.current = movements;
  // The movement to highlight. During playback (and while paused) that is the one
  // under the playhead. When stopped, fall back to the selected column, so the
  // highlight also answers "which movement does this column belong to?" while you
  // are placing boundaries.
  const activeMovementIndex = movementIndexForColumn(
    playhead != null ? playhead : selectedColumn,
    movements
  );
  // Show/hide the movement boundary markers (triangles + lines). Hidden by
  // default so listeners/buyers see a clean image; the movements/tempo map is
  // unaffected — this only controls whether the markers are DRAWN. Placing a
  // mark auto-shows them; opening a composition resets to hidden.
  const [showBoundaries, setShowBoundaries] = useState(false);
  const showBoundariesRef = useRef(showBoundaries); showBoundariesRef.current = showBoundaries;
  const musicKeyRef = useRef(musicKey); musicKeyRef.current = musicKey;
  const loudnessPaletteRef = useRef(loudnessPalette); loudnessPaletteRef.current = loudnessPalette;
  // Spoken forms for key/scale so the voice says "E flat" not "Eb", etc.
  const spokenRoot = (r) => ({ C:"C", Db:"D flat", D:"D", Eb:"E flat", E:"E", F:"F", Gb:"G flat", G:"G", Ab:"A flat", A:"A", Bb:"B flat", B:"B" }[r] || r);
  const spokenScale = (s) => ({ major:"major", minor:"minor", pentatonic:"pentatonic", blues:"blues", wholetone:"whole tone", chromatic:"chromatic" }[s] || s);
  // Show grid overlay on the fractal canvas. When true, a 12x9 grid of focusable
  // cells is overlaid on the canvas. The user can tab through them and press
  // Enter on any cell to zoom into that cell's region of the complex plane.
  const [showGrid, setShowGrid] = useState(() => saved?.showGrid ?? false);
  const [fractalType, setFractalType] = useState(() => playerPreset?.fractalType ?? saved?.fractalType ?? DEFAULT_SPEC.fractalType);
  const [view, setView] = useState(() => playerPreset?.view ?? saved?.view ?? DEFAULT_SPEC.view);
  const [maxIter, setMaxIter] = useState(() => playerPreset?.maxIter ?? saved?.maxIter ?? DEFAULT_SPEC.maxIter);
  const [aaFactor, setAaFactor] = useState(() => saved?.aaFactor ?? 1); // anti-aliasing supersample factor (1 = off)
  const [juliaC, setJuliaC] = useState(() => playerPreset?.juliaC ?? saved?.juliaC ?? DEFAULT_SPEC.juliaC);
  const [inputFractalType, setInputFractalType] = useState(() => saved?.fractalType ?? DEFAULT_SPEC.fractalType);
  const [inputXMin, setInputXMin] = useState(() => String(saved?.view?.xMin ?? DEFAULT_SPEC.view.xMin));
  const [inputXMax, setInputXMax] = useState(() => String(saved?.view?.xMax ?? DEFAULT_SPEC.view.xMax));
  const [inputYMin, setInputYMin] = useState(() => String(saved?.view?.yMin ?? DEFAULT_SPEC.view.yMin));
  const [inputYMax, setInputYMax] = useState(() => String(saved?.view?.yMax ?? DEFAULT_SPEC.view.yMax));
  const [inputMaxIter, setInputMaxIter] = useState(() => String(saved?.maxIter ?? DEFAULT_SPEC.maxIter));
  const [inputJuliaRe, setInputJuliaRe] = useState(() => String(saved?.juliaC?.re ?? DEFAULT_SPEC.juliaC.re));
  const [inputJuliaIm, setInputJuliaIm] = useState(() => String(saved?.juliaC?.im ?? DEFAULT_SPEC.juliaC.im));
  const [status, setStatus] = useState("Rendering…");
  const [isPlaying, setIsPlaying] = useState(false);

  // ── PLAY DELAY ────────────────────────────────────────────────────────────
  // A countdown before playback begins, so you can arm Play, switch to the
  // window you are recording, and have the scan start on a clean screen.
  // Session-only (not saved with a composition). Zero = start immediately.
  const [playDelay, setPlayDelay] = useState(0);
  const [delayLeft, setDelayLeft] = useState(null);   // null = not counting
  const [openCompositionName, setOpenCompositionName] = useState(null); // name of the open composition, shown in the transport
  const playDelayRef = useRef(0);
  const delayTimerRef = useRef(null);
  useEffect(() => { playDelayRef.current = playDelay; }, [playDelay]);

  // ── DIAGNOSTICS (read-only) ─────────────────────────────────────────────────
  // Measures what a scan will ask of the audio engine. Calls the same region and
  // loudness code playback calls, but only to count — it never schedules a note,
  // and no playback code path knows this exists.
  const [scanReport, setScanReport] = useState(null);
  const [columnReport, setColumnReport] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Escape -> band (0..255), the same mapping makeLoudnessLookup uses internally.
  // Rebuilt here rather than exported from the engine, so the engine stays
  // untouched. Returns null if there is no fractal yet.
  function makeBandFor() {
    const data = fractalData;
    if (!data) return null;
    const escaped = [];
    for (let i = 0; i < data.rawIndex.length; i++) {
      if (data.interior[i] !== 1) escaped.push(data.rawIndex[i]);
    }
    escaped.sort((a, b) => a - b);
    if (!escaped.length) return null;
    return (esc) => {
      let lo = 0, hi = escaped.length - 1, ans = 0;
      while (lo <= hi) { const m = (lo + hi) >> 1; if (escaped[m] <= esc) { ans = m; lo = m + 1; } else hi = m - 1; }
      const t = escaped.length > 1 ? ans / (escaped.length - 1) : 0;
      return Math.round(t * 255);
    };
  }

  function runScanAnalysis() {
    if (!fractalData) { setStatus("Compute a fractal first"); return; }
    setAnalyzing(true);
    // Yield a frame so the button visibly responds before the sweep runs.
    setTimeout(() => {
      try {
        const orientation = scanOrientation;
        const scanExtent = orientation !== "horizontal" ? WIDTH : HEIGHT;
        // HOISTED: getLoudnessLookup() sorts the entire escape array. Calling it
        // per region would make this O(n log n) per region instead of once.
        const lookup = getLoudnessLookup();
        const report = analyzeScan({
          scanExtent,
          orientation,
          buildRegions: (c, o) => buildInstrumentalRegions(c, o),
          tempoForColumn,
          lookup,
          movements: movementsRef.current || [],
        });
        setScanReport(report);
        const silent = report.silentRuns.reduce((s, r) => s + (r.to - r.from + 1), 0);
        const msg = `Peak ${report.peakVoices} voices at column ${report.peakCol}. ${silent} silent columns.`;
        setStatus(msg);
        speak(msg);
      } catch (err) {
        console.error(err);
        setStatus(`Analysis failed: ${err.message}`);
      } finally {
        setAnalyzing(false);
      }
    }, 0);
  }

  function runColumnInspection(col) {
    if (!fractalData || col == null) { setStatus("Select a column first"); return; }
    try {
      const rep = inspectColumn({
        col,
        orientation: scanOrientation,
        buildRegions: (c, o) => buildInstrumentalRegions(c, o),
        tempoForColumn,
        lookup: getLoudnessLookup(),
        bandFor: makeBandFor(),
      });
      setColumnReport(rep);
      const sounding = rep.rows.filter((r) => r.notes.length).length;
      const msg = sounding === 0
        ? `Column ${col} is silent: ${rep.rows.length} regions, none firing a note.`
        : `Column ${col}: ${rep.rows.length} regions, ${sounding} sounding, ${rep.voices} voices.`;
      setStatus(msg);
      speak(msg);
    } catch (err) {
      console.error(err);
      setStatus(`Inspection failed: ${err.message}`);
    }
  }

  // Announce each movement as the scan enters it. This is the accessible
  // counterpart of the row highlight: a blind listener cannot see the table, and
  // the movements are the structure of the composition. Only while playing, only
  // when there is more than one movement, and only on a change — so it stays out
  // of the way.
  const spokenMovementRef = useRef(-1);
  useEffect(() => {
    if (!isPlaying || movements.length <= 1) { spokenMovementRef.current = -1; return; }
    if (activeMovementIndex < 0) return;
    if (activeMovementIndex === spokenMovementRef.current) return;
    spokenMovementRef.current = activeMovementIndex;
    const mv = movements[activeMovementIndex];
    const named = TEMPO_OPTIONS.find((t) => t.ms === mv.tempoMs);
    const tempoName = named ? named.name : feelFor(mv.tempoMs);
    speak(`Movement ${activeMovementIndex + 1}, ${tempoName}.`);
  }, [activeMovementIndex, isPlaying, movements]);
  const [orbitPath, setOrbitPath] = useState([]);
  const [stereoWidth, setStereoWidth] = useState(() => saved?.stereoWidth ?? 1.0);
  const [presetKey, setPresetKey] = useState(() => saved?.presetKey ?? "mandelbrotFull");
  const [presets, setPresets] = useState(() => {
    // Merge DEFAULT_PRESETS into saved presets so newly-added built-ins
    // always appear, while saved imports are preserved.  DEFAULT_PRESETS
    // wins on key collisions so we get fresh built-in defaults.
    const savedPresets = saved?.presets || {};
    return { ...savedPresets, ...DEFAULT_PRESETS };
  });
  const [palettes, setPalettes] = useState(() => {
    const savedPalettes = saved?.palettes || {};
    return { ...savedPalettes, ...DEFAULT_PALETTES };
  });
  const [paletteName, setPaletteName] = useState(() => playerPreset?.paletteName ?? saved?.paletteName ?? "classic");
  const [paletteBank, setPaletteBank] = useState(() => {
    // Build the bank for a built-in gradient palette up front, so the active
    // palette is a real bank from the very first render. This removes the
    // startup window where a gradient palette was selected but its bank hadn't
    // been derived yet (the source of the Classic-on-fresh-load grey grid).
    // Imported palettes carry no stops and are loaded from IDB asynchronously,
    // so they stay null here and arrive via that load path.
    const initialName = playerPreset?.paletteName ?? saved?.paletteName ?? "classic";
    const stops = DEFAULT_PALETTES[initialName]?.stops;
    return stops ? gradientToBank(stops) : null;
  });
  const [colorAffectsSound, setColorAffectsSound] = useState(() => saved?.colorAffectsSound ?? true);
  const [musicVolume, setMusicVolume] = useState(() => saved?.musicVolume ?? 100);
  const [voiceVolume, setVoiceVolume] = useState(() => saved?.voiceVolume ?? 20);
  // Screen reader users run their own speech; ours would double every label.
  // Persisted separately from a composition so it is set once, not per file.
  const [spokenLabels, setSpokenLabels] = useState(() => {
    try { return window.localStorage.getItem("fmSpokenLabels") !== "off"; } catch (_) { return true; }
  });
  const spokenLabelsRef = useRef(true); spokenLabelsRef.current = spokenLabels;
  const [nav, setNav] = useState(() => ({
    history: saved?.history ?? [],
    historyIndex: saved?.historyIndex ?? -1,
  }));
  const [showHelp, setShowHelp] = useState(false);
  // Caregiver setup dialog — opened via ?setup=1 URL param or
  // "Accessibility setup" button in Help. Stores its state as a single
  // JSON blob in localStorage so first-paint logic can read it
  // synchronously and the dialog itself is a thin editor.
  const [showSetup, setShowSetup] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('setup') === '1';
    } catch (_) { return false; }
  });
  const [accessibilitySetup, setAccessibilitySetup] = useState(() => {
    try {
      const raw = localStorage.getItem('fractal_accessibilitySetup');
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return {
      configured: false,         // becomes true once caregiver clicks "Done"
      voiceAutoStart: false,     // start voice on page load (no click needed)
      eyeTrackingAutoStart: false, // start gaze tracking after voice activates
      dwellMs: 1000,             // 800 / 1000 / 1500 / 2000
      micAlwaysOn: false,        // future-use; not wired yet to existing voice code
      minimalToolbar: false,     // hide non-essential toolbar buttons
    };
  });
  const accessibilitySetupRef = useRef(accessibilitySetup);
  useEffect(() => {
    accessibilitySetupRef.current = accessibilitySetup;
    try { localStorage.setItem('fractal_accessibilitySetup', JSON.stringify(accessibilitySetup)); } catch (_) {}
  }, [accessibilitySetup]);
  const [importModal, setImportModal] = useState(null);
  // Names of palettes currently cached in local IDB (paletteBank:* keys).
  // Refreshed whenever the import modal opens so the cache-aware UI
  // ("Refresh from library" buttons, "Load from cache" labels) reflects
  // the latest state. Stored as a Set for fast .has() lookups in row render.
  const [cachedPaletteNames, setCachedPaletteNames] = useState(() => new Set());
  // Whenever the import modal opens, scan IDB for cached palettes.
  useEffect(() => {
    if (!importModal) return;
    let cancelled = false;
    (async () => {
      try {
        const keys = await idbKeys("paletteBank:");
        if (cancelled) return;
        setCachedPaletteNames(new Set(keys.map(k => k.replace("paletteBank:", ""))));
      } catch (err) {
        console.warn("Failed to enumerate cached palettes:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [importModal]);

  // ── Voice control state ────────────────────────────────────────────────────
  const [voiceReady, setVoiceReady] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('');
  const [userName, setUserName] = useState(() => localStorage.getItem('fractal_userName') || '');
  const [userLanguage, setUserLanguage] = useState(() => localStorage.getItem('fractal_language') || 'en-US');
  const [onboardingDone, setOnboardingDone] = useState(() => !!localStorage.getItem('fractal_userName'));
  // Eye tracking state
  const [eyeCalibrating, setEyeCalibrating] = useState(false);
  const [eyeCalibClicks, setEyeCalibClicks] = useState({});
  const [eyeTrackingActive, setEyeTrackingActive] = useState(false);
  const eyeCalibrationDoneRef = useRef(null);
  const dwellRingRef = useRef(null);
  const dwellStateRef = useRef({ element: null, startTime: 0 });
  const gazePollRef = useRef(null);
  const recognitionRef = useRef(null);
  useEffect(() => {
    function activateVoice() {
      if (voiceStateRef.current !== 'idle') return;

      voiceStateRef.current = 'listening';
      beginListening();

      window.removeEventListener('keydown', activateVoice);
      window.removeEventListener('mousedown', activateVoice);
    }

    window.addEventListener('keydown', activateVoice);
    window.addEventListener('mousedown', activateVoice);

    return () => {
      window.removeEventListener('keydown', activateVoice);
      window.removeEventListener('mousedown', activateVoice);
    };
  }, []);

  const userLanguageRef = useRef(localStorage.getItem('fractal_language') || 'en-US');
  const handleVoiceCommandRef = useRef(null);
  const awaitingNameRef = useRef(false);
  const idleTimerRef = useRef(null);
  const isVoiceActiveRef = useRef(false);

  const [tiffExporting, setTiffExporting] = useState(false);
  const [tiffExportProgress, setTiffExportProgress] = useState(0);
  const [tiffExportSize, setTiffExportSize] = useState(TIFF_EXPORT_PRESETS[1]); // 4K default
  const [pngExportSize, setPngExportSize] = useState(PNG_EXPORT_PRESETS[0]); // Canvas default
  const [pngExporting, setPngExporting] = useState(false);
  const userTier = useUserTier();

  // Keep the selected export resolutions within the user's tier. If the current
  // selection is locked for this tier (e.g. tier changed, or a higher-res option
  // was selected before being gated), snap down to the highest resolution this
  // tier can actually export, so the dropdown never shows a disabled option as
  // the active choice. PNG always has Canvas as a free fallback; TIFF may have
  // no available option on the free tier, so only clamp when one exists.
  useEffect(() => {
    if (!can(capabilityForPngSize(pngExportSize), userTier)) {
      const best = [...PNG_EXPORT_PRESETS].reverse()
        .find((p) => can(capabilityForPngSize(p), userTier));
      if (best && best.label !== pngExportSize.label) setPngExportSize(best);
    }
    if (!can(capabilityForSize(tiffExportSize), userTier)) {
      const best = [...TIFF_EXPORT_PRESETS].reverse()
        .find((p) => can(capabilityForSize(p), userTier));
      if (best && best.label !== tiffExportSize.label) setTiffExportSize(best);
    }
  }, [userTier, pngExportSize, tiffExportSize]);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const synthRef = useRef(null);
  const orbitTimerRef = useRef(null);
  const bandCompleteTimerRef = useRef(null); // dedicated timer for band-tap completion
  const contourOscRef = useRef(null); // active contour-glide oscillator (Stage 5)
  const contourOscListRef = useRef([]); // ALL contour-glide oscillators, stopped on stop
  const contourPathRef = useRef(null); // selected contour { points, closed, level } (Stage 5)
  const [contourSequence, setContourSequence] = useState(() => saved?.contourSequence ?? []); // ordered contours for a piece (Stage 5 slice 4b)
  const contourSequenceRef = useRef(contourSequence); contourSequenceRef.current = contourSequence;
  const [currentContourIndex, setCurrentContourIndex] = useState(-1); // which sequence row is sounding now (-1 = none)
  const contourMarkerRef = useRef(null); // current {x,y} dot on the contour during glide
  const contourRafRef = useRef(null); // requestAnimationFrame id for the marker
  function drawContourMarker() {
    if (canvasRef.current && fractalDataRef.current) {
      drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalDataRef.current, playheadRef.current, [], "vertical", [], contourMarkerRef.current);
    }
  }
  const fractalMusicEngineRef = useRef(null);
  const instrumentalTableRef = useRef(null);
  const clickTimerRef = useRef(null);
  const orbitPathRef = useRef([]);
  const pendingBrowseRef = useRef(null);
  const suppressFocusRef = useRef(false);
  const isPlayingRef = useRef(false);
  const fractalColorsRef = useRef(null);
  const fractalDataRef = useRef(null);
  const viewRef = useRef(view);
  const modeRef = useRef(mode);
  const pendingSelectionRef = useRef(null);
  const dragStartRef = useRef(null);
  const dragCurrentRef = useRef(null);
  const isDraggingRef = useRef(false);
  const colorEditorWindowRef = useRef(null);
  const soundEditorWindowRef = useRef(null);
  // Bumped each time the sound-editor popup announces 'ready' (opens). Added to
  // the frame-broadcast effect's deps so that opening the editor re-runs the
  // effect and pushes the CURRENT fractal image — fixing a race where the editor
  // opened while a just-loaded composition was still recomputing and kept the
  // stale pre-composition image.
  const [soundEditorOpenNonce, setSoundEditorOpenNonce] = useState(0);
  const pendingEditorCloseRef = useRef(false);
  const colorEditorChannelRef = useRef(null);
  const soundEditorChannelRef = useRef(null);
  // Monotonic version stamp for frames sent to the color-editor popup. Bumped on
  // every fractalData change; included in both 'frame' and 'init' messages so the
  // popup can drop any frame older than the newest it has already painted. This
  // prevents a late-arriving stale frame from overwriting the current preview.
  // Seed the frame counter with a timestamp rather than 0. The editor popup can
  // outlive a main-app reload (recompile / hard refresh) and keeps the highest
  // seq it has seen; if a reloaded app restarted its counter at 0, every new
  // frame would look "stale" to that popup and be dropped, freezing the editor
  // on its last image (usually the default Mandelbrot) while the main canvas
  // moved on. A timestamp base always exceeds any prior session's count.
  const frameSeqRef = useRef(Date.now());
  // Tracks the last palette (fullColors) reference broadcast to the editor, so
  // the grid colours ride along on the frame message only when they change.
  const lastSentFullColorsRef = useRef(null);
  const stopPlaybackRef = useRef(null);
  const paletteBankRef = useRef(null);
  const paletteColorsRef = useRef(
    // Seed with Classic so a send that fires before the first in-render sync
    // (or before the gradient effect commits paletteBank) never reads null and
    // falls through to the grey fallback. The in-render sync below keeps it
    // current for every other palette and edit.
    DEFAULT_PALETTES.classic?.stops
      ? gradientToBank(DEFAULT_PALETTES.classic.stops).fullColors
      : null
  );
  // Keep paletteFilename and palettePage available via refs too, so the
  // BroadcastChannel handler (registered with [] deps to avoid re-registering
  // on every state change) can read current values rather than the mount-time
  // closure-captured values. Without these, save flows under stale closures
  // would write to the wrong filename, and page-aware logic would target the
  // wrong page index.
  const paletteFilenameRef = useRef("none loaded");
  const palettePageRef = useRef(0);
  const paletteLabelRef = useRef("");
  const voiceSpeakRef = useRef(null); // always current paletteBank for stale-closure-safe access
  const startPlaybackRef = useRef(null);

  const [paletteFilename, setPaletteFilename] = useState(() => saved?.paletteFilename ?? "none loaded");
  const [palettePage, setPalettePage] = useState(() => saved?.palettePage ?? 0);
  const [usePaletteBank, setUsePaletteBank] = useState(() => saved?.usePaletteBank ?? false);
  // True only when the user has explicitly opened a palette this session —
  // prevents the editor appearing just because a palette was restored from IndexedDB
  const [paletteLoaded, setPaletteLoaded] = useState(false);

  // Colour editor state (mirrors the original ColorMapper sliders)
  const [editorRedStart, setEditorRedStart] = useState(0);
  const [editorRedStep, setEditorRedStep] = useState(0);
  const [editorRedLighter, setEditorRedLighter] = useState(true);
  const [editorGreenStart, setEditorGreenStart] = useState(0);
  const [editorGreenStep, setEditorGreenStep] = useState(0);
  const [editorGreenLighter, setEditorGreenLighter] = useState(true);
  const [editorBlueStart, setEditorBlueStart] = useState(0);
  const [editorBlueStep, setEditorBlueStep] = useState(0);
  const [editorBlueLighter, setEditorBlueLighter] = useState(true);
  const [editorSmoothing, setEditorSmoothing] = useState(false);

  const [importedRaster, setImportedRaster] = useState(null); // pixels — restored separately below
  const [importedImgMeta, setImportedImgMeta] = useState(() => saved?.importedImgMeta ?? null);
  const [imgLibrary, setImgLibrary] = useState([]); // filenames of .img files saved in IndexedDB
  const [imgImportHighlight, setImgImportHighlight] = useState(false);
  const [paletteListOpen, setPaletteListOpen] = useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const paletteListTriggerRef = useRef(null);

  const totalScanMs = WIDTH * lineDrawMs;

  const fallbackPaletteBank = useMemo(() => buildFallbackPaletteBank(), []);
  // editedFullColors is set when the colour editor modifies a page; null = use loaded palette
  const [editedFullColors, setEditedFullColors] = useState(null);
  const editedFullColorsRef = useRef(null);
  // Keep ref in sync so applyEditorPage always reads the latest value
  useEffect(() => { editedFullColorsRef.current = editedFullColors; }, [editedFullColors]);
  const fullColors = useMemo(() => {
    if (editedFullColors) return editedFullColors;
    if (paletteBank?.fullColors) return paletteBank.fullColors;
    // No bank yet (e.g. default Classic before the gradient effect commits):
    // resolve the active gradient directly so neither the image nor any editor
    // message falls back to the grey ramp while a real palette is selected.
    const stops = palettes[paletteName]?.stops;
    if (stops) return gradientToBank(stops).fullColors;
    return fallbackPaletteBank.fullColors;
  }, [editedFullColors, paletteBank, fallbackPaletteBank, palettes, paletteName]);

  // Unify palettes into banks. When a built-in gradient palette is active,
  // represent it as a bank so the colour editor always shows real colours and
  // coloring is uniform across all palettes. Imported banks set paletteBank via
  // their own load path (they carry `bank: true` and have no `stops`), so this
  // effect skips them.
  useEffect(() => {
    const p = palettes[paletteName];
    if (p && p.stops) {
      setPaletteBank(gradientToBank(p.stops));
      setEditedFullColors(null);
      setUsePaletteBank(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paletteName, palettes]);

  const fractalData = useMemo(() => {
    if (importedRaster && importedImgMeta) {
      return renderImportedRaster(
        WIDTH,
        HEIGHT,
        importedRaster,
        importedImgMeta.sourceWidth,
        importedImgMeta.sourceHeight,
        maxIter,
        paletteName,
        palettes,
        usePaletteBank,
        fullColors
      );
    }

    return renderFormulaFractal(
      WIDTH,
      HEIGHT,
      maxIter,
      view,
      fractalType,
      juliaC,
      paletteName,
      palettes,
      usePaletteBank,
      fullColors,
      aaFactor
    );
  }, [
    importedRaster,
    importedImgMeta,
    maxIter,
    paletteName,
    palettes,
    usePaletteBank,
    fullColors,
    view,
    fractalType,
    juliaC,
    aaFactor,
  ]);

  // Per-cell stats for the 15×10 navigation grid. Used by the cell focus
  // announcement to give sight-impaired users a compass-style bearing
  // toward areas of detail. Memoized on fractalData + maxIter because the
  // grid bins are deterministic from those two values; recomputing on
  // every render would be wasteful (375k-pixel scan per render).
  const gridStats = useMemo(
    () => buildGridStats(fractalData, WIDTH, HEIGHT, maxIter),
    [fractalData, maxIter]
  );

  // Keep fractal colors ref current and broadcast to color editor popup
  useEffect(() => {
    fractalColorsRef.current = fractalData?.colors ?? null;
    if (fractalData) frameSeqRef.current += 1; // version stamp for this frame
    const ch = colorEditorChannelRef.current;
    if (ch && fractalData?.colors) {
      // Ride the palette colours along the frame, but resolve them the same way
      // every other editor message does (paletteColorsRef), so a transient null
      // paletteBank can never send the grey fallback while a real palette is
      // active. Track changes against the resolved colours, not the raw memo.
      const resolvedFC = paletteColorsRef.current;
      const fcChanged = lastSentFullColorsRef.current !== resolvedFC;
      if (fcChanged) lastSentFullColorsRef.current = resolvedFC;
      try { ch.postMessage({
        type: 'frame',
        seq: frameSeqRef.current,
        colors: Array.from(fractalData.colors),
        rawIndex: fractalData.rawIndex ? Array.from(fractalData.rawIndex) : null,
        interior: fractalData.interior ? Array.from(fractalData.interior) : null,
        fullColors: fcChanged && resolvedFC ? Array.from(resolvedFC) : null,
        allPageControls: fcChanged ? buildAllPageControls(paletteBank) : null,
        width: WIDTH, height: HEIGHT,
      }); } catch (_) {}
    }
  }, [fractalData]);

  // When the palette bank changes while the Color Editor is already open, push
  // the new colours (and derived slider controls) so its swatch grid matches
  // the image instead of staying on whatever it loaded when first opened. Keyed
  // on paletteBank only — editor edits change editedFullColors, not paletteBank,
  // so this can't loop back on the editor's own edits.
  useEffect(() => {
    const ch = colorEditorChannelRef.current;
    if (!ch) return;
    if (!fullColors || fullColors.length === 0) return;
    const allPC = buildAllPageControls(paletteBank);
    const page = palettePageRef.current || 0;
    const pc = allPC[page] || {};
    try { ch.postMessage({
      type: 'state',
      palettePage: page,
      numPages: paletteBank?.pages?.length || 0,
      editorRedStart:    pc.rS ?? 0, editorRedStep:  pc.rT ?? 0, editorRedLighter:   pc.rL ?? true,
      editorGreenStart:  pc.gS ?? 0, editorGreenStep: pc.gT ?? 0, editorGreenLighter: pc.gL ?? true,
      editorBlueStart:   pc.bS ?? 0, editorBlueStep:  pc.bT ?? 0, editorBlueLighter:  pc.bL ?? true,
      editorSmoothing:   pc.sm ?? false,
      allPageControls: allPC,
      fullColors: Array.from(paletteColorsRef.current || fullColors),
    }); } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullColors, paletteBank]);
  // Broadcast frame data to the sound editor popup so it can display the fractal
  // for click-to-play preview
  useEffect(() => {
    const ch = soundEditorChannelRef.current;
    if (ch && fractalData?.rawIndex) {
      try {
        ch.postMessage({
          type: 'frame',
          colors: Array.from(fractalData.colors),
          rawIndex: Array.from(fractalData.rawIndex),
          width: WIDTH,
          height: HEIGHT,
        });
      } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fractalData, soundEditorOpenNonce]);
  useEffect(() => { fractalDataRef.current = fractalData; }, [fractalData]);
  // When band mode is active (and after the fractal changes), extract the view's
  // bands so right-click region selection can resolve escape values.
  useEffect(() => {
    if (soundMode === "band" && fractalData?.rawIndex) computeViewBands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundMode, fractalData]);
  useEffect(() => { viewRef.current = view; }, [view]);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { paletteLabelRef.current = palettes[paletteName]?.label || paletteName; }, [paletteName, palettes]);

  // ── Restore binary state from IndexedDB on first mount ────────────────────
  // The JSON state blob (in localStorage) only holds plain serialisable values.
  // The three large binary objects — palette bank colours, edited colours, and
  // the imported raster — live in IndexedDB where there is no size limit.
  // We restore them once on startup, asynchronously.
  useEffect(() => {
    async function restoreBinary() {
      // paletteBank not restored — same reasoning as editedColors below:
      // stale palette data from a previous session can corrupt the display
      // (a buggy save in an earlier version could write all-black pages to
      // IDB, and there's no reliable way to tell from inside the app what's
      // intentional vs broken). The user's actual palettes live in .tmp
      // files on disk and in the Firebase library; re-import to use them.
      // Wipe any historical entries so they don't sit around indefinitely.
      try { await idbDelete("paletteBank"); } catch (_) {}
      try { await idbDelete("paletteMeta"); } catch (_) {}

      // editedColors not restored — stale editor colors corrupt the display

      if (saved?.importedImgMeta) {
        const rasterBuf = await idbGet("raster");
        if (rasterBuf) {
          try { setImportedRaster(new Uint32Array(rasterBuf)); }
          catch (err) { console.error("restoreBinary raster failed:", err); }
        }
      }
    }

    restoreBinary();
    // Restore img library index from IndexedDB
    idbKeys("imgLib:").then(keys => {
      const names = keys.map(k => k.replace("imgLib:", ""));
      if (names.length) setImgLibrary(names);
    });

    // Restore the local palette library from IndexedDB. Each previously-loaded
    // palette is stored under paletteBank:{filename} and persists across
    // reloads. We add an entry to the palettes map for each one so they show
    // up in the dropdown immediately, without needing a Firebase round-trip.
    // If the user's saved paletteFilename matches one of these, also restore
    // the active paletteBank in memory so the fractal renders in colour.
    (async () => {
      try {
        const keys = await idbKeys("paletteBank:");
        if (!keys.length) return;
        const names = keys.map(k => k.replace("paletteBank:", ""));
        // Add every cached palette to the palettes map so they're all visible
        // in the dropdown. We merge with existing map (which may already have
        // built-in gradient palettes) rather than replacing it.
        setPalettes(prev => {
          const next = { ...prev };
          for (const name of names) {
            if (!next[name]) next[name] = { label: name.replace(/\.tmp$/i, ''), bank: true };
          }
          return next;
        });
        // If the user's last-active palette is in the cache, restore it
        // immediately so the fractal can render with colour right away.
        const activeFilename = saved?.paletteFilename;
        if (activeFilename && names.includes(activeFilename)) {
          const buf = await idbGet(`paletteBank:${activeFilename}`);
          if (buf) {
            const parsed = parsePaletteBank(buf);
            setPaletteBank(parsed);
            // Restore editor sliders from page 0's controls.
            if (parsed.pages?.[0]?.controls) {
              const c = parsed.pages[0].controls;
              setEditorRedStart(Math.max(0, Math.min(255, c.redSlider & 0xff)));
              setEditorRedStep(Math.max(0, Math.min(255, c.redStep & 0xff)));
              setEditorRedLighter(c.redDirection === 1);
              setEditorGreenStart(Math.max(0, Math.min(255, c.greenSlider & 0xff)));
              setEditorGreenStep(Math.max(0, Math.min(255, c.greenStep & 0xff)));
              setEditorGreenLighter(c.greenDirection === 1);
              setEditorBlueStart(Math.max(0, Math.min(255, c.blueSlider & 0xff)));
              setEditorBlueStep(Math.max(0, Math.min(255, c.blueStep & 0xff)));
              setEditorBlueLighter(c.blueDirection === 1);
              setEditorSmoothing(c.smoothing === 1);
            }
          }
        }
      } catch (err) {
        console.warn("Failed to restore palette banks from IDB:", err);
      }
    })();

    // Also list every palette in the Firebase library so they ALL show in the
    // dropdown's Imported section on every load — even before being cached
    // locally. This survives the startup cache wipe above and works on a
    // freshly deployed app (Storage is separate from hosting), so a published
    // palette like Aurora is always visible without a manual re-import. Only
    // NAMES are merged here; the actual colours are fetched from Firebase on
    // demand when the palette is selected (the Imported-section click handler
    // already does fetchLibraryFiles -> downloadLibraryFile -> import). We
    // never trust cached colour data across startup — only fresh fetches — so
    // the corruption-protection wipe stays intact.
    (async () => {
      try {
        const files = await fetchLibraryFiles();
        if (!files || !files.length) return;
        setPalettes(prev => {
          const next = { ...prev };
          for (const f of files) {
            const nm = f.name;
            if (!nm) continue;
            if (!next[nm]) next[nm] = { label: nm.replace(/\.tmp$/i, ''), bank: true };
          }
          return next;
        });
      } catch (err) {
        console.warn("Firebase palette-library listing failed (dropdown shows cached only):", err);
      }
    })();
    // Firebase rehydrate fallback: if the user's saved paletteFilename is for
    // a bank-type palette but IDB doesn't have it (e.g., the user ran the app
    // before this caching code was added, or their IDB was cleared), try
    // fetching from Firebase. On success, the bytes get cached to IDB through
    // importPaletteFromBuffer, so this fallback won't fire on subsequent
    // reloads. Failures are silent — the user can re-load via the library UI.
    (async () => {
      const filename = saved?.paletteFilename;
      if (!filename || filename === 'none loaded') return;
      const wasBank = saved?.palettes?.[filename]?.bank;
      if (!wasBank) return;
      // Skip if IDB already has it — the restore-from-IDB block above will
      // have handled that case.
      try {
        const cached = await idbGet(`paletteBank:${filename}`);
        if (cached) return;
      } catch (_) { /* fall through to Firebase */ }
      try {
        const files = await fetchLibraryFiles();
        const file = files.find(f => f.name === filename || f.name === filename + '.new');
        if (!file) return;
        const buf = await downloadLibraryFile(file.downloadUrl);
        // Use importPaletteFromBuffer so the bytes also get cached to IDB
        // for next time, and the standard side effects (paletteName etc.)
        // happen consistently.
        await importPaletteFromBuffer(buf, filename);
      } catch (err) {
        console.warn('Palette bank Firebase rehydrate failed:', err);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist JSON state whenever any saveable value changes ─────────────────
  // Skip in kiosk mode (Ambient Player playback): saving the preset's coords
  // would overwrite the user's last Toolkit view, so when they later visit
  // the Toolkit at "/" they'd land on the Player piece instead of their work.
  useEffect(() => {
    if (kioskMode) return;
    saveJsonState({
      lineDrawMs, mode, playhead, selectedColumn,
      fractalType, view, maxIter, juliaC,
      paletteName, stereoWidth, presetKey,
      voiceVolume, musicVolume,
      paletteFilename, palettePage, usePaletteBank, colorAffectsSound,
      showGrid, aaFactor, scanOrientation,
      // editor state not saved — always starts fresh
      importedImgMeta,
      presets, palettes,
      history: nav.history, historyIndex: nav.historyIndex,
    });
  }, [
    kioskMode,
    lineDrawMs, mode, playhead, selectedColumn,
    fractalType, view, maxIter, juliaC,
    paletteName, stereoWidth, presetKey,
    voiceVolume, musicVolume,
    paletteFilename, palettePage, usePaletteBank, colorAffectsSound,
    showGrid, aaFactor, scanOrientation,
    importedImgMeta, presets, palettes,
    nav,
  ]);

  // paletteBank not persisted to IndexedDB — restoreBinary above explains why
  // restoring it is unsafe. Writing it without reading is just wasted I/O and
  // a foot-gun (any bug that ever corrupts paletteBank.fullColors would get
  // baked into IDB and resurface on the next session). Re-import is the
  // canonical path to restore a palette across reloads.

  // ── Persist editedFullColors to IndexedDB whenever it changes ──────────────
  // This is the array that actually drives the rendered colours when the user
  // has been working in the colour editor.  Saving it means coming back the
  // next day shows exactly the colours that were on screen, not the original
  // imported palette.
  // Session persistence disabled: keep edited colours and the imported raster
  // out of IndexedDB so a reload always starts from scratch. We actively clear
  // them (rather than just not writing) so blobs from an earlier build go away.
  useEffect(() => {
    try { idbDelete("editedColors"); } catch (_) {}
  }, [editedFullColors]);

  useEffect(() => {
    try { idbDelete("raster"); } catch (_) {}
  }, [importedRaster]);

  // BroadcastChannel listener — receives control changes from the color editor popup
  useEffect(() => {
    const ch = new BroadcastChannel('fractal_color_editor');
    colorEditorChannelRef.current = ch;
    // After a hard refresh (Ctrl-Shift-R) of the main app, an already-open
    // editor popup does NOT reload, so it never re-sends 'ready' and the init
    // handshake never re-fires — leaving its swatch grid on whatever it had
    // (typically the grey fallback). Proactively push the current palette a
    // few times as the app settles, so any open popup re-syncs on its own.
    // paletteColorsRef resolves the active gradient even before paletteBank
    // commits, so this never pushes grey while a real palette is selected.
    const broadcastPaletteState = () => {
      const c = colorEditorChannelRef.current;
      const src = paletteColorsRef.current;
      if (!c || !src || src.length === 0) return;
      const pb = paletteBankRef.current;
      const allPC = buildAllPageControls(pb);
      const page = palettePageRef.current || 0;
      const pc = allPC[page] || deriveControls(src.slice(0, 256 * 3)) || {};
      try { c.postMessage({
        type: 'state',
        paletteLabel: paletteLabelRef.current,
        paletteFilename: paletteFilenameRef.current,
        palettePage: page,
        numPages: pb?.pages?.length || 256,
        editorRedStart: pc.rS ?? 0, editorRedStep: pc.rT ?? 0, editorRedLighter: pc.rL ?? true,
        editorGreenStart: pc.gS ?? 0, editorGreenStep: pc.gT ?? 0, editorGreenLighter: pc.gL ?? true,
        editorBlueStart: pc.bS ?? 0, editorBlueStep: pc.bT ?? 0, editorBlueLighter: pc.bL ?? true,
        editorSmoothing: pc.sm ?? false,
        allPageControls: allPC,
        fullColors: Array.from(src),
      }); } catch (_) {}
    };
    const reReady = setTimeout(broadcastPaletteState, 250);
    const reReady2 = setTimeout(broadcastPaletteState, 900);
    ch.onmessage = (e) => {
      const d = e.data;
      // This effect was registered with [] deps, so the closure captures
      // paletteBank/paletteFilename/palettePage from mount time when they
      // were initial values. By reading from refs here and shadowing the
      // closure-captured names, every code path below gets current values
      // without needing per-line rewrites. paletteBankRef et al are kept
      // current via in-render assignment near the top of the component.
      const paletteBank = paletteBankRef.current;
      const paletteFilename = paletteFilenameRef.current;
      const palettePage = palettePageRef.current;
      if (d.type === 'ready') {
        // Send all page controls so editor can switch pages without round-trips
        const allPC = paletteBankRef.current?.pages?.map(pg => {
          const c = pg?.controls;
          if (controlsAreMeaningful(c)) {
            return {
              rS: Math.max(0, Math.min(255, c.redSlider   & 0xff)),
              rT: Math.max(0, Math.min(255, c.redStep     & 0xff)),
              rL: c.redDirection   === 1,
              gS: Math.max(0, Math.min(255, c.greenSlider & 0xff)),
              gT: Math.max(0, Math.min(255, c.greenStep   & 0xff)),
              gL: c.greenDirection === 1,
              bS: Math.max(0, Math.min(255, c.blueSlider  & 0xff)),
              bT: Math.max(0, Math.min(255, c.blueStep    & 0xff)),
              bL: c.blueDirection  === 1,
              sm: c.smoothing === 1,
            };
          }
          // No meaningful stored controls — derive approximate sliders from the
          // page's actual colours so the editor opens matching the palette.
          if (pg?.colors && pg.colors.length >= 256 * 3) {
            return deriveControls(pg.colors);
          }
          return null;
        }) || [];
        // When paletteBank hasn't committed yet (default Classic on fresh
        // load) allPC is empty; derive page sliders from the resolved colours
        // so the editor doesn't open with all-zero sliders.
        const pc = allPC[palettePage]
          || deriveControls((paletteColorsRef.current || fallbackPaletteBank.fullColors).slice(0, 256 * 3))
          || {};
        const pbRef = paletteBankRef.current;
        try { ch.postMessage({
          type: 'init',
          seq: frameSeqRef.current,
          paletteLabel: paletteLabelRef.current,
          paletteFilename: paletteFilenameRef.current,
          palettePage,
          numPages: pbRef?.pages?.length || 0,
          editorRedStart:    pc.rS ?? 0,
          editorRedStep:     pc.rT ?? 0,
          editorRedLighter:  pc.rL ?? true,
          editorGreenStart:  pc.gS ?? 0,
          editorGreenStep:   pc.gT ?? 0,
          editorGreenLighter: pc.gL ?? true,
          editorBlueStart:   pc.bS ?? 0,
          editorBlueStep:    pc.bT ?? 0,
          editorBlueLighter: pc.bL ?? true,
          editorSmoothing:   pc.sm ?? false,
          allPageControls: allPC,
          fullColors: (() => {
            const src = paletteColorsRef.current || fallbackPaletteBank.fullColors;
            return src ? Array.from(src) : [];
          })(),
          colors: fractalColorsRef.current ? Array.from(fractalColorsRef.current) : [],
          rawIndex: fractalDataRef.current?.rawIndex ? Array.from(fractalDataRef.current.rawIndex) : null,
          interior: fractalDataRef.current?.interior ? Array.from(fractalDataRef.current.interior) : null,
          width: WIDTH, height: HEIGHT,
        }); } catch (_) {}
      } else if (d.type === 'setPage') {
        const p = d.page;
        setPalettePage(p);
        loadEditorPage(p);
      } else if (d.type === 'setRed') {
        if (d.start !== undefined) setEditorRedStart(d.start);
        if (d.step !== undefined) setEditorRedStep(d.step);
        if (d.lighter !== undefined) setEditorRedLighter(d.lighter);
      } else if (d.type === 'setGreen') {
        if (d.start !== undefined) setEditorGreenStart(d.start);
        if (d.step !== undefined) setEditorGreenStep(d.step);
        if (d.lighter !== undefined) setEditorGreenLighter(d.lighter);
      } else if (d.type === 'setBlue') {
        if (d.start !== undefined) setEditorBlueStart(d.start);
        if (d.step !== undefined) setEditorBlueStep(d.step);
        if (d.lighter !== undefined) setEditorBlueLighter(d.lighter);
      } else if (d.type === 'setSmoothing') {
        setEditorSmoothing(d.value);
      } else if (d.type === 'apply') {
        applyEditorPage(d.page, d.rStart, d.rStep, d.rL, d.gStart, d.gStep, d.gL, d.bStart, d.bStep, d.bL, d.smoothing);
      } else if (d.type === 'resetPage') {
        setEditedFullColors(null);
        loadEditorPage(palettePage);
      } else if (d.type === 'applyAll') {
        const base = editedFullColorsRef.current || paletteBank?.fullColors || fallbackPaletteBank.fullColors;
        if (base) {
          const next = new Uint8Array(base);
          const pages = paletteBank?.pages?.length || 256;
          for (let pg = 0; pg < pages; pg++) {
            const pgOffset = pg * 256;
            let r = d.rStart, g = d.gStart, b = d.bStart;
            let revR = !d.rL, revG = !d.gL, revB = !d.bL;
            for (let i = 0; i < 256; i++) {
              const idx = (pgOffset + i) * 3;
              next[idx] = Math.abs(r % 256); next[idx+1] = Math.abs(g % 256); next[idx+2] = Math.abs(b % 256);
              if (!revR) { r += d.rStep; if (r >= 256 && d.smoothing) { r -= d.rStep; revR = true; } }
              else { r -= d.rStep; if (r < 0) { if (d.smoothing) { r += d.rStep; revR = false; } else r += 256; } }
              if (!revG) { g += d.gStep; if (g >= 256 && d.smoothing) { g -= d.gStep; revG = true; } }
              else { g -= d.gStep; if (g < 0) { if (d.smoothing) { g += d.gStep; revG = false; } else g += 256; } }
              if (!revB) { b += d.bStep; if (b >= 256 && d.smoothing) { b -= d.bStep; revB = true; } }
              else { b -= d.bStep; if (b < 0) { if (d.smoothing) { b += d.bStep; revB = false; } else b += 256; } }
            }
          }
          setEditedFullColors(next);
        }
      } else if (d.type === 'previewColors') {
        // Editor recolors its own canvas locally — main app unchanged
      } else if (d.type === 'saveWithColors' || d.type === 'saveAsWithColors') {
        // Apply the edited page colours to the in-memory palette
        applyEditorPage(d.page, d.rStart, d.rStep, d.rL, d.gStart, d.gStep, d.gL, d.bStart, d.bStep, d.bL, d.smoothing);
        setUsePaletteBank(true);
        setPaletteLoaded(true);
        // Determine the filename to save under. For Save: keep the current
        // canonical filename (overwrites the in-IDB cached version). For
        // Save As: use the user-supplied newName, ensuring it ends in .tmp.
        const isSaveAs = d.type === 'saveAsWithColors';
        let saveName;
        if (isSaveAs) {
          const raw = String(d.newName || '').trim();
          if (!raw) { setStatus('Save As: name is empty, aborting.'); return; }
          // Strip any trailing .tmp the user typed, we re-add it for consistency.
          saveName = raw.replace(/\.tmp$/i, '') + '.tmp';
        } else {
          const baseName = (paletteFilenameRef.current && paletteFilenameRef.current !== 'none loaded')
            ? paletteFilenameRef.current.replace(/\.tmp$/, '')
            : 'palette';
          saveName = baseName + '.tmp';
        }
        // Source of truth for the saved bytes: prefer the popup's localColors
        // (sent as d.colors), which reflects every per-page edit this session.
        const popupColors = (d.colors && d.colors.length >= 256 * 3)
          ? new Uint8Array(d.colors)
          : null;
        const src2 = popupColors || editedFullColorsRef.current || paletteBank?.fullColors || fallbackPaletteBank.fullColors;
        if (!src2) return;
        const numPages2 = paletteBank?.pages?.length || 256;
        const pageSize2 = 28 + 256 * 12;
        const buf2 = new ArrayBuffer(numPages2 * pageSize2);
        const dv2 = new DataView(buf2);
        const allPC = Array.isArray(d.allPageControls) ? d.allPageControls : [];
        for (let pg = 0; pg < numPages2; pg++) {
          const base2 = pg * pageSize2;
          const pc = allPC[pg] || null;
          const c2 = paletteBank?.pages[pg]?.controls || {};
          const pick = (popKey, popDefault, bankKey, bankDefault) => {
            if (pc && pc[popKey] !== undefined) return pc[popKey];
            if (c2[bankKey] !== undefined) return c2[bankKey];
            return popDefault !== undefined ? popDefault : bankDefault;
          };
          const rS = pick('rS', 0, 'redSlider', 0);
          const rT = pick('rT', 0, 'redStep', 0);
          const rL = pc && pc.rL !== undefined ? (pc.rL ? 1 : 0)
                    : (c2.redDirection !== undefined ? c2.redDirection : 1);
          const gS = pick('gS', 0, 'greenSlider', 0);
          const gT = pick('gT', 0, 'greenStep', 0);
          const gL = pc && pc.gL !== undefined ? (pc.gL ? 1 : 0)
                    : (c2.greenDirection !== undefined ? c2.greenDirection : 1);
          const bS = pick('bS', 0, 'blueSlider', 0);
          const bT = pick('bT', 0, 'blueStep', 0);
          const bL = pc && pc.bL !== undefined ? (pc.bL ? 1 : 0)
                    : (c2.blueDirection !== undefined ? c2.blueDirection : 1);
          const sm = pc && pc.sm !== undefined ? (pc.sm ? 1 : 0)
                    : (c2.smoothing !== undefined ? c2.smoothing : 0);
          dv2.setInt32(base2 + 0,  rS, false);
          dv2.setInt32(base2 + 4,  rT, false);
          dv2.setUint8(base2 + 8,  rL);
          dv2.setInt32(base2 + 9,  gS, false);
          dv2.setInt32(base2 + 13, gT, false);
          dv2.setUint8(base2 + 17, gL);
          dv2.setInt32(base2 + 18, bS, false);
          dv2.setInt32(base2 + 22, bT, false);
          dv2.setUint8(base2 + 26, bL);
          dv2.setUint8(base2 + 27, sm);
          let off2 = base2 + 28;
          for (let i = 0; i < 256; i++) {
            const ci2 = (pg * 256 + i) * 3;
            dv2.setInt32(off2, src2[ci2]   || 0, false); off2 += 4;
            dv2.setInt32(off2, src2[ci2+1] || 0, false); off2 += 4;
            dv2.setInt32(off2, src2[ci2+2] || 0, false); off2 += 4;
          }
        }
        const parsed2 = parsePaletteBank(buf2);
        const displayName2 = saveName.replace(/\.tmp$/i, '');
        // Commit the palette to in-memory state + local IDB cache. Factored out
        // so we can call it AFTER the save-as collision check (below), not before.
        const commitLocally2 = () => {
          setPaletteBank(parsed2);
          setPaletteFilename(saveName);
          setPaletteName(saveName);
          setPaletteLoaded(true);
          setUsePaletteBank(true);
          setEditedFullColors(null);
          setPalettes(prev => ({ ...prev, [saveName]: { label: displayName2, bank: true } }));
          (async () => {
            try { await idbSet(`paletteBank:${saveName}`, buf2.slice(0)); }
            catch (err) { console.warn(`Failed to cache palette ${saveName} to IDB:`, err); }
          })();
        };
        if (!isSaveAs) {
          // Save: local-only, immediate. No Firebase, no file download.
          commitLocally2();
          setStatus(`Saved ${displayName2} locally.`);
          speak(`Saved ${displayName2}.`);
          return;
        }
        // Save As: check Firebase for an existing entry FIRST. If found, ask the
        // user to confirm the overwrite BEFORE anything is written locally or
        // uploaded. Only on confirmation (or no collision) do we commit + upload.
        (async () => {
          let collides = false;
          try {
            const files = await fetchLibraryFiles();
            collides = !!files.find(f => f.name === saveName);
          } catch (err) {
            console.warn('Could not check Firebase library, proceeding:', err);
          }
          if (collides) {
            const win = colorEditorWindowRef.current;
            const target = (win && !win.closed) ? win : window;
            const ok = target.confirm(`A palette named "${displayName2}" already exists in the library. Replace it?`);
            if (!ok) {
              setStatus(`Save As cancelled. ${displayName2} already in library.`);
              speak(`Save cancelled.`);
              return;
            }
          }
          // Confirmed (or no collision): commit locally, then upload.
          commitLocally2();
          try {
            await uploadLibraryFile(saveName, buf2);
            setStatus(`Saved ${displayName2} to library.`);
            speak(`Saved ${displayName2} to library.`);
          } catch (err) {
            console.error('Library upload failed:', err);
            setStatus(`Saved ${displayName2} locally — library upload failed (see console).`);
          }
        })();
      } else if (d.type === 'savePalette' || d.type === 'saveAsPalette') {
        // Trigger save — reuse existing save logic by dispatching a click on a hidden element
        const src = editedFullColorsRef.current || paletteBank?.fullColors || fallbackPaletteBank.fullColors;
        if (!src || !paletteBank) return;
        const isSaveAs = d.type === 'saveAsPalette';
        let saveName;
        if (isSaveAs) {
          const raw = String(d.newName || '').trim();
          if (!raw) { setStatus('Save As: name is empty, aborting.'); return; }
          saveName = raw.replace(/\.tmp$/i, '') + '.tmp';
        } else {
          saveName = paletteFilenameRef.current && paletteFilenameRef.current !== 'none loaded'
            ? paletteFilenameRef.current
            : 'palette.tmp';
        }
        const numPages = paletteBank.pages.length;
        const pageSize = 28 + 256 * 12;
        const buf = new ArrayBuffer(numPages * pageSize);
        const dv = new DataView(buf);
        for (let pg = 0; pg < numPages; pg++) {
          const base2 = pg * pageSize;
          const c = paletteBank.pages[pg]?.controls || {};
          dv.setInt32(base2 + 0, c.redSlider || 0, false);
          dv.setInt32(base2 + 4, c.redStep || 0, false);
          dv.setUint8(base2 + 8, c.redDirection || 0);
          dv.setInt32(base2 + 9, c.greenSlider || 0, false);
          dv.setInt32(base2 + 13, c.greenStep || 0, false);
          dv.setUint8(base2 + 17, c.greenDirection || 0);
          dv.setInt32(base2 + 18, c.blueSlider || 0, false);
          dv.setInt32(base2 + 22, c.blueStep || 0, false);
          dv.setUint8(base2 + 26, c.blueDirection || 0);
          dv.setUint8(base2 + 27, c.smoothing || 0);
          let off = base2 + 28;
          for (let i = 0; i < 256; i++) {
            const ci = (pg * 256 + i) * 3;
            dv.setInt32(off, src[ci], false); off += 4;
            dv.setInt32(off, src[ci+1], false); off += 4;
            dv.setInt32(off, src[ci+2], false); off += 4;
          }
        }
        const displayName = saveName.replace(/\.tmp$/i, '');
        // Commit to in-memory state + local cache. Factored out so save-as can
        // call it only AFTER the collision check + confirm below.
        const commitLocally = () => {
          if (isSaveAs) {
            setPaletteFilename(saveName);
            setPaletteName(saveName);
            setPalettes(prev => ({ ...prev, [saveName]: { label: displayName, bank: true } }));
          }
          (async () => {
            try { await idbSet(`paletteBank:${saveName}`, buf.slice(0)); }
            catch (err) { console.warn(`Failed to cache palette ${saveName} to IDB:`, err); }
          })();
        };
        if (!isSaveAs) {
          commitLocally();
          setStatus(`Saved ${displayName} locally.`);
          speak(`Saved ${displayName}.`);
          return;
        }
        // Save As: check for a name collision FIRST and confirm the overwrite
        // BEFORE writing anything locally or uploading.
        (async () => {
          let collides = false;
          try {
            const files = await fetchLibraryFiles();
            collides = !!files.find(f => f.name === saveName);
          } catch (err) {
            console.warn('Could not check Firebase library, proceeding:', err);
          }
          if (collides) {
            const win = colorEditorWindowRef.current;
            const target = (win && !win.closed) ? win : window;
            const ok = target.confirm(`A palette named "${displayName}" already exists in the library. Replace it?`);
            if (!ok) {
              setStatus(`Save As cancelled. ${displayName} already in library.`);
              speak(`Save cancelled.`);
              return;
            }
          }
          // Confirmed (or no collision): commit locally, then upload.
          commitLocally();
          try {
            await uploadLibraryFile(saveName, buf);
            setStatus(`Saved ${displayName} to library.`);
            speak(`Saved ${displayName} to library.`);
          } catch (err) {
            console.error('Library upload failed:', err);
            setStatus(`Saved ${displayName} locally — library upload failed (see console).`);
          }
        })();
      }
    };
    return () => { clearTimeout(reReady); clearTimeout(reReady2); ch.close(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // BroadcastChannel listener — syncs key/scale with the sound editor popup.
  useEffect(() => {
    const ch = new BroadcastChannel('fractal_sound_editor');
    soundEditorChannelRef.current = ch;
    ch.onmessage = (e) => {
      const d = e.data || {};
      if (d.type === 'ready') {
        // Popup just opened — send it the current key so it shows the right values.
        setSoundEditorOpenNonce((n) => n + 1); // re-run the frame effect to push the current image
        const mk = musicKeyRef.current || { root: 'Eb', scale: 'major' };
        try {
          const fd = fractalDataRef.current;
          ch.postMessage({
            type: 'init',
            root: mk.root,
            scale: mk.scale,
            instruments: Object.values(GM_INSTRUMENTS).flat(),
            palette: loudnessPaletteRef.current,
            colors: fd?.colors ? Array.from(fd.colors) : null,
            rawIndex: fd?.rawIndex ? Array.from(fd.rawIndex) : null,
            width: WIDTH,
            height: HEIGHT,
          });
        } catch (_) {}
      } else if (d.type === 'key') {
        // Popup changed key/scale — update the app.
        if (d.root && d.scale) setMusicKey({ root: d.root, scale: d.scale });
      } else if (d.type === 'palette') {
        // Popup edited the loudness palette — update the app (playback reads it live).
        if (Array.isArray(d.palette)) setLoudnessPalette(d.palette);
      } else if (d.type === 'downloadSoundMap') {
        // Download the current loudness palette as a .json file the user owns.
        if (d.name && Array.isArray(d.palette)) {
          try {
            const safe = String(d.name).replace(/[^A-Za-z0-9._ -]/g, '_').replace(/\.json$/i, '');
            const filename = `${safe}.soundmap.json`;
            const payload = { kind: 'fractalToolkit.soundmap', version: 1, palette: d.palette };
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = filename; a.click();
            URL.revokeObjectURL(url);
            setStatus(`Saved sound map ${filename}`);
          } catch (_) {}
        }
      } else if (d.type === 'applySoundMap') {
        // The popup read a .soundmap.json file itself (a same-window user gesture,
        // which the browser allows) and sent us the parsed palette. Apply it just
        // like handleImportSoundMap does. This replaces the old 'pickSoundMap'
        // flow, where the main window tried to open the file dialog on the popup's
        // behalf and the browser blocked it ("file chooser needs user activation").
        if (Array.isArray(d.palette)) {
          setLoudnessPalette(d.palette);
          try { ch.postMessage({ type: 'state', palette: d.palette }); } catch (_) {}
          setStatus('Loaded sound map from file.');
        }
      } else if (d.type === 'saveSoundMapLib') {
        // Save to the local library by name, warning before replacing an existing map.
        if (d.name && Array.isArray(d.palette)) {
          (async () => {
            let exists = false;
            try { exists = (await idbGetJson('soundMap:' + d.name)) != null; } catch (_) {}
            if (exists) {
              const win = soundEditorWindowRef.current;
              const target = (win && !win.closed) ? win : window;
              const ok = target.confirm(`A sound map named "${d.name}" already exists in your library. Replace it?`);
              if (!ok) { setStatus(`Save cancelled — ${d.name} already in library.`); return; }
            }
            try {
              await idbSetJson('soundMap:' + d.name, d.palette);
              setStatus(`Saved ${d.name} to library.`);
              const keys = await idbKeys('soundMap:');
              const names = keys.map(k => k.replace('soundMap:', '')).sort();
              try { ch.postMessage({ type: 'soundMapSaved', name: d.name }); } catch (_) {}
              try { ch.postMessage({ type: 'soundMapLibList', names }); } catch (_) {}
            } catch (err) {
              setStatus(`Could not save ${d.name} to library.`);
            }
          })();
        }
      } else if (d.type === 'listSoundMapsLib') {
        (async () => {
          try {
            const keys = await idbKeys('soundMap:');
            const names = keys.map(k => k.replace('soundMap:', '')).sort();
            try { ch.postMessage({ type: 'soundMapLibList', names }); } catch (_) {}
          } catch (_) {}
        })();
      } else if (d.type === 'loadSoundMapLib') {
        if (d.name) {
          (async () => {
            try {
              const pal = await idbGetJson('soundMap:' + d.name);
              if (Array.isArray(pal)) {
                setLoudnessPalette(pal);
                try { ch.postMessage({ type: 'state', palette: pal }); } catch (_) {}
                setStatus(`Loaded ${d.name} from library.`);
              }
            } catch (_) {}
          })();
        }
      } else if (d.type === 'previewStart') {
        // Sound editor started preview — voice the sustained chord at the band.
        (async () => {
          try {
            await ensureAudio();
            if (!fractalMusicEngineRef.current) {
              fractalMusicEngineRef.current = new FractalMusicEngine(audioContextRef.current);
              await fractalMusicEngineRef.current.load();
              fractalMusicEngineRef.current.setKey({ root: musicKeyRef.current.root, scale: musicKeyRef.current.scale });
            }
            const eng = fractalMusicEngineRef.current;
            const band = (typeof d.band === 'number') ? d.band : 128;
            await eng.loadForPalette(loudnessPaletteRef.current);
            const notes = notesAtBand(loudnessPaletteRef.current, { ...musicKeyRef.current, baseOctave: musicKeyRef.current.baseOctave ?? 4 }, band, 100, harmonyModeRef.current === "chordal");
            eng.previewVoice(notes);
          } catch (_) {}
        })();
      } else if (d.type === 'previewUpdate') {
        // A slider moved — update ONLY that instrument in the held chord.
        const eng = fractalMusicEngineRef.current;
        if (eng && eng.loaded) {
          try {
            const band = (typeof d.band === 'number') ? d.band : 128;
            const notes = notesAtBand(loudnessPaletteRef.current, { ...musicKeyRef.current, baseOctave: musicKeyRef.current.baseOctave ?? 4 }, band, 100, harmonyModeRef.current === "chordal");
            const one = notes.find((n) => n.instrument === d.instrument);
            eng.previewUpdateOne(d.instrument, one ? { midi: one.midi, velocity: one.velocity } : null);
          } catch (_) {}
        }
      } else if (d.type === 'previewStop') {
        // Sound editor stopped preview.
        const eng = fractalMusicEngineRef.current;
        if (eng) { try { eng.previewStop(); } catch (_) {} }
      }
    };
    return () => { try { ch.close(); } catch (_) {} };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const ch = colorEditorChannelRef.current;
    if (!ch) return;
    // Only broadcast main-app-authoritative fields. editor* values are NOT
    // included: after the initial 'init' message, the popup owns editor
    // state in its own S object and per-page allPageControls cache. Echoing
    // main-app editor* values back to the popup on every state change
    // overwrote popup-remembered per-page edits — e.g. the user checks
    // smoothing on page 0, navigates away and back, popup correctly
    // restores S.sm=true from allPageControls[0], main app's loadEditorPage
    // re-broadcasts the unchanged paletteBank stored value editorSmoothing=
    // false, popup state handler clobbers S.sm back to false. paletteLabel
    // and palettePage are safe to broadcast (paletteLabel is derived from
    // main-app paletteName; palettePage broadcasts back to popup are
    // idempotent because popup already updated S.page before posting setPage).
    try { ch.postMessage({ type: 'state', paletteLabel: palettes[paletteName]?.label || paletteName, paletteFilename, palettePage }); } catch (_) {}
  }, [palettePage, paletteName, palettes]);

  // ── Voice speak helper ────────────────────────────────────────────────────
  function getBestVoice(lang) {
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;
    const prefix = lang.split('-')[0].toLowerCase();
    // 1. Exact match on lang code (e.g. fr-FR), prefer local voice
    const exactLocal = voices.find(v => v.lang.toLowerCase() === lang.toLowerCase() && v.localService);
    if (exactLocal) return exactLocal;
    // 2. Exact match, any (network ok)
    const exactAny = voices.find(v => v.lang.toLowerCase() === lang.toLowerCase());
    if (exactAny) return exactAny;
    // 3. Prefix match, prefer local (e.g. fr-BE, fr-CA for fr)
    const prefixLocal = voices.find(v => v.lang.toLowerCase().startsWith(prefix) && v.localService);
    if (prefixLocal) return prefixLocal;
    // 4. Prefix match, any
    const prefixAny = voices.find(v => v.lang.toLowerCase().startsWith(prefix));
    return prefixAny || null;
  }
  
  // Lightweight label announcer — for focus/change events.
  // Cancels any in-flight utterance and speaks immediately. Does NOT touch the
  // mic or voice state, so listening continues uninterrupted while the user
  // navigates with Tab/arrow keys.
  function speakLabel(text) {
    if (!('speechSynthesis' in window)) return;
    if (!text) return;
    // Off when the user has a screen reader doing this job already.
    if (!spokenLabelsRef.current) return;
    if (suppressFocusRef.current) return;
    // Don't interrupt an in-flight voiceSpeak utterance — it has an onEnd
    // callback that needs to fire to restart voice listening. If we cancel
    // it via window.speechSynthesis.cancel() below, the callback never fires
    // and listening hangs in 'speaking' state.
    if (voiceStateRef.current === 'speaking') return;
    // Chrome's speech engine can get into a stuck state after repeated rapid
    // cancel+speak cycles (which is what Tab traversal causes — each focus
    // event cancels the previous and starts a new utterance). After enough
    // cycles, .speak() silently no-ops. The .resume() call below revives
    // the engine if it's paused (a related stuck state), and using a tiny
    // setTimeout lets the cancel complete before the next speak begins.
    try { window.speechSynthesis.cancel(); } catch (_) {}
    try { window.speechSynthesis.resume(); } catch (_) {}
    setTimeout(() => {
      try {
        const utt = new SpeechSynthesisUtterance(String(text));
        utt.lang = userLanguageRef.current || 'en-US';
        utt.rate = 0.95;
        utt.volume = Math.max(0, Math.min(1, voiceVolume / 100));
        window.speechSynthesis.speak(utt);
      } catch (_) {}
    }, 0);
  }

  function voiceSpeak(text, lang, onEnd) {
    if (!('speechSynthesis' in window)) {
      onEnd?.();
      return;
    }

    // 🔴 STOP listening while speaking
    voiceStateRef.current = 'speaking';
    recognitionRef.current?.abort();
    if (mediaRecorderRef.current) {
      if (typeof mediaRecorderRef.current.stop === 'function') { try { mediaRecorderRef.current.stop(); } catch(_) {} }
      else { mediaRecorderRef.current.stopped = true; }
      mediaRecorderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = lang || userLanguageRef.current || 'en-US';
    utt.rate = 0.95;
    utt.volume = Math.max(0, Math.min(1, voiceVolume / 100));

    utt.onend = () => {
      voiceStateRef.current = 'listening';
      if (onEnd) {
        onEnd();
      } else {
        startListening(userLanguageRef.current);
      }
    };
    // If the utterance is interrupted (e.g. another speak() is queued, or
    // speechSynthesis.cancel() is called), browsers fire onerror instead of
    // (or sometimes in addition to) onend. Without handling onerror, voice
    // listening would stay stuck in the 'speaking' state. We do the same
    // cleanup as onend, guarded so we don't double-fire if both events arrive.
    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      voiceStateRef.current = 'listening';
      if (onEnd) onEnd();
      else startListening(userLanguageRef.current);
    };
    utt.onend = cleanup;
    utt.onerror = cleanup;

    // Prime the Bluetooth SCO output: send a short utterance first so the audio
    // link is already up by the time the real message starts. Without this, the
    // first ~300ms of speech is clipped on most BT headsets.
    if (!voiceSpeak._primed) {
      voiceSpeak._primed = true;
      const primer = new SpeechSynthesisUtterance(' ');  // single space = ~150ms of nothing
      primer.lang = utt.lang;
      primer.volume = 0.01;
      window.speechSynthesis.speak(primer);
    }

    window.speechSynthesis.speak(utt);
  }

  // ── Whisper-based voice input ─────────────────────────────────────────────
  // Records audio via MediaRecorder, detects silence to auto-stop,
  // sends to OpenAI Whisper for transcription, then passes to handleVoiceCommand.
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const streamRef = useRef(null);

  function startListening(lang, onTranscript) {
    dlog('[Whisper] startListening called, state=', voiceStateRef.current);
    if (voiceStateRef.current !== 'listening') return;
    if (mediaRecorderRef.current) return;
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        streamRef.current = stream;
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const sampleRate = audioCtx.sampleRate;
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        const dataArray = new Uint8Array(analyser.fftSize);
        const pcmChunks = [];
        const speechDetectedRef = { current: false };
        // Track average RMS during speech to filter out RF noise spikes
        let speechRmsSum = 0;
        let speechRmsCount = 0;
        const scriptProcessor = audioCtx.createScriptProcessor(4096, 1, 1);
        source.connect(scriptProcessor);
        scriptProcessor.connect(audioCtx.destination);
        scriptProcessor.onaudioprocess = (e) => { pcmChunks.push(new Float32Array(e.inputBuffer.getChannelData(0))); };
        const recState = { stopped: false };
        mediaRecorderRef.current = recState;
        const stopRecording = async () => {
          if (recState.stopped) return;
          recState.stopped = true;
          scriptProcessor.disconnect(); source.disconnect();
          stream.getTracks().forEach(t => t.stop()); audioCtx.close();
          mediaRecorderRef.current = null; streamRef.current = null;
          if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
          setIsListening(false);

          if (!speechDetectedRef.current || pcmChunks.length === 0) {
            if (voiceStateRef.current === 'listening') setTimeout(() => startListening(lang, onTranscript), 400);
            return;
          }

          // Reject if average RMS during speech was too low — likely RF noise not voice
          // Real voice averages 15-40 RMS; RF noise spikes average 1-4
          const avgRms = speechRmsCount > 0 ? speechRmsSum / speechRmsCount : 0;
          dlog('[Whisper] avg speech RMS=', avgRms.toFixed(2));
          if (avgRms < MIN_AVG_RMS) {
            dlog('[Whisper] skipping — avg RMS', avgRms.toFixed(2), '< threshold', MIN_AVG_RMS, '— likely noise/music not voice');
            if (voiceStateRef.current === 'listening') setTimeout(() => startListening(lang, onTranscript), 400);
            return;
          }

          const totalSamples = pcmChunks.reduce((s, c) => s + c.length, 0);
          const wavBuffer = new ArrayBuffer(44 + totalSamples * 2);
          const view = new DataView(wavBuffer);
          const ws = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
          ws(0,'RIFF'); view.setUint32(4,36+totalSamples*2,true); ws(8,'WAVE');
          ws(12,'fmt '); view.setUint32(16,16,true); view.setUint16(20,1,true); view.setUint16(22,1,true);
          view.setUint32(24,sampleRate,true); view.setUint32(28,sampleRate*2,true);
          view.setUint16(32,2,true); view.setUint16(34,16,true);
          ws(36,'data'); view.setUint32(40,totalSamples*2,true);
          let offset = 44;
          for (const chunk of pcmChunks) {
            for (let i = 0; i < chunk.length; i++) {
              const s = Math.max(-1, Math.min(1, chunk[i]));
              view.setInt16(offset, s < 0 ? s*32768 : s*32767, true); offset += 2;
            }
          }
          const blob = new Blob([wavBuffer], { type: 'audio/wav' });
          if (blob.size < 8000) { if (voiceStateRef.current === 'listening') setTimeout(() => startListening(lang, onTranscript), 400); return; }
          try {
            // Vocabulary biasing prompt for Whisper. The classic whisper-1
            // model treats this as a soft hint about expected vocabulary
            // without echoing it as output, unlike gpt-4o-mini-transcribe
            // which had a known issue of returning the prompt verbatim when
            // audio was ambiguous (silence, music bleed-through, etc.).
            // We keep the prompt short — just a handful of common command
            // words — to minimize any residual echo risk.
            const prompt = 'pause play stop resume back forward save help describe mute unmute zoom in zoom out tab enter escape mark here new movement boundary section';

            const audioBase64 = await blobToBase64(blob);
            const language = (lang || userLanguageRef.current || 'en-US').split('-')[0];
            const { data } = await aiTranscribe({ audioBase64, mimeType: 'audio/wav', language, prompt });
            const transcript = data.text?.trim();
            dlog('[Whisper] transcript:', transcript);
            if (transcript && transcript.length > 0) {
              if (onTranscript) { onTranscript(transcript); }
              else { handleVoiceCommandRef.current?.(transcript); }
            }
            else { if (voiceStateRef.current === 'listening') setTimeout(() => startListening(lang, onTranscript), 400); }
          } catch (err) { console.error('[Whisper] fetch error:', err); if (voiceStateRef.current === 'listening') setTimeout(() => startListening(lang, onTranscript), 400); }
        };
        setIsListening(true);
        dlog('[Whisper] recording started');
        let logCount = 0;
        const MAX_DURATION = 8000;
        const SILENCE_AFTER_SPEECH = 1500;
        const SILENCE_BEFORE_SPEECH = 4000;
        // Higher thresholds during playback to filter out music bleed
        // through Bluetooth.  Music typically registers around RMS 5-15;
        // real voice through close-mic BT is 20-40.
        const SILENCE_THRESHOLD = isPlayingRef.current ? 18 : 5;
        const MIN_AVG_RMS = isPlayingRef.current ? 22 : 8;
        const maxTimer = setTimeout(() => { if (!recState.stopped) { dlog('[Whisper] max duration reached, stopping'); stopRecording(); } }, MAX_DURATION);
        const checkSilence = () => {
          if (recState.stopped) { clearTimeout(maxTimer); return; }
          analyser.getByteTimeDomainData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) { const v = dataArray[i]-128; sum += v*v; }
          const rms = Math.sqrt(sum / dataArray.length);
          logCount++;
          if (logCount % 5 === 0) dlog('[Whisper] rms=', rms.toFixed(2), 'speech=', speechDetectedRef.current);
          if (rms > SILENCE_THRESHOLD) {
            if (!speechDetectedRef.current) dlog('[Whisper] speech detected, rms=', rms.toFixed(2));
            speechDetectedRef.current = true;
            // Accumulate RMS for average calculation
            speechRmsSum += rms;
            speechRmsCount++;
            if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
          } else if (speechDetectedRef.current) {
            if (!silenceTimerRef.current) {
              silenceTimerRef.current = setTimeout(() => { if (!recState.stopped) { dlog('[Whisper] silence after speech, stopping'); clearTimeout(maxTimer); stopRecording(); } }, SILENCE_AFTER_SPEECH);
            }
          } else {
            if (!silenceTimerRef.current) {
              silenceTimerRef.current = setTimeout(() => { if (!recState.stopped) { dlog('[Whisper] no speech detected, stopping'); clearTimeout(maxTimer); stopRecording(); } }, SILENCE_BEFORE_SPEECH);
            }
          }
          if (!recState.stopped) setTimeout(checkSilence, 100);
        };
        setTimeout(checkSilence, 200);
      })
      .catch(err => { console.error('[Whisper] getUserMedia error:', err); setVoiceStatus('Microphone unavailable.'); voiceStateRef.current = 'idle'; setVoiceReady(false); });
  }

  function stopListeningWhisper() {
    if (mediaRecorderRef.current) {
      if (typeof mediaRecorderRef.current.stop === 'function') { try { mediaRecorderRef.current.stop(); } catch(_) {} }
      else { mediaRecorderRef.current.stopped = true; }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    mediaRecorderRef.current = null;
    setIsListening(false);
  }

  // Accessibility setup is caregiver-driven (see SetupDialog). The app no
  // longer asks the end user any feature-choice questions at startup —
  // those would be redundant for a configured installation and confusing
  // for an unconfigured one. Configuration happens once, ahead of time,
  // by someone who isn't relying on AT to operate the form.

  // Called from voice-startup after greeting. If caregiver enabled
  // eyeTrackingAutoStart in the SetupDialog, kicks off WebGazer with the
  // configured dwell time. No-op otherwise. Safe to call repeatedly —
  // startEyeTracking already guards against double-loading WebGazer.
  function maybeAutoStartEyeTracking() {
    if (!accessibilitySetupRef.current?.eyeTrackingAutoStart) return;
    if (eyeTrackingActive) return;
    try { startEyeTracking(userLanguageRef.current || 'en-US', () => {}); }
    catch (err) { console.error('[A11y] eyeTrackingAutoStart failed:', err); }
  }
  
  // ── Eye tracking (WebGazer.js) ─────────────────────────────────────────────
  // Loaded on demand from CDN. Calibration: 9 dots × 5 clicks each.
  // Dwell: gaze on a focusable element for 800ms → click.

  // Force-hide WebGazer's DOM artifacts (video preview, face overlay,
  // prediction-point) via CSS. WebGazer's .showVideoPreview(false) and
  // .showPredictionPoints(false) APIs are unreliable — they sometimes
  // silently fail or take effect only after the video stream initialises,
  // leaving the user staring at their own face on top of the fractal.
  // This CSS rule is the belt-and-braces fix. We add it on first
  // eye-tracking start and never remove it; toggling visibility is done
  // by adding/removing the 'webgazer-show' class to <body>.
  function installWebGazerCSS() {
    if (document.getElementById('webgazer-css')) return;
    const style = document.createElement('style');
    style.id = 'webgazer-css';
    style.textContent = `
      #webgazerVideoFeed, #webgazerVideoContainer, #webgazerFaceOverlay,
      #webgazerFaceFeedbackBox, #webgazerGazeDot {
        display: none !important;
      }
      body.webgazer-show #webgazerVideoFeed,
      body.webgazer-show #webgazerVideoContainer,
      body.webgazer-show #webgazerFaceOverlay,
      body.webgazer-show #webgazerFaceFeedbackBox,
      body.webgazer-show #webgazerGazeDot {
        display: block !important;
      }
    `;
    document.head.appendChild(style);
  }

  // Latest gaze prediction in screen coords; updated by the WebGazer
  // listener. Used by the accuracy check after calibration.
  const lastGazeRef = useRef({ x: 0, y: 0, t: 0 });

  async function startEyeTracking(lang, onDone, opts = {}) {
    const { recalibrateOnly = false } = opts;
    const M = {
      'en-US': { load:'Loading eye tracking.', cal:'Calibration. Look at each dot and click it five times.', done:'Calibration complete. Eye tracking is active.', err:'Could not start eye tracking.', check:'Accuracy check. Look at each green dot when it appears.', poor:'Accuracy is poor. Recalibrate, or accept anyway?', good:'Accuracy is good.' },
      'fr-FR': { load:'Chargement du suivi oculaire.', cal:'Calibrage. Regardez chaque point et cliquez cinq fois.', done:'Calibrage terminé. Suivi oculaire actif.', err:'Impossible de démarrer le suivi oculaire.', check:'Vérification de la précision. Regardez chaque point vert quand il apparaît.', poor:'Précision faible. Recalibrer ou accepter?', good:'Précision bonne.' },
      'es-ES': { load:'Cargando seguimiento ocular.', cal:'Calibración. Mira cada punto y haz clic cinco veces.', done:'Calibración completa. Seguimiento ocular activo.', err:'No se pudo iniciar el seguimiento ocular.', check:'Verificación de precisión. Mira cada punto verde cuando aparezca.', poor:'Precisión baja. ¿Recalibrar o aceptar?', good:'Precisión buena.' },
      'de-DE': { load:'Eye-Tracking wird geladen.', cal:'Kalibrierung. Schauen Sie auf jeden Punkt und klicken Sie fünfmal.', done:'Kalibrierung abgeschlossen. Eye-Tracking aktiv.', err:'Eye-Tracking konnte nicht gestartet werden.', check:'Genauigkeitsprüfung. Schauen Sie auf jeden grünen Punkt, wenn er erscheint.', poor:'Genauigkeit gering. Neu kalibrieren oder akzeptieren?', good:'Genauigkeit gut.' },
      'it-IT': { load:'Caricamento tracciamento oculare.', cal:'Calibrazione. Guarda ogni punto e fai clic cinque volte.', done:'Calibrazione completata. Tracciamento oculare attivo.', err:'Impossibile avviare il tracciamento oculare.', check:'Verifica della precisione. Guarda ogni punto verde quando appare.', poor:'Precisione bassa. Ricalibrare o accettare?', good:'Precisione buona.' },
    };
    const m = M[lang] || M['en-US'];

    voiceSpeak(m.load, lang, async () => {
      try {
        installWebGazerCSS();
        // Skip the heavy CDN + begin() path on recalibrate — WebGazer is
        // already loaded and running, we just need to re-run the dot phase.
        if (!recalibrateOnly) {
          if (!window.webgazer) {
            await new Promise((resolve, reject) => {
              const s = document.createElement('script');
              s.src = 'https://cdn.jsdelivr.net/npm/webgazer@3.3.0/dist/webgazer.min.js';
              s.onload = resolve;
              s.onerror = () => reject(new Error('CDN load failed'));
              document.head.appendChild(s);
            });
          }
          await window.webgazer
            .setRegression('ridge')
            .setGazeListener((data) => {
              if (data) lastGazeRef.current = { x: data.x, y: data.y, t: performance.now() };
            })
            .begin();
        }
        // Hide WebGazer's UI: video preview and the on-screen gaze dot. The
        // CSS we installed above will keep them hidden regardless of what
        // WebGazer's own toggles do.
        try { window.webgazer.showVideoPreview(false); } catch (_) {}
        try { window.webgazer.showPredictionPoints(false); } catch (_) {}
        document.body.classList.remove('webgazer-show');

        // 9-point calibration. We show the dot temporarily during this
        // phase so the user can see where the tracker thinks they're
        // looking — useful diagnostic feedback. Off again afterwards.
        try { window.webgazer.showPredictionPoints(true); } catch (_) {}
        document.body.classList.add('webgazer-show');
        setEyeCalibClicks({});
        await new Promise((resolve) => {
          eyeCalibrationDoneRef.current = resolve;
          setEyeCalibrating(true);
          voiceSpeak(m.cal, lang);
        });
        setEyeCalibrating(false);
        // Brief pause for state to settle before the accuracy check.
        await new Promise(r => setTimeout(r, 200));

        // Accuracy check: show 4 corner targets in sequence, ask the user
        // to look at each, measure error vs predicted gaze. If average
        // error is poor (>200px), prompt the caregiver to recalibrate.
        voiceSpeak(m.check, lang);
        const errors = await runAccuracyCheck();
        const avgErr = errors.length ? errors.reduce((s,e) => s+e, 0) / errors.length : 999;
        dlog('[EyeTracking] Accuracy check errors (px):', errors, 'avg:', avgErr.toFixed(0));

        // Hide the prediction dot for normal use — distracting.
        try { window.webgazer.showPredictionPoints(false); } catch (_) {}
        document.body.classList.remove('webgazer-show');

        // Decision point. Threshold of 200px is generous — WebGazer
        // routinely hits 100-150px even on a good day. >200px means the
        // calibration didn't take and dwell-on-controls won't work
        // reliably for small targets.
        if (avgErr > 200) {
          const accept = await caregiverConfirm(`${m.poor} Average error ${Math.round(avgErr)} pixels.`, ['Recalibrate', 'Accept anyway']);
          if (accept === 'Recalibrate') {
            // Recurse with recalibrateOnly to avoid re-loading WebGazer.
            return startEyeTracking(lang, onDone, { recalibrateOnly: true });
          }
        }

        setEyeTrackingActive(true);
        startDwellLoop();
        voiceSpeak(m.done, lang, onDone);
      } catch (err) {
        console.error('[EyeTracking] startEyeTracking failed:', err);
        voiceSpeak(m.err, lang, onDone);
      }
    });
  }

  // Show 4 corner targets one at a time, wait for the user's gaze to settle
  // near each (or 2.5s, whichever comes first), then return the per-target
  // error distance in pixels. Used by startEyeTracking to decide whether
  // calibration was good enough.
  function runAccuracyCheck() {
    const positions = [
      { x: window.innerWidth * 0.12, y: window.innerHeight * 0.18 },
      { x: window.innerWidth * 0.88, y: window.innerHeight * 0.18 },
      { x: window.innerWidth * 0.12, y: window.innerHeight * 0.82 },
      { x: window.innerWidth * 0.88, y: window.innerHeight * 0.82 },
    ];
    const errors = [];
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:99999;pointer-events:none;';
    const target = document.createElement('div');
    target.style.cssText = 'position:absolute;width:40px;height:40px;border-radius:50%;background:#22c55e;border:3px solid white;transform:translate(-50%,-50%);';
    overlay.appendChild(target);
    document.body.appendChild(overlay);

    return new Promise(async (resolve) => {
      try {
        for (const pos of positions) {
          target.style.left = pos.x + 'px';
          target.style.top = pos.y + 'px';
          // Settle: wait 800ms for the user to fixate, then sample for 700ms
          // and take the median gaze distance from the target.
          await new Promise(r => setTimeout(r, 800));
          const samples = [];
          const sampleStart = performance.now();
          while (performance.now() - sampleStart < 700) {
            const g = lastGazeRef.current;
            if (g && g.t > sampleStart - 100) {
              const dx = g.x - pos.x;
              const dy = g.y - pos.y;
              samples.push(Math.sqrt(dx * dx + dy * dy));
            }
            await new Promise(r => setTimeout(r, 50));
          }
          if (samples.length) {
            samples.sort((a, b) => a - b);
            errors.push(samples[Math.floor(samples.length / 2)]);
          }
        }
      } finally {
        document.body.removeChild(overlay);
        resolve(errors);
      }
    });
  }

  // Simple confirm-style modal driven by voice prompt + button click. Used
  // by the post-calibration accuracy check to ask the caregiver whether to
  // recalibrate. Resolves with the clicked option's label.
  function caregiverConfirm(message, options) {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:99999;display:flex;align-items:center;justify-content:center;';
      const box = document.createElement('div');
      box.style.cssText = 'background:#0f172a;border:1px solid #334155;border-radius:14px;padding:32px;max-width:500px;color:#f8fafc;text-align:center;';
      const msg = document.createElement('div');
      msg.textContent = message;
      msg.style.cssText = 'font-size:16px;margin-bottom:20px;line-height:1.5;';
      box.appendChild(msg);
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:10px;justify-content:center;';
      for (const opt of options) {
        const btn = document.createElement('button');
        btn.textContent = opt;
        btn.style.cssText = 'background:#1e293b;border:1px solid #334155;color:#f8fafc;padding:10px 20px;border-radius:8px;cursor:pointer;font-size:14px;';
        btn.onclick = () => { document.body.removeChild(overlay); resolve(opt); };
        row.appendChild(btn);
      }
      box.appendChild(row);
      overlay.appendChild(box);
      document.body.appendChild(overlay);
      voiceSpeak(message, userLanguageRef.current || 'en-US');
    });
  }
  
  function stopEyeTracking() {
    // Stop the dwell polling loop
    if (gazePollRef.current) {
      clearInterval(gazePollRef.current);
      gazePollRef.current = null;
    }
    // Reset dwell state and hide the ring
    dwellStateRef.current = { element: null, startTime: 0 };
    if (dwellRingRef.current) {
      dwellRingRef.current.style.display = 'none';
    }
    // Tell WebGazer to stop processing frames (camera stays on but no predictions)
    if (window.webgazer) {
      try {
        window.webgazer.pause();
        window.webgazer.showPredictionPoints(false);
      } catch (_) {}
    }
    setEyeTrackingActive(false);
  }

  function startDwellLoop() {
    const DWELL_MS = accessibilitySetupRef.current?.dwellMs || 1000;
    const TICK_MS = 50;
    const SMOOTH_WINDOW = 10;       // average last N predictions
    const STICKY_RADIUS = 80;       // px — tolerate gaze drift this far from current target
    const recent = [];

    if (gazePollRef.current) clearInterval(gazePollRef.current);
    gazePollRef.current = setInterval(async () => {
      if (!window.webgazer) return;
      let pred;
      try { pred = await window.webgazer.getCurrentPrediction(); } catch (_) { return; }
      if (!pred) return;

      // Smooth gaze with rolling average
      recent.push({ x: pred.x, y: pred.y });
      if (recent.length > SMOOTH_WINDOW) recent.shift();
      const avgX = recent.reduce((s, p) => s + p.x, 0) / recent.length;
      const avgY = recent.reduce((s, p) => s + p.y, 0) / recent.length;

      let target = findFocusableUnderPoint(avgX, avgY);

      // Sticky dwell: if we're already dwelling on something and gaze briefly
      // drifts off, but the current dwell target is still within STICKY_RADIUS
      // of the gaze, keep dwelling on it.
      const current = dwellStateRef.current.element;
      if (current && target !== current) {
        const r = current.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dist = Math.hypot(avgX - cx, avgY - cy);
        if (dist < STICKY_RADIUS) target = current;
      }

      if (target !== dwellStateRef.current.element) {
        dwellStateRef.current = { element: target, startTime: target ? performance.now() : 0 };
        updateDwellRing(target, 0);
      } else if (target) {
        const elapsed = performance.now() - dwellStateRef.current.startTime;
        const progress = Math.min(1, elapsed / DWELL_MS);
        updateDwellRing(target, progress);
        if (progress >= 1) {
          try { target.click(); } catch (_) {}
          dwellStateRef.current = { element: null, startTime: 0 };
          updateDwellRing(null, 0);
        }
      }
    }, TICK_MS);
  }
  
  function findFocusableUnderPoint(x, y) {
    let cur = document.elementFromPoint(x, y);
    while (cur && cur !== document.body) {
      if (cur.matches?.('button, input, select, [tabindex]:not([tabindex="-1"]), a[href]')) return cur;
      cur = cur.parentElement;
    }
    return null;
  }

  function updateDwellRing(element, progress) {
    let ring = dwellRingRef.current;
    if (!ring) {
      ring = document.createElement('div');
      ring.style.cssText = 'position:fixed;pointer-events:none;border-radius:8px;border:3px solid transparent;z-index:99999;transition:border-color 0.05s,border-width 0.05s;';
      document.body.appendChild(ring);
      dwellRingRef.current = ring;
    }
    if (!element) { ring.style.display = 'none'; return; }
    const r = element.getBoundingClientRect();
    ring.style.display = 'block';
    ring.style.left = (r.left - 4) + 'px';
    ring.style.top = (r.top - 4) + 'px';
    ring.style.width = (r.width + 8) + 'px';
    ring.style.height = (r.height + 8) + 'px';
    // Blue (59,130,246) → Green (34,197,94) as progress 0→1
    const cr = Math.round(59 + (34 - 59) * progress);
    const cg = Math.round(130 + (197 - 130) * progress);
    const cb = Math.round(246 + (94 - 246) * progress);
    ring.style.borderColor = `rgb(${cr},${cg},${cb})`;
    ring.style.borderWidth = (3 + progress * 3) + 'px';
  }

  const autoActivateBtnRef = useRef(null);
  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    // If a caregiver has configured accessibility and explicitly disabled
    // voiceAutoStart, respect that and skip the focus. Otherwise (unconfigured
    // app, or voiceAutoStart explicitly on) focus the activation button so
    // the first keypress kicks off voice.
    const s = accessibilitySetupRef.current;
    if (s?.configured && !s.voiceAutoStart) return;
    navigator.mediaDevices.enumerateDevices().then(devices => {
      if (devices.some(d => d.kind === 'audioinput') && autoActivateBtnRef.current) autoActivateBtnRef.current.focus();
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Onboarding / auto-start ────────────────────────────────────────────────
  // Browsers block speechSynthesis and SpeechRecognition until a user gesture.
  // We wait for the first click or keydown, then start the voice sequence.
  useEffect(() => {
    let started = false;

    function startVoice() {
      if (started) return;
      started = true;
      dlog('[Voice] startVoice fired. onboardingDone=', onboardingDone, 'lang=', userLanguageRef.current);
      window.removeEventListener('click', startVoice, true);
      window.removeEventListener('keydown', startVoice, true);
      setVoiceReady(true);

      if (onboardingDone) {
        isVoiceActiveRef.current = true;
        dlog('[Voice] Speaking startup message...');
        const activated = {
          'en-US': 'Voice activated.',
          'fr-FR': 'Voix activée.',
          'es-ES': 'Voz activada.',
          'de-DE': 'Stimme aktiviert.',
          'it-IT': 'Voce attivata.',
        };
        const lang = userLanguageRef.current || 'en-US';
        const message = (activated[lang] || activated['en-US']) + ' ' + buildShortOrientation(lang);
        voiceSpeak(message, lang, () => {
          dlog('[Voice] Startup message done, starting recognition...');
          voiceStateRef.current = 'listening';
          // If the caregiver enabled auto-eye-tracking, start it now.
          maybeAutoStartEyeTracking();
          startListening(lang);
        });
        return;
      }

      // First-time onboarding
      const browserLang = navigator.language || 'en-US';
      const langMap = { en: 'en-US', fr: 'fr-FR', es: 'es-ES', de: 'de-DE', it: 'it-IT' };
      const prefix = browserLang.split('-')[0];
      const lang = langMap[prefix] || 'en-US';
      dlog('[Voice] First-time onboarding, lang=', lang);
      if (!localStorage.getItem('fractal_language')) {
        setUserLanguage(lang);
        userLanguageRef.current = lang;
        localStorage.setItem('fractal_language', lang);
      }
      const welcomes = { 'en-US':'Welcome to Fractal Music.', 'fr-FR':'Bienvenue dans Fractal Music.', 'es-ES':'Bienvenido a Fractal Music.', 'de-DE':'Willkommen bei Fractal Music.', 'it-IT':'Benvenuto in Fractal Music.' };
      const nameQs = { 'en-US':'What shall I call you?', 'fr-FR':'Comment dois-je vous appeler?', 'es-ES':'¿Cómo debo llamarte?', 'de-DE':'Wie soll ich dich nennen?', 'it-IT':'Come devo chiamarti?' };
      voiceSpeak((welcomes[lang]||welcomes['en-US'])+' '+(nameQs[lang]||nameQs['en-US']), lang, () => {
        awaitingNameRef.current = true;
        startListening(lang, (name) => {
          const clean = name.replace(/^(my name is|call me|i am|i'm)\s*/i,'').trim();
          setUserName(clean);
          localStorage.setItem('fractal_userName', clean);
          setOnboardingDone(true);
          isVoiceActiveRef.current = true;
          const greets = { 'en-US':`Nice to meet you, ${clean}. Voice-activated listening started.`, 'fr-FR':`Enchanté, ${clean}. Écoute vocale activée.`, 'es-ES':`Encantado, ${clean}. Escucha por voz activada.`, 'de-DE':`Schön Sie kennenzulernen, ${clean}. Sprachsteuerung gestartet.`, 'it-IT':`Piacere, ${clean}. Ascolto vocale attivato.` };
          voiceSpeak(greets[lang]||greets['en-US'], lang, () => {
            // If the caregiver enabled auto-eye-tracking, start it now.
            maybeAutoStartEyeTracking();
            beginListening();
          });
        });
      });
    }

    window.addEventListener('click', startVoice, true);
    window.addEventListener('keydown', startVoice, true);
    return () => {
      window.removeEventListener('click', startVoice, true);
      window.removeEventListener('keydown', startVoice, true);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Execute voice command ──────────────────────────────────────────────────
  // Keep the ref always current so autoRestart callbacks never call a stale or null function.
  useEffect(() => { handleVoiceCommandRef.current = handleVoiceCommand; });
  // ── Orientation helpers ────────────────────────────────────────────────────
  // Spoken summaries of the app's current state. Short version is read after
  // the "Voice activated" greeting on returning sessions so a user inheriting
  // the app (especially in a shared environment) hears immediately what was
  // left behind. Long version is read on demand via the "where am I" voice
  // command for users who want every detail.
  function buildShortOrientation(lang) {
    const presetName = presets[presetKey]?.label || null;
    const paletteLabel = palettes[paletteName]?.label || paletteName;
    const fractalLabel = fractalType === 'julia' ? 'Julia' : 'Mandelbrot';
    const viewDesc = presetName || `custom ${fractalLabel} view`;
    const modeDesc = mode === 'orbit' ? 'orbit mode' : 'scan mode';

    const tmpl = {
      'en-US': `Currently showing ${viewDesc}, palette ${paletteLabel}, ${modeDesc}. Say "where am I" for full details, or speak a command.`,
      'fr-FR': `Affichage actuel : ${viewDesc}, palette ${paletteLabel}, ${modeDesc}. Dites "où suis-je" pour plus de détails, ou parlez une commande.`,
      'es-ES': `Mostrando actualmente: ${viewDesc}, paleta ${paletteLabel}, ${modeDesc}. Di "dónde estoy" para más detalles, o habla un comando.`,
      'de-DE': `Aktuell angezeigt: ${viewDesc}, Palette ${paletteLabel}, ${modeDesc}. Sagen Sie "wo bin ich" für alle Details, oder sprechen Sie einen Befehl.`,
      'it-IT': `Attualmente mostrato: ${viewDesc}, palette ${paletteLabel}, ${modeDesc}. Di "dove sono" per più dettagli, o parla un comando.`,
    };
    return tmpl[lang] || tmpl['en-US'];
  }

  function buildLongOrientation(lang) {
    // Build a state summary in pieces. Each piece is a self-contained
    // phrase so we can compose them naturally in each language without
    // worrying about word order or grammar.
    const presetName = presets[presetKey]?.label || null;
    const paletteLabel = palettes[paletteName]?.label || paletteName;
    const fractalLabel = fractalType === 'julia' ? 'Julia' : 'Mandelbrot';
    const modeDesc = mode === 'orbit' ? 'orbit' : 'scan';

    // Zoom depth: width of the visible region in the complex plane.
    // Useful to communicate "how zoomed-in" without reading coordinates.
    const xWidth = Math.abs(view.xMax - view.xMin);
    let zoomDesc;
    if (xWidth > 2) zoomDesc = 'full view';
    else if (xWidth > 0.5) zoomDesc = 'wide zoom';
    else if (xWidth > 0.01) zoomDesc = 'moderate zoom';
    else if (xWidth > 0.0001) zoomDesc = 'deep zoom';
    else zoomDesc = 'very deep zoom';

    const parts = {
      'en-US': [
        `Fractal type: ${fractalLabel}.`,
        presetName ? `View: ${presetName}.` : `View: custom ${zoomDesc}.`,
        `Maximum iterations: ${maxIter}.`,
        `Palette: ${paletteLabel}.`,
        `Mode: ${modeDesc}.`,
        `Music volume ${musicVolume}, voice volume ${voiceVolume}.`,
        `Column speed: ${(lineDrawMs / 1000).toFixed(2)} seconds.`,
        `Stereo width: ${stereoWidth.toFixed(2)}.`,
        colorAffectsSound ? 'Color influences sound is on.' : 'Color influences sound is off.',
        showGrid ? 'Grid overlay is on.' : null,
        eyeTrackingActive ? 'Eye tracking is active.' : null,
      ].filter(Boolean),
      'fr-FR': [
        `Type de fractale : ${fractalLabel}.`,
        presetName ? `Vue : ${presetName}.` : `Vue : ${zoomDesc} personnalisé.`,
        `Itérations maximum : ${maxIter}.`,
        `Palette : ${paletteLabel}.`,
        `Mode : ${modeDesc}.`,
        `Volume musique ${musicVolume}, volume voix ${voiceVolume}.`,
        `Vitesse de colonne : ${(lineDrawMs / 1000).toFixed(2)} secondes.`,
        `Largeur stéréo : ${stereoWidth.toFixed(2)}.`,
        colorAffectsSound ? 'La couleur influence le son.' : 'La couleur n\'influence pas le son.',
        showGrid ? 'Grille superposée activée.' : null,
        eyeTrackingActive ? 'Suivi oculaire actif.' : null,
      ].filter(Boolean),
      'es-ES': [
        `Tipo de fractal: ${fractalLabel}.`,
        presetName ? `Vista: ${presetName}.` : `Vista: ${zoomDesc} personalizado.`,
        `Iteraciones máximas: ${maxIter}.`,
        `Paleta: ${paletteLabel}.`,
        `Modo: ${modeDesc}.`,
        `Volumen música ${musicVolume}, volumen voz ${voiceVolume}.`,
        `Velocidad de columna: ${(lineDrawMs / 1000).toFixed(2)} segundos.`,
        `Ancho estéreo: ${stereoWidth.toFixed(2)}.`,
        colorAffectsSound ? 'El color influye en el sonido.' : 'El color no influye en el sonido.',
        showGrid ? 'Rejilla activada.' : null,
        eyeTrackingActive ? 'Seguimiento ocular activo.' : null,
      ].filter(Boolean),
      'de-DE': [
        `Fraktaltyp: ${fractalLabel}.`,
        presetName ? `Ansicht: ${presetName}.` : `Ansicht: benutzerdefinierter ${zoomDesc}.`,
        `Maximale Iterationen: ${maxIter}.`,
        `Palette: ${paletteLabel}.`,
        `Modus: ${modeDesc}.`,
        `Musiklautstärke ${musicVolume}, Sprachlautstärke ${voiceVolume}.`,
        `Spaltengeschwindigkeit: ${(lineDrawMs / 1000).toFixed(2)} Sekunden.`,
        `Stereobreite: ${stereoWidth.toFixed(2)}.`,
        colorAffectsSound ? 'Farbe beeinflusst den Klang.' : 'Farbe beeinflusst den Klang nicht.',
        showGrid ? 'Raster aktiv.' : null,
        eyeTrackingActive ? 'Eye-Tracking aktiv.' : null,
      ].filter(Boolean),
      'it-IT': [
        `Tipo di frattale: ${fractalLabel}.`,
        presetName ? `Vista: ${presetName}.` : `Vista: ${zoomDesc} personalizzato.`,
        `Iterazioni massime: ${maxIter}.`,
        `Palette: ${paletteLabel}.`,
        `Modalità: ${modeDesc}.`,
        `Volume musica ${musicVolume}, volume voce ${voiceVolume}.`,
        `Velocità di colonna: ${(lineDrawMs / 1000).toFixed(2)} secondi.`,
        `Larghezza stereo: ${stereoWidth.toFixed(2)}.`,
        colorAffectsSound ? 'Il colore influenza il suono.' : 'Il colore non influenza il suono.',
        showGrid ? 'Griglia attiva.' : null,
        eyeTrackingActive ? 'Tracciamento oculare attivo.' : null,
      ].filter(Boolean),
    };
    return (parts[lang] || parts['en-US']).join(' ');
  }

  async function handleVoiceCommand(transcript) {
    setVoiceStatus(`Heard: "${transcript}"`);
    // Whisper hallucination filter. On short/quiet clips (common during a scan,
    // between spoken commands) Whisper emits stock fillers — "thank you", "you",
    // "thanks for watching" — with no real speech behind them. Acting on these
    // pauses the scan (non-transport actions get rewritten to 'pause'). Treat a
    // transcript that is ONLY such filler as noise: ignore it, keep listening.
    // NOTE: 'bye'/'goodbye' are deliberately NOT here — they may be real intent.
    const _clean = transcript.trim().toLowerCase().replace(/[.,!?]/g, '');
    const _HALLUCINATIONS = new Set([
      'thank you', 'thanks', 'you', 'thank you very much',
      'thanks for watching', 'thank you for watching', 'okay', 'ok', ''
    ]);
    if (_HALLUCINATIONS.has(_clean)) {
      dlog('[Voice] Ignoring likely Whisper hallucination:', transcript);
      if (voiceStateRef.current === 'listening') startListening(userLanguageRef.current);
      return;
    }
    // Onboarding name capture. If we're waiting for the user's name (set when
    // onboarding asked "What shall I call you?"), treat ANY non-hallucination
    // transcript as the name — regardless of which listen cycle caught it. This
    // is robust against the ~40 startListening call sites that don't pass the
    // name callback; without this flag, the name would fall through to the
    // command parser and be reported as "Not understood".
    if (awaitingNameRef.current) {
      awaitingNameRef.current = false;
      const _nm = transcript.replace(/^(my name is|call me|i am|i'm)\s*/i, '').replace(/[.,!?]/g, '').trim();
      const _lang = userLanguageRef.current || 'en-US';
      setUserName(_nm);
      localStorage.setItem('fractal_userName', _nm);
      setOnboardingDone(true);
      isVoiceActiveRef.current = true;
      const _greets = { 'en-US':`Nice to meet you, ${_nm}. Voice-activated listening started.`, 'fr-FR':`Enchant\u00e9, ${_nm}. \u00c9coute vocale activ\u00e9e.`, 'es-ES':`Encantado, ${_nm}. Escucha por voz activada.`, 'de-DE':`Sch\u00f6n Sie kennenzulernen, ${_nm}. Sprachsteuerung gestartet.`, 'it-IT':`Piacere, ${_nm}. Ascolto vocale attivato.` };
      voiceSpeak(_greets[_lang] || _greets['en-US'], _lang, () => { maybeAutoStartEyeTracking(); beginListening(); });
      return;
    }

    // Fast-path for boundary marking. "mark here" and its variants are fixed,
    // unambiguous commands — routing them through the AI parser adds a round-trip
    // (latency) and a failure mode (the model occasionally returns idle/unknown,
    // which during a scan gets rewritten to pause). Match them directly here and
    // dispatch markBoundary, exactly as the Enter key does. This runs AFTER the
    // hallucination filter (so noise is still ignored) and BEFORE interpretVoiceCommand.
    const _MARK_PHRASES = new Set([
      'mark here', 'mark', 'mark boundary', 'mark this', 'mark this point',
      'mark this spot', 'new movement', 'new section', 'new movement here',
      'section boundary', 'drop a marker', 'mark point', 'mark now'
    ]);
    if (_MARK_PHRASES.has(_clean)) {
      dlog('[Voice] Mark fast-path:', transcript, '-> markBoundary');
      try { if (markBoundaryRef.current) markBoundaryRef.current(); } catch (_) {}
      if (voiceStateRef.current === 'listening') startListening(userLanguageRef.current);
      return;
    }
    const context = { fractalType, userLanguage, userName, xMin: view.xMin, xMax: view.xMax, yMin: view.yMin, yMax: view.yMax, imgLibrary: imgLibrary.join(", ") || "empty", paletteNames: Object.values(palettes).map(p => p.label).join(", ") };
    const action = await interpretVoiceCommand(transcript, context);
    // If Claude returns idle for a non-empty transcript, override to unknown
    // so the user always gets feedback rather than silence
    if (action.action === 'idle' && transcript.trim().length > 0) {
      action.action = 'unknown';
    }

    // Context-aware filtering during scan playback. While a scan is running
    // the only sensible commands are transport controls (pause, resume,
    // startScan, whereAmI). Anything else — misheard speech, the user
    // saying "stop" expecting it to mean pause, ambient room noise — is
    // rewritten to 'pause'. The user's stance: if the app pauses
    // unintentionally, "resume here" recovers cheaply; but if the app
    // keeps playing while the user is trying to issue any command, the
    // music drowns out the voice and the user can't make progress.
    //
    // Note especially: 'stopListening' is rewritten to 'pause' too. We
    // never turn off voice activation as a side effect of "stop" or
    // similar — the mic button in the toolbar remains the only way to
    // end voice control, since accidentally killing voice mid-demo
    // requires the user to find and click that button, breaking the
    // hands-free workflow this app is built around.
    if (isPlayingRef.current) {
      const SCAN_ALLOWED = new Set(['pause', 'resume', 'startScan', 'whereAmI', 'markBoundary', 'previewOrbit']);
      if (!SCAN_ALLOWED.has(action.action)) {
        // Ignore unrecognized commands during a scan (mishearings, Whisper
        // hallucinations) rather than pausing. Uninterrupted playback matters
        // more to a composer than a safety-pause; real transport commands
        // (pause, resume, markBoundary, whereAmI) are in SCAN_ALLOWED and still
        // work. A dropped real command can simply be repeated. This also means
        // 'stopListening' is ignored mid-scan, preserving the old protection
        // that "stop"/"goodbye" never kills voice control during playback.
        dlog('[Voice] Ignoring unrecognized action during scan (not pausing):', action.action, 'transcript:', transcript);
        if (voiceStateRef.current === 'listening') startListening(userLanguageRef.current);
        return;
      }
    }

    // All spoken responses come from Claude in the user's detected language
    const say = (fallback, onEnd) => voiceSpeak(action.spokenResponse || fallback, userLanguageRef.current, onEnd);

    // If a browseList is pending, the user is responding to an open list.
    // Try the list-selection paths FIRST against the raw transcript, since
    // the user may say things like "switch to orbit mode" or "jump to orbit"
    // that contain a list option as a substring even though the LLM parsed
    // them as separate actions (setMode, jumpTo, etc.). Only if nothing
    // matches do we abandon the list and let the LLM's action run.
    const bypassBrowse = ['tabForward','tabBack','pressEnter','pressSpace','arrowUp','arrowDown','stopListening'];
    const pb = pendingBrowseRef.current;
    if (pb && !bypassBrowse.includes(action.action)) {
      const h = transcript.toLowerCase().trim();

      // Cancel beats everything.
      if (['cancel','annuler','cancelar'].some(w => h.includes(w))) {
        pendingBrowseRef.current = null;
        voiceSpeak('Cancelled.', userLanguageRef.current, () => beginListening());
        return;
      }

      // Label match: exact, substring-in-label, or substring-in-transcript.
      const match = pb.options.find(o => o.label.toLowerCase() === h)
        || pb.options.find(o => o.label.toLowerCase().includes(h))
        || pb.options.find(o => h.includes(o.label.toLowerCase()));
      if (match) {
        pendingBrowseRef.current = null;
        pb.onSelect(match.value, match.label);
        return;
      }

      // Positional ("item 12", "fifth", "number 3").
      const itemNum = parseItemNumber(h);
      if (itemNum !== null && itemNum >= 1 && itemNum <= pb.options.length) {
        const opt = pb.options[itemNum - 1];
        pendingBrowseRef.current = null;
        pb.onSelect(opt.value, opt.label);
        return;
      }

      // Nothing matched the list. If the LLM returned a recognised action,
      // abandon the browse and fall through to normal handling — the user
      // has moved on. Otherwise re-prompt with the choices.
      const isFreshCommand = action.action && action.action !== 'unknown' && action.action !== 'idle';
      if (isFreshCommand) {
        pendingBrowseRef.current = null;
        // Fall through to the action dispatch below.
      } else {
        const labels = pb.options.map((o, i) => `item ${i + 1}, ${o.label}`).join('. ');
        voiceSpeak(`I didn’t understand that. Say: ${labels}.`, userLanguageRef.current, () => {
          beginListening();
        });
        return;
      }
    }

    // If a color-editor close is pending confirmation, intercept the response.
    if (pendingEditorCloseRef.current) {
      const h = transcript.toLowerCase().trim();
      if (/\b(cancel|never\s*mind|nevermind|abort)\b/.test(h)) {
        pendingEditorCloseRef.current = false;
        voiceSpeak('Cancelled. Color editor still open.', userLanguageRef.current, () => {
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        return;
      }
      if (/\b(save|save\s*first|save\s*it)\b/.test(h)) {
        pendingEditorCloseRef.current = false;
        const win = colorEditorWindowRef.current;
        if (win && !win.closed) {
          try { win.document.getElementById('saveBtn')?.click(); } catch (_) {}
        }
        voiceSpeak('Saving and closing.', userLanguageRef.current, () => {
          if (win && !win.closed) {
            // Suppress the popup's beforeunload prompt — see comment in
            // buildColorEditorHtml. Programmatic close + native confirm has
            // caused browser freezes on Linux/Wayland, so we always set this
            // before calling win.close().
            try { win.__forceClose = true; } catch (_) {}
            try { win.close(); } catch (_) {}
          }
          colorEditorWindowRef.current = null;
          suppressFocusRef.current = true;
          document.getElementById('color-editor-btn')?.focus();
          voiceSpeak('Color editor closed.', userLanguageRef.current, () => {
            suppressFocusRef.current = false;
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
        });
        return;
      }
      if (/\b(close\s*anyway|discard|force\s*close|yes|confirm)\b/.test(h)) {
        pendingEditorCloseRef.current = false;
        const win = colorEditorWindowRef.current;
        if (win && !win.closed) {
          try { win.__forceClose = true; } catch (_) {}
          try { win.close(); } catch (_) {}
        }
        colorEditorWindowRef.current = null;
        suppressFocusRef.current = true;
        document.getElementById('color-editor-btn')?.focus();
        voiceSpeak('Color editor closed. Edits discarded.', userLanguageRef.current, () => {
          suppressFocusRef.current = false;
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        return;
      }
      voiceSpeak("Please say save first, close anyway, or cancel.", userLanguageRef.current, () => {
        if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
      });
      return;
    }

    switch (action.action) {
      case 'actions': {
        // Compound action: run each sub-action in sequence, then speak the
        // top-level summary.  Each sub-action's spokenResponse is suppressed
        // (we use the top-level one).
        const list = Array.isArray(action.list) ? action.list : [];
        for (const sub of list) {
          const subAction = { ...sub, spokenResponse: '' };
          // Reuse this same dispatcher recursively but skip TTS/listening
          // logic by inlining only the colorEditor case (compound is currently
          // documented for color-editor commands only — extend here if needed).
          if (subAction.action === 'colorEditor') {
            const win = colorEditorWindowRef.current;
            if (!win || win.closed) continue;
            const doc = win.document;
            const fire = (el, type) => {
              if (!el) return;
              el.dispatchEvent(new win.Event(type, { bubbles: true }));
            };
            const setRange = (id, value) => {
              const el = doc.getElementById(id);
              if (!el) return;
              el.value = String(Math.max(0, Math.min(255, Math.round(value))));
              fire(el, 'input'); fire(el, 'change');
            };
            switch (subAction.sub) {
              case 'set': {
                const idMap = {
                  r: { value: 'rss', step: 'rts' },
                  g: { value: 'gss', step: 'gts' },
                  b: { value: 'bss', step: 'bts' },
                };
                const id = idMap[subAction.channel]?.[subAction.field];
                if (id) setRange(id, Number(subAction.value) || 0);
                break;
              }
              case 'dir': {
                const id = `${subAction.channel}${subAction.up ? 'up' : 'dn'}`;
                doc.getElementById(id)?.click();
                break;
              }
              case 'page': {
                const inp = doc.getElementById('pageInput');
                if (!inp) break;
                const cur = Number(inp.value) || 0;
                const next = subAction.absolute != null
                  ? Math.max(0, Math.min(255, Math.round(Number(subAction.absolute))))
                  : Math.max(0, Math.min(255, cur + (Number(subAction.delta) || 0)));
                inp.value = String(next);
                fire(inp, 'change');
                break;
              }
              case 'smoothing': {
                const cb = doc.getElementById('smoothCb');
                if (!cb) break;
                cb.checked = !!subAction.value;
                fire(cb, 'change');
                break;
              }
              case 'reset':    { doc.getElementById('resetBtn')?.click(); break; }
              case 'applyAll': { doc.getElementById('applyAllBtn')?.click(); break; }
              case 'save':     { doc.getElementById('saveBtn')?.click(); break; }
            }
          }
        }
        if (action.spokenResponse) say(action.spokenResponse);
        break;
      }
      case 'loadPreset': {
        const p = presets[action.presetKey];
        if (p) {
          setPresetKey(action.presetKey);
          setFractalType(p.fractalType); setView(p.view); setMaxIter(p.maxIter); setJuliaC(p.juliaC);
          setInputFractalType(p.fractalType);
          setInputXMin(String(p.view.xMin)); setInputXMax(String(p.view.xMax));
          setInputYMin(String(p.view.yMin)); setInputYMax(String(p.view.yMax));
          setInputMaxIter(String(p.maxIter));
          resetHistory(p.view, p.fractalType, p.maxIter, p.juliaC);
          say(`Loading ${p.label}.`);
          setVoiceStatus(`Loaded: ${p.label}`);
        } else { say('Preset not found.'); }
        break;
      }
      case 'zoom': {
        let nv;
        if (action.xMin != null && action.xMax != null && action.yMin != null && action.yMax != null) {
          // Explicit target rectangle supplied by the parser.
          nv = { xMin: action.xMin, xMax: action.xMax, yMin: action.yMin, yMax: action.yMax };
        } else {
          // Directional "zoom in"/"zoom out" with no coordinates: scale the
          // current view around its center, like the wheel/keyboard zoom.
          const v = viewRef.current;
          const cx = (v.xMin + v.xMax) / 2;
          const cy = (v.yMin + v.yMax) / 2;
          const factor = action.direction === 'out' ? 1.5 : 0.75;
          const halfX = (v.xMax - v.xMin) * 0.5 * factor;
          const halfY = (v.yMax - v.yMin) * 0.5 * factor;
          nv = { xMin: cx - halfX, xMax: cx + halfX, yMin: cy - halfY, yMax: cy + halfY };
        }
        setView(nv); setInputXMin(String(nv.xMin)); setInputXMax(String(nv.xMax)); setInputYMin(String(nv.yMin)); setInputYMax(String(nv.yMax));
        pushHistory(nv, fractalType, maxIter, juliaC);
        say(action.spokenResponse || `Zooming ${action.direction || 'in'}.`); setVoiceStatus('Zoomed');
        break;
      }
      case 'back':
        if (nav.historyIndex > 0) { navigateHistory(nav.historyIndex - 1); say('Going back.'); }
        else say('No previous view.');
        break;
      case 'forward':
        if (nav.historyIndex < nav.history.length - 1) { navigateHistory(nav.historyIndex + 1); say('Going forward.'); }
        else say('No next view.');
        break;
      case 'goToSection': {
        const sectionMap = { fractal: 'section-fractal', palette: 'section-palette', transport: 'section-transport' };
        const sectionId = sectionMap[action.section] || 'section-fractal';
        const sectionEl = document.getElementById(sectionId);
        const firstFocusable = sectionEl?.querySelector('button, input, select, [tabindex="0"]');
        if (firstFocusable) {
          suppressFocusRef.current = true;
          firstFocusable.focus();
        }
        // Keep suppress active until speech ends, then clear and restart listening
        voiceSpeak(action.spokenResponse || `Navigated to ${action.section}.`, userLanguageRef.current, () => {
          suppressFocusRef.current = false;
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        return;
      }
      case 'setMode': {
        const newMode = action.mode === 'orbit' ? 'orbit' : 'scan';
        setMode(newMode);
        say(newMode === 'orbit' ? 'Orbit mode.' : 'Scan mode.');
        break;
      }
      case 'focusModeSelector':
        say('Mode selector focused. Use arrow keys to switch between scan and orbit.');
        setTimeout(() => document.querySelector('select[value="' + mode + '"], #mode-select')?.focus(), 100);
        break;
      case 'setFractalType': {
        const ft = action.fractalType === 'julia' ? 'julia' : 'mandelbrot';
        setInputFractalType(ft);
        setFractalType(ft);
        say(action.spokenResponse || `Fractal type set to ${ft}.`);
        break;
      }
      case 'setMaxIterations': {
        const mi = Math.max(10, Math.min(10000, Math.round(action.value || 500)));
        setInputMaxIter(String(mi));
        setMaxIter(mi);
        say(action.spokenResponse || `Max iterations set to ${mi}.`);
        break;
      }
      case 'setJuliaC': {
        const re = action.re ?? -0.8;
        const im = action.im ?? 0.156;
        setInputJuliaRe(String(re));
        setInputJuliaIm(String(im));
        setJuliaC({ re, im });
        say(action.spokenResponse || `Julia c set to ${re} plus ${im}i.`);
        break;
      }
      case 'setMaxIter': {
        const mi = parseInt(action.value);
        if (mi > 0) { setInputMaxIter(String(mi)); setMaxIter(mi); say(action.spokenResponse || `Max iterations set to ${mi}.`); }
        else { say('Please say a positive number.'); }
        break;
      }
      case 'renderFractal':
        applyFractalSpec();
        say(action.spokenResponse || 'Rendering fractal.');
        break;
      case 'setColumnSpeed': {
        const ms = Math.round(clamp((action.seconds || 1) * 1000, 250, 4000) / 50) * 50;
        setLineDrawMs(ms);
        say(action.spokenResponse || `Column speed set to ${(ms/1000).toFixed(2)} seconds.`);
        break;
      }
      case 'setStereoWidth': {
        const w = clamp(Math.round((action.value ?? 1) / 0.05) * 0.05, 0, 1);
        setStereoWidth(w);
        say(action.spokenResponse || `Stereo width set to ${w.toFixed(2)}.`);
        break;
      }
      case 'setMusicVolume': {
        const v = clamp(Math.round(action.value ?? 100), 0, 100);
        setMusicVolume(v);
        say(action.spokenResponse || `Music volume set to ${v}.`);
        break;
      }
      case 'setVoiceVolume': {
        const v = clamp(Math.round(action.value ?? 20), 0, 100);
        setVoiceVolume(v);
        say(action.spokenResponse || `Voice volume set to ${v}.`);
        break;
      }
      case 'setScanOrientation': {
        const o = action.value === 'horizontal' ? 'horizontal' : 'vertical';
        setScanOrientation(o);
        say(action.spokenResponse || (o === 'horizontal'
          ? 'Horizontal scan. Takes effect on next play.'
          : 'Vertical scan. Takes effect on next play.'));
        break;
      }
      case 'tabForward':   pendingBrowseRef.current = null; focusAndAnnounce(false); return;
      case 'tabBack':      pendingBrowseRef.current = null; focusAndAnnounce(true);  return;
      case 'pressEnter':   { const el=document.activeElement; if(el) el.click(); return; }
      case 'pressSpace':   { const el=document.activeElement; if(el&&(el.type==='checkbox'||el.type==='radio')){el.click();setTimeout(()=>voiceSpeak(el.checked?'On.':'Off.',userLanguageRef.current),50);}else if(el){el.click();} return; }
      case 'arrowUp':      { const el=document.activeElement; if(el){el.dispatchEvent(new KeyboardEvent('keydown',{key:'ArrowUp',bubbles:true}));setTimeout(()=>{if(el.type==='range')voiceSpeak(el.value,userLanguageRef.current);},50);} return; }
      case 'arrowDown':    { const el=document.activeElement; if(el){el.dispatchEvent(new KeyboardEvent('keydown',{key:'ArrowDown',bubbles:true}));setTimeout(()=>{if(el.type==='range')voiceSpeak(el.value,userLanguageRef.current);},50);} return; }
      case 'startScan':
        if (mode !== 'scan') setMode('scan');
        voiceSpeak('Starting scan.', userLanguageRef.current, () => {
          startPlayback(0);
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        return;
      case 'resume':
        if (mode !== 'scan') setMode('scan');
        // Announce first, restart listening when speech ends.  Then start
        // playback.  startPlayback also calls speak() internally; calling it
        // before our voiceSpeak completes would cancel our utterance, leaving
        // voice stuck in 'speaking' state with no restart.
        voiceSpeak('Resuming.', userLanguageRef.current, () => {
          startPlayback(playhead ?? 0);
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        return;
      case 'markBoundary': {
        // Mark a movement boundary at the current scan position — same action
        // as the Enter key. markBoundary handles the guard (must be playing),
        // the split, the spoken "Marked at column N", and the amber canvas
        // marker. Hands-free section-marking for blind users composing by ear.
        try { if (markBoundaryRef.current) markBoundaryRef.current(); } catch (_) {}
        beginListening();
        return;
      }
      case 'previewOrbit': {
        // Speak the structural classification of an orbit candidate point
        // WITHOUT generating audio.  Lets blind users probe candidate points
        // before committing.  Accepts the same parameter shapes as orbitLineAt
        // and playOrbit:
        //   { position: 0..1 }            — fraction up the paused column
        //   { re, im }                    — explicit complex coordinates
        //   {}                            — view center
        let sampleX, sampleY, label;
        if (typeof action.position === 'number') {
          const col = playhead ?? selectedColumn;
          if (col == null || !fractalData) {
            voiceSpeak('No scan line active. Play and pause first.', userLanguageRef.current, () => beginListening());
            return;
          }
          const regions = extractColumnRegions(fractalData, col, WIDTH, HEIGHT, maxIter);
          const exteriorRegions = regions.filter(r => r.type !== 'inside');
          if (exteriorRegions.length === 0) {
            voiceSpeak('The current line is entirely inside the set.', userLanguageRef.current, () => beginListening());
            return;
          }
          const topY = exteriorRegions[0].yStart;
          const bottomY = exteriorRegions[exteriorRegions.length - 1].yEnd;
          sampleY = Math.round(topY + (1 - action.position) * (bottomY - topY));
          sampleX = col;
          label = `${Math.round(action.position * 100)} percent up column ${col}`;
        } else if (typeof action.re === 'number' && typeof action.im === 'number') {
          const { x, y } = complexToScreen(action.re, action.im);
          sampleX = Math.round(x); sampleY = Math.round(y);
          label = `Point ${action.re.toFixed(3)}, ${action.im.toFixed(3)}`;
        } else {
          sampleX = Math.floor(WIDTH / 2);
          sampleY = Math.floor(HEIGHT / 2);
          label = 'View center';
        }
        const msg = classifyPoint(sampleX, sampleY, label);
        voiceSpeak(msg || `${label} is unclassifiable; render may not be ready.`, userLanguageRef.current, () => beginListening());
        return;
      }
      case 'orbitLineAt': {
        const col = playhead ?? selectedColumn;
        if (col == null || !fractalData) { say('No scan line active. Play and pause first.'); break; }
        const pos = action.position ?? 0.5;
        const regions = extractColumnRegions(fractalData, col, WIDTH, HEIGHT, maxIter);
        const exteriorRegions = regions.filter(r => r.type !== 'inside');
        if (exteriorRegions.length === 0) { say('The current line is entirely inside the set.'); break; }
        const topY = exteriorRegions[0].yStart;
        const bottomY = exteriorRegions[exteriorRegions.length - 1].yEnd;
        const targetY = topY + (1 - pos) * (bottomY - topY);
        const { cx: orbitRe, cy: orbitIm } = screenToComplex(col, targetY);
        setMode('orbit'); pendingBrowseRef.current = null;
        say(action.spokenResponse || `Playing orbit at ${Math.round(pos * 100)} percent up the line.`);
        setTimeout(() => playOrbit(orbitRe, orbitIm), 300); break;
      }
      case 'playOrbitHere': {
        const re = (view.xMin + view.xMax) / 2; const im = (view.yMin + view.yMax) / 2;
        setMode('orbit'); pendingBrowseRef.current = null;
        say(action.spokenResponse || 'Playing orbit at view center.');
        setTimeout(() => playOrbit(re, im), 300); break;
      }
      case 'playOrbit': {
        const re = action.re ?? (view.xMin + view.xMax) / 2;
        const im = action.im ?? (view.yMin + view.yMax) / 2;
        setMode('orbit'); pendingBrowseRef.current = null;
        say(action.spokenResponse || `Playing orbit at ${re.toFixed(3)}, ${im.toFixed(3)}.`);
        setTimeout(() => playOrbit(re, im), 300); break;
      }
      case 'listOrbitPoints': {
        pendingBrowseRef.current = null;
        const pts = [
          { label: 'View center', value: null },
          { label: 'The origin', value: { re: 0, im: 0 } },
          { label: 'Seahorse valley', value: { re: -0.75, im: 0.1 } },
          { label: 'Elephant valley', value: { re: 0.35, im: 0.0 } },
          { label: 'Triple spiral', value: { re: -0.088, im: 0.654 } },
          { label: 'Mini Mandelbrot', value: { re: -1.755, im: 0.0 } },
          { label: 'Star burst', value: { re: -0.5, im: 0.5 } },
        ];
        browseList('Orbit points', pts, (pt, label) => {
          const re = pt ? pt.re : (view.xMin + view.xMax) / 2;
          const im = pt ? pt.im : (view.yMin + view.yMax) / 2;
          setMode('orbit');
          voiceSpeak(`Playing orbit at ${label}.`, userLanguageRef.current, () => {
            playOrbit(re, im); beginListening();
          });
        }); return;
      }
      case 'whereAmI': {
        const lang = userLanguageRef.current || 'en-US';
        const cx = ((view.xMin + view.xMax) / 2).toFixed(4);
        const cy = ((view.yMin + view.yMax) / 2).toFixed(4);
        const w = (view.xMax - view.xMin).toFixed(4);
        const sampleX = playhead != null ? playhead : Math.floor(WIDTH / 2);
        const sampleY = Math.floor(HEIGHT / 2);
        const focusLabel = playhead != null ? `The playhead column ${sampleX}` : 'The view center';
        const structureMsg = classifyPoint(sampleX, sampleY, focusLabel);
        const intro = `${fractalType === 'julia' ? 'Julia set' : 'Mandelbrot set'}. Center ${cx}, ${cy}. Width ${w}.`;
        // Append the full app-state orientation: palette, mode, volumes,
        // column speed, stereo width, toggles. Useful in shared
        // environments where a user inheriting the app needs to know not
        // just what's on screen but how every audio/visual control is
        // currently set.
        const state = buildLongOrientation(lang);
        const outro = playhead != null ? 'Say preview halfway, preview at the top, or orbit halfway to play.' : 'Pan or zoom to explore, or play full scan to begin.';
        voiceSpeak(`${intro} ${structureMsg} ${state} ${outro}`, lang, () => {
          beginListening();
        });
        setVoiceStatus(`Center: ${cx}, ${cy}`); return;
      }
      case 'pause':
        stopPlayback(); say('Paused.'); break;
      case 'reset':
        stopPlayback(); setPlayhead(null); setSelectedColumn(null);
        if (canvasRef.current && fractalDataRef.current) drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalDataRef.current, null, []);
        say(action.spokenResponse || 'Reset.'); break;
      case 'savePNG':
        saveImage('png'); break;
      case 'saveIMG':
        saveImg(); break;
      case 'listPalettes': {
        pendingBrowseRef.current = null;
        // If a modal dialog is open, the global palette list isn't what the
        // user wants — they're already viewing a modal-scoped list.  Tell
        // them to tab through the modal instead.
        const dialogOpen = !!document.querySelector('[role="dialog"]');
        if (dialogOpen) {
          say('A dialog is open. Say tab forward to browse it, or escape to close.');
          break;
        }
        const opts = Object.entries(palettes).map(([k, p]) => ({ label: p.label, value: k }));
        browseList('Palette list', opts, (key, label) => {
          setPaletteName(key);
          if (palettes[key]?.bank) { setUsePaletteBank(true); setPaletteLoaded(true); loadEditorPage(palettePage); }
          else { setUsePaletteBank(false); setPaletteLoaded(false); }
          voiceSpeak(`${label} selected.`, userLanguageRef.current, () => beginListening());
          setVoiceStatus(`Palette: ${label}`);
        }); return;
      }
      case 'selectPalette': {
        // Find palette key by matching label or key against action.paletteName
        const target = (action.paletteName || '').toLowerCase();
        const match = Object.entries(palettes).find(([k, p]) =>
          k.toLowerCase() === target || p.label.toLowerCase() === target ||
          p.label.toLowerCase().includes(target) || k.toLowerCase().includes(target)
        );
        if (match) {
          const [key, pal] = match;
          setPaletteName(key);
          if (palettes[key]?.bank) { setUsePaletteBank(true); setPaletteLoaded(true); }
          else { setUsePaletteBank(false); setPaletteLoaded(false); }
          say(`Palette set to ${pal.label}.`);
          setVoiceStatus(`Palette: ${pal.label}`);
        } else {
          say(`Palette not found. ${Object.values(palettes).map(p => p.label).join(', ')}.`);
        }
        break;
      }
      case 'openColorEditor': {
        if (colorEditorWindowRef.current && !colorEditorWindowRef.current.closed) {
          colorEditorWindowRef.current.focus();
        } else {
          const w = window.open('about:blank','','width=520,height=620,resizable=yes,scrollbars=yes');
          if (w) { colorEditorWindowRef.current = w; w.document.open(); w.document.write(buildColorEditorHtml()); w.document.close(); }
        }
        say('Opening color editor.'); break;
      }
      case 'closeHelp': {
        if (showHelp) {
          setShowHelp(false);
          // Suppress the help button's onFocus speech so it doesn't cancel
          // our "Help closed" TTS mid-utterance.  The voiceSpeak below relies
          // on its onEnd callback to restart listening; if cancelled, voice
          // gets stuck in 'speaking' state and the mic never comes back.
          suppressFocusRef.current = true;
          setTimeout(() => document.getElementById('help-btn')?.focus(), 0);
          voiceSpeak('Help closed.', userLanguageRef.current, () => {
            suppressFocusRef.current = false;
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
        } else {
          say('Help is not open.');
        }
        return;
      }
      case 'closeColorEditor': {
        const win = colorEditorWindowRef.current;
        if (!win || win.closed) { say('Color editor is not open.'); break; }

        // Check dirty flag inside the popup
        const isDirty = !!(win.__editorDirty);
        if (isDirty && !pendingEditorCloseRef.current) {
          // Ask for confirmation; user's next answer is intercepted earlier
          // in this handler (before action dispatch) via pendingEditorCloseRef.
          pendingEditorCloseRef.current = true;
          voiceSpeak(
            "You have unsaved edits in the color editor. Say save first, close anyway, or cancel.",
            userLanguageRef.current,
            () => { if (isVoiceActiveRef.current) startListening(userLanguageRef.current); }
          );
          return;
        }

        // Either not dirty, or user already confirmed force-close
        pendingEditorCloseRef.current = false;
        try { win.__forceClose = true; } catch (_) {}
        try { win.close(); } catch (_) {}
        colorEditorWindowRef.current = null;
        suppressFocusRef.current = true;
        document.getElementById('color-editor-btn')?.focus();
        voiceSpeak('Color editor closed.', userLanguageRef.current, () => {
          suppressFocusRef.current = false;
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        return;
      }
      case 'colorEditor': {
        // Voice control proxy for the color editor popup. Drives its inputs by
        // dispatching real input/change/click events on elements inside the
        // popup's document, which fires the popup's own handlers.
        const win = colorEditorWindowRef.current;
        if (!win || win.closed) {
          say('Color editor is not open.');
          break;
        }
        const doc = win.document;
        const fire = (el, type) => {
          if (!el) return;
          el.dispatchEvent(new win.Event(type, { bubbles: true }));
        };
        const setRange = (id, value) => {
          const el = doc.getElementById(id);
          if (!el) return;
          el.value = String(Math.max(0, Math.min(255, Math.round(value))));
          fire(el, 'input');
          fire(el, 'change');
        };
        switch (action.sub) {
          case 'set': {
            // channel: r/g/b; field: value/step
            const idMap = {
              r: { value: 'rss', step: 'rts' },
              g: { value: 'gss', step: 'gts' },
              b: { value: 'bss', step: 'bts' },
            };
            const id = idMap[action.channel]?.[action.field];
            if (!id) { say('Unknown channel or field.'); break; }
            setRange(id, Number(action.value) || 0);
            break;
          }
          case 'dir': {
            // channel: r/g/b; up: true/false → click the up or down button
            const id = `${action.channel}${action.up ? 'up' : 'dn'}`;
            const btn = doc.getElementById(id);
            if (btn) btn.click();
            break;
          }
          case 'page': {
            const inp = doc.getElementById('pageInput');
            if (!inp) break;
            const cur = Number(inp.value) || 0;
            const next = action.absolute != null
              ? Math.max(0, Math.min(255, Math.round(Number(action.absolute))))
              : Math.max(0, Math.min(255, cur + (Number(action.delta) || 0)));
            inp.value = String(next);
            fire(inp, 'change');
            break;
          }
          case 'smoothing': {
            const cb = doc.getElementById('smoothCb');
            if (!cb) break;
            cb.checked = !!action.value;
            fire(cb, 'change');
            break;
          }
          case 'reset':    { doc.getElementById('resetBtn')?.click(); break; }
          case 'applyAll': { doc.getElementById('applyAllBtn')?.click(); break; }
          case 'save':     { doc.getElementById('saveBtn')?.click(); break; }
          default: say(`Unknown color editor command.`); break;
        }
        if (action.spokenResponse) say(action.spokenResponse);
        break;
      }
      case 'pressKey': {
        // Synthesize a real keyboard event on the currently-focused element
        // (or document.body as fallback) so all existing keyboard handlers fire.
        const target = document.activeElement && document.activeElement !== document.body
          ? document.activeElement
          : document.body;
        const init = {
          key: action.key,
          code: action.key === ' ' ? 'Space' : action.key,
          bubbles: true,
          cancelable: true,
          ctrlKey: !!action.ctrl,
          shiftKey: !!action.shift,
          altKey: !!action.alt,
          metaKey: !!action.meta,
        };
        // Special case Tab: dispatching keydown doesn't actually move focus,
        // so we manually advance/regress through focusable elements.  When a
        // modal dialog is open, restrict to elements within the dialog so we
        // don't tab to invisible controls behind the overlay.
        if (action.key === 'Tab') {
          const dialog = document.activeElement?.closest?.('[role="dialog"]');
          const root = dialog || document;
          const focusables = Array.from(root.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )).filter(el => !el.disabled && el.offsetParent !== null);
          const cur = focusables.indexOf(document.activeElement);
          const next = action.shift
            ? (cur <= 0 ? focusables.length - 1 : cur - 1)
            : (cur < 0 || cur >= focusables.length - 1 ? 0 : cur + 1);
          const target = focusables[next];
          target?.focus();
          // Explicitly announce the new control.  The element's own onFocus
          // handler may already speak via speakLabel(), but Bluetooth/timing
          // can cause that to be missed; a deliberate announcement here is
          // reliable.  We compute a label the same way listItems does.
          if (target) {
            const ariaLabel = target.getAttribute('aria-label');
            const labelFor = target.id ? document.querySelector(`label[for="${target.id}"]`) : null;
            const wrapLabel = target.closest('label');
            let label =
              ariaLabel ||
              labelFor?.textContent?.trim() ||
              wrapLabel?.textContent?.trim() ||
              null;
            if (!label) {
              if (target.tagName === 'INPUT' && target.type === 'range') {
                const prev = target.previousElementSibling;
                label = prev?.tagName === 'LABEL' ? prev.textContent?.trim()
                      : target.parentElement?.querySelector('label')?.textContent?.trim();
              } else if (target.tagName === 'SELECT' && target.options[target.selectedIndex]) {
                label = `${target.options[target.selectedIndex].text} dropdown`;
              } else {
                label = (target.textContent || target.value || '').trim();
              }
            }
            if (label) speakLabel(label);
          }
        } else {
          // Repeat-count support: "right arrow 3" → 3 keydowns spaced apart.
          // Defensive clamp at 50 in case the LLM doesn't respect its cap.
          // The 60ms spacing lets the receiving handler (canvas grid nav,
          // slider step, etc.) update React state between events; without
          // it, fast-fire events can coalesce or fire faster than React
          // can re-render, causing missed steps.
          const rawCount = Number.isFinite(action.count) ? Math.floor(action.count) : 1;
          const count = Math.max(1, Math.min(50, rawCount));
          if (count === 1) {
            target.dispatchEvent(new KeyboardEvent('keydown', init));
            target.dispatchEvent(new KeyboardEvent('keyup', init));
          } else {
            // Re-resolve target on each iteration in case the keypress
            // moves focus (it doesn't for arrow keys on the canvas, but
            // future-proofing). Use setTimeout chain rather than a tight
            // loop so React can render between presses.
            let i = 0;
            const fireOne = () => {
              const t2 = document.activeElement && document.activeElement !== document.body
                ? document.activeElement
                : document.body;
              t2.dispatchEvent(new KeyboardEvent('keydown', init));
              t2.dispatchEvent(new KeyboardEvent('keyup', init));
              i += 1;
              if (i < count) setTimeout(fireOne, 60);
            };
            fireOne();
          }
          // Enter on a button should also click it (single-press only —
          // repeat-Enter on a button doesn't make semantic sense).
          if (action.key === 'Enter' && target.tagName === 'BUTTON' && count === 1) {
            try { target.click(); } catch (_) {}
          }
        }
        if (action.spokenResponse) say(action.spokenResponse);
        break;
      }

      case 'listItems': {
        // Find a sensible scope: open dialog > section containing focus > document
        const focused = document.activeElement;
        const dialog = focused?.closest?.('[role="dialog"]');
        const section = focused?.closest?.('[id^="section-"]');
        const root = dialog || section || document;
        // Collect every focusable control (buttons, sliders, dropdowns, checkboxes, etc.)
        const controls = Array.from(root.querySelectorAll(
          'button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter(el => el.offsetParent !== null);
        // Inside dialogs we generally want all controls.  Inside a section,
        // sometimes there's a structured "list of rows" (palette/IMG library)
        // worth highlighting separately — detect by checking for buttons with
        // checkbox siblings.
        const rows = controls.filter(el => {
          if (el.tagName !== 'BUTTON') return false;
          const parent = el.parentElement;
          return parent && parent.querySelector('input[type="checkbox"]');
        });
        const items = rows.length > 0 ? rows : controls;
        if (items.length === 0) {
          voiceSpeak('No items found here.', userLanguageRef.current, () => {
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
          return;
        }
        // Compute a friendly label for each control
        const labelOf = (el) => {
          const aria = el.getAttribute('aria-label');
          if (aria) return aria;
          const lblFor = el.id ? document.querySelector(`label[for="${el.id}"]`) : null;
          if (lblFor) return lblFor.textContent?.trim();
          const wrap = el.closest('label');
          if (wrap) return wrap.textContent?.trim();
          // Slider next to a label sibling
          if (el.tagName === 'INPUT' && el.type === 'range') {
            const prev = el.previousElementSibling;
            if (prev && prev.tagName === 'LABEL') return prev.textContent?.trim();
            const parentLabel = el.parentElement?.querySelector('label');
            if (parentLabel) return parentLabel.textContent?.trim();
          }
          if (el.tagName === 'SELECT' && el.options[el.selectedIndex]) {
            return `${el.options[el.selectedIndex].text} (dropdown)`;
          }
          return (el.textContent || el.value || el.placeholder || el.tagName).toString().trim();
        };
        const names = items.map(labelOf).filter(Boolean);
        const intro = dialog ? `${names.length} items in this dialog:` : `${names.length} controls:`;
        const speak = `${intro} ${names.join(', ')}. Say jump to followed by an item name to focus it.`;
        voiceSpeak(speak, userLanguageRef.current, () => {
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        return;
      }
      case 'describe': {
        const focused = document.activeElement;
        if (!focused || focused === document.body) {
          voiceSpeak('Nothing is focused. Tab to a control first.', userLanguageRef.current, () => {
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
          return;
        }
        const desc = focused.getAttribute('data-description');
        if (desc) {
          voiceSpeak(desc, userLanguageRef.current, () => {
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
          return;
        }
        // Fallback: announce the control's label and note that no description
        // is available.
        const label =
          focused.getAttribute('aria-label') ||
          (focused.id && document.querySelector(`label[for="${focused.id}"]`)?.textContent?.trim()) ||
          focused.closest('label')?.textContent?.trim() ||
          (focused.tagName === 'SELECT' && focused.options[focused.selectedIndex]?.text) ||
          focused.textContent?.trim() ||
          'this control';
        voiceSpeak(`No description available for ${label}.`, userLanguageRef.current, () => {
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        return;
      }
      case 'focusCanvas': {
        const c = canvasRef.current;
        if (!c) { say('Canvas not available.'); break; }
        suppressFocusRef.current = true;
        // When the grid overlay is visible, focus the first grid cell instead
        // of the canvas itself. The user wants to interact with the picture;
        // when the grid is on, the way to do that is by tabbing through cells
        // and pressing Enter, so landing on cell 1 is the useful entry point.
        // If the grid is off, fall through to canvas focus (arrow-key pan etc).
        let gridFirstCell = null;
        if (showGrid && c.parentElement) {
          gridFirstCell = c.parentElement.querySelector('div[role="button"]');
        }
        if (gridFirstCell) {
          gridFirstCell.focus({ preventScroll: false });
          voiceSpeak(
            'Grid cell 1 focused. Tab to move between cells, Enter to zoom.',
            userLanguageRef.current,
            () => {
              suppressFocusRef.current = false;
              if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
            }
          );
        } else {
          c.focus({ preventScroll: false });
          voiceSpeak(action.spokenResponse || 'Canvas focused. Use arrow keys to pan.', userLanguageRef.current, () => {
            suppressFocusRef.current = false;
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
        }
        return;
      }
      case 'setShowGrid': {
        // value: true (show), false (hide), or 'toggle'
        const want = action.value === 'toggle' ? !showGrid : !!action.value;
        setShowGrid(want);
        // Also move keyboard focus to the Show grid checkbox so the user can
        // immediately say "describe" or otherwise interact with it. Without
        // this, focus stays wherever it was (often a different button), and
        // describe reads the previously-focused element's description.
        suppressFocusRef.current = true;
        // Defer the focus call until after React has re-rendered with the
        // new showGrid value, so the checkbox's checked state is in sync.
        setTimeout(() => {
          const cb = document.querySelector('input[aria-label="Show grid overlay on canvas"]');
          if (cb) cb.focus({ preventScroll: false });
        }, 0);
        const msg = action.spokenResponse
          || (want ? 'Grid shown.' : 'Grid hidden.');
        voiceSpeak(msg, userLanguageRef.current, () => {
          suppressFocusRef.current = false;
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        return;
      }
      case 'pan': {
        const v = viewRef.current;
        const dx = (v.xMax - v.xMin) * 0.15;
        const dy = (v.yMax - v.yMin) * 0.15;
        let nv;
        switch (action.direction) {
          case 'left':  nv = {...v, xMin:v.xMin-dx, xMax:v.xMax-dx}; break;
          case 'right': nv = {...v, xMin:v.xMin+dx, xMax:v.xMax+dx}; break;
          case 'up':    nv = {...v, yMin:v.yMin+dy, yMax:v.yMax+dy}; break;
          case 'down':  nv = {...v, yMin:v.yMin-dy, yMax:v.yMax-dy}; break;
          default: say('Pan which direction?'); return;
        }
        stopPlaybackRef.current();
        setView(nv);
        setInputXMin(String(nv.xMin)); setInputXMax(String(nv.xMax));
        setInputYMin(String(nv.yMin)); setInputYMax(String(nv.yMax));
        say(action.spokenResponse || `Panning ${action.direction}.`);
        break;
      }
      case 'jumpTo': {
        const target = String(action.target || '').toLowerCase().trim();
        if (!target) {
          voiceSpeak("Jump to what?", userLanguageRef.current, () => {
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
          return;
        }
        // Collect every visible, focusable element along with its label text.
        // Label is determined the same way as keyboard navigation announcements:
        // aria-label > <label> association > textContent (for buttons/selects).
        // If a modal is open, restrict to its controls.
        const dialog = document.activeElement?.closest?.('[role="dialog"]');
        const root = dialog || document;
        const elements = Array.from(root.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter(el => el.offsetParent !== null);
        const labelOf = (el) => {
          const aria = el.getAttribute('aria-label');
          if (aria) return aria;
          const lblFor = el.id ? document.querySelector(`label[for="${el.id}"]`) : null;
          if (lblFor) return lblFor.textContent || '';
          const wrap = el.closest('label');
          if (wrap) return wrap.textContent || '';
          if (el.tagName === 'SELECT' && el.options[el.selectedIndex]) {
            return `${el.options[el.selectedIndex].text}`;
          }
          return el.textContent || el.value || el.placeholder || '';
        };
        // Score every element: highest score wins.
        // 3 = exact label match, 2 = label starts with target,
        // 1 = label contains target (word boundary), 0 = no match.
        // Tiebreaker: shorter label = better (more specific).
        const norm = (s) => s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
        const targetN = norm(target);
        const scored = elements.map(el => {
          const lblN = norm(labelOf(el));
          if (!lblN) return { el, score: 0, lbl: '' };
          let score = 0;
          if (lblN === targetN) score = 3;
          else if (lblN.startsWith(targetN + ' ') || lblN.startsWith(targetN)) score = 2;
          else if (new RegExp(`\\b${targetN.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(lblN)) score = 1;
          return { el, score, lbl: labelOf(el).trim() };
        }).filter(x => x.score > 0);

        if (scored.length === 0) {
          voiceSpeak(`No control found for ${target}.`, userLanguageRef.current, () => {
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
          return;
        }
        scored.sort((a, b) => b.score - a.score || a.lbl.length - b.lbl.length);
        const best = scored[0];
        // If multiple top matches with the same score AND similar specificity,
        // ask the user to disambiguate.
        const topScore = best.score;
        const topMatches = scored.filter(x => x.score === topScore && Math.abs(x.lbl.length - best.lbl.length) <= 3);
        if (topMatches.length > 1) {
          const list = topMatches.slice(0, 4).map(x => x.lbl).join(', ');
          voiceSpeak(`Multiple matches found: ${list}. Please be more specific.`, userLanguageRef.current, () => {
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
          return;
        }
        // Focus and announce
        suppressFocusRef.current = true;
        best.el.focus();
        voiceSpeak(best.lbl, userLanguageRef.current, () => {
          suppressFocusRef.current = false;
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        return;
      }

      case 'openList': {
        // Determine the most relevant list trigger:
        //  1. If focus is on a SELECT or aria-haspopup="listbox" element, use it.
        //  2. Otherwise, find the section currently containing focus and use
        //     the first SELECT or aria-haspopup="listbox" inside it.
        //  3. Otherwise, fall back to the first list trigger on the page.
        const isListTrigger = (el) => {
          if (!el) return false;
          if (el.tagName === 'SELECT') return true;
          if (el.getAttribute && el.getAttribute('aria-haspopup') === 'listbox') return true;
          return false;
        };
        let sel = document.activeElement;
        if (!isListTrigger(sel)) {
          const focused = document.activeElement;
          const containingSection = focused?.closest?.('#section-fractal, #section-palette, #section-transport');
          sel = containingSection?.querySelector('select, [aria-haspopup="listbox"]')
              || document.querySelector('select, [aria-haspopup="listbox"]');
        }
        if (!sel) {
          voiceSpeak('No list available here.', userLanguageRef.current, () => {
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
          return;
        }

        // Custom listbox-style buttons (e.g. the palette selector) are toggled
        // by clicking the trigger, not via showPicker().  Detect and handle
        // them separately.
        const isCustomListbox = sel.tagName !== 'SELECT' && sel.getAttribute('aria-haspopup') === 'listbox';
        if (isCustomListbox) {
          suppressFocusRef.current = true;
          sel.focus();
          // Click toggles open/close; the button's own onClick speaks the state.
          sel.click();
          // Restore voice listening shortly after; the button's voiceSpeak
          // call will have already been issued.
          setTimeout(() => {
            suppressFocusRef.current = false;
            if (isVoiceActiveRef.current && voiceStateRef.current !== 'speaking') {
              startListening(userLanguageRef.current);
            }
          }, 300);
          return;
        }

        // Native <select> path
        // Determine a friendly list name from known IDs
        let listName = 'List';
        if (sel.id === 'preset-select') listName = 'Preset list';
        else if (sel.id === 'fractal-type-select') listName = 'Fractal type list';
        else if (sel.id === 'mode-select') listName = 'Mode list';
        const currentOption = sel.options[sel.selectedIndex];
        const currentLabel = currentOption ? currentOption.text : '';
        // Focus the select before announcing — focus event fires synchronously
        // and its onFocus handler is suppressed because we'll set the suppress
        // flag right before.
        suppressFocusRef.current = true;
        sel.focus();
        // Try to programmatically open the dropdown.
        // showPicker() is the modern API (Chromium 99+, Safari 17+). It works
        // for native <select> via user activation chain; if not supported or
        // fails, fall back to a synthetic mousedown.
        try {
          if (typeof sel.showPicker === 'function') {
            sel.showPicker();
          } else {
            const rect = sel.getBoundingClientRect();
            sel.dispatchEvent(new MouseEvent('mousedown', {
              bubbles: true, cancelable: true, view: window, button: 0,
              clientX: rect.left + rect.width / 2,
              clientY: rect.top + rect.height / 2,
            }));
          }
        } catch (_) {
          // showPicker can throw NotAllowedError if not in a user-activation
          // context — that's fine, arrow keys still work to navigate.
        }
        voiceSpeak(
          `${listName} is open. Press the up arrow to go to the previous item, and press the down arrow to go to the next one. The currently selected item is ${currentLabel}.`,
          userLanguageRef.current,
          () => {
            suppressFocusRef.current = false;
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          }
        );
        return;
      }

      case 'startEyeTracking':
        voiceSpeak('Starting eye tracking.', userLanguageRef.current, () => {
          startEyeTracking(userLanguageRef.current || 'en-US', () => {});
        });
        return;

      case 'recalibrateEyeTracking':
        if (!eyeTrackingActive) {
          say('Eye tracking is not active. Say start eye tracking first.');
          break;
        }
        // Stop the dwell loop so the user can click calibration dots without
        // triggering dwell-clicks on the dots themselves (the dots ARE buttons).
        if (gazePollRef.current) { clearInterval(gazePollRef.current); gazePollRef.current = null; }
        dwellStateRef.current = { element: null, startTime: 0 };
        if (dwellRingRef.current) dwellRingRef.current.style.display = 'none';
        voiceSpeak('Recalibrating.', userLanguageRef.current, () => {
          startEyeTracking(userLanguageRef.current || 'en-US', () => {}, { recalibrateOnly: true });
        });
        return;

      case 'stopEyeTracking':
        stopEyeTracking();
        say('Eye tracking stopped.');
        break;

      case 'importPalette':
        setImportModal('palette');
        say(action.spokenResponse || 'Opening palette import.');
        break;

      case 'togglePaletteBank':
        setUsePaletteBank(!!action.value);
        say(action.spokenResponse || (action.value ? 'Palette bank on.' : 'Palette bank off.'));
        break;

      case 'toggleColorSound':
        setColorAffectsSound(!!action.value);
        say(action.spokenResponse || (action.value ? 'Color influences sound on.' : 'Color influences sound off.'));
        break;

      case 'focusControl': {
        const name = (action.controlName || '').toLowerCase();
        const controlMap = {
          'column speed': '#input-column-speed',
          'stereo width': '#input-stereo-width',
          'music volume': '#input-music-volume',
          'voice volume': '#transport-voice-volume',
          'max iterations': '#input-maxiter',
          'x min': '#input-xmin',
          'x max': '#input-xmax',
          'y min': '#input-ymin',
          'y max': '#input-ymax',
          'preset': '#section-fractal select',
          'fractal type': '#fractal-type-select',
          'mode': '#mode-select',
          'palette': '#palette-select',
        };
        // Find best match
        const key = Object.keys(controlMap).find(k => name.includes(k)) || null;
        const selector = key ? controlMap[key] : null;
        const el = selector ? document.querySelector(selector) : null;
        if (el) {
          suppressFocusRef.current = true;
          el.focus();
          setTimeout(() => {
            suppressFocusRef.current = false;
            const lbl = el.getAttribute('aria-label') ||
              document.querySelector(`label[for="${el.id}"]`)?.textContent?.trim() ||
              el.previousElementSibling?.textContent?.trim() ||
              key || el.tagName;
            const val = el.type === 'range' ? `. Currently ${el.value}.` :
              el.tagName === 'SELECT' ? `. Currently ${el.options[el.selectedIndex]?.text}.` :
              el.type === 'text' ? `. Value: ${el.value}.` : '';
            voiceSpeak(`${lbl}${val}`, userLanguageRef.current);
          }, 50);
        } else {
          say(`Couldn't find ${action.controlName}.`);
        }
        return;
      }

      case 'importImg':
        // Open the import modal directly — no button click needed.
        // The OS file picker itself still requires a user gesture, but the
        // modal (library list + local file option) can be opened programmatically.
        say('Import dialog is open. Tab through the files and press Enter to load one.');
        setImportModal('img');
        break;
      case 'openImgFromLibrary': {
        const fname = action.filename;
        if (!fname) { say('No filename specified.'); break; }
        idbGet(`imgLib:${fname}`).then(buf => {
          if (buf) {
            importImgFromBuffer(buf, fname);
            say(`Loading ${fname}.`);
          } else {
            // Try fuzzy match — find closest name in library
            const match = imgLibrary.find(n => n.toLowerCase().includes(fname.replace(/\.img$/i,'').toLowerCase()));
            if (match) {
              idbGet(`imgLib:${match}`).then(b => { if (b) importImgFromBuffer(b, match); });
              say(`Loading ${match}.`);
            } else {
              say(`I could not find ${fname} in your library.`);
            }
          }
        });
        break;
      }
      case 'setLanguage': {
        const newLang = action.lang || 'en-US';
        setUserLanguage(newLang);
        userLanguageRef.current = newLang;
        localStorage.setItem('fractal_language', newLang);
        // Use the spokenResponse from Claude (already in the right language)
        voiceSpeak(action.spokenResponse || 'Language updated.', newLang);
        setVoiceStatus(`Language: ${newLang}`);
        break;
      }
      case 'question':
        say('I could not find an answer.'); setVoiceStatus('Question answered'); break;
      case 'help':
        say('Here are some things you can say.'); setVoiceStatus('Help spoken'); break;
      case 'listPresets': {
        pendingBrowseRef.current = null;
        if (document.querySelector('[role="dialog"]')) {
          say('A dialog is open. Say tab forward to browse it, or escape to close.');
          break;
        }
        const opts = Object.entries(presets).map(([k, p]) => ({ label: p.label, value: k }));
        browseList('Preset list', opts, (key, label) => {
          const p = presets[key]; if (!p) return;
          if (bandSequenceRef.current.length) { setBandSequence([]); setStatus("Regions cleared for the new image."); }
          setPresetKey(key); setFractalType(p.fractalType); setView(p.view); setMaxIter(p.maxIter); setJuliaC(p.juliaC);
          setInputFractalType(p.fractalType);
          setInputXMin(String(p.view.xMin)); setInputXMax(String(p.view.xMax));
          setInputYMin(String(p.view.yMin)); setInputYMax(String(p.view.yMax));
          setInputMaxIter(String(p.maxIter));
          voiceSpeak(`${label} loaded.`, userLanguageRef.current, () => beginListening());
          setVoiceStatus(`Preset: ${label}`);
        }); return;
      }
      case 'listFractalTypes': {
        pendingBrowseRef.current = null;
        if (document.querySelector('[role="dialog"]')) {
          say('A dialog is open. Say tab forward to browse it, or escape to close.');
          break;
        }
        const opts = [{ label: 'Mandelbrot', value: 'mandelbrot' }, { label: 'Julia', value: 'julia' }];
        browseList('Fractal type', opts, (value, label) => {
          setInputFractalType(value); setFractalType(value);
          voiceSpeak(`${label} selected.`, userLanguageRef.current, () => beginListening());
        }); return;
      }
      case 'openDropdown': {
        const focused = document.activeElement;
        const inSection = (id) => !!document.getElementById(id)?.contains(focused);
        if (inSection('section-palette')) {
          handleVoiceCommandRef.current?.('list palettes');
        } else if (inSection('section-fractal')) {
          // Use the focused element to decide which list to open
          // Fractal type select → list fractal types; anything else → list presets
          const fractalTypeSelect = document.getElementById('fractal-type-select');
          if (focused === fractalTypeSelect) {
            handleVoiceCommandRef.current?.('list fractal types');
          } else {
            handleVoiceCommandRef.current?.('list presets');
          }
        } else {
          handleVoiceCommandRef.current?.('list modes');
        }
        return;
      }
      case 'listModes': {
        pendingBrowseRef.current = null;
        if (document.querySelector('[role="dialog"]')) {
          say('A dialog is open. Say tab forward to browse it, or escape to close.');
          break;
        }
        setTimeout(() => document.getElementById('mode-select')?.focus(), 50);
        const opts = [{ label: 'Scan mode', value: 'scan' }, { label: 'Orbit mode', value: 'orbit' }];
        browseList('Playback mode. Scan plays music across the image. Orbit traces a path from a chosen point', opts, (value, label) => {
          setMode(value); pendingBrowseRef.current = null;
          const follow = value === 'orbit' ? `${label} selected. Say orbit the line, or list orbit points.` : `${label} selected.`;
          voiceSpeak(follow, userLanguageRef.current, () => {
            document.getElementById('mode-select')?.focus();
            beginListening();
          });
        }, 'mode-select'); return;
      }
      case 'stopListening':
        dlog('[Voice] stopListening triggered by:', JSON.stringify(action));
        voiceSpeak(action.spokenResponse || 'Goodbye.', userLanguageRef.current);
        stopPlayback(); setPlayhead(null); setSelectedColumn(null);
        pendingBrowseRef.current = null;
        if (canvasRef.current && fractalDataRef.current) drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalDataRef.current, null, []);
        stopListening(); return;
      case 'idle':
        // Genuinely unclear — background noise, non-command — restart silently
        setVoiceStatus('...');
        voiceStateRef.current = 'listening';
        setTimeout(() => { if (isVoiceActiveRef.current) startListening(userLanguageRef.current); }, 400);
        return;
      default:
        // Recognisable words that don't match a command — tell the user
        dlog('[Voice] Unknown action:', action.action, 'Transcript:', transcript, 'Full action object:', JSON.stringify(action));
        voiceSpeak("I didn't understand that. Please try again.", userLanguageRef.current, () => {
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
        setVoiceStatus('Not understood');
        return;
    }
    // Trailing fallback: cases that didn't speak need their listening restarted.
    // Cases that DID call say(...) are already in the 'speaking' state — for
    // those we must NOT call say again, or it will queue a duplicate utterance
    // and double-announce (e.g. Claude returns spokenResponse "Going forward."
    // and the case's own say('Going forward.') already spoke it).
    if (voiceStateRef.current === 'speaking') {
      // A say(...) inside the case is already in flight. Its onEnd will restart
      // listening for us. Do nothing.
      return;
    }
    // The case did not speak. Use the LLM's spokenResponse if any (will also
    // restart listening via voiceSpeak.onEnd); otherwise just restart listening.
    if (action.spokenResponse) {
      say(action.spokenResponse);
    } else if (isVoiceActiveRef.current) {
      startListening(userLanguageRef.current);
    }
  }

  // ── focusAndAnnounce ───────────────────────────────────────────────────────
  function focusAndAnnounce(shiftKey) {
    // If focus is inside an open modal dialog, restrict tab navigation to
    // controls within that dialog so we don't tab into elements visually
    // hidden behind the overlay.
    const current = document.activeElement;
    const dialog = current?.closest?.('[role="dialog"]');
    const root = dialog || document;
    const focusable = Array.from(root.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(el => el.offsetParent !== null);
    const idx = focusable.indexOf(current);
    const next = idx === -1
      ? (shiftKey ? focusable[focusable.length-1] : focusable[0])
      : (shiftKey ? focusable[(idx-1+focusable.length)%focusable.length] : focusable[(idx+1)%focusable.length]);
    if (next) {
      suppressFocusRef.current = true;
      next.focus();
      setTimeout(() => {
        const el = document.activeElement;
        if (!el || el === document.body) { suppressFocusRef.current = false; return; }
        const label =
          el.getAttribute('aria-label') ||
          el.closest('label')?.textContent?.trim() ||
          document.querySelector(`label[for="${el.id}"]`)?.textContent?.trim() ||
          // previousElementSibling only for inputs — never for buttons/selects
          // (would read the previous button's text)
          (el.tagName === 'INPUT' ? el.previousElementSibling?.textContent?.trim() : null) ||
          el.textContent?.trim()?.substring(0, 40) ||
          el.tagName.toLowerCase();
        const value =
          el.type === 'range' ? `. Currently ${el.value}.` :
          el.type === 'checkbox' ? `. ${el.checked ? 'On' : 'Off'}.` :
          el.tagName === 'SELECT' ? `. Currently ${el.options[el.selectedIndex]?.text}.` : '';
        // Keep suppress active until speech ends, then clear and restart listening
        voiceSpeak(`${label}${value}`, userLanguageRef.current, () => {
          suppressFocusRef.current = false;
          if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
        });
      }, 50);
    }
  }

  // ── browseList ─────────────────────────────────────────────────────────────
  // Stores options in pendingBrowseRef, speaks them, then restarts normal listening.
  // The user can say a choice, "tab", "escape" — all go through handleVoiceCommand.
  function browseList(listName, options, onSelect, focusId) {
    const lang = userLanguageRef.current;
    pendingBrowseRef.current = { options, onSelect };

    // Cancel words across a few common languages. Matched as whole words so
    // that an option label happening to contain "stop" wouldn't be confused
    // for the cancel verb.
    const cancelRe = /\b(stop|cancel|escape|never\s*mind|nevermind|annuler|cancelar|alto|pare)\b/i;

    const findMatch = (text) => {
      const t = text.toLowerCase().trim();
      return options.find(o => o.label.toLowerCase() === t)
          || options.find(o => o.label.toLowerCase().includes(t))
          || options.find(o => t.includes(o.label.toLowerCase()));
    };

    // Web Speech API — local, free, runs only between items while the app is
    // silent, so the TTS audio never feeds back into the recognizer. On
    // browsers without it (e.g. Firefox), listenBriefly is a no-op and the
    // physical Escape key remains the only way to interrupt mid-reading.
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;

    // Inter-item listen window with raw-audio level detection driving the
    // silence timer. Web Speech API does the transcription; getUserMedia +
    // an AnalyserNode watches the actual RMS level in parallel. Any sound
    // above the threshold resets the silence countdown, so the gap stays
    // open as long as the user is making noise — Web Speech API's internal
    // onspeechstart is too conservative to catch short fast utterances
    // reliably, so we use raw audio as the trigger instead.
    //
    // Timing:
    //   INITIAL_SILENCE: max wait if no sound at all (user didn't react)
    //   POST_SOUND_GRACE: silence required after the last detected sound
    //                     before we consider the user "done speaking"
    //   HARD_CAP: absolute ceiling, in case audio detection gets stuck on
    //             a continuous noise source (fan, music bleed, etc.)
    const INITIAL_SILENCE = 1200;
    const POST_SOUND_GRACE = 900;
    const HARD_CAP = 5000;
    const RMS_THRESHOLD = 4; // anything above background — quiet rooms float ~1-3

    const listenBriefly = () => new Promise(resolve => {
      let resolved = false;
      let rec = null;
      let stream = null;
      let audioCtx = null;
      let source = null;
      let pollTimer = null;
      let hardCapTimer = null;
      let lastTranscript = '';
      let lastSoundAt = 0;
      let soundEverHeard = false;
      const startedAt = Date.now();

      const cleanup = () => {
        try { rec?.abort(); } catch (_) {}
        try { source?.disconnect(); } catch (_) {}
        try { stream?.getTracks().forEach(t => t.stop()); } catch (_) {}
        try { audioCtx?.close(); } catch (_) {}
        if (pollTimer) clearTimeout(pollTimer);
        if (hardCapTimer) clearTimeout(hardCapTimer);
        if (pendingBrowseRef.current) pendingBrowseRef.current.abortListen = null;
      };
      const done = (text) => {
        if (resolved) return;
        resolved = true;
        cleanup();
        resolve(text);
      };

      // Start Web Speech API recognition (transcription). If it returns a
      // final result we accept it immediately even if the audio poll hasn't
      // declared silence yet.
      if (SR) {
        try {
          rec = new SR();
          rec.lang = lang || 'en-US';
          rec.continuous = false;
          rec.interimResults = true;
          rec.maxAlternatives = 1;
          rec.onresult = (ev) => {
            let t = '';
            for (let i = 0; i < ev.results.length; i++) t += ev.results[i][0].transcript;
            lastTranscript = t;
            // Final result: jump straight to resolution.
            if (ev.results[ev.results.length - 1].isFinal) done(t);
          };
          rec.onerror = () => { /* fall through to audio poll timeout */ };
          rec.onend = () => { /* don't done() — audio poll decides */ };
          rec.start();
        } catch (_) { rec = null; }
      }

      // Open getUserMedia for raw level detection. Runs in parallel with the
      // recognizer and uses a different code path so a slow/unavailable
      // recognizer doesn't block sound-driven gap extension.
      navigator.mediaDevices?.getUserMedia?.({ audio: true }).then(s => {
        if (resolved) { s.getTracks().forEach(t => t.stop()); return; }
        stream = s;
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);

        const poll = () => {
          if (resolved) return;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / buf.length) * 100;

          const now = Date.now();
          if (rms > RMS_THRESHOLD) {
            lastSoundAt = now;
            soundEverHeard = true;
          }

          if (soundEverHeard) {
            // Wait for the user to actually finish speaking before resolving.
            if (now - lastSoundAt > POST_SOUND_GRACE) {
              // lastTranscript already holds the most recent interim hit.
              done(lastTranscript);
              return;
            }
          } else if (now - startedAt > INITIAL_SILENCE) {
            // Nothing heard, gap can close — next item.
            done(null);
            return;
          }
          pollTimer = setTimeout(poll, 40);
        };
        poll();
      }).catch(() => {
        // No mic / permission denied — fall back to pure time-cap behaviour.
        setTimeout(() => done(lastTranscript || null), INITIAL_SILENCE);
      });

      // Absolute ceiling regardless of audio state — protects against being
      // stuck listening forever if audio levels never drop (e.g. continuous
      // background noise).
      hardCapTimer = setTimeout(() => done(lastTranscript || null), HARD_CAP);

      // Escape hook (called by the keydown handler when the user wants to
      // collapse the window immediately).
      if (pendingBrowseRef.current) pendingBrowseRef.current.abortListen = () => done(null);
    });

    const finish = (mode, payload) => {
      if (mode === 'selected') {
        pendingBrowseRef.current = null;
        onSelect(payload.value, payload.label);
        return;
      }
      if (mode === 'cancelled') {
        pendingBrowseRef.current = null;
        voiceSpeak('Cancelled.', lang, () => beginListening());
        return;
      }
      // Completed reading without interruption — keep pendingBrowseRef live
      // so the user can still pick by voice or say "cancel" afterwards.
      voiceSpeak('Say your choice, say cancel, or press Escape to stop.', lang, () => {
        if (focusId) document.getElementById(focusId)?.focus();
        beginListening();
      });
    };

    const speakItem = (i) => {
      // pendingBrowseRef.current nulled by the Escape handler → bail silently.
      if (!pendingBrowseRef.current) return;
      if (i >= options.length) { finish('completed'); return; }
      const label = options[i].label;
      // Prefix the number so the user can choose by position ("item 12")
      // without having to memorise or pronounce a long name. Two sentences
      // produces a natural micro-pause in TTS between the number and label.
      voiceSpeak(`Item ${i + 1}. ${label}.`, lang, async () => {
        if (!pendingBrowseRef.current) return; // Escape during speech
        const heard = await listenBriefly();
        if (!pendingBrowseRef.current) return; // Escape during the gap
        if (heard) {
          const t = heard.toLowerCase().trim();
          if (cancelRe.test(t)) { finish('cancelled'); return; }
          // Try label match first — if the user said an actual name (e.g.
          // a preset literally called "First Light"), we want the label to
          // win over the ordinal interpretation of "first".
          const labelHit = findMatch(t);
          if (labelHit) { finish('selected', labelHit); return; }
          // Then try positional ("item 12", "fifth", "number 3").
          const itemNum = parseItemNumber(t);
          if (itemNum !== null && itemNum >= 1 && itemNum <= options.length) {
            finish('selected', options[itemNum - 1]);
            return;
          }
          // Otherwise: background noise or mishearing — ignore and move on.
        }
        speakItem(i + 1);
      });
    };

    // Intro line, then start the per-item reading. The intro doubles as a
    // one-shot reminder of the available interaction modes — without this,
    // the user discovers them only by accident or by reading docs.
    voiceSpeak(
      `${listName}. Say a name, an item number, or stop to cancel.`,
      lang,
      () => speakItem(0)
    );
  }

  // Auto-focus the canvas after the first fractal render completes.
  // This ensures keyboard shortcuts (arrow keys, space, +/-) work immediately
  // on page load without the user having to click first. We track whether we've
  // done this with a ref so it only fires once.
  const initialFocusDone = useRef(false);
  useEffect(() => {
    setStatus("Ready");
    if (!initialFocusDone.current && canvasRef.current) {
      initialFocusDone.current = true;
      canvasRef.current.focus();
      setPlayhead(null); setSelectedColumn(null);
    }
  }, [fractalData]);

  // Seed the history stack with the initial view on first render — but only if
  // no history was restored from saved state. If history was restored, we leave
  // it intact so Back/Forward work exactly as they did before the reload.
  const historySeeded = useRef(false);
  useEffect(() => {
    if (historySeeded.current) return;
    historySeeded.current = true;
    if (nav.history.length === 0) {
      setNav({ history: [{ view, fractalType, maxIter, juliaC }], historyIndex: 0 });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canvasRef.current || !fractalData) return;
    drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalData, showSoundbar ? playhead : null, orbitPath, scanOrientation, (showBoundariesRef.current ? movementsRef.current.map(m => m.startCol).filter(c => c > 0) : []));
  }, [fractalData, playhead, orbitPath, showSoundbar]);

  useEffect(() => {
    if (mode !== "orbit") {
      setOrbitPath(prev => prev.length === 0 ? prev : []);
    }
  }, [mode]);

  // Apply musicVolume slider to the Web Audio master gain — completely separate from voice
  useEffect(() => {
    if (synthRef.current?.master) {
      synthRef.current.master.gain.setTargetAtTime(
        musicVolume / 100,
        synthRef.current.audioContext.currentTime,
        0.05
      );
    }
  }, [musicVolume]);

  useEffect(() => {
    return () => {
      if (orbitTimerRef.current) clearTimeout(orbitTimerRef.current);
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      if (audioContextRef.current) {
        // Suspend rather than close so the Bluetooth audio session stays alive,
        // preventing the HFP mic profile from dropping on Linux/PipeWire.
        audioContextRef.current.suspend().catch(() => {});
      }
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Keep stable function refs current so once-registered handlers call latest version
  stopPlaybackRef.current = stopPlayback;
  paletteBankRef.current = paletteBank;
  // Always-current resolved palette colours for editor messages. Falls back to
  // building the active gradient's bank on the spot, so a transient null
  // paletteBank (e.g. during mount) can never send the grey fallback while a
  // real palette is selected.
  paletteColorsRef.current =
    editedFullColors
    || paletteBank?.fullColors
    || (palettes[paletteName]?.stops ? gradientToBank(palettes[paletteName].stops).fullColors : null)
    || fallbackPaletteBank.fullColors;
  paletteFilenameRef.current = paletteFilename;
  palettePageRef.current = palettePage;
  voiceSpeakRef.current = voiceSpeak;
  startPlaybackRef.current = startPlayback;

  // ── Wheel zoom (non-passive so we can preventDefault) ──────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function handleWheel(e) {
      // Only zoom when Ctrl is held — bare scrolling should move the page,
      // not trap the user inside the canvas when they want to reach the controls.
      if (!e.ctrlKey) return;
      e.preventDefault();
      const v = viewRef.current;
      const rect = canvas.getBoundingClientRect();
      const mx = ((e.clientX - rect.left) / rect.width) * WIDTH;
      const my = ((e.clientY - rect.top) / rect.height) * HEIGHT;
      const cx = v.xMin + (mx / WIDTH) * (v.xMax - v.xMin);
      const cy = v.yMax - (my / HEIGHT) * (v.yMax - v.yMin);

      const zoomFactor = e.deltaY < 0 ? 0.75 : 1 / 0.75;
      const newW = (v.xMax - v.xMin) * zoomFactor;
      const newH = (v.yMax - v.yMin) * zoomFactor;

      const fxRatio = mx / WIDTH;
      const fyRatio = my / HEIGHT;
      const newXMin = cx - fxRatio * newW;
      const newXMax = newXMin + newW;
      const newYMax = cy + fyRatio * newH;
      const newYMin = newYMax - newH;

      stopPlaybackRef.current();
      setView({ xMin: newXMin, xMax: newXMax, yMin: newYMin, yMax: newYMax });
      setInputXMin(String(newXMin));
      setInputXMax(String(newXMax));
      setInputYMin(String(newYMin));
      setInputYMax(String(newYMax));
      setPlayhead(null);
      setSelectedColumn(null);
    }

    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleWheel);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Suppress browser page zoom everywhere except the canvas ───────────────
  // Ctrl+wheel/Ctrl+plus/Ctrl+minus normally zoom the entire page, which makes
  // controls grow huge.  Inside the canvas, Ctrl+wheel is captured by the
  // canvas-specific handler above (which fractal-zooms instead).  Outside the
  // canvas, we want to do nothing rather than let the browser zoom.
  useEffect(() => {
    function suppressZoomWheel(e) {
      if (!e.ctrlKey) return;
      // Allow the canvas's own handler to do its job
      if (canvasRef.current && canvasRef.current.contains(e.target)) return;
      e.preventDefault();
    }
    function suppressZoomKey(e) {
      if (!(e.ctrlKey || e.metaKey)) return;
      // Block Ctrl+= / Ctrl++ / Ctrl+- / Ctrl+0 (page zoom shortcuts)
      if (e.key === '=' || e.key === '+' || e.key === '-' || e.key === '0') {
        e.preventDefault();
      }
    }
    window.addEventListener('wheel', suppressZoomWheel, { passive: false });
    window.addEventListener('keydown', suppressZoomKey);
    return () => {
      window.removeEventListener('wheel', suppressZoomWheel);
      window.removeEventListener('keydown', suppressZoomKey);
    };
  }, []);

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  useEffect(() => {
    const SECTION_IDS = ['section-fractal', 'section-palette', 'section-transport'];

    function getSectionFocusables(sectionEl) {
      return Array.from(sectionEl.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(el => el.offsetParent !== null);
    }

    function getElLabel(el) {
      if (!el || el === document.body) return '';
      const label =
        el.getAttribute('aria-label') ||
        el.closest('label')?.textContent?.trim() ||
        document.querySelector(`label[for="${el.id}"]`)?.textContent?.trim() ||
        // previousElementSibling only for inputs — never for buttons/selects (would read sibling button text)
        (el.tagName === 'INPUT' ? el.previousElementSibling?.textContent?.trim() : null) ||
        el.textContent?.trim()?.substring(0, 40) ||
        el.tagName.toLowerCase();
      const value =
        el.type === 'range'    ? `. Currently ${el.value}.` :
        el.type === 'checkbox' ? `. ${el.checked ? 'On' : 'Off'}.` :
        el.tagName === 'SELECT'? `. Currently ${el.options[el.selectedIndex]?.text}.` :
        (el.type === 'text' || el.type === 'number') ? `. Value: ${el.value}.` : '';
      return `${label}${value}`;
    }

    function announceEl(el) {
      const text = getElLabel(el);
      if (text) voiceSpeakRef.current?.(text, userLanguageRef.current);
    }

    // selectOpen tracks whether the user has "opened" a select for scrolling
    // by pressing Enter/Space on it. Up/Down only scroll options when open.
    let selectOpen = false;
    let selectOriginalIndex = -1;

    // Speak navigation labels directly via speechSynthesis — avoids React
    // state changes that would cause re-renders and steal focus from checkboxes.
    let navSeq = 0;
    function navSpeak(text, onDone) {
      window.speechSynthesis.cancel();
      const utt = new SpeechSynthesisUtterance(text);
      utt.lang = userLanguageRef.current || 'en-US';
      utt.rate = 0.95;
      utt.onend = () => { if (onDone) onDone(); };
      window.speechSynthesis.speak(utt);
    }

    function moveFocusInSection(section, delta) {
      navSeq++;
      const mySeq = navSeq;
      window.speechSynthesis.cancel();

      const focusables = getSectionFocusables(section);
      const focused = document.activeElement;
      const idx = focusables.indexOf(focused);

      if (idx === -1) {
        if (focusables.length) {
          const target = focusables[0];
          const label = getElLabel(target);
          suppressFocusRef.current = true;
          target.focus();
          setTimeout(() => {
            if (navSeq !== mySeq) return;
            navSpeak(label, () => { suppressFocusRef.current = false; });
          }, 50);
        }
        return;
      }
      const next = idx + delta;
      if (next < 0) {
        navSpeak('Beginning of section.');
      } else if (next >= focusables.length) {
        navSpeak('End of section.');
      } else {
        const target = focusables[next];
        const label = getElLabel(target);
        dlog('[Nav] Moving to:', label, 'idx:', next);
        suppressFocusRef.current = true;
        target.focus();
        setTimeout(() => {
          if (navSeq !== mySeq) return;
          dlog('[Nav] Speaking:', label, '| activeElement:', document.activeElement?.tagName, document.activeElement?.type);
          navSpeak(label, () => {
            suppressFocusRef.current = false;
            if (isVoiceActiveRef.current) startListening(userLanguageRef.current);
          });
        }, 50);
      }
    }

    function handleKeyDown(e) {
      const focused = document.activeElement;
      const tag = focused?.tagName;
      const isSelect = tag === 'SELECT';
      const isInput = (tag === 'TEXTAREA') || (tag === 'INPUT' && focused?.type !== 'range' && focused?.type !== 'checkbox');
      const isRange = tag === 'INPUT' && focused?.type === 'range';
      const isCheckbox = tag === 'INPUT' && focused?.type === 'checkbox';
      const isCanvas = focused === canvasRef.current || !focused || focused === document.body;
      const section = focused?.closest?.('[id^="section-"]');

      // ── Ctrl+Arrow: jump between sections ──────────────────────────────
      if (e.ctrlKey && (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        selectOpen = false;
        // Cancel any in-progress announcement immediately so the user
        // doesn't have to wait through speech from sections they passed through
        if (window.speechSynthesis.speaking) {
          voiceStateRef.current = 'listening'; // prevent onEnd from restarting listening twice
          window.speechSynthesis.cancel();
        }
        const currentIdx = section ? SECTION_IDS.indexOf(section.id) : -1;
        const delta = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
        const nextIdx = currentIdx === -1 ? 0 : (currentIdx + delta + SECTION_IDS.length) % SECTION_IDS.length;
        const nextSection = document.getElementById(SECTION_IDS[nextIdx]);
        if (nextSection) {
          const focusables = getSectionFocusables(nextSection);
          if (focusables.length) {
            suppressFocusRef.current = true;
            focusables[0].focus();
            setTimeout(() => { suppressFocusRef.current = false; }, 100);
            const sName = SECTION_IDS[nextIdx].replace('section-', '');
            const el = focusables[0];
            const lbl = el.getAttribute('aria-label') || el.closest('label')?.textContent?.trim() ||
              document.querySelector(`label[for="${el.id}"]`)?.textContent?.trim() ||
              el.textContent?.trim()?.substring(0, 40) || el.tagName.toLowerCase();
            const val = el.tagName === 'SELECT' ? `. Currently ${el.options[el.selectedIndex]?.text}.` : '';
            navSpeak(`${sName.charAt(0).toUpperCase() + sName.slice(1)} section. ${lbl}${val}`);
          }
        }
        return;
      }

      // ── Enter/Space on select: open it for scrolling ────────────────────
      if (isSelect && (e.key === 'Enter' || e.key === ' ') && !selectOpen) {
        e.preventDefault();
        selectOpen = true;
        selectOriginalIndex = focused.selectedIndex;
        voiceSpeakRef.current?.(
          `${focused.options[focused.selectedIndex]?.text}. Use up and down to scroll, Enter to confirm, Escape to cancel.`,
          userLanguageRef.current
        );
        return;
      }

      // ── Select is open: Up/Down scroll options ───────────────────────────
      if (isSelect && selectOpen) {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          const delta = e.key === 'ArrowDown' ? 1 : -1;
          const newIdx = Math.max(0, Math.min(focused.options.length - 1, focused.selectedIndex + delta));
          focused.selectedIndex = newIdx;
          voiceSpeakRef.current?.(focused.options[newIdx]?.text || '', userLanguageRef.current);
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          focused.dispatchEvent(new Event('change', { bubbles: true }));
          const lbl = focused.getAttribute('aria-label') ||
            document.querySelector(`label[for="${focused.id}"]`)?.textContent?.trim() || 'Selected';
          voiceSpeakRef.current?.(`${lbl}: ${focused.options[focused.selectedIndex]?.text}.`, userLanguageRef.current);
          selectOpen = false; selectOriginalIndex = -1;
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          // Escape's top priority is always to stop audio playback (the
          // scanning "soundbar"), even when a select is open for scrolling.
          const wasPlaying = isPlayingRef.current;
          if (wasPlaying) {
            try { stopPlaybackRef.current(); } catch (_) {}
            setPlayhead(null); setSelectedColumn(null);
            pendingSelectionRef.current = null;
            if (canvasRef.current && fractalDataRef.current)
              drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalDataRef.current, null, []);
          }
          // Then close the open select, reverting it to its prior value.
          if (selectOriginalIndex !== -1) {
            focused.selectedIndex = selectOriginalIndex;
            focused.dispatchEvent(new Event('change', { bubbles: true }));
          }
          voiceSpeakRef.current?.(wasPlaying ? 'Stopped.' : 'Cancelled.', userLanguageRef.current);
          selectOpen = false; selectOriginalIndex = -1;
          return;
        }
      }

      // ── Up/Down: move focus within section (when select is NOT open) ─────
      if (!isInput && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        if (section) {
          e.preventDefault(); // prevent browser adjusting slider value
          selectOpen = false;
          window.speechSynthesis.cancel();
          voiceStateRef.current = 'listening';
          moveFocusInSection(section, e.key === 'ArrowDown' ? 1 : -1);
          return;
        }
      }

      // ── Text/number inputs handle their own keys ──────────────────────────
      if (isInput) return;

      // ── Canvas / global keys ─────────────────────────────────────────────
      // ── Enter/Space on checkbox: toggle it ──────────────────────────────
      if ((e.key === 'Enter' || e.key === ' ') && focused?.type === 'checkbox') {
        e.preventDefault();
        focused.click();
        return;
      }

      switch (e.key) {
        case "Enter":
          // Mark a movement boundary at the current scan position (live compose).
          // Only during active playback, and never when a form control/dialog is
          // focused (there Enter keeps its normal confirm meaning).
          if (isSelect || isCheckbox || isInput || focused?.closest('[role="dialog"], [role="listbox"]')) return;
          if (isPlayingRef.current) { e.preventDefault(); markBoundaryRef.current(); canvasRef.current?.focus(); return; }
          return;
        case " ":
          if (focused?.type === "checkbox" || focused?.id === "palette-select" ||
              focused?.closest('[role="dialog"], [role="listbox"]')) return;
          e.preventDefault();
          if (isPlayingRef.current) { stopPlaybackRef.current(); }
          else { startPlaybackRef.current(0); }
          break;
        case "Backspace":
        case "Delete":
          if (soundModeRef.current === "path" && document.activeElement === canvasRef.current) {
            e.preventDefault();
            const next = pathVerticesRef.current.slice(0, -1);
            setPathVertices(next);
            drawPathOverlay(next);
            speakLabel(next.length ? `Removed. ${next.length} left.` : "Path empty.");
            return;
          }
          break;
        case "Escape":
          // A running play-delay countdown is cancelled first: it is the most
          // recent thing the user armed, so it is what Escape should undo.
          if (delayTimerRef.current != null) {
            e.preventDefault();
            cancelPlayDelay();
            speakLabel("Play delay cancelled.");
            return;
          }
          // If a column-set drag is armed, Escape cancels it (clears the armed
          // contour, wipes the drag, removes the band overlay) and returns.
          if (colDragForRef.current != null) {
            e.preventDefault();
            setColDragFor(null);
            dragStartRef.current = null; dragCurrentRef.current = null;
            isDraggingRef.current = false;
            if (canvasRef.current && fractalData) drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalData, playhead, orbitPath, scanOrientation, (showBoundariesRef.current ? movementsRef.current.map(m => m.startCol).filter(c => c > 0) : []));
            speakLabel("Column selection cancelled.");
            return;
          }
          if (!isPlayingRef.current && soundModeRef.current === "path" && pathVerticesRef.current.length > 0) {
            e.preventDefault();
            setPathVertices([]);
            drawPathOverlay([]);
            speakLabel("Path cleared.");
            return;
          }
          // A stale "select open" flag must never swallow Escape — stopping
          // playback takes priority. Clear it and fall through.
          selectOpen = false;
          e.preventDefault();
          // ALWAYS stop playback first. This is the single most important
          // action of Escape: when audio is happening, the user wants it
          // to stop, and that must work regardless of what else is going
          // on (stuck voice state, pending list, etc.). Previously a
          // stuck voiceStateRef === 'speaking' would short-circuit the
          // whole handler and leave audio playing with no obvious way to
          // stop it short of closing the tab.
          if (isPlayingRef.current) {
            try { stopPlaybackRef.current(); } catch (_) {}
            setPlayhead(null); setSelectedColumn(null);
            pendingSelectionRef.current = null;
            if (canvasRef.current && fractalDataRef.current)
              drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalDataRef.current, null, []);
          }
          // Then cancel any in-flight speech / pending list. Both effects
          // can fire on the same keypress — stopping playback doesn't
          // preclude also silencing the narrator.
          if (voiceStateRef.current === 'speaking' || pendingBrowseRef.current) {
            try { pendingBrowseRef.current?.abortListen?.(); } catch (_) {}
            pendingBrowseRef.current = null;
            if ('speechSynthesis' in window) {
              try { window.speechSynthesis.cancel(); } catch (_) {}
            }
            // Force voice state out of 'speaking' so any subsequent
            // Escape press isn't gated on it. If it really was speaking,
            // the utterance's onerror/onend will run and restart
            // listening; setting it to 'listening' here is just defensive
            // against the stuck-state scenario.
            voiceStateRef.current = 'listening';
            return;
          }
          // If we were already idle (no playback, no speech), still clear
          // any selection state so Escape always feels responsive.
          setPlayhead(null); setSelectedColumn(null);
          pendingSelectionRef.current = null;
          if (canvasRef.current && fractalDataRef.current)
            drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalDataRef.current, null, []);
          break;
        case "ArrowLeft": { if (!isCanvas) return; e.preventDefault();
          const vl = viewRef.current; const dxl = (vl.xMax-vl.xMin)*0.15;
          const nvl = {...vl, xMin:vl.xMin-dxl, xMax:vl.xMax-dxl};
          stopPlaybackRef.current(); setView(nvl); setInputXMin(String(nvl.xMin)); setInputXMax(String(nvl.xMax)); break; }
        case "ArrowRight": { if (!isCanvas) return; e.preventDefault();
          const vr = viewRef.current; const dxr = (vr.xMax-vr.xMin)*0.15;
          const nvr = {...vr, xMin:vr.xMin+dxr, xMax:vr.xMax+dxr};
          stopPlaybackRef.current(); setView(nvr); setInputXMin(String(nvr.xMin)); setInputXMax(String(nvr.xMax)); break; }
        case "ArrowUp": { if (!isCanvas) return; e.preventDefault();
          const vu = viewRef.current; const dyu = (vu.yMax-vu.yMin)*0.15;
          const nvu = {...vu, yMin:vu.yMin+dyu, yMax:vu.yMax+dyu};
          stopPlaybackRef.current(); setView(nvu); setInputYMin(String(nvu.yMin)); setInputYMax(String(nvu.yMax)); break; }
        case "ArrowDown": { if (!isCanvas) return; e.preventDefault();
          const vd = viewRef.current; const dyd = (vd.yMax-vd.yMin)*0.15;
          const nvd = {...vd, yMin:vd.yMin-dyd, yMax:vd.yMax-dyd};
          stopPlaybackRef.current(); setView(nvd); setInputYMin(String(nvd.yMin)); setInputYMax(String(nvd.yMax)); break; }
        case "+": case "=": { if (!isCanvas) return; e.preventDefault();
          const vp = viewRef.current; const cxp=(vp.xMin+vp.xMax)/2, cyp=(vp.yMin+vp.yMax)/2;
          const nvp = {xMin:cxp-(vp.xMax-vp.xMin)*0.375, xMax:cxp+(vp.xMax-vp.xMin)*0.375, yMin:cyp-(vp.yMax-vp.yMin)*0.375, yMax:cyp+(vp.yMax-vp.yMin)*0.375};
          stopPlaybackRef.current(); setView(nvp); setInputXMin(String(nvp.xMin)); setInputXMax(String(nvp.xMax)); setInputYMin(String(nvp.yMin)); setInputYMax(String(nvp.yMax)); break; }
        case "-": { if (!isCanvas) return; e.preventDefault();
          const vm = viewRef.current; const cxm=(vm.xMin+vm.xMax)/2, cym=(vm.yMin+vm.yMax)/2;
          const nwm=(vm.xMax-vm.xMin)/0.75, nhm=(vm.yMax-vm.yMin)/0.75;
          const nvm = {xMin:cxm-nwm/2, xMax:cxm+nwm/2, yMin:cym-nhm/2, yMax:cym+nhm/2};
          stopPlaybackRef.current(); setView(nvm); setInputXMin(String(nvm.xMin)); setInputXMax(String(nvm.xMax)); setInputYMin(String(nvm.yMin)); setInputYMax(String(nvm.yMax)); break; }
        default: break;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

// Place this near your other handler functions (e.g. near where you have
// the playback / preset / palette handlers, around line 6000–7000).
//
// All the parameters come straight from the existing app state — no new
// state is being introduced, and we are not touching the existing rendering
// pipeline at all. We just call renderFormulaFractal again with bigger
// dimensions.

async function handleTIFFExport(sizeOverride) {
  if (tiffExporting) return;

  // The resolution to export at: an explicit override (used by the Save as TIFF
  // menu, which picks a size and saves in one action) or the current state.
  const size = sizeOverride || tiffExportSize;

  // Tier gate. The button should already be disabled for free users, but
  // we re-check here because UI state can be bypassed and this is the
  // single point through which TIFF data leaves the app.
  const capability = capabilityForSize(size);
  logExport(capability);
  if (!can(capability, userTier)) {
    const required = tierRequiredFor(capability);
    const tierName = required === TIERS.STUDIO ? "Studio" : "Artist";
    if (typeof speakLabel === "function") {
      speakLabel(`TIFF save requires the ${tierName} tier.`);
    }
    alert(`Saving as TIFF at ${size.label} requires the ${tierName} tier.`);
    return;
  }

  setTiffExporting(true);
  setTiffExportProgress(0);

  // Snapshot the current fractal spec so it cannot change mid-render.
  const spec = {
    view: { ...view },
    maxIter,
    fractalType,
    juliaC: { ...juliaC },
    paletteName,
    palettes,
    usePaletteBank,
    fullColors,
  };

  const filename = buildTIFFFilename(spec, size);

  try {
    await exportFractalAsTIFF(spec, {
      width: size.width,
      height: size.height,
      filename,
      onProgress: setTiffExportProgress,
      // Adapter: exportFractalAsTIFF expects a render function with this
      // signature so the module stays decoupled from App.js.
      renderFn: (w, h, mi, v, ft, jc, pn, pls, upb, fc) =>
        renderFormulaFractal(w, h, mi, v, ft, jc, pn, pls, upb, fc),
    });

    if (typeof speakLabel === "function") {
      speakLabel(`Save complete. ${size.label}.`);
    }
  } catch (err) {
    console.error("TIFF export failed:", err);
    if (typeof speakLabel === "function") {
      speakLabel("Save failed.");
    }
    alert(`TIFF export failed: ${err.message}`);
  } finally {
    setTiffExporting(false);
    setTiffExportProgress(0);
  }
}

// Build a useful filename from the current fractal spec so the user can
// tell exports apart in their Downloads folder.
function buildTIFFFilename(spec, size) {
  const parts = ["fractal"];
  parts.push(spec.fractalType);
  if (spec.fractalType === "julia") {
    parts.push(`c${spec.juliaC.re.toFixed(3)}_${spec.juliaC.im.toFixed(3)}`);
  }
  parts.push(`x${spec.view.xMin.toFixed(4)}_${spec.view.xMax.toFixed(4)}`);
  parts.push(`y${spec.view.yMin.toFixed(4)}_${spec.view.yMax.toFixed(4)}`);
  parts.push(`iter${spec.maxIter}`);
  parts.push(`${size.width}x${size.height}`);
  return parts.join("_") + ".tiff";
}

// Map a TIFF_EXPORT_PRESETS entry to its capability key in userTier.js.
function capabilityForSize(size) {
  if (size.width <= 2000)  return "saveTiff_2k";
  if (size.width <= 4000)  return "saveTiff_4k";
  if (size.width <= 8000)  return "saveTiff_8k";
  return "saveTiff_12k";
}

// Map a PNG_EXPORT_PRESETS entry to its capability key in userTier.js.
function capabilityForPngSize(size) {
  if (size.isCanvas)       return "savePng_canvas";
  if (size.width <= 2000)  return "savePng_2k";
  if (size.width <= 4000)  return "savePng_4k";
  if (size.width <= 8000)  return "savePng_8k";
  return "savePng_12k";
}
  function speak(text) {
    if (!("speechSynthesis" in window)) return;
    if (voiceVolume <= 0) return;

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = voiceVolume / 100;
    window.speechSynthesis.speak(utterance);
  }

  function stopListening() {
    isVoiceActiveRef.current = false;
    voiceStateRef.current = 'idle';
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    // Stop Whisper recording if active
    stopListeningWhisper();
    // Also stop any legacy Web Speech recognizer
    if (recognitionRef.current) { try { recognitionRef.current.abort(); } catch (_) {} recognitionRef.current = null; }
    window.speechSynthesis.cancel();
    setIsListening(false);
    setVoiceStatus('');
  }

  async function ensureAudio() {
    if (!audioContextRef.current) {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextCtor();
      audioContextRef.current = audioContext;
      synthRef.current = createSynthGraph(audioContext);
      // Apply current music volume immediately — the useEffect won't re-fire for a new synth
      synthRef.current.master.gain.value = musicVolume / 100;
    }

    if (audioContextRef.current.state === "suspended") {
      await audioContextRef.current.resume();
    }

    return synthRef.current;
  }

  function openSoundEditor() {
    if (soundEditorWindowRef.current && !soundEditorWindowRef.current.closed) {
      soundEditorWindowRef.current.focus();
      return;
    }
    const w = window.open('about:blank','','width=520,height=320,resizable=yes,scrollbars=yes');
    if (w) {
      soundEditorWindowRef.current = w;
      w.document.open();
      w.document.write(buildSoundEditorHtml(Math.max(0, Math.min(1, voiceVolume / 100))));
      w.document.close();
    }
  }
  // Clears a running countdown. Safe to call when nothing is counting.
  function cancelPlayDelay() {
    if (delayTimerRef.current) {
      clearInterval(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    setDelayLeft(null);
  }

  // What the Play buttons call. With no delay set this is startPlayback
  // itself, so the existing path is untouched; the countdown only wraps it.
  function startPlaybackDelayed(startColumn = 0) {
    cancelPlayDelay();
    const secs = playDelayRef.current | 0;
    if (secs <= 0) return startPlayback(startColumn);

    setDelayLeft(secs);
    speak(`Playing in ${secs} seconds. Press Escape to cancel.`);
    delayTimerRef.current = setInterval(() => {
      setDelayLeft(prev => {
        const next = (prev ?? 1) - 1;
        if (next > 0) return next;
        clearInterval(delayTimerRef.current);
        delayTimerRef.current = null;
        startPlayback(startColumn);
        return null;
      });
    }, 1000);
  }

  function stopPlayback() {
    cancelPlayDelay();
    if (startPlayback._cancel) { startPlayback._cancel(); startPlayback._cancel = null; }
    if (orbitTimerRef.current) {
      clearTimeout(orbitTimerRef.current);
      orbitTimerRef.current = null;
    }

    if (bandCompleteTimerRef.current) {
      clearTimeout(bandCompleteTimerRef.current);
      bandCompleteTimerRef.current = null;
    }
    // Stop AND disconnect EVERY contour oscillator, not just the first. Later
    // contours are scheduled to start in the future; disconnect silences them
    // even before they start, and this works even if the cancel closure was
    // lost. Fixes multi-contour glide not stopping on Esc/Pause.
    if (contourOscListRef.current && contourOscListRef.current.length) {
      for (const o of contourOscListRef.current) {
        try { o.stop(); } catch (_) {}
        try { o.disconnect(); } catch (_) {}
      }
      contourOscListRef.current = [];
    }
    if (contourOscRef.current) {
      try { contourOscRef.current.stop(); } catch (_) {}
      try { contourOscRef.current.disconnect(); } catch (_) {}
      contourOscRef.current = null;
    }
    if (contourRafRef.current) { cancelAnimationFrame(contourRafRef.current); contourRafRef.current = null; }
    if (pathRafRef.current) { cancelAnimationFrame(pathRafRef.current); pathRafRef.current = null; }
    if (pathMarkerRef.current !== null || pathMarkerBRef.current !== null) {
      pathMarkerRef.current = null;
      pathMarkerBRef.current = null;
      if (soundModeRef.current === "path" && (pathVerticesRef.current || []).length) drawPathOverlay(pathVerticesRef.current);
    }
    if (contourMarkerRef.current) {
      contourMarkerRef.current = null;
      if (canvasRef.current && fractalDataRef.current) drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalDataRef.current, playheadRef.current, [], "vertical", [], null);
    }
    // Stop any audio scheduled up front (band-tap fast path). smplr players have
    // a .stop() that cancels all sounding + scheduled notes. Without this, a
    // prior run's queued taps keep firing when a new Play starts.
    try {
      const _eng = fractalMusicEngineRef.current;
      if (_eng && _eng.players) {
        for (const _k in _eng.players) {
          try { _eng.players[_k].stop(); } catch (_) {}
          // stop() only stops started voices; clear the scheduler's pending
          // queue so future-scheduled notes (long paths) don't keep firing.
          try { if (_eng.players[_k].scheduler) _eng.players[_k].scheduler.stop(); } catch (_) {}
        }
      }
    } catch (_) {}
    // Path mode schedules notes far into the future; smplr's player.stop() does
    // not always cancel not-yet-started notes, so also invoke each note's own
    // stop-fn captured when it was scheduled.
    if (pathStopsRef.current && pathStopsRef.current.length) {
      for (const _s of pathStopsRef.current) { try { _s(); } catch (_) {} }
      pathStopsRef.current = [];
    }
    orbitPathRef.current = [];
    setIsPlaying(false);
    setStatus("Stopped");
    setOrbitPath(prev => prev.length === 0 ? prev : []);

    // Redraw canvas preserving playhead position
    if (canvasRef.current && fractalDataRef.current) {
      drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalDataRef.current, playhead, [], scanOrientation, (showBoundariesRef.current ? movementsRef.current.map(m => m.startCol).filter(c => c > 0) : []));
    }

    // Hard-cancel every scheduled audio event so future-dated
    // linearRampToValueAtTime calls don't keep playing after we ask the
    // synth to stop. Without this, a scan with long column-speed values
    // could have ramps queued seconds into the future on osc1/osc2/drone
    // frequencies and the filter, which would keep producing tone tails
    // until they ran out — Escape would feel unresponsive even though it
    // had fired correctly.
    if (synthRef.current) {
      try {
        const s = synthRef.current;
        const t = s.audioContext.currentTime;
        for (const node of [s.osc1, s.osc2, s.drone]) {
          try { node.frequency.cancelScheduledValues(t); } catch (_) {}
        }
        try { s.filter.frequency.cancelScheduledValues(t); } catch (_) {}
        silenceSynth(s);
      } catch (err) {
        console.error('[Playback] silenceSynth failed:', err);
      }
    }
  }

  // Core palette import logic — accepts an ArrayBuffer so it can be called
  // from both the local file picker and the Firebase library modal.
  async function importPaletteFromBuffer(buffer, filename) {
    try {
      const parsed = parsePaletteBank(buffer);
      setPaletteBank(parsed);
      setPaletteFilename(filename);
      setPalettePage(0);
      setEditedFullColors(null);
      setUsePaletteBank(true);
      setPaletteLoaded(true);
      if (parsed.pages?.[0]?.controls) {
        const c = parsed.pages[0].controls;
        setEditorRedStart(Math.max(0, Math.min(255, c.redSlider & 0xff)));
        setEditorRedStep(Math.max(0, Math.min(255, c.redStep & 0xff)));
        setEditorRedLighter(c.redDirection === 1);
        setEditorGreenStart(Math.max(0, Math.min(255, c.greenSlider & 0xff)));
        setEditorGreenStep(Math.max(0, Math.min(255, c.greenStep & 0xff)));
        setEditorGreenLighter(c.greenDirection === 1);
        setEditorBlueStart(Math.max(0, Math.min(255, c.blueSlider & 0xff)));
        setEditorBlueStep(Math.max(0, Math.min(255, c.blueStep & 0xff)));
        setEditorBlueLighter(c.blueDirection === 1);
        setEditorSmoothing(c.smoothing === 1);
      }
      setStatus(`Imported palette bank ${filename}`);
      speak(`Imported palette ${filename}`);
      setPalettes(prev => ({ ...prev, [filename]: { label: filename.replace(/\.tmp$/i, ''), bank: true } }));
      setPaletteName(filename);
      // Cache the raw bank bytes to IndexedDB so the palette persists across
      // reloads. Stored under key paletteBank:{filename}. We .slice(0) to copy
      // the buffer rather than transfer ownership (caller may still use it).
      // Failure is non-fatal — in-memory state is correct; only persistence is lost.
      try {
        await idbSet(`paletteBank:${filename}`, buffer.slice(0));
      } catch (err) {
        console.warn(`Failed to cache palette ${filename} to IDB:`, err);
      }
    } catch (err) {
      console.error(err);
      setStatus("Palette import failed");
      speak("Palette import failed");
    }
  }

  async function handleImportPalette(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const buffer = await file.arrayBuffer();
    await importPaletteFromBuffer(buffer, file.name);
    event.target.value = "";
  }

  async function importImgFromBuffer(buffer, filename) {
    try {
      const spec = parseNativeImg(buffer);

      stopPlayback();
      setPlayhead(null);
      setSelectedColumn(null);

      setFractalType(spec.fractalType);
      setView(spec.view);
      setMaxIter(spec.maxIter);
      setJuliaC(spec.juliaC);

      setInputFractalType(spec.fractalType);
      setInputXMin(String(spec.view.xMin));
      setInputXMax(String(spec.view.xMax));
      setInputYMin(String(spec.view.yMin));
      setInputYMax(String(spec.view.yMax));
      setInputMaxIter(String(spec.maxIter));
      setInputJuliaRe(String(spec.juliaC.re));
      setInputJuliaIm(String(spec.juliaC.im));

      setImportedRaster(spec.raster);
      setImportedImgMeta({
        fileName: filename,
        sourceWidth: spec.sourceWidth,
        sourceHeight: spec.sourceHeight,
        paletteId: spec.paletteId,
        xfactorText: spec.xfactorText,
        yfactorText: spec.yfactorText,
        drawingMode: spec.drawingMode,
      });

      const msg = spec.paletteId
        ? `Imported ${filename} — was saved with palette.${spec.paletteId}. Load that palette bank (or any other) and enable it to colour the image.`
        : `Imported ${filename}${spec.legacyFormat ? " (legacy format)" : ""}. Import any palette bank and enable it to colour the image.`;
      setStatus(msg);
      speak(`Imported ${filename}`);

      const presetLabel = filename.replace(/\.img$/i, "");
      setPresets({
        ...presets,
        [filename]: {
          label: presetLabel,
          fractalType: spec.fractalType,
          view: spec.view,
          maxIter: spec.maxIter,
          juliaC: spec.juliaC,
        },
      });
      setPresetKey(filename);
      // Persist to local library so voice commands can load it by name
      await idbSet(`imgLib:${filename}`, buffer);
      setImgLibrary(prev => prev.includes(filename) ? prev : [...prev, filename]);
    } catch (err) {
      console.error(err);
      const fmt = detectImageFormat(buffer);
      let reason;
      if (fmt) {
        reason = `that's a ${fmt} image, not a fractal .img file. Convert it to .img first (e.g. with the recolor/tonative tools).`;
      } else {
        reason = (err && err.message)
          ? err.message.replace(/^Not a valid \.img file:\s*/i, "")
          : "unrecognised file format.";
      }
      setStatus(`Can't import ${filename}: ${reason}`);
      speak(fmt ? `That is a ${fmt} image, not a fractal file.` : "Could not import that file.");
    }
  }

  async function handleImportImg(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const buffer = await file.arrayBuffer();
    await importImgFromBuffer(buffer, file.name);
    event.target.value = "";
  }
  // Open a TIFF saved by the Toolkit. Only the TIFF's tags are read (a few KB,
  // even for a 12K file); the pixels are ignored and the image is re-rendered
  // from the parameters that tiffExport.js embeds in ImageDescription.
  async function importTiffFromFile(file) {
    const filename = file.name;
    let spec;
    try {
      const meta = await readTIFFMetadata(file);
      spec = parseToolkitTIFFDescription(meta.description);
    } catch (err) {
      setStatus(`Can't open ${filename}: ${err && err.message ? err.message : "unreadable file"}.`);
      speak("Could not open that file.");
      return;
    }
    if (!spec) {
      setStatus(`${filename} has no Toolkit parameters. Only TIFFs saved by the Toolkit from September 11, 2026 on can be reopened.`);
      speak("That TIFF has no saved fractal parameters.");
      return;
    }
    try {
      stopPlayback();
      setPlayhead(null);
      setSelectedColumn(null);
      clearImportedRaster();
      // Regions and the open-composition name belong to the previous image.
      if (bandSequenceRef.current.length) setBandSequence([]);
      setOpenCompositionName(null);

      // ── fractal (mirrors importComposition) ──
      const jc = spec.juliaC || juliaC;  // Mandelbrot files keep the current c
      const mi = clamp(Math.floor(spec.maxIter), 10, MAXCOLRS - 1);
      setFractalType(spec.fractalType); setInputFractalType(spec.fractalType);
      setView(spec.view);
      setInputXMin(String(spec.view.xMin));
      setInputXMax(String(spec.view.xMax));
      setInputYMin(String(spec.view.yMin));
      setInputYMax(String(spec.view.yMax));
      setMaxIter(mi); setInputMaxIter(String(mi));
      setJuliaC(jc);
      setInputJuliaRe(String(jc.re));
      setInputJuliaIm(String(jc.im));
      pushHistory(spec.view, spec.fractalType, mi, jc);  // Back returns to the previous view

      // ── palette (mirrors the palette list: local cache, then the library) ──
      let paletteNote = "";
      const key = spec.paletteName;
      const pal = key ? palettes[key] : null;
      if (pal) {
        setPaletteName(key);
        if (pal.bank) {
          let loaded = false;
          try {
            const cached = await idbGet(`paletteBank:${key}`);
            if (cached) { await importPaletteFromBuffer(cached, key); loaded = true; }
          } catch (_) {}
          if (!loaded) {
            try {
              const files = await fetchLibraryFiles();
              const lib = files.find(f => f.name === key || f.name === key + ".new");
              if (lib) {
                const buf = await downloadLibraryFile(lib.downloadUrl);
                await importPaletteFromBuffer(buf, lib.name);
                loaded = true;
              }
            } catch (err) {
              console.warn("Open TIFF: palette library lookup failed:", err);
            }
          }
          if (!loaded) {
            paletteNote = ` Palette bank "${pal.label || key}" couldn't be loaded, so the colours may differ.`;
          }
        }
      } else if (key) {
        paletteNote = ` Its palette "${key}" isn't in your palette list, so the current palette is shown.`;
      }

      const kind = spec.fractalType === "julia" ? "Julia set" : "Mandelbrot set";
      setStatus(`Opened ${filename}: ${kind} view restored.${paletteNote}`);
      speak(`Opened ${filename}.${paletteNote}`);
    } catch (err) {
      console.error(err);
      setStatus(`Could not apply ${filename}: ${err && err.message ? err.message : "unknown error"}.`);
      speak("Could not open that file.");
    }
  }

  async function handleImportTiff(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    await importTiffFromFile(file);
    event.target.value = "";
  }

  // Import a .fmc.json composition (schema v2). Validates format/version, then
  // applies fractal, orientation, timing, key, and loudness palette. Mirrors
  // importImgFromBuffer's fractal-apply (incl. the setInput* mirror fields).
  // Load a curated example composition from Firebase Storage by download URL.
  async function loadExampleComposition(downloadUrl, name) {
    try {
      setStatus(`Loading ${name}...`);
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      const text = await res.text();
      importComposition(text, name);
    } catch (err) {
      setStatus(`Could not load ${name}: ${err && err.message ? err.message : 'unknown error'}.`);
    }
  }
  // Fetch the example-composition list once, when the picker is first needed.
  async function ensureExampleComps() {
    if (exampleComps.length > 0) return;
    try {
      const files = await fetchCompositionFiles();
      setExampleComps(files);
    } catch (err) {
      setStatus('Could not reach the example-composition library.');
    }
  }
  function importComposition(text, filename) {
    let c;
    try {
      c = JSON.parse(text);
    } catch (err) {
      setStatus(`Could not read ${filename}: not valid JSON.`);
      speak("That file is not a valid composition.");
      return;
    }
    if (!c || c.format !== "fractal-music-composition") {
      setStatus(`${filename} is not a fractal-music composition file.`);
      speak("That is not a composition file.");
      return;
    }
    if (c.version !== 2) {
      setStatus(`${filename}: unsupported composition version ${c.version}. This app reads version 2.`);
      speak(`Unsupported composition version ${c.version}.`);
      return;
    }
    try {
      stopPlayback();
      setPlayhead(null);
      setSelectedColumn(null);
      // Clear any imported .img raster so the useMemo computes the composition's
      // fractal from the loaded parameters instead of showing a stale raster.
      clearImportedRaster();
      // Hide boundary markers on open, so a listener/buyer sees a clean image.
      // The movements/tempo map is fully restored; only the markers are hidden.
      setShowBoundaries(false);
      // ── fractal (mirror importImgFromBuffer) ──
      const f = c.fractal || {};
      if (f.fractalType) { setFractalType(f.fractalType); setInputFractalType(f.fractalType); }
      if (f.view) {
        setView(f.view);
        setInputXMin(String(f.view.xMin));
        setInputXMax(String(f.view.xMax));
        setInputYMin(String(f.view.yMin));
        setInputYMax(String(f.view.yMax));
      }
      if (typeof f.maxIter === "number") { setMaxIter(f.maxIter); setInputMaxIter(String(f.maxIter)); }
      if (f.juliaC) {
        setJuliaC(f.juliaC);
        setInputJuliaRe(String(f.juliaC.re));
        setInputJuliaIm(String(f.juliaC.im));
      }
      // ── orientation + timing ──
      if (c.orientation) setScanOrientation(c.orientation);
      if (c.timing && typeof c.timing.baseNoteMs === "number") setLineDrawMs(c.timing.baseNoteMs);
      // ── sound: key + loudness palette ──
      if (c.globalKey && c.globalKey.root && c.globalKey.scale) {
        setMusicKey({ root: c.globalKey.root, scale: c.globalKey.scale });
      }
      // Restore sound mode (sine vs instrumental) and the instrument selection.
      // Guarded so older compositions without these fields keep current defaults.
      if (c.soundMode) setSoundMode(c.soundMode);
      setBandSequence(Array.isArray(c.bandSequence) ? c.bandSequence : []);
      setBandSections(Array.isArray(c.bandSections) ? c.bandSections : []);
      if (typeof c.contourSpeed === "number") setContourSpeed(c.contourSpeed);
      if (typeof c.contourGap === "number") setContourGap(c.contourGap);
      if (c.contourPitchMode === "axis" || c.contourPitchMode === "archHeight") setContourPitchMode(c.contourPitchMode);
      if (typeof c.tripletRate === "number") setTripletRate(c.tripletRate);
      if (typeof c.tripletBpm === "number" && c.tripletBpm >= 20) setTripletBpm(c.tripletBpm);
      if (c.pathDrawMode === "vertices" || c.pathDrawMode === "freehand") setPathDrawMode(c.pathDrawMode);
      if (typeof c.pathNotesPerSeg === "number") setPathNotesPerSeg(c.pathNotesPerSeg);
      if (typeof c.pathRate === "number") setPathRate(c.pathRate);
      if (typeof c.pathResolution === "number") setPathResolution(c.pathResolution);
      if (typeof c.pathInstrument === "string") setPathInstrument(c.pathInstrument);
      if (c.pathPitchFit === "raw" || c.pathPitchFit === "shift" || c.pathPitchFit === "linear" || c.pathPitchFit === "rank") setPathPitchFit(c.pathPitchFit);
      if (Array.isArray(c.pathLengths)) {
        const pl = c.pathLengths.map((L) => (Number.isFinite(L) && L > 0 ? Math.min(16, L) : 1));
        setPathLengths(pl);
        pathLengthsRef.current = pl;
      }
      if (typeof c.pathRepeats === "number") setPathRepeats(Math.max(1, Math.min(16, c.pathRepeats)));
      if (typeof c.pathTranspose === "number") setPathTranspose(Math.max(-24, Math.min(24, c.pathTranspose)));
      if (typeof c.pathTransposeStep === "number") setPathTransposeStep(Math.max(-12, Math.min(12, c.pathTransposeStep)));
      if (typeof c.pathPause === "number") setPathPause(Math.max(0, Math.min(8, c.pathPause)));
      if (c.pathActiveVoice === "A" || c.pathActiveVoice === "B") setPathActiveVoice(c.pathActiveVoice);
      if (typeof c.pathRateRatio === "number") setPathRateRatio(Math.max(0.1, Math.min(12, c.pathRateRatio)));
      if (c.pathOther && typeof c.pathOther === "object") {
        const o = {
          verts: Array.isArray(c.pathOther.verts) ? c.pathOther.verts
            .filter((v) => v && typeof v.x === "number" && typeof v.y === "number")
            .map((v) => ({ x: Math.max(0, Math.min(WIDTH - 1, Math.round(v.x))), y: Math.max(0, Math.min(HEIGHT - 1, Math.round(v.y))) })) : [],
          lengths: Array.isArray(c.pathOther.lengths) ? c.pathOther.lengths : [],
          instrument: typeof c.pathOther.instrument === "string" ? c.pathOther.instrument : "flute",
          notesPerSeg: typeof c.pathOther.notesPerSeg === "number" ? c.pathOther.notesPerSeg : 1,
        };
        setPathOther(o); pathOtherRef.current = o;
      }
      if (Array.isArray(c.pathPasses)) {
        const pp = c.pathPasses
          .filter((p) => p && typeof p === "object")
          .map((p, i) => ({
            id: p.id || `p${i}${Math.random().toString(36).slice(2, 6)}`,
            repeats: Math.max(1, Math.min(16, Number(p.repeats) || 1)),
            transpose: Math.max(-48, Math.min(48, Number(p.transpose) || 0)),
            pause: Math.max(0, Math.min(8, Number(p.pause) || 0)),
            rate: Number(p.rate) > 0 ? Math.max(0.5, Math.min(16, Number(p.rate))) : 0,
            velocity: Number(p.velocity) > 0 ? Math.max(1, Math.min(127, Math.round(Number(p.velocity)))) : 0,
            instrument: (typeof p.instrument === "string") ? p.instrument : "",
          }));
        setPathPasses(pp);
        pathPassesRef.current = pp;
      }
      if (Array.isArray(c.pathVertices)) {
        const pv = c.pathVertices
          .filter((v) => v && typeof v.x === "number" && typeof v.y === "number")
          .map((v) => ({ x: Math.max(0, Math.min(WIDTH - 1, Math.round(v.x))), y: Math.max(0, Math.min(HEIGHT - 1, Math.round(v.y))) }));
        setPathVertices(pv);
        pathVerticesRef.current = pv;
        // Show the coordinates in the box too, so a loaded path is editable
        // straight away rather than looking empty.
        const pl = Array.isArray(c.pathLengths) ? c.pathLengths : [];
        setPathVertsText(pv.map((v, i) => {
          const L = pl[i];
          const base = `${Math.round(v.x)},${Math.round(v.y)}`;
          return (Number.isFinite(L) && L > 0 && L !== 1) ? `${base}:${L}` : base;
        }).join(" "));
      }
      contourPathRef.current = (c.contourPath && Array.isArray(c.contourPath.points) && c.contourPath.points.length >= 3) ? c.contourPath : null;
      setContourSequence(Array.isArray(c.contourSequence) ? c.contourSequence.filter((x) => x && Array.isArray(x.points) && x.points.length >= 3).map((x) => ({ ...x, speed: (typeof x.speed === "number" ? x.speed : null), wave: (x.wave || "sine"), colMin: (typeof x.colMin === "number" ? x.colMin : 0), colMax: (typeof x.colMax === "number" ? x.colMax : (WIDTH - 1)) })) : []);
      setHarmonyMode(c.harmonyMode === "chordal" || c.harmonyMode === "grieg" ? "chordal" : "fractal");
      if (Array.isArray(c.soundStops)) setSoundStops(c.soundStops);
      // Loudness palette: prefer the composition-level field; fall back to the
      // legacy location (movements[0].loudnessPalette) for older saves.
      const legacyMv = Array.isArray(c.movements) ? c.movements[0] : null;
      const pal = Array.isArray(c.loudnessPalette) ? c.loudnessPalette
        : (legacyMv && Array.isArray(legacyMv.loudnessPalette) ? legacyMv.loudnessPalette : null);
      if (pal) setLoudnessPalette(pal);
      // Movements tempo map: restore the full array (or a single default movement).
      if (Array.isArray(c.movements) && c.movements.length && (c.movements[0].startCol !== undefined)) {
        setMovements(c.movements.map((m) => ({
          startCol: m.startCol ?? 0,
          endCol: m.endCol ?? (WIDTH - 1),
          tempoMs: m.tempoMs ?? 600,
          endRestBeats: m.endRestBeats ?? 0,
        })));
      } else {
        setMovements([{ startCol: 0, endCol: WIDTH - 1, tempoMs: 600, endRestBeats: 0 }]);
      }
      setPlayhead(null); // don't restore a saved sound-bar position
      // ── color palette: set name/flag, and AUTO-LOAD the bank from IDB if it's
      // cached locally (so exact colours restore). If the bank isn't cached on
      // this machine, keep the fractal/key/sound restore and note the missing bank.
      if (c.colorPalette) {
        if (c.colorPalette.name) setPaletteName(c.colorPalette.name);
        setUsePaletteBank(!!c.colorPalette.usePaletteBank);
      }
      setOpenCompositionName(filename);
      setStatus(`Imported composition "${c.title || filename}".`);
      speak(`Loaded composition ${c.title || filename}.`);
      const bankRef = (c.colorPalette && c.colorPalette.usePaletteBank) ? c.colorPalette.paletteBankRef : null;
      if (bankRef) {
        idbGet(`paletteBank:${bankRef}`).then(async (buf) => {
          if (buf) {
            try { await importPaletteFromBuffer(buf, bankRef); }
            catch (e) { console.warn('composition palette-bank apply failed:', e); }
          } else {
            setStatus(`Imported composition "${c.title || filename}". Palette bank "${bankRef}" isn't cached here — import it to restore the exact colours.`);
          }
        }).catch(() => {});
      }
    } catch (err) {
      console.error(err);
      setStatus(`Could not apply composition: ${err && err.message ? err.message : "unknown error"}.`);
      speak("Could not apply the composition.");
    }
  }
  async function handleImportComposition(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    importComposition(text, file.name);
    event.target.value = "";
  }
  async function handleImportSoundMap(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const obj = JSON.parse(text);
      const pal = Array.isArray(obj) ? obj : (obj && Array.isArray(obj.palette) ? obj.palette : null);
      if (pal) {
        setLoudnessPalette(pal);
        // Echo the loaded palette back to the sound editor so its table refreshes.
        try { soundEditorChannelRef.current?.postMessage({ type: 'state', palette: pal }); } catch (_) {}
        setStatus(`Loaded sound map ${file.name}`);
      } else {
        setStatus(`${file.name} is not a sound map file.`);
      }
    } catch (err) {
      setStatus(`Could not read sound map: ${err && err.message ? err.message : 'unknown error'}.`);
    }
    event.target.value = "";
  }

  function clearImportedRaster() {
    setImportedRaster(null);
    setImportedImgMeta(null);
  }

  // Regenerate one page of the colour palette using the ColorMapper algorithm,
  // then push the result into editedFullColors to trigger an immediate re-render.
  function applyEditorPage(page, rStart, rStep, rLighter, gStart, gStep, gLighter,
                           bStart, bStep, bLighter, smoothing) {
    // Start from the currently active full-color array (use ref for latest value)
    const base = editedFullColorsRef.current
      ? editedFullColorsRef.current
      : paletteBank?.fullColors
        ? paletteBank.fullColors
        : fallbackPaletteBank.fullColors;

    const next = new Uint8Array(base); // copy
    const pageOffset = page * COLORS_PER_PAGE;

    let r = rStart, g = gStart, b = bStart;
    let revR = !rLighter, revG = !gLighter, revB = !bLighter;

    for (let i = 0; i < COLORS_PER_PAGE; i++) {
      const idx = (pageOffset + i) * 3;
      next[idx]     = Math.max(0, Math.min(255, Math.abs(r % 256)));
      next[idx + 1] = Math.max(0, Math.min(255, Math.abs(g % 256)));
      next[idx + 2] = Math.max(0, Math.min(255, Math.abs(b % 256)));

      // Red channel step — mirrors ColorMapper.new_color exactly
      if (!revR) {
        r += rStep;
        if (r >= 256 && smoothing) { r -= rStep; revR = true; }
      } else {
        r -= rStep;
        if (r < 0) {
          if (smoothing) { r += rStep; revR = false; }
          else { r += 256; }
        }
      }
      // Green channel step
      if (!revG) {
        g += gStep;
        if (g >= 256 && smoothing) { g -= gStep; revG = true; }
      } else {
        g -= gStep;
        if (g < 0) {
          if (smoothing) { g += gStep; revG = false; }
          else { g += 256; }
        }
      }
      // Blue channel step
      if (!revB) {
        b += bStep;
        if (b >= 256 && smoothing) { b -= bStep; revB = true; }
      } else {
        b -= bStep;
        if (b < 0) {
          if (smoothing) { b += bStep; revB = false; }
          else { b += 256; }
        }
      }
    }

    setEditedFullColors(next);
  }

  // Load a page's stored controls into the editor sliders. If the page has
  // no stored controls (e.g. fallback palette bank with controls: null),
  // fall through to safe defaults instead of returning early — otherwise the
  // editor's slider/smoothing state would "stick" from the previous page.
  function loadEditorPage(page) {
    const src = paletteBank || fallbackPaletteBank;
    const c = src?.pages?.[page]?.controls;
    if (!c) {
      setEditorRedStart(0);   setEditorRedStep(0);   setEditorRedLighter(true);
      setEditorGreenStart(0); setEditorGreenStep(0); setEditorGreenLighter(true);
      setEditorBlueStart(0);  setEditorBlueStep(0);  setEditorBlueLighter(true);
      setEditorSmoothing(false);
      return;
    }
    setEditorRedStart(Math.max(0, Math.min(255, c.redSlider & 0xff)));
    setEditorRedStep(Math.max(0, Math.min(255, c.redStep & 0xff)));
    setEditorRedLighter(c.redDirection === 1);
    setEditorGreenStart(Math.max(0, Math.min(255, c.greenSlider & 0xff)));
    setEditorGreenStep(Math.max(0, Math.min(255, c.greenStep & 0xff)));
    setEditorGreenLighter(c.greenDirection === 1);
    setEditorBlueStart(Math.max(0, Math.min(255, c.blueSlider & 0xff)));
    setEditorBlueStep(Math.max(0, Math.min(255, c.blueStep & 0xff)));
    setEditorBlueLighter(c.blueDirection === 1);
    setEditorSmoothing(c.smoothing === 1);
  }

  function playSingleRegionTone(synth, region, startTime, durationMs, maxIterLocal) {
    const now = startTime;
    const dur = durationMs / 1000;

    const attackEnd = now + 0.02;
    const releaseTime = now + Math.max(0.03, dur - 0.02);
    const endTime = now + dur;

    // The set interior is rendered solid black on-screen; treat it as
    // inaudible too, so a sweep across the image gives the listener a
    // matching silence wherever the cursor is "inside" the set. Without
    // this, the interior used to produce a low pad (180 Hz drone) that
    // obscured the boundary transitions on either side. We still schedule a
    // flat-zero envelope so any previously-ramping gain on a shared synth
    // is brought down cleanly within this column's window; the oscillator
    // frequencies are left at whatever the prior column set them to since
    // there's nothing audible to be heard either way.
    if (region.type === "inside") {
      synth.gain1.gain.setValueAtTime(0.0001, now);
      synth.gain2.gain.setValueAtTime(0.0001, now);
      synth.droneGain.gain.setValueAtTime(0.0001, now);
      synth.gain1.gain.linearRampToValueAtTime(0.0001, endTime);
      synth.gain2.gain.linearRampToValueAtTime(0.0001, endTime);
      synth.droneGain.gain.linearRampToValueAtTime(0.0001, endTime);
      return;
    }

    // Build a sequence of "frames" — pitch/brightness/roughness snapshots
    // spread across the region's duration. In colorAffectsSound mode each
    // frame is one of the region's sampled iter values mapped through
    // SOUND_MAP, producing a glissando that traces the region's internal
    // gradient. In fixed-pitch modes there's just one frame so the synth
    // behaves like a single sustained tone (preserving the prior contract
    // for those modes).
    //
    // This is what makes asymmetric scans actually sound asymmetric: a
    // region whose iter values rise top-to-bottom produces an ascending
    // glide; a mirror-image region with values falling top-to-bottom
    // produces a descending glide. With the previous single-mean approach
    // both rendered as the same flat pitch.
    let frames;
    if (colorAffectsSound && region.samples && region.samples.length > 0) {
      const N = region.samples.length;
      frames = region.samples.map((v, i) => {
        const idx = soundMapIndexFromEscape(v, maxIterLocal);
        const entry = SOUND_MAP[idx];
        return {
          // Evenly distribute frames across the full region duration. With
          // N samples we get N control points: frame 0 at the region start,
          // last frame at the region end, the rest spaced in between.
          tOffset: N === 1 ? 0 : (i / (N - 1)) * dur,
          pitchHz: entry.pitchHz,
          filterHz: entry.filterHz,
          roughness: entry.roughness,
        };
      });
    } else if (region.type === "boundary") {
      frames = [{ tOffset: 0, pitchHz: 400, filterHz: 1200, roughness: 0.01 }];
    } else {
      frames = [{ tOffset: 0, pitchHz: 320, filterHz: 1400, roughness: 0.08 }];
    }

    // Roughness drives osc2's detune (chord-like fattening) and the gain
    // envelope's peaks. Within a region the roughness varies along with
    // pitch, but the detune ratio has to be picked once because osc2's
    // frequency is parameterised relative to osc1's. Using the mean gives
    // a consistent timbre across the region's glissando.
    const meanRoughness = frames.reduce((s, f) => s + f.roughness, 0) / frames.length;
    const detuneRatio = 1 + 0.002 + 0.08 * meanRoughness;

    // Initial setValueAtTime anchors the curve at the region start, then
    // linearRampToValueAtTime draws straight-line segments to each
    // successive frame. The Web Audio scheduler interpolates between
    // ramps automatically, so the audible result is a smooth glide.
    synth.osc1.frequency.setValueAtTime(frames[0].pitchHz, now);
    synth.osc2.frequency.setValueAtTime(frames[0].pitchHz * detuneRatio, now);
    synth.drone.frequency.setValueAtTime(frames[0].pitchHz * 0.5, now);
    synth.filter.frequency.setValueAtTime(frames[0].filterHz, now);
    for (let i = 1; i < frames.length; i += 1) {
      const t = now + frames[i].tOffset;
      synth.osc1.frequency.linearRampToValueAtTime(frames[i].pitchHz, t);
      synth.osc2.frequency.linearRampToValueAtTime(frames[i].pitchHz * detuneRatio, t);
      synth.drone.frequency.linearRampToValueAtTime(frames[i].pitchHz * 0.5, t);
      synth.filter.frequency.linearRampToValueAtTime(frames[i].filterHz, t);
    }

    // Gain envelope: ramp up, hold, ramp down — all via future-safe setValueAtTime
    synth.gain1.gain.setValueAtTime(0.0001, now);
    synth.gain2.gain.setValueAtTime(0.0001, now);
    synth.droneGain.gain.setValueAtTime(0.0001, now);

    let peak1, peak2, peakDrone;
    if (region.type === "boundary" || colorAffectsSound) {
      peak1 = 0.09; peak2 = 0.015 + 0.09 * meanRoughness; peakDrone = 0.008;
    } else {
      peak1 = 0.06; peak2 = 0.02; peakDrone = 0.01;
    }

    synth.gain1.gain.linearRampToValueAtTime(peak1, attackEnd);
    synth.gain2.gain.linearRampToValueAtTime(peak2, attackEnd);
    synth.droneGain.gain.linearRampToValueAtTime(peakDrone, attackEnd);

    synth.gain1.gain.linearRampToValueAtTime(peak1, releaseTime);
    synth.gain2.gain.linearRampToValueAtTime(peak2, releaseTime);
    synth.droneGain.gain.linearRampToValueAtTime(peakDrone, releaseTime);

    synth.gain1.gain.linearRampToValueAtTime(0.0001, endTime);
    synth.gain2.gain.linearRampToValueAtTime(0.0001, endTime);
    synth.droneGain.gain.linearRampToValueAtTime(0.0001, endTime);
  }

  // Build integer-exact regions for one scan line, for instrumental mode.
  function buildInstrumentalRegions(linePos, orientation) {
    const data = fractalData;
    if (!data?.rawIndex || !data?.interior) return [];
    const isVertical = orientation !== "horizontal";
    const scanLen = isVertical ? HEIGHT : WIDTH;
    const idxAt = isVertical ? (i) => i * WIDTH + linePos : (i) => linePos * WIDTH + i;
    const regions = [];
    let start = 0, cur = data.rawIndex[idxAt(0)], curIn = data.interior[idxAt(0)] === 1;
    for (let i = 1; i < scanLen; i++) {
      const e = data.rawIndex[idxAt(i)], isIn = data.interior[idxAt(i)] === 1;
      if (e !== cur || isIn !== curIn) {
        regions.push({ height: i - start, escape: cur, inSet: curIn });
        start = i; cur = e; curIn = isIn;
      }
    }
    regions.push({ height: scanLen - start, escape: cur, inSet: curIn });
    // merge runs shorter than 3
    const merged = [];
    for (const r of regions) {
      if (merged.length && r.height < 3) merged[merged.length - 1].height += r.height;
      else merged.push({ ...r });
    }
    return merged;
  }

  // Lazily build the percentile + table lookup for the current image (cached per fractalData).
  function getInstrumentalTableLookup() {
    const data = fractalData;
    const escaped = [];
    for (let i = 0; i < data.rawIndex.length; i++) {
      if (data.interior[i] !== 1) escaped.push(data.rawIndex[i]);
    }
    escaped.sort((a, b) => a - b);
    const percentile = (e) => {
      if (escaped.length === 0) return 0;
      let lo = 0, hi = escaped.length - 1, ans = 0;
      while (lo <= hi) { const m = (lo+hi)>>1; if (escaped[m] <= e) { ans=m; lo=m+1; } else hi=m-1; }
      return ans / (escaped.length - 1);
    };
    return makeSoundPaletteLookup(percentile, soundStops);
  }
  // Build the LOUDNESS-MODEL lookup for the current image: escape → array of
  // notes (one per active instrument, at its computed loudness). Mirrors
  // getInstrumentalTableLookup's percentile construction.
  function getLoudnessLookup() {
    const data = fractalData;
    const escaped = [];
    for (let i = 0; i < data.rawIndex.length; i++) {
      if (data.interior[i] !== 1) escaped.push(data.rawIndex[i]);
    }
    escaped.sort((a, b) => a - b);
    const percentile = (e) => {
      if (escaped.length === 0) return 0;
      let lo = 0, hi = escaped.length - 1, ans = 0;
      while (lo <= hi) { const m = (lo+hi)>>1; if (escaped[m] <= e) { ans=m; lo=m+1; } else hi=m-1; }
      return ans / (escaped.length - 1);
    };
    return makeLoudnessLookup(percentile, loudnessPalette, { ...musicKey, baseOctave: musicKey.baseOctave ?? 4 }, 100, maxIter, harmonyMode === "chordal");
  }
  
  // ── BAND MODE playback (Stage 4 Slice 3) ────────────────────────────────────
  // Plays the bandSequence regions SEQUENTIALLY: each region sweeps the image in
  // turn, at its own column speed, sounding only its band on its instrument at
  // its pitch (scale degree + octave in the current key), thickness -> velocity,
  // sustained across contiguous present-runs. No auto-play of unselected bands.
  // ── CONTOUR GLIDE (Stage 5, Slice 1 — synthesis proof on a test path) ──────
  // Walk an ordered boundary path and glide a single sine oscillator so its
  // frequency traces the path's HEIGHT (row): top of image = high pitch, bottom
  // = low. Slice 1 uses a hand-made TEST path (a circle) to validate that the
  // scheduled-ramp glide sounds smooth and musical, BEFORE building real contour
  // extraction (slice 2). Closed vs open paths only differ in whether the last
  // point returns to the first — the synthesis is identical.
  // Trace the equipotential contour at a clicked image point and store it as the
  // selected contour for Contour glide mode. Speaks a summary; a later slice will
  // highlight the traced path on the canvas.
  // Right-click in contour mode: trace the contour at the cursor and APPEND it to
  // the ordered sequence (a piece is a list of contours played in turn).
  function addContourAt(px, py) {
    const d = fractalDataRef.current;
    if (!d || !d.rawIndex) { setStatus("Compute a fractal first."); return; }
    const x = clamp(Math.floor(px), 0, WIDTH - 1);
    const y = clamp(Math.floor(py), 0, HEIGHT - 1);
    const res = traceContour(d.rawIndex, WIDTH, HEIGHT, x, y);
    if (!res || !res.points || res.points.length < 3) {
      speakLabel("No contour there — right-click on a coloured band.");
      return;
    }
    const entry = { id: Date.now() + Math.random(), points: res.points, closed: res.closed, level: res.level, speed: null, wave: "sine", colMin: 0, colMax: WIDTH - 1 };
    const n = contourSequenceRef.current.length + 1;
    speakLabel(`Added contour ${n}, escape value ${res.level}, ${res.closed ? "closed loop" : "open arc"}.`);
    setContourSequence((seq) => [...seq, entry]);
  }

  function selectContourAt(px, py) {
    const d = fractalDataRef.current;
    if (!d || !d.rawIndex) { setStatus("Compute a fractal first."); return; }
    const x = clamp(Math.floor(px), 0, WIDTH - 1);
    const y = clamp(Math.floor(py), 0, HEIGHT - 1);
    const res = traceContour(d.rawIndex, WIDTH, HEIGHT, x, y);
    if (!res || !res.points || res.points.length < 3) {
      contourPathRef.current = null;
      setStatus("No contour there — click on a coloured band, not inside the set.");
      speak("No contour there.");
      return;
    }
    contourPathRef.current = res;
    const kind = res.closed ? "closed loop" : "open arc";
    setStatus(`Contour at escape value ${res.level}: ${res.points.length} points, ${kind}. Press Play.`);
    speak(`Contour, escape value ${res.level}, ${kind}. Press play.`);
  }

  // Layered mode (Bach "Jesu" project): plays the contour envelope AND a
  // flute triplet layer derived from the same region's escape bands, overlaid
  // on one audio clock. STAGE 1: just runs the envelope (playContour); the
  // triplet layer is added in stage 2.
  async function playLayered() {
    return playContour();
  }

  async function playPath() {
    // Read the escape soundmap ALONG the drawn path: each straight segment is
    // sampled at N evenly-spaced points; at each, the escape value maps through
    // the canonical soundmap (realize) to a note. Crossing the bands gives a
    // melody; a point inside the set is a rest (it keeps its time slot).
    // Voice A is whichever is drawn first in the composition, not whichever is
    // being edited — so playback does not change when you switch voices.
    const editing = { verts: pathVerticesRef.current || [], lengths: pathLengthsRef.current || [],
                      instrument: pathInstrumentRef.current, notesPerSeg: pathNotesPerSegRef.current };
    const parked = pathOtherRef.current || { verts: [], lengths: [], instrument: "flute", notesPerSeg: 1 };
    const voiceA = pathActiveVoiceRef.current === "A" ? editing : parked;
    const voiceB = pathActiveVoiceRef.current === "A" ? parked : editing;
    const verts = voiceA.verts;
    if (!verts || verts.length < 2) { speakLabel("Draw at least two vertices, then Play."); return; }
    const ac = audioContextRef.current;
    if (!ac) { speakLabel("Audio not ready."); return; }
    const fd = fractalDataRef.current;
    const raw = fd && fd.rawIndex ? fd.rawIndex : null;
    const interior = fd && fd.interior ? fd.interior : null;
    if (!raw) { speakLabel("Compute a fractal first."); return; }
    let eng;
    try {
      eng = fractalMusicEngineRef.current || (fractalMusicEngineRef.current = new FractalMusicEngine(ac));
      await eng.load();
      eng.setKey({ root: (musicKeyRef.current || {}).root || "Eb", scale: (musicKeyRef.current || {}).scale || "major" });
    } catch (_) { speakLabel("Could not load instruments."); return; }
    try { if (eng.ensureInstrument) await eng.ensureInstrument(voiceA.instrument); } catch (_) {}
    if (voiceB.verts && voiceB.verts.length >= 2 && voiceB.instrument !== voiceA.instrument) {
      try { if (eng.ensureInstrument) await eng.ensureInstrument(voiceB.instrument); } catch (_) {}
    }
    const player = eng.players[voiceA.instrument] || eng.players["flute"];
    if (!player) { speakLabel("Instrument not available."); return; }
    const key = { ...(musicKeyRef.current || { root: "Eb", scale: "major" }), baseOctave: (musicKeyRef.current && musicKeyRef.current.baseOctave) || 4 };
    const N = Math.max(1, Math.round(voiceA.notesPerSeg || 4));
    const rate = Math.max(0.5, pathRateRef.current || 4);
    const interval = 1 / rate;
    const noteDur = Math.min(0.5, interval * 0.9);
    // PASS 1 — read the escape value at every sample point up front, so the
    // pitch mapping can be fitted to the range this path actually covers.
    // -1 marks a rest (inside the set); rests are excluded from the fit but
    // still occupy their time slot during scheduling.
    const sampleAlong = (vs, n) => {
      const out = [];
      for (let i = 0; i < vs.length - 1; i++) {
        const a = vs[i], b = vs[i + 1];
        for (let j = 0; j < n; j++) {
          const f = j / n;
          const sx = Math.max(0, Math.min(WIDTH - 1, Math.round(a.x + (b.x - a.x) * f)));
          const sy = Math.max(0, Math.min(HEIGHT - 1, Math.round(a.y + (b.y - a.y) * f)));
          const si = sy * WIDTH + sx;
          const rest = (interior && interior[si] === 1) || !raw[si];
          out.push(rest ? -1 : raw[si]);
        }
      }
      return out;
    };
    const samples = sampleAlong(verts, N);
    const hasB = !!(voiceB.verts && voiceB.verts.length >= 2);
    const NB = Math.max(1, Math.round(voiceB.notesPerSeg || 1));
    const samplesB = hasB ? sampleAlong(voiceB.verts, NB) : [];
    // Fit those escape values into a musical span. Raw uses the escape value
    // directly as a scale degree, which is what the app has always done — fine
    // while a path stays inside a few bands, but a jump from 33 to 206 is about
    // twenty-five octaves. Linear rescales min..max onto the span, preserving
    // relative interval sizes. Rank spreads the DISTINCT values evenly across
    // the span: intervals flatten, but the melodic contour survives however
    // lopsided the escape distribution happens to be.
    const scaleLen = (SCALES[key.scale] || SCALES.major).length;
    const span = scaleLen * 2; // two octaves
    const fitMode = pathPitchFitRef.current || "rank";
    let degreeOf = (v) => v;
    if (fitMode !== "raw") {
      // Fitted across BOTH voices, so a voice drawn on higher escape values
      // really does sound higher. Fitting each separately would rebase them
      // onto the same pitch and lose the registral relationship.
      let lo = Infinity, hi = -Infinity, nHeard = 0;
      for (const arr of [samples, samplesB]) {
        for (let i = 0; i < arr.length; i++) {
          const v = arr[i];
          if (v < 0) continue;
          nHeard++;
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
      }
      if (nHeard > 1 && hi > lo) {
        if (fitMode === "shift") {
          // Exact escape steps, just moved down into range.
          degreeOf = (v) => v - lo;
        } else if (fitMode === "linear") {
          degreeOf = (v) => Math.round(((v - lo) / (hi - lo)) * span);
        } else {
          const distinct = Array.from(new Set(samples.concat(samplesB).filter((v) => v >= 0))).sort((p, q) => p - q);
          const rank = new Map(distinct.map((v, i) => [v, i]));
          const denom = Math.max(1, distinct.length - 1);
          degreeOf = (v) => Math.round((rank.get(v) / denom) * span);
        }
      }
    }
    // Per-sample length multipliers. A length belongs to the segment starting
    // at its vertex, so with N > 1 it applies to all that segment's notes.
    const expandLens = (vs, byVert, n) => {
      const out = [];
      for (let i = 0; i < vs.length - 1; i++) {
        const L = (byVert || [])[i];
        const mult = (Number.isFinite(L) && L > 0) ? L : 1;
        for (let j = 0; j < n; j++) out.push(mult);
      }
      return out;
    };
    const lens = expandLens(verts, voiceA.lengths, N);
    const lensB = hasB ? expandLens(voiceB.verts, voiceB.lengths, NB) : [];
    const repeats = Math.max(1, Math.min(16, pathRepeatsRef.current || 1));
    const baseTr = Math.max(-24, Math.min(24, pathTransposeRef.current || 0));
    const stepTr = Math.max(-12, Math.min(12, pathTransposeStepRef.current || 0));
    const pauseSec = Math.max(0, Math.min(8, pathPauseRef.current || 0));

    // Expand the schedule into a flat list of {transpose, pause} passes. A
    // non-empty Passes list wins; otherwise fall back to the single-pass
    // fields, which behave exactly as before.
    const passList = [];
    const declared = pathPassesRef.current || [];
    if (declared.length) {
      for (const p of declared) {
        const n = Math.max(1, Math.min(16, Number(p.repeats) || 1));
        const tr = Math.max(-48, Math.min(48, Number(p.transpose) || 0));
        const pz = Math.max(0, Math.min(8, Number(p.pause) || 0));
        // 0 (or blank) inherits the global Notes/sec slider.
        const pr = Number(p.rate) > 0 ? Math.max(0.5, Math.min(16, Number(p.rate))) : rate;
        // 0 inherits the default velocity; blank inherits the path instrument.
        const pv = Number(p.velocity) > 0 ? Math.max(1, Math.min(127, Math.round(Number(p.velocity)))) : 96;
        const pi = (typeof p.instrument === "string" && p.instrument) ? p.instrument : pathInstrumentRef.current;
        for (let i = 0; i < n; i++) passList.push({ tr, pause: pz, rate: pr, vel: pv, inst: pi });
      }
    } else {
      for (let rep = 0; rep < repeats; rep++) {
        passList.push({ tr: baseTr + stepTr * rep, pause: pauseSec, rate, vel: 96, inst: pathInstrumentRef.current });
      }
    }
    // Load each distinct instrument once, before scheduling starts — awaiting
    // mid-schedule would shift every note after it.
    for (const inst of Array.from(new Set(passList.map((p) => p.inst)))) {
      if (inst && inst !== pathInstrumentRef.current) {
        try { if (eng.ensureInstrument) await eng.ensureInstrument(inst); } catch (_) {}
      }
    }

    const t0 = ac.currentTime + 0.15;
    let cursor = t0;
    const timeline = [];
    const timelineB = [];
    for (const pass of passList) {
      // Interval and note length follow this pass's rate, not the global one.
      const passInterval = 1 / (pass.rate || rate);
      const passNoteDur = Math.min(8, passInterval * 0.9);
      const passPlayer = eng.players[pass.inst] || player;
      const passVel = pass.vel || 96;
      // Voice B runs at voice A's rate times the ratio, so the two stay locked
      // in proportion through every tempo change in the pass list.
      const ratio = Math.max(0.1, Math.min(12, pathRateRatioRef.current || 3));
      const bInterval = passInterval / ratio;
      const bNoteDur = Math.min(8, bInterval * 0.9);
      const bPlayer = eng.players[voiceB.instrument] || passPlayer;
      let bCursor = cursor;
      if (hasB) {
        for (let idx = 0; idx < samplesB.length; idx++) {
          const slot = bInterval * (lensB[idx] || 1);
          const when = bCursor;
          timelineB.push({ t: when, v: Math.min(voiceB.verts.length - 1, Math.floor(idx / NB)) });
          bCursor += slot;
          const esc = samplesB[idx];
          if (esc < 0) continue;
          const midi = realize(degreeOf(esc), 0, key) + pass.tr;
          if (midi < 0 || midi > 127) continue;
          const dur = Math.min(slot * 0.95, Math.max(0.05, bNoteDur * (lensB[idx] || 1)));
          try { const _s = bPlayer.start({ note: midi, velocity: passVel, time: when, duration: dur }); if (typeof _s === "function") pathStopsRef.current.push(_s); } catch (_) {}
        }
      }
      for (let idx = 0; idx < samples.length; idx++) {
        const slot = passInterval * (lens[idx] || 1);
        const when = cursor;
        // Which vertex this sample belongs to. Rests are recorded too, so the
        // marker keeps moving through silence inside the set.
        timeline.push({ t: when, v: Math.min(verts.length - 1, Math.floor(idx / N)) });
        cursor += slot;
        const esc = samples[idx];
        if (esc < 0) continue; // rest inside the set — slot still consumed
        const midi = realize(degreeOf(esc), 0, key) + pass.tr;
        if (midi < 0 || midi > 127) continue; // outside MIDI — skip, do not clamp
        const dur = Math.min(slot * 0.95, Math.max(0.05, passNoteDur * (lens[idx] || 1)));
        try { const _s = passPlayer.start({ note: midi, velocity: passVel, time: when, duration: dur }); if (typeof _s === "function") pathStopsRef.current.push(_s); } catch (_) {}
      }
      // A pass runs until its LONGER voice finishes.
      if (hasB && bCursor > cursor) cursor = bCursor;
      cursor += pass.pause;
    }
    // Step the marker from the audio clock. Only repaint when the vertex
    // changes — drawPathOverlay redraws the whole fractal underneath.
    pathTimelineRef.current = timeline;
    pathTimelineBRef.current = timelineB;
    if (pathRafRef.current) { cancelAnimationFrame(pathRafRef.current); pathRafRef.current = null; }
    const tEndAll = cursor;
    let lastV = -2, lastVB = -2;
    const atTime = (tl, now) => {
      for (let i = tl.length - 1; i >= 0; i--) if (tl[i].t <= now) return tl[i].v;
      return null;
    };
    const tickPathMarker = () => {
      const now = ac.currentTime;
      if (now >= tEndAll) {
        pathMarkerRef.current = null;
        pathMarkerBRef.current = null;
        if (lastV !== null || lastVB !== null) {
          lastV = null; lastVB = null;
          drawPathOverlay(pathVerticesRef.current);
        }
        pathRafRef.current = null;
        return;
      }
      const v = atTime(pathTimelineRef.current, now);
      const vb = atTime(pathTimelineBRef.current, now);
      // Repaint only when one of them actually moves — the overlay redraws the
      // whole fractal underneath.
      if (v !== lastV || vb !== lastVB) {
        lastV = v; lastVB = vb;
        pathMarkerRef.current = v;
        pathMarkerBRef.current = vb;
        drawPathOverlay(pathVerticesRef.current);
      }
      pathRafRef.current = requestAnimationFrame(tickPathMarker);
    };
    pathRafRef.current = requestAnimationFrame(tickPathMarker);

    const total = (samples.length + samplesB.length) * passList.length;
    setIsPlaying(true);
    setStatus(`Drawn path — ${verts.length} vertices, ${total} notes…`);
    const endMs = (cursor - ac.currentTime + 0.3) * 1000;
    setTimeout(() => {
      setIsPlaying(false);
      setStatus(`Drawn path finished — ${total} notes over ${passList.length} pass${passList.length === 1 ? "" : "es"}.`);
      speak("Composition complete.");
    }, Math.max(200, endMs));
  }
  async function playContour() {
    const synth = await ensureAudio();
    const ac = audioContextRef.current;
    // FALLBACK path (used only when the sequence is empty): the single previewed
    // contour, else a smooth test circle. The sequence itself is played
    // segment-by-segment below, each contour keeping its own waveform + speed.
    let path;
    if (contourPathRef.current && contourPathRef.current.points && contourPathRef.current.points.length >= 3) {
      path = contourPathRef.current.points.slice();
      if (contourPathRef.current.closed) path.push({ ...path[0] });
    } else {
      const cx = WIDTH / 2, cy = HEIGHT / 2, R = Math.min(WIDTH, HEIGHT) * 0.35;
      path = [];
      const N = 240;
      for (let i = 0; i <= N; i++) {
        const a = (i / N) * 2 * Math.PI - Math.PI / 2;
        path.push({ x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) });
      }
    }
    // Frequency from DISTANCE ABOVE/BELOW THE REAL AXIS (imaginary = 0). Points
    // mirror-symmetric across the axis are equidistant, so they sound identical —
    // making the Mandelbrot set's x-axis symmetry audible. On-axis = low pitch,
    // far from axis = high. Exponential so pitch is perceptually even.
    const fMin = 110, fMax = 1760; // A2..A6, four octaves
    const _v = viewRef.current || view;
    const yAxis = HEIGHT * _v.yMax / (_v.yMax - _v.yMin);
    const maxDist = HEIGHT / 2;
    const _pitchMode = contourPitchModeRef.current || "axis";
    // freqOf(pt, seg): in "axis" mode pitch = distance from the real axis (the
    // original mapping; makes x-axis symmetry audible). In "archHeight" mode
    // pitch follows the point's height normalized to THIS contour's own y-range,
    // inverted so the arch's visual peak (smaller screen-y = higher on screen)
    // is the HIGHEST pitch — so any arch sings its own shape across the full
    // range. Flat contours (vertical span < 4px) fall back to the axis mapping
    // so a near-horizontal contour can't blow up on a near-zero denominator.
    const freqOf = (pt, seg) => {
      if (_pitchMode === "archHeight" && seg && (seg.yHi - seg.yLo) >= 4) {
        const frac = (seg.yHi - pt.y) / (seg.yHi - seg.yLo); // 1 at top (peak), 0 at bottom
        const d = Math.max(0, Math.min(1, frac));
        return fMin * Math.pow(fMax / fMin, d);
      }
      const d = Math.max(0, Math.min(1, Math.abs(pt.y - yAxis) / maxDist));
      return fMin * Math.pow(fMax / fMin, d);
    };
    // Build one SEGMENT per contour: each has its own point list, waveform, and
    // traversal speed (per-contour speed falls back to the global slider). A
    // single previewed contour (or the test circle) is one sine segment at global
    // speed. Segments play back-to-back with a breath gap, each its own voice.
    const globalPxPerSec = contourSpeedRef.current || 130;
    const seqNow = contourSequenceRef.current;
    let segSources = null;
    if (seqNow && seqNow.length > 0) {
      segSources = seqNow
        .filter((c) => c.points && c.points.length >= 3)
        .map((c) => {
          // Column window: keep only points whose x is within [colMin,colMax],
          // in path order. On an arch this keeps both the up-stroke and the
          // down-stroke through those columns (two fragments) — as intended.
          // If a window leaves fewer than 3 points, fall back to the full
          // contour so a bad window never produces silence.
          const cLo = (typeof c.colMin === "number") ? c.colMin : 0;
          const cHi = (typeof c.colMax === "number") ? c.colMax : (WIDTH - 1);
          const full = (cLo <= 0 && cHi >= WIDTH - 1);
          let base = c.points;
          if (!full) {
            const win = c.points.filter((pt) => pt.x >= cLo && pt.x <= cHi);
            base = (win.length >= 3) ? win : c.points;
          }
          const pts = base.slice();
          // Re-close only if we're using the FULL loop (a windowed fragment of a
          // closed loop is an open arc and must not be re-joined end-to-end).
          if (c.closed && base === c.points) pts.push({ x: c.points[0].x, y: c.points[0].y });
          return { pts, wave: c.wave || "sine", pxPerSec: (typeof c.speed === "number" && c.speed > 0) ? c.speed : globalPxPerSec };
        });
    }
    if (!segSources || segSources.length === 0) {
      segSources = [{ pts: path, wave: "sine", pxPerSec: globalPxPerSec }];
    }
    const level = (soundModeRef.current === "layered") ? 0.13 : 0.22; // duck the glide under the flute in layered mode
    const breath = (typeof contourGapRef.current === "number") ? contourGapRef.current : 0.18; // silence between contours // seconds of silence between contours
    const t0 = ac.currentTime + 0.08;
    const segments = [];
    let cursor = t0;
    let _segIdx = 0;
    for (const src of segSources) {
      let plen = 0;
      for (let i = 1; i < src.pts.length; i++) {
        plen += Math.hypot(src.pts[i].x - src.pts[i - 1].x, src.pts[i].y - src.pts[i - 1].y);
      }
      let dur = Math.max(1.0, Math.min(120, plen / src.pxPerSec));
      // Layered mode: snap the phrase to a whole number of beats so the triplet
      // figuration lands squarely. Contour mode is untouched -- there the glide
      // speed alone should decide the duration.
      if (soundModeRef.current === "layered") {
        const _beat = 60 / Math.max(20, Math.min(200, tripletBpmRef.current || 52));
        dur = Math.max(_beat, Math.round(dur / _beat) * _beat);
      }
      const stepSec = dur / Math.max(1, src.pts.length - 1);
      let _yLo = Infinity, _yHi = -Infinity;
      for (const _pt of src.pts) { if (_pt.y < _yLo) _yLo = _pt.y; if (_pt.y > _yHi) _yHi = _pt.y; }
      segments.push({ pts: src.pts, wave: src.wave, tStart: cursor, tEnd: cursor + dur, stepSec, seqIndex: _segIdx, yLo: _yLo, yHi: _yHi });
      _segIdx += 1;
      cursor = cursor + dur + breath;
    }
    const tEndAll = segments.length ? segments[segments.length - 1].tEnd : t0;
    // Cancel wiring — stop ALL oscillators.
    let cancelled = false;
    const oscList = [];
    const prevCancel = startPlayback._cancel;
    if (prevCancel) prevCancel();
    startPlayback._cancel = () => { cancelled = true; for (const o of oscList) { try { o.stop(); } catch (_) {} try { o.disconnect(); } catch (_) {} } };
    setIsPlaying(true);
    setStatus((seqNow && seqNow.length > 0) ? `Contour glide — ${seqNow.length} contours…` : "Contour glide…");
    // Schedule one oscillator per segment, each with its own waveform + glide.
    for (const seg of segments) {
      const osc = ac.createOscillator();
      osc.type = seg.wave;
      const g = ac.createGain();
      g.gain.value = 0;
      osc.connect(g); g.connect(synth.master);
      osc.frequency.setValueAtTime(freqOf(seg.pts[0], seg), seg.tStart);
      let t = seg.tStart;
      for (let i = 1; i < seg.pts.length; i++) {
        t += seg.stepSec;
        osc.frequency.linearRampToValueAtTime(freqOf(seg.pts[i], seg), t);
      }
      g.gain.setValueAtTime(0, seg.tStart);
      g.gain.linearRampToValueAtTime(level, seg.tStart + 0.04);
      g.gain.setValueAtTime(level, seg.tEnd - 0.06);
      g.gain.linearRampToValueAtTime(0, seg.tEnd + 0.02);
      osc.start(seg.tStart);
      osc.stop(seg.tEnd + 0.06);
      oscList.push(osc);
    }
    contourOscRef.current = oscList[0] || null;
    contourOscListRef.current = oscList;
    // ── LAYERED MODE: flute triplet layer (Bach "Jesu"). Walk each contour on
    // the SAME audio clock as the glide; at an even triplet rate, sample the
    // escape band just inside the boundary at that point, map it to a scale
    // degree, and fire a flute note. One geometric object (the contour) drives
    // both the envelope glide and the triplets — the self-similarity claim.
    if (soundModeRef.current === "layered") {
      try {
        const eng = fractalMusicEngineRef.current || (fractalMusicEngineRef.current = new FractalMusicEngine(audioContextRef.current));
        await eng.load();
        eng.setKey({ root: (musicKeyRef.current || {}).root || "Eb", scale: (musicKeyRef.current || {}).scale || "major" });
        const flute = eng.players["flute"];
        const fd = fractalDataRef.current;
        const raw = fd && fd.rawIndex ? fd.rawIndex : null;
        const key = { ...(musicKeyRef.current || { root: "Eb", scale: "major" }), baseOctave: (musicKeyRef.current && musicKeyRef.current.baseOctave) || 4 };
        // Groups per second from the dotted-quarter tempo. At the score's 52
        // this is 0.867/sec; the old slider bottomed out at 1 (= 60) and
        // defaulted to 6 (= 360).
        const bpm = Math.max(20, Math.min(200, tripletBpmRef.current || 52));
        const rate = bpm / 60;
        // Sample the escape band at a SPECIFIC depth (px) toward the interior
        // (downward, into the bulb) at screen point (px,py). 0 = in-set.
        const escapeAtDepth = (px, py, depth) => {
          if (!raw) return 0;
          const xx = Math.max(0, Math.min(WIDTH - 1, Math.round(px)));
          const yy = Math.max(0, Math.min(HEIGHT - 1, Math.round(py + depth)));
          const v = raw[yy * WIDTH + xx];
          return (v && v > 0) ? v : 0;
        };
        if (flute) {
          // Each TRIPLET is a 3-note inward arpeggio: sample the escape banding
          // at three increasing depths into the interior, so the three notes
          // differ in pitch (melodic) and consecutive triplets track how the
          // interior banding changes along the contour.
          const depths = [3, 10, 20];
          // Sample the escape field at an arbitrary screen point.
          const escapeAt = (px, py) => {
            if (!raw) return 0;
            const xx = Math.max(0, Math.min(WIDTH - 1, Math.round(px)));
            const yy = Math.max(0, Math.min(HEIGHT - 1, Math.round(py)));
            const v = raw[yy * WIDTH + xx];
            return (v && v > 0) ? v : 0;
          };
          // Unit normal to the contour at index i, oriented toward the BANDED
          // (escaped) side. The old code always stepped +y, which on a bulb-top
          // arc walks straight into the set: every sample reads 0, so every note
          // repeats. Here the tangent comes from the neighbouring path points,
          // rotated 90 degrees, and we keep whichever of the two normal
          // directions actually lands on escaped pixels.
          const normalAt = (pts, i) => {
            const a = pts[Math.max(0, i - 1)];
            const b = pts[Math.min(pts.length - 1, i + 1)];
            let tx = b.x - a.x, ty = b.y - a.y;
            const len = Math.hypot(tx, ty) || 1;
            tx /= len; ty /= len;
            let nx = -ty, ny = tx;                 // rotate tangent 90 degrees
            const p = pts[i];
            const probe = 6;
            const plus  = escapeAt(p.x + nx * probe, p.y + ny * probe);
            const minus = escapeAt(p.x - nx * probe, p.y - ny * probe);
            // Prefer the side with escaped pixels; if both escape, take the one
            // reading HIGHER (away from the set, where the banding is finest).
            if (minus > plus) { nx = -nx; ny = -ny; }
            return { nx, ny };
          };
          let lastBand = 0; // carried ACROSS triplets and segments, not reset per group
          for (const seg of segments) {
            const dur = seg.tEnd - seg.tStart;
            const nTrip = Math.max(2, Math.round(dur * rate)); // triplets/sec
            const tripDur = dur / nTrip;
            const noteDur = Math.min(0.4, (tripDur / 3) * 0.9);
            for (let t = 0; t < nTrip; t++) {
              const frac = t / nTrip;
              const tripStart = seg.tStart + frac * dur;
              for (let n = 0; n < 3; n++) {
                // Each note reads its OWN point on the contour -- the path
                // index advances per note now, not per group -- and samples
                // along the local normal there rather than straight down.
                const noteFrac = (t + n / 3) / nTrip;
                const pi = Math.max(0, Math.min(seg.pts.length - 1, Math.round(noteFrac * (seg.pts.length - 1))));
                const pt = seg.pts[pi];
                const nrm = normalAt(seg.pts, pi);
                let esc = escapeAt(pt.x + nrm.nx * depths[n], pt.y + nrm.ny * depths[n]);
                if (!esc) esc = lastBand; else lastBand = esc; // hold last band if in-set
                const degree = esc % 15;
                const midi = realize(degree, 0, key);
                const when = tripStart + n * (tripDur / 3);
                const velocity = (n === 0) ? 110 : 82;
                try { flute.start({ note: midi, velocity, time: when, duration: noteDur }); } catch (_) {}
              }
            }
          }
        }
      } catch (_) { /* engine load failed — envelope still plays */ }
    }
    // Marker driven by the AUDIO CLOCK via requestAnimationFrame: find the active
    // segment by ac.currentTime, then the point within it. Position is derived
    // from the clock, so it cannot drift; between contours (a breath) the dot hides.
    const tickMarker = () => {
      if (cancelled) { contourMarkerRef.current = null; setCurrentContourIndex(-1); drawContourMarker(); return; }
      const now = ac.currentTime;
      if (now >= tEndAll) { contourMarkerRef.current = null; setCurrentContourIndex(-1); drawContourMarker(); return; }
      let active = null;
      for (const seg of segments) {
        if (now >= seg.tStart && now <= seg.tEnd) { active = seg; break; }
      }
      if (active) {
        const prog = (now - active.tStart) / Math.max(1e-6, active.tEnd - active.tStart);
        const idx = Math.max(0, Math.min(active.pts.length - 1, Math.round(prog * (active.pts.length - 1))));
        contourMarkerRef.current = { x: active.pts[idx].x, y: active.pts[idx].y };
        if (typeof active.seqIndex === "number") setCurrentContourIndex(active.seqIndex);
      } else {
        contourMarkerRef.current = null; // between contours (breath)
        setCurrentContourIndex(-1); // between contours: no row highlighted
      }
      drawContourMarker();
      contourRafRef.current = requestAnimationFrame(tickMarker);
    };
    contourRafRef.current = requestAnimationFrame(tickMarker);
    // Completion.
    const totalMs = (tEndAll + 0.1 - ac.currentTime) * 1000;
    bandCompleteTimerRef.current = setTimeout(() => {
      bandCompleteTimerRef.current = null;
      if (!cancelled) { setStatus("Contour finished."); speak("Contour complete."); }
      setIsPlaying(false);
    }, Math.max(0, totalMs));
  }

  async function playBandSequence() {
    const seq = bandSequenceRef.current;
    if (!seq || !seq.length) { setStatus("No regions selected. Right-click a region to add it."); speak("No regions selected."); return; }
    let bands = viewBandsRef.current;
    if (!bands) bands = computeViewBands();
    if (!bands) { setStatus("Compute a fractal first."); return; }

    await ensureAudio();
    if (!fractalMusicEngineRef.current) {
      fractalMusicEngineRef.current = new FractalMusicEngine(audioContextRef.current);
    }
    setStatus("Loading instruments…");
    await fractalMusicEngineRef.current.load();
    const eng = fractalMusicEngineRef.current;
    // Preload every region's instrument.
    for (const r of seq) { try { await eng.ensureInstrument(r.instrument); } catch (_) {} }
    // Preload section instruments too — a section may use one no region does.
    for (const sec of (bandSectionsRef.current || [])) { try { await eng.ensureInstrument(sec.instrument); } catch (_) {} }

    // Cancel wiring — matches startPlayback._cancel so Stop/Esc work.
    let cancelled = false;
    const prevCancel = startPlayback._cancel;
    if (prevCancel) prevCancel();
    startPlayback._cancel = () => { cancelled = true; };
    setIsPlaying(true);
    const baseKey = musicKeyRef.current || { root: "Eb", scale: "major" };
    const key = { baseOctave: 4, ...baseKey, baseOctave: (baseKey.baseOctave ?? 4) };

    // ── TAP FAST PATH ───────────────────────────────────────────────────────
    // When EVERY region is a tap (fast s/col ≤ 50 ms), schedule the whole echo
    // up front on the AUDIO clock — evenly spaced, precise, and immune to
    // main-thread stalls from complex full-width bands (which otherwise bunched
    // the taps and could stall completion). Web Audio plays each note at its
    // exact scheduled time regardless of the main thread; the playhead visual is
    // driven separately and may stutter under load without affecting the sound.
    const allTaps = seq.every((r) => (r.columnSpeedMs || 1000) <= 50);
    if (allTaps) {
      const ac = audioContextRef.current;
      const t0 = ac.currentTime + 0.12;
      const tapTimes = [];
      const tapMids = [];
      let acc = 0;
      // Build the pass list. Each pass applies one instrument + octave offset to
      // the WHOLE sequence. Sections (if any) expand into repeats × sections;
      // otherwise a single pass uses each region's own instrument (null marker).
      const secs = bandSectionsRef.current;
      const passes = [];
      if (secs && secs.length) {
        for (const sec of secs) {
          const reps = Math.max(1, sec.repeats || 1);
          for (let r = 0; r < reps; r++) passes.push({ instrument: sec.instrument, octaveOffset: sec.octaveOffset || 0 });
        }
      } else {
        passes.push(null); // null = per-region instrument & octave (single pass)
      }
      // Schedule every region for every pass. Fade resets each pass (fresh
      // volume), so a repeated section pulses rather than fading across repeats.
      for (let pi = 0; pi < passes.length; pi++) {
        const pass = passes[pi];
        for (let i = 0; i < seq.length; i++) {
          const region = seq[i];
          const colMap = bands.get(region.escapeValue);
          let firstCol = WIDTH, lastCol = -1;
          if (colMap) for (const [c] of colMap) { if (c < firstCol) firstCol = c; if (c > lastCol) lastCol = c; }
          const mid = lastCol < 0 ? Math.round(WIDTH / 2) : Math.round((firstCol + lastCol) / 2);
          const when = t0 + acc / 1000;
          tapTimes.push(when); tapMids.push(mid);
          const instName = pass ? pass.instrument : region.instrument;
          const octOff = (pass ? pass.octaveOffset : 0) + (region.octaveOffset || 0);
          const pl = eng.players[instName];
          if (pl && colMap) {
            const midi = realize(region.scaleDegree, octOff, key);
            const frac = seq.length > 1 ? i / (seq.length - 1) : 0;
            const velocity = Math.max(1, Math.min(127, Math.round(88 - 36 * frac)));
            try { pl.start({ note: midi, velocity, time: when, duration: 0.15 }); } catch (_) {}
          }
          const gapMs = Math.max(180, (region.columnSpeedMs || 10) * 18);
          acc += gapMs;
        }
        // Extra breath between passes so each repeat/section is distinct.
        if (pi < passes.length - 1) acc += 220;
      }
      setStatus(passes.length > 1 ? `Playing ${passes.length} passes…` : `Playing ${seq.length} taps…`);
      // Visual ticker: move the playhead to each tap's midpoint at its scheduled
      // time. Cosmetic only — completion is on its own timer below so it fires
      // reliably even if this chain is cleared.
      let vi = 0;
      const tickVisual = () => {
        if (cancelled) return;
        if (vi >= tapMids.length) return;
        liveColumnRef.current = tapMids[vi];
        if (showSoundbarRef.current) setPlayhead(tapMids[vi]);
        const nextAt = (vi + 1 < tapTimes.length) ? tapTimes[vi + 1] : (tapTimes[vi] + 0.2);
        vi += 1;
        const delayMs = Math.max(0, (nextAt - ac.currentTime) * 1000);
        orbitTimerRef.current = setTimeout(tickVisual, delayMs);
      };
      const startDelay = Math.max(0, (t0 - ac.currentTime) * 1000);
      orbitTimerRef.current = setTimeout(tickVisual, startDelay);
      // Dedicated completion timer at the total duration (last tap + note tail),
      // independent of the visual chain so it always fires.
      const totalMs = acc + 350;
      bandCompleteTimerRef.current = setTimeout(() => {
        bandCompleteTimerRef.current = null;  // fired; nothing to clear
        if (!cancelled) {
          // Announce FIRST — before the state changes below, which can trigger
          // effects that cancel speech. Starting the utterance now protects it.
          setStatus("Band composition finished.");
          speak("Composition complete.");
        }
        setIsPlaying(false); setPlayhead(null); setSelectedColumn(null);
      }, totalMs);
      return;
    }

    for (let ri = 0; ri < seq.length && !cancelled; ri++) {
      const region = seq[ri];
      setStatus(`Region ${ri + 1} of ${seq.length}: band ${region.escapeValue}`);
      speak(`Region ${ri + 1}, band ${region.escapeValue}.`);
      await sweepOneRegion(region, bands, key, () => cancelled, ri, seq.length);
    }

    if (!cancelled) { setStatus("Band composition finished."); speak("Composition complete."); }
    setIsPlaying(false);
    setPlayhead(null);
    setSelectedColumn(null);
  }

  // Sweep ONE region: advance the playhead column-by-column at region speed,
  // scheduling one sustained note per contiguous present-run of the band.
  // Resolves when the sweep reaches the last column (so the caller can await
  // regions in sequence). Honors the isCancelled() check for Stop.
  function sweepOneRegion(region, bands, key, isCancelled, seqIndex = 0, seqLen = 1) {
    return new Promise((resolve) => {
      const colMap = bands.get(region.escapeValue);
      if (!colMap) { resolve(); return; }
      const eng = fractalMusicEngineRef.current;
      const p = eng.players[region.instrument];
      const midi = realize(region.scaleDegree, region.octaveOffset || 0, key);
      const colMs = region.columnSpeedMs || 1000;

      // Peak thickness for velocity scaling within this region, and the band's
      // actual column extent (first present col → last) so we sweep only where
      // the band exists — no dead time through empty columns.
      let maxThick = 1;
      let firstCol = WIDTH, lastCol = -1;
      for (const [c, rec] of colMap) {
        if (rec.thickness > maxThick) maxThick = rec.thickness;
        if (c < firstCol) firstCol = c;
        if (c > lastCol) lastCol = c;
      }
      if (lastCol < 0) { resolve(); return; }
      // ── TAP MODE ──────────────────────────────────────────────────────────
      // Very fast column speed (<= 50 ms/col, i.e. 0.05 s/col or less) means the
      // composer wants a short staccato tap, not a sustained sweep. The region
      // fires ONE brief note at its (thickness-derived) volume, then yields to
      // the next region after a short gap. The gap scales with s/col, so a
      // slightly larger fast value (0.03) spaces an echo wider and a tiny one
      // (0.01) tightens it. A run of concentric bands, thick→thin, becomes an
      // echo: same note repeated, each tap softer.
      if (colMs <= 50) {
        const nowT = audioContextRef.current.currentTime;
        if (p) {
          // Fade across the SEQUENCE: first region loudest, last softest — a
          // clean echo decay, independent of band geometry. frac: 0 → 1.
          const frac = seqLen > 1 ? seqIndex / (seqLen - 1) : 0;
          const velocity = Math.max(1, Math.min(127, Math.round(88 - 36 * frac)));
          // Short note so each tap is distinct (doesn't bleed into the next).
          try { p.start({ note: midi, velocity, time: nowT + 0.02, duration: 0.15 }); } catch (_) {}
        }
        const mid = Math.round((firstCol + lastCol) / 2);
        liveColumnRef.current = mid;
        if (showSoundbarRef.current) setPlayhead(mid);
        // Gap keeps taps separated: at least 180 ms (longer than the note), and
        // scaling with s/col so a larger fast value spaces the echo wider.
        const gapMs = Math.max(180, colMs * 18);
        orbitTimerRef.current = setTimeout(() => { resolve(); }, gapMs);
        return;
      }

      // Precompute contiguous present-runs and schedule a sustained note per run,
      // relative to audio-context time, as the playhead advances.
      const ctxNow = () => audioContextRef.current.currentTime;
      const t0 = ctxNow() + 0.1;
      // Schedule audio for all runs up front (accurate), advance playhead by timer.
      if (p) {
        let runStart = -1, runThickSum = 0, runLen = 0;
        const flush = (endCol) => {
          if (runStart < 0) return;
          const when = t0 + (runStart - firstCol) * (colMs / 1000);
          const durationSec = runLen * (colMs / 1000);
          const avg = runThickSum / runLen;
          const velocity = Math.max(1, Math.min(127, Math.round(40 + 87 * (avg / maxThick))));
          try { p.start({ note: midi, velocity, time: when, duration: durationSec }); } catch (_) {}
          runStart = -1; runThickSum = 0; runLen = 0;
        };
        for (let col = 0; col < WIDTH; col++) {
          const rec = colMap.get(col);
          if (rec && rec.thickness >= 1) {
            if (runStart < 0) runStart = col;
            runThickSum += rec.thickness; runLen++;
          } else { flush(col); }
        }
        flush(WIDTH);
      }

      // Advance the playhead visual from firstCol to lastCol at region speed,
      // then resolve so the next region begins. Sweeping only the band's extent
      // keeps the composition flowing region-to-region with no dead gaps.
      let col = firstCol;
      const step = () => {
        if (isCancelled()) { resolve(); return; }
        if (col > lastCol) { resolve(); return; }
        liveColumnRef.current = col;
        if (showSoundbarRef.current) setPlayhead(col);
        col += 1;
        orbitTimerRef.current = setTimeout(step, colMs);
      };
      step();
    });
  }

  // Toggle a page-wide busy cursor. A class with an !important universal rule
  // overrides per-element cursors (the Play button, the canvas) so the wait is
  // visible wherever the pointer sits. The <style> is injected once, lazily.
  function setBusyCursor(on) {
    let el = document.getElementById("busy-cursor-style");
    if (on) {
      if (!el) {
        el = document.createElement("style");
        el.id = "busy-cursor-style";
        el.textContent = "body.app-busy, body.app-busy * { cursor: progress !important; }";
        document.head.appendChild(el);
      }
      document.body.classList.add("app-busy");
    } else {
      document.body.classList.remove("app-busy");
    }
  }
  async function startPlayback(startColumn = 0) {
    setBusyCursor(true);
    const synth = await ensureAudio();
    if (soundMode === "band") { stopPlayback(); try { return await playBandSequence(); } finally { setBusyCursor(false); } }
    if (soundMode === "contour") { stopPlayback(); try { return await playContour(); } finally { setBusyCursor(false); } }
    if (soundMode === "layered") { stopPlayback(); try { return await playLayered(); } finally { setBusyCursor(false); } }
    if (soundMode === "path") { stopPlayback(); try { return await playPath(); } finally { setBusyCursor(false); } }
    if (soundMode === "instrumental") {
      if (!fractalMusicEngineRef.current) {
        fractalMusicEngineRef.current = new FractalMusicEngine(audioContextRef.current);
      }
      setStatus("Loading instruments…");
      await fractalMusicEngineRef.current.load();
      fractalMusicEngineRef.current.setKey({ root: musicKey.root, scale: musicKey.scale });
      instrumentalTableRef.current = getInstrumentalTableLookup();
      await fractalMusicEngineRef.current.loadForPalette(loudnessPalette);
    }
    stopPlayback();

    const orientation = scanOrientation;
    const isVertical = orientation !== "horizontal";
    const scanExtent = isVertical ? WIDTH : HEIGHT;
    let currentColumn = clamp(startColumn, 0, scanExtent - 1);
    let cancelled = false;
    const cancelToken = { get cancelled() { return cancelled; } };
    const prevCancel = startPlayback._cancel;
    if (prevCancel) prevCancel();
    startPlayback._cancel = () => { cancelled = true; };

    setIsPlaying(true);
    setBusyCursor(false);
    setStatus(`Playing full scan: ${WIDTH} columns, ${(lineDrawMs / 1000).toFixed(2)} s each`);
    speak("Scanning fractal.");

    const playNextColumn = () => {
      if (cancelToken.cancelled) return;
      if (currentColumn >= scanExtent) {
        silenceSynth(synth);
        orbitTimerRef.current = null;
        setIsPlaying(false);
        setPlayhead(null);
        setStatus("Playback finished");
        speak("Scan complete.");
        return;
      }

      liveColumnRef.current = currentColumn;
      setPlayhead(currentColumn);
      setSelectedColumn(currentColumn);
      // Movements tempo map: this column's play time comes from the movement
      // that contains it (falls back to the Column-speed slider otherwise).
      const colMs = tempoForColumn(currentColumn);
      if (soundMode === "instrumental" && fractalMusicEngineRef.current?.loaded) {
        const engine = fractalMusicEngineRef.current;
        const regions = buildInstrumentalRegions(currentColumn, orientation);
        const loudnessLookup = getLoudnessLookup();
        const totalHeight = regions.reduce((s, r) => s + r.height, 0) || 1;
        const startTime = engine.ctx.currentTime + 0.02;
        let accMs = 0;
        regions.forEach((r) => {
          const regionMs = (r.height / totalHeight) * colMs;
          if (!r.inSet) {
            engine.playRegionLoudness(r.escape, loudnessLookup, startTime + accMs / 1000, (regionMs / 1000) * 0.9);
          }
          accMs += regionMs;
        });
      } else {
        const regions = extractLineRegions(fractalData, currentColumn, WIDTH, HEIGHT, maxIter, orientation);
        if (regions.length > 0) {
          const totalHeight = regions.reduce((sum, r) => sum + r.height, 0);
          const startTime = synth.audioContext.currentTime + 0.02;
          const pan = clamp(stereoWidth * mapRange(currentColumn, 0, scanExtent - 1, -1, 1), -1, 1);
          synth.panner.pan.setValueAtTime(pan, startTime);
          synth.gain1.gain.cancelScheduledValues(startTime);
          synth.gain2.gain.cancelScheduledValues(startTime);
          synth.droneGain.gain.cancelScheduledValues(startTime);
          let accumulatedMs = 0;
          regions.forEach((region) => {
            const regionMs = (region.height / totalHeight) * colMs;
            const regionStart = startTime + accumulatedMs / 1000;
            playSingleRegionTone(synth, region, regionStart, regionMs, maxIter);
            accumulatedMs += regionMs;
          });
        }
      }
      // Advance. If this column is the last of a movement with an end rest,
      // add that rest (endRestBeats "beats", a beat = one column at this tempo)
      // as extra silence before the next column.
      let advanceMs = colMs;
      const _movs = movementsRef.current || [];
      for (let _i = 0; _i < _movs.length; _i++) {
        if (_movs[_i].endCol === currentColumn && (_movs[_i].endRestBeats || 0) > 0) {
          advanceMs += _movs[_i].endRestBeats * colMs;
          break;
        }
      }
      currentColumn += 1;
      orbitTimerRef.current = setTimeout(playNextColumn, advanceMs);
    };

    playNextColumn();
  }

  async function previewColumn(column) {
    const synth = await ensureAudio();
    stopPlayback();

    const regions = extractColumnRegions(fractalData, column, WIDTH, HEIGHT, maxIter);

    if (regions.length === 0) {
      setStatus(`Previewing column ${column}`);
      return;
    }

    setPlayhead(column);
    setSelectedColumn(column);
    setStatus(`Previewing column ${column}`);

    const totalHeight = regions.reduce((sum, r) => sum + r.height, 0);
    const startTime = synth.audioContext.currentTime + 0.05;

    synth.gain1.gain.cancelScheduledValues(startTime);
    synth.gain2.gain.cancelScheduledValues(startTime);
    synth.droneGain.gain.cancelScheduledValues(startTime);

    let accumulatedMs = 0;
    regions.forEach((region) => {
      const regionMs = (region.height / totalHeight) * lineDrawMs;
      const regionStart = startTime + accumulatedMs / 1000;
      playSingleRegionTone(synth, region, regionStart, regionMs, maxIter);
      accumulatedMs += regionMs;
    });

    setTimeout(() => {
      setStatus(`Previewed column ${column}: ${regions.length} regions in ${(lineDrawMs / 1000).toFixed(2)} s`);
    }, lineDrawMs + 100);
  }

  function getCanvasCoordinates(event) {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * WIDTH;
    const y = ((event.clientY - rect.top) / rect.height) * HEIGHT;
    return { x, y };
  }

  function screenToComplex(x, y) {
    const cx = view.xMin + (x / WIDTH) * (view.xMax - view.xMin);
    const cy = view.yMax - (y / HEIGHT) * (view.yMax - view.yMin);
    return { cx, cy };
  }

  // Classify a screen-space pixel by its structural relationship to the
  // fractal boundary.  Used by 'where am I' and 'preview orbit' commands so
  // blind users can build a mental map of the canvas before generating audio.
  // Returns an English description.
  function classifyPoint(sampleX, sampleY, label) {
    const fd = fractalDataRef.current;
    if (!fd || !fd.interior || !fd.image) return '';
    const RADIUS = 8;
    const cIdx = sampleY * WIDTH + sampleX;
    if (cIdx < 0 || cIdx >= fd.interior.length) return '';
    const centerInside = fd.interior[cIdx] === 1;
    let interiorCount = 0, totalCount = 0, maxIterSeen = -Infinity;
    for (let dy = -RADIUS; dy <= RADIUS; dy++) {
      for (let dx = -RADIUS; dx <= RADIUS; dx++) {
        const nx = sampleX + dx, ny = sampleY + dy;
        if (nx < 0 || nx >= WIDTH || ny < 0 || ny >= HEIGHT) continue;
        const ni = ny * WIDTH + nx;
        totalCount++;
        if (fd.interior[ni] === 1) interiorCount++;
        else if (fd.image[ni] > maxIterSeen) maxIterSeen = fd.image[ni];
      }
    }
    const fracInterior = totalCount > 0 ? interiorCount / totalCount : 0;
    if (centerInside && fracInterior >= 0.95)
      return `${label} is deep inside the set. Orbit will not escape.`;
    if (centerInside)
      return `${label} is inside the set, very close to the boundary.`;
    if (fracInterior >= 0.05)
      return `${label} is just outside the boundary, where interesting orbits live.`;
    if (maxIterSeen > 0 && maxIterSeen < maxIter * 0.2)
      return `${label} is far from the boundary, in the deep exterior. Orbit will escape quickly.`;
    if (maxIterSeen > 0 && maxIterSeen < maxIter * 0.6)
      return `${label} is in the exterior, moderately far from the boundary.`;
    return `${label} is in the exterior, near the boundary.`;
  }

  function complexToScreen(zx, zy) {
    const x = ((zx - view.xMin) / (view.xMax - view.xMin)) * WIDTH;
    const y = ((view.yMax - zy) / (view.yMax - view.yMin)) * HEIGHT;
    return { x, y };
  }

  function drawZoomOverlay() {
    if (!canvasRef.current || !fractalData) return;

    drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalData, playhead, orbitPath, scanOrientation, (showBoundariesRef.current ? movementsRef.current.map(m => m.startCol).filter(c => c > 0) : []));

    const ctx = canvasRef.current.getContext("2d");

    // Draw the pending selection (awaiting right-click confirmation) in solid white
    const pending = pendingSelectionRef.current;
    if (pending && !isDraggingRef.current) {
      const scaleX = canvasRef.current.width  / WIDTH;
      const scaleY = canvasRef.current.height / HEIGHT;
      ctx.save();
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.strokeRect(
        pending.left * scaleX, pending.top * scaleY,
        (pending.right - pending.left) * scaleX,
        (pending.bottom - pending.top) * scaleY
      );
      // Semi-transparent fill so the selection area is clearly visible
      ctx.fillStyle = "rgba(255,255,255,0.08)";
      ctx.fillRect(
        pending.left * scaleX, pending.top * scaleY,
        (pending.right - pending.left) * scaleX,
        (pending.bottom - pending.top) * scaleY
      );
      ctx.restore();
      return;
    }

    // Draw the in-progress drag rectangle as a dashed outline
    const start = dragStartRef.current;
    const current = dragCurrentRef.current;
    if (!start || !current) return;

    const scaleX = canvasRef.current.width  / WIDTH;
    const scaleY = canvasRef.current.height / HEIGHT;

    let x1 = start.x, y1 = start.y;
    let x2 = current.x, y2 = current.y;

    const dxRaw = x2 - x1;
    const dyRaw = y2 - y1;
    const aspect = WIDTH / HEIGHT;

    let w = Math.abs(dxRaw);
    let h = Math.abs(dyRaw);

    if (w < 1 || h < 1) return;

    if (w / h > aspect) { h = w / aspect; }
    else                 { w = h * aspect; }

    x2 = x1 + Math.sign(dxRaw || 1) * w;
    y2 = y1 + Math.sign(dyRaw || 1) * h;

    const left = Math.min(x1, x2);
    const top  = Math.min(y1, y2);

    ctx.save();
    ctx.strokeStyle = "white";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(left * scaleX, top * scaleY, Math.abs(x2 - x1) * scaleX, Math.abs(y2 - y1) * scaleY);
    ctx.restore();
  }

  async function playOrbit(cx, cy) {
    const synth = await ensureAudio();

    stopPlayback();
    setOrbitPath([]);
    orbitPathRef.current = [];
    setIsPlaying(true);
    setStatus("Playing orbit");
    speak("Playing orbit.");

    let zx = fractalType === "julia" ? cx : 0;
    let zy = fractalType === "julia" ? cy : 0;
    const cRe = fractalType === "julia" ? juliaC.re : cx;
    const cIm = fractalType === "julia" ? juliaC.im : cy;

    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      const scaleX = canvasRef.current.width / WIDTH; const scaleY = canvasRef.current.height / HEIGHT;
      const sp = complexToScreen(cx, cy);
      ctx.fillStyle = "rgba(255,255,0,0.95)";
      ctx.fillRect(Math.round(sp.x*scaleX-3),Math.round(sp.y*scaleY-3),Math.ceil(7*scaleX),Math.ceil(7*scaleY));
    }

    let iter = 0;

    const step = () => {
      const xx = zx * zx;
      const yy = zy * zy;
      const magSq = xx + yy;

      if (magSq > ESCAPE_RADIUS_SQUARED || iter >= maxIter) {
        silenceSynth(synth);
        orbitTimerRef.current = null;
        setIsPlaying(false);
        setStatus(`Orbit finished: ${iter} points`);
        speak(`Orbit finished. ${iter} points.`);
        return;
      }

      const newZy = 2 * zx * zy + cIm;
      const newZx = xx - yy + cRe;
      zx = newZx;
      zy = newZy;

      const pt = complexToScreen(zx, zy);
      if (pt.x >= 0 && pt.x < WIDTH && pt.y >= 0 && pt.y < HEIGHT) {
        orbitPathRef.current.push(pt);
        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext("2d");
          const scaleX = canvasRef.current.width / WIDTH;
          const scaleY = canvasRef.current.height / HEIGHT;
          ctx.fillStyle = "rgba(255,255,255,0.85)";
          ctx.fillRect(Math.round(pt.x*scaleX-1),Math.round(pt.y*scaleY-1),Math.ceil(3*scaleX),Math.ceil(3*scaleY));
        }
      }

      const magnitude = Math.sqrt(zx * zx + zy * zy);
      const pitch = mapRange(Math.log(magnitude + 1), 0, 3.0, 140, 1200);
      const pan = clamp(stereoWidth * mapRange(zx, view.xMin, view.xMax, -1, 1), -1, 1);
      const brightness = mapRange(clamp(magnitude, 0, 10), 0, 10, 500, 5000);

      const now = synth.audioContext.currentTime;
      synth.osc1.frequency.setTargetAtTime(pitch, now, 0.015);
      synth.osc2.frequency.setTargetAtTime(pitch * 1.5, now, 0.015);
      synth.drone.frequency.setTargetAtTime(Math.max(70, pitch * 0.4), now, 0.02);

      synth.gain1.gain.setTargetAtTime(0.1, now, 0.01);
      synth.gain2.gain.setTargetAtTime(0.05, now, 0.01);
      synth.droneGain.gain.setTargetAtTime(0.02, now, 0.02);
      synth.filter.frequency.setTargetAtTime(brightness, now, 0.01);
      synth.panner.pan.setTargetAtTime(pan, now, 0.01);

      iter += 1;
      orbitTimerRef.current = setTimeout(step, 70);
    };

    step();
  }

  function handleMouseDown(event) {
    if (event.button !== 0) return; // only respond to left button
    const pos = getCanvasCoordinates(event);
    if (soundModeRef.current === "path") {
      if (pathDrawModeRef.current === "freehand") {
        // Begin a freehand sweep; raw points collected on move, resampled on release.
        freehandRawRef.current = [{ x: pos.x, y: pos.y }];
        isDraggingRef.current = true;
        return;
      }
      // Left-click drops a vertex; straight segments connect them. No zoom drag.
      const v = { x: Math.round(pos.x), y: Math.round(pos.y) };
      const next = [...pathVerticesRef.current, v];
      setPathVertices(next);
      drawPathOverlay(next);
      speakLabel(`Vertex ${next.length}.`);
      return;
    }
    dragStartRef.current = pos;
    dragCurrentRef.current = pos;
    isDraggingRef.current = true;
  }
  function drawColBandOverlay() {
    if (!canvasRef.current || !fractalData) return;
    drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalData, playhead, orbitPath, scanOrientation, (showBoundariesRef.current ? movementsRef.current.map(m => m.startCol).filter(c => c > 0) : []));
    const start = dragStartRef.current, current = dragCurrentRef.current;
    if (!start || !current) return;
    const ctx = canvasRef.current.getContext("2d");
    const scaleX = canvasRef.current.width / WIDTH;
    const x0 = Math.min(start.x, current.x) * scaleX;
    const x1 = Math.max(start.x, current.x) * scaleX;
    ctx.save();
    ctx.fillStyle = "rgba(34,211,238,0.18)";
    ctx.fillRect(x0, 0, x1 - x0, canvasRef.current.height);
    ctx.strokeStyle = "rgba(34,211,238,0.95)";
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x0, 0); ctx.lineTo(x0, canvasRef.current.height);
    ctx.moveTo(x1, 0); ctx.lineTo(x1, canvasRef.current.height); ctx.stroke();
    ctx.restore();
  }

  // Repaint the fractal, then draw the drawn-path vertices as emerald dots
  // with straight segments between them. Pass the vertex array explicitly
  // (setState is async, so the ref may lag by one placement).
  // Loading a composition repaints the fractal, which erases the path overlay.
  // The repaint can land either side of the restore, so redraw from an effect
  // rather than from the restore itself.
  useEffect(() => {
    if (soundMode !== "path") return;
    if (!pathVertices || pathVertices.length === 0) return;
    if (!fractalDataRef.current) return;
    drawPathOverlay(pathVertices);
    // isPlaying is here because Play calls stopPlayback() first, which repaints
    // the fractal and wipes the overlay. Without it the path disappears for the
    // whole performance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fractalData, soundMode, pathVertices, pathShowCoords, isPlaying]);

  function drawPathOverlay(vertsArg) {
    // Refs, not state. The keydown listener is registered once and keeps the
    // copy of this function it captured, so reading fractalData directly meant
    // Escape repainted the boot-time image over the loaded composition.
    const fd = fractalDataRef.current;
    if (!canvasRef.current || !fd) return;
    drawFractal(canvasRef.current, WIDTH, HEIGHT, fd, playheadRef.current, orbitPathRef.current, scanOrientation, (showBoundariesRef.current ? movementsRef.current.map(m => m.startCol).filter(c => c > 0) : []));
    const verts = vertsArg || pathVerticesRef.current;
    if (!verts || verts.length === 0) return;
    const ctx = canvasRef.current.getContext("2d");
    const sx = canvasRef.current.width / WIDTH;
    const sy = canvasRef.current.height / HEIGHT;
    ctx.save();
    if (verts.length > 1) {
      ctx.strokeStyle = "rgba(52,211,153,0.95)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      verts.forEach((p, i) => {
        const X = p.x * sx, Y = p.y * sy;
        if (i === 0) ctx.moveTo(X, Y); else ctx.lineTo(X, Y);
      });
      ctx.stroke();
    }
    // The parked voice, dimmed, so both are visible while editing either.
    const other = (pathOtherRef.current || {}).verts || [];
    if (other.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(other[0].x * sx, other[0].y * sy);
      for (let i = 1; i < other.length; i++) ctx.lineTo(other[i].x * sx, other[i].y * sy);
      ctx.lineWidth = Math.max(1, sx); ctx.strokeStyle = "rgba(129,140,248,0.55)"; ctx.stroke();
      ctx.fillStyle = "rgba(129,140,248,0.75)";
      other.forEach((p) => { ctx.beginPath(); ctx.arc(p.x * sx, p.y * sy, 3 * sx, 0, Math.PI * 2); ctx.fill(); });
      // The parked voice gets its marker too, so both lines can be watched.
      const parkedIsB = pathActiveVoiceRef.current === "A";
      const om = parkedIsB ? pathMarkerBRef.current : pathMarkerRef.current;
      if (om !== null && om >= 0 && om < other.length) {
        const q = other[om];
        ctx.beginPath(); ctx.arc(q.x * sx, q.y * sy, 7 * sx, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(251,191,36,0.95)"; ctx.fill();
        ctx.lineWidth = 2 * sx; ctx.strokeStyle = "rgba(255,255,255,0.85)"; ctx.stroke();
      }
    }
    const mk = (pathActiveVoiceRef.current === "A") ? pathMarkerRef.current : pathMarkerBRef.current;
    verts.forEach((p, i) => {
      // Vertex 1 amber so the reading direction is obvious.
      ctx.fillStyle = (i === 0) ? "rgba(251,191,36,1)" : "rgba(52,211,153,1)";
      ctx.beginPath(); ctx.arc(p.x * sx, p.y * sy, 4 * sx, 0, Math.PI * 2); ctx.fill();
    });
    if (mk !== null && mk >= 0 && mk < verts.length) {
      // The note sounding right now: a larger magenta dot with a white ring.
      const p = verts[mk];
      ctx.beginPath(); ctx.arc(p.x * sx, p.y * sy, 8 * sx, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(236,72,153,1)"; ctx.fill();
      ctx.lineWidth = 2 * sx; ctx.strokeStyle = "rgba(255,255,255,0.9)"; ctx.stroke();
    }
    if (pathShowCoordsRef.current && verts.length) {
      // Labels stack in a column against the edge the path is FURTHEST from,
      // so they never cover the dots or the moving marker. A faint leader line
      // ties each label to its vertex.
      const CW = canvasRef.current.width, CH = canvasRef.current.height;
      const fs = Math.max(9, Math.round(11 * sx));
      ctx.font = `${fs}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      ctx.textBaseline = "bottom";

      const texts = verts.map((p, i) => `${i + 1} (${Math.round(p.x)},${Math.round(p.y)})`);
      let maxW = 0;
      for (const t of texts) maxW = Math.max(maxW, ctx.measureText(t).width);
      const padX = fs * 0.35, padY = fs * 0.25;
      const boxW = maxW + padX * 2, boxH = fs + padY * 2;

      const meanX = verts.reduce((a, p) => a + p.x, 0) / verts.length;
      const onLeft = meanX > WIDTH / 2;          // path on the right → labels left
      const colX = onLeft ? 8 * sx : CW - 8 * sx - boxW;

      const margin = 8 * sy;
      const avail = Math.max(boxH, CH - margin * 2);
      const lineH = Math.min(boxH * 1.15, avail / verts.length);
      // Same marker the dots use — the active voice's, not always voice A's.
      const mk = (pathActiveVoiceRef.current === "A") ? pathMarkerRef.current : pathMarkerBRef.current;

      verts.forEach((p, i) => {
        const top = margin + i * lineH;
        const cy = top + boxH / 2;
        // Leader line from the inner edge of the plate to the vertex.
        ctx.beginPath();
        ctx.moveTo(onLeft ? colX + boxW : colX, cy);
        ctx.lineTo(p.x * sx, p.y * sy);
        ctx.lineWidth = Math.max(1, sx * 0.5);
        ctx.strokeStyle = (i === mk) ? "rgba(236,72,153,0.75)" : "rgba(148,163,184,0.28)";
        ctx.stroke();
      });

      verts.forEach((p, i) => {
        const top = margin + i * lineH;
        ctx.fillStyle = "rgba(2,6,23,0.82)";
        ctx.fillRect(colX, top, boxW, boxH);
        ctx.fillStyle = (i === mk) ? "rgba(236,72,153,1)"
                      : (i === 0) ? "rgba(251,191,36,1)"
                      : "rgba(226,232,240,1)";
        ctx.fillText(texts[i], colX + padX, top + boxH - padY);
      });
    }
    ctx.restore();
  }

  // Resample a raw freehand sweep to points spaced `spacing` px apart along its
  // arc length — so a longer sweep yields more notes (reading the plane).
  function resampleByArcLength(raw, spacing) {
    if (!raw || raw.length === 0) return [];
    const out = [{ x: raw[0].x, y: raw[0].y }];
    let carry = 0; // distance since the last emitted point
    for (let i = 1; i < raw.length; i++) {
      let ax = raw[i - 1].x, ay = raw[i - 1].y;
      const bx = raw[i].x, by = raw[i].y;
      let L = Math.hypot(bx - ax, by - ay);
      if (L === 0) continue;
      const dx = (bx - ax) / L, dy = (by - ay) / L;
      while (carry + L >= spacing) {
        const step = spacing - carry;
        ax += dx * step; ay += dy * step;
        out.push({ x: ax, y: ay });
        L -= step; carry = 0;
      }
      carry += L;
    }
    // Include the pen-up point if it fell well past the last emitted point.
    const last = raw[raw.length - 1], lo = out[out.length - 1];
    if (Math.hypot(last.x - lo.x, last.y - lo.y) > spacing * 0.4) out.push({ x: last.x, y: last.y });
    return out;
  }

  // Live emerald line following the raw freehand sweep (before resampling).
  function drawFreehandOverlay() {
    if (!canvasRef.current || !fractalData) return;
    drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalData, playhead, orbitPath, scanOrientation, (showBoundariesRef.current ? movementsRef.current.map(m => m.startCol).filter(c => c > 0) : []));
    const raw = freehandRawRef.current;
    if (!raw || raw.length < 2) return;
    const ctx = canvasRef.current.getContext("2d");
    const sx = canvasRef.current.width / WIDTH;
    const sy = canvasRef.current.height / HEIGHT;
    ctx.save();
    ctx.strokeStyle = "rgba(52,211,153,0.9)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    raw.forEach((p, i) => { const X = p.x * sx, Y = p.y * sy; if (i === 0) ctx.moveTo(X, Y); else ctx.lineTo(X, Y); });
    ctx.stroke();
    ctx.restore();
  }

  function handleMouseMove(event) {
    if (!isDraggingRef.current) return;
    dragCurrentRef.current = getCanvasCoordinates(event);
    if (freehandRawRef.current) {
      const p = dragCurrentRef.current;
      freehandRawRef.current.push({ x: p.x, y: p.y });
      drawFreehandOverlay();
      return;
    }
    if (colDragForRef.current != null) { drawColBandOverlay(); return; }
    drawZoomOverlay();
  }

  function handleMouseUp(event) {
    if (event.button !== 0) return; // only respond to left button
    if (!isDraggingRef.current) return;

    dragCurrentRef.current = getCanvasCoordinates(event);
    isDraggingRef.current = false;

    // FREEHAND: resample the raw sweep by arc length into path vertices.
    if (freehandRawRef.current) {
      const raw = freehandRawRef.current;
      freehandRawRef.current = null;
      const endp = dragCurrentRef.current;
      if (endp) raw.push({ x: endp.x, y: endp.y });
      const K = Math.max(2, pathResolutionRef.current || 12);
      const verts = resampleByArcLength(raw, K).map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) }));
      if (verts.length < 2) { speakLabel("Sweep too short."); drawPathOverlay(pathVerticesRef.current); return; }
      setPathVertices(verts);
      drawPathOverlay(verts);
      speakLabel(`Freehand path, ${verts.length} points.`);
      return;
    }

    const start = dragStartRef.current;
    const end = dragCurrentRef.current;
    if (!start || !end) return;
    const cid = colDragForRef.current;
    if (cid != null) {
      const a = clamp(Math.round(Math.min(start.x, end.x)), 0, WIDTH - 1);
      const b = clamp(Math.round(Math.max(start.x, end.x)), 0, WIDTH - 1);
      dragStartRef.current = null; dragCurrentRef.current = null;
      setColDragFor(null);
      if (b - a < 2) {
        setContourSequence((seq) => seq.map((x) => x.id === cid ? { ...x, colMin: 0, colMax: WIDTH - 1 } : x));
        speakLabel("Column range cleared to full width.");
      } else {
        setContourSequence((seq) => seq.map((x) => x.id === cid ? { ...x, colMin: a, colMax: b } : x));
        speakLabel(`Columns ${a} to ${b} set.`);
      }
      if (canvasRef.current && fractalData) drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalData, playhead, orbitPath, scanOrientation, (showBoundariesRef.current ? movementsRef.current.map(m => m.startCol).filter(c => c > 0) : []));
      return;
    }

    const dx = Math.abs(end.x - start.x);
    const dy = Math.abs(end.y - start.y);

    if (dx < 5 && dy < 5) {
      // Treat as a click rather than a drag
      pendingSelectionRef.current = null;
      const pos = { x: end.x, y: end.y };

      // If a scan has already begun (playhead is set), treat the click as
      // either a pause-at-this-column gesture (if currently playing) or a
      // move-the-playhead gesture (if paused). Either way, no zoom — the
      // user's expectation once they've started a scan is that clicks
      // navigate within it. Until the first scan starts, the click falls
      // through to the existing single-click logic below (orbit play /
      // column preview).
      if (modeRef.current === "scan" && playhead !== null && playhead !== undefined) {
        const isVertical = scanOrientation !== "horizontal";
        // In horizontal mode the scan line is a ROW (y); in vertical, a COLUMN (x).
        const linePos = isVertical
          ? clamp(Math.floor(pos.x), 0, WIDTH - 1)
          : clamp(Math.floor(pos.y), 0, HEIGHT - 1);
        const lineWord = isVertical ? "column" : "row";
        setPlayhead(linePos);
        if (isPlayingRef.current) {
          stopPlayback();
          setStatus(`Paused at ${lineWord} ${linePos}. Resume here to continue.`);
          speak(`Paused at ${lineWord} ${linePos}.`);
        } else {
          setStatus(`Playhead at ${lineWord} ${linePos}. Press Resume here to play from this point.`);
          speak(`Playhead at ${lineWord} ${linePos}.`);
          if (canvasRef.current && fractalDataRef.current) {
            drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalDataRef.current, linePos, [], scanOrientation, (showBoundariesRef.current ? movementsRef.current.map(m => m.startCol).filter(c => c > 0) : []));
          }
        }
        dragStartRef.current = null;
        dragCurrentRef.current = null;
        return;
      }

      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        if (modeRef.current === "orbit") {
          const { cx, cy } = screenToComplex(pos.x, pos.y);
          playOrbit(cx, cy);
        } else if (soundModeRef.current === "contour" || soundModeRef.current === "layered") {
          selectContourAt(pos.x, pos.y);
        } else {
          const column = clamp(Math.floor(pos.x), 0, WIDTH - 1);
          previewColumn(column);
        }
      }, 220);
      dragStartRef.current = null;
      dragCurrentRef.current = null;
      return;
    }

    // Compute the aspect-corrected selection rectangle and store it as pending.
    // The zoom is NOT committed yet — the user must right-click to confirm.
    let x1 = start.x, y1 = start.y;
    let x2 = end.x,   y2 = end.y;

    const dxRaw = x2 - x1;
    const dyRaw = y2 - y1;
    const aspect = WIDTH / HEIGHT;
    let w = Math.abs(dxRaw);
    let h = Math.abs(dyRaw);

    if (w / h > aspect) { h = w / aspect; }
    else                 { w = h * aspect; }

    x2 = x1 + Math.sign(dxRaw || 1) * w;
    y2 = y1 + Math.sign(dyRaw || 1) * h;

    const left   = Math.min(x1, x2);
    const right  = Math.max(x1, x2);
    const top    = Math.min(y1, y2);
    const bottom = Math.max(y1, y2);

    // Store as pending — drawZoomOverlay will keep drawing the rectangle
    pendingSelectionRef.current = { left, right, top, bottom };

    // Keep drawing the selection rectangle so the user can see what they drew
    drawZoomOverlay();

    dragStartRef.current = null;
    dragCurrentRef.current = null;

    setStatus("Right-click inside the selection to zoom, or drag again to redraw.");
  }

  function handleMouseLeave() {
    if (!isDraggingRef.current) return;
    // The user moved the mouse off the canvas while dragging.
    // Clear the in-progress drag but leave any pending selection intact —
    // they might right-click back onto the canvas to commit it.
    isDraggingRef.current = false;
    dragStartRef.current = null;
    dragCurrentRef.current = null;
    // Redraw to show the pending selection (if any) or just the clean fractal
    if (canvasRef.current && fractalDataRef.current) {
      drawZoomOverlay();
    }
  }

  // Save the current fractal as a native .img file compatible with the original
  // Java app and importable by other users of this app.
  //
  // Two cases handled:
  //   1. Formula-computed fractal (no imported raster): use fractalData.rawIndex
  //      as the pixel data and current state for all parameters.
  //   2. Imported .img file: use the original importedRaster and importedImgMeta
  //      so the file round-trips perfectly back to its original form.
  function saveImg() {
    const d = new Date();
    const ts = d.getFullYear() + "-" +
      String(d.getMonth() + 1).padStart(2, "0") + "-" +
      String(d.getDate()).padStart(2, "0") + "-" +
      String(d.getHours()).padStart(2, "0") + "-" +
      String(d.getMinutes()).padStart(2, "0") + "-" +
      String(d.getSeconds()).padStart(2, "0");
    const filename = `fractal-${ts}.img`;

    let raster, params;

    if (importedRaster && importedImgMeta) {
      raster = importedRaster;
      params = {
        fractalType,
        maxIter, view, juliaC,
        paletteId:   importedImgMeta.paletteId   || "0",
        xfactorText: importedImgMeta.xfactorText || "1",
        yfactorText: importedImgMeta.yfactorText || "1",
        drawingMode: importedImgMeta.drawingMode || 0,
      };
    } else {
      if (!fractalData?.rawIndex) {
        setStatus("No fractal data to save yet — render first.");
        voiceSpeak("No fractal data to save yet.", userLanguageRef.current);
        return;
      }
      raster = fractalData.rawIndex;
      params = {
        fractalType, maxIter, view, juliaC,
        paletteId:   paletteFilename !== "none loaded" ? paletteFilename.replace(/^palette\./, "") : "0",
        xfactorText: "1", yfactorText: "1", drawingMode: 0,
      };
    }

    const buf  = writeNativeImg(raster, WIDTH, HEIGHT, params);
    const blob = new Blob([buf], { type: "application/octet-stream" });

    // Always use the anchor-tag download path (works for voice/keyboard
    // activation, where showSaveFilePicker would fail without a real
    // user-gesture event).  File lands in the browser's downloads folder.
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    setStatus(`Saved ${filename}`);
    voiceSpeak(`Saved as ${filename}.`, userLanguageRef.current);
  }
  // Save the current composition as a .fmc.json file (schema v2 — loudness model).
  // Gathers the current fractal, color palette, key, timing, and loudness palette
  // into one implicit movement (movements/Stage 3 will replace this with the real
  // movement list). Downloads via the same anchor mechanism as saveImg.
  function saveComposition() {
    const d = new Date();
    const ts = d.getFullYear() + "-" +
      String(d.getMonth() + 1).padStart(2, "0") + "-" +
      String(d.getDate()).padStart(2, "0") + "-" +
      String(d.getHours()).padStart(2, "0") + "-" +
      String(d.getMinutes()).padStart(2, "0") + "-" +
      String(d.getSeconds()).padStart(2, "0");
    const title = (paletteName && paletteName !== "none loaded") ? paletteName : "composition";
    const scanExtent = (scanOrientation === "vertical" ? WIDTH : HEIGHT) - 1;
    const composition = {
      format: "fractal-music-composition",
      version: 2,
      title,
      createdAt: d.toISOString(),
      fractal: {
        fractalType,
        juliaC: (fractalType === "julia") ? juliaC : null,
        view,
        maxIter,
      },
      colorPalette: {
        name: paletteName || null,
        stops: null,
        usePaletteBank,
        paletteBankRef: usePaletteBank ? (paletteName || null) : null,
      },
      orientation: scanOrientation,
      scan: { start: 0, end: scanExtent },
      playhead: null, // transient playback state — never persisted with the piece
      globalKey: { root: musicKey.root, scale: musicKey.scale },
      soundMode: soundMode,
      // Path mode state. Previously none of this was persisted, so a drawn
      // path lived only as long as the tab.
      pathVertices: (pathVerticesRef.current || []).map((v) => ({ x: Math.round(v.x), y: Math.round(v.y) })),
      pathDrawMode: pathDrawMode,
      pathNotesPerSeg: pathNotesPerSeg,
      pathRate: pathRate,
      pathResolution: pathResolution,
      pathInstrument: pathInstrument,
      pathPitchFit: pathPitchFit,
      pathLengths: pathLengths,
      pathRepeats: pathRepeats,
      pathTranspose: pathTranspose,
      pathTransposeStep: pathTransposeStep,
      pathPause: pathPause,
      pathPasses: pathPasses,
      pathActiveVoice: pathActiveVoice,
      pathRateRatio: pathRateRatio,
      pathOther: {
        verts: ((pathOtherRef.current || {}).verts || []).map((v) => ({ x: Math.round(v.x), y: Math.round(v.y) })),
        lengths: (pathOtherRef.current || {}).lengths || [],
        instrument: (pathOtherRef.current || {}).instrument || "flute",
        notesPerSeg: (pathOtherRef.current || {}).notesPerSeg || 1,
      },
      harmonyMode: harmonyMode,
      bandSequence: bandSequence,
      bandSections: bandSections,
      contourSpeed: contourSpeed,
      contourGap: contourGap,
      contourPitchMode: contourPitchMode,
      tripletRate: tripletRate,
      tripletBpm: tripletBpm,
      contourPath: contourPathRef.current || null,
      contourSequence: contourSequenceRef.current || [],
      soundStops: soundStops,
      maxLoud: 100,
      timing: { mode: "proportional", mergeBelow: 1, baseNoteMs: lineDrawMs, heightFactor: 1 },
      loudnessPalette: loudnessPalette,
      movements: movements.map((m) => ({
        startCol: m.startCol,
        endCol: m.endCol,
        tempoMs: m.tempoMs,
        endRestBeats: m.endRestBeats || 0,
      })),
    };
    const filename = `${title}-${ts}.fmc.json`;
    const blob = new Blob([JSON.stringify(composition, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    setStatus(`Saved composition ${filename}`);
    voiceSpeak(`Saved composition as ${filename}.`, userLanguageRef.current);
  }

  // Save the current canvas as a PNG file.
  // Apply the free-tier watermark to an offscreen canvas copy.
  // Returns either the original canvas (paid tiers) or a watermarked copy.
  function applyWatermarkIfFree(srcCanvas) {
    if (userTier !== TIERS.FREE) return srcCanvas;
    const w = srcCanvas.width;
    const h = srcCanvas.height;
    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const octx = off.getContext("2d");
    octx.drawImage(srcCanvas, 0, 0);

    // Font size scales with canvas size: ~1.6% of canvas height, min 12px.
    const fontPx = Math.max(12, Math.round(h * 0.016));
    const pad = Math.round(fontPx * 0.9);
    const text = "The Fractals Artist Toolkit \u00b7 fractal-music.web.app";

    octx.font = `${fontPx}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
    octx.textBaseline = "bottom";
    octx.textAlign = "right";
    octx.shadowColor = "rgba(0, 0, 0, 0.85)";
    octx.shadowBlur = Math.max(2, Math.round(fontPx * 0.25));
    octx.shadowOffsetX = 1;
    octx.shadowOffsetY = 1;
    octx.fillStyle = "rgba(255, 255, 255, 0.78)";
    octx.fillText(text, w - pad, h - pad);

    return off;
  }

  // Build a useful PNG filename from the current fractal spec.
  function buildPngFilename(spec, size) {
    const parts = ["fractal"];
    parts.push(spec.fractalType);
    if (spec.fractalType === "julia") {
      parts.push(`c${spec.juliaC.re.toFixed(3)}_${spec.juliaC.im.toFixed(3)}`);
    }
    parts.push(`x${spec.view.xMin.toFixed(4)}_${spec.view.xMax.toFixed(4)}`);
    parts.push(`y${spec.view.yMin.toFixed(4)}_${spec.view.yMax.toFixed(4)}`);
    parts.push(`iter${spec.maxIter}`);
    if (!size.isCanvas) {
      parts.push(`${size.width}x${size.height}`);
    }
    return parts.join("_") + ".png";
  }

  async function saveImage(format, sizeOverride) {
    if (pngExporting) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // The resolution to export at: an explicit override (used by the Save as PNG
    // menu, which picks a size and saves in one action) or the current state.const capability = capabilityForSize
    const size = sizeOverride || pngExportSize;

    // Tier gate.  The selected resolution must be available to this user;
    // otherwise show the same alert pattern as the TIFF path.
    const cap = capabilityForPngSize(size);
    logExport(cap);
    if (!can(cap, userTier)) {
      const required = tierRequiredFor(cap);
      const tierName = required === TIERS.STUDIO ? "Studio" : "Artist";
      if (typeof speakLabel === "function") {
        speakLabel(`PNG save at ${size.label} requires the ${tierName} tier.`);
      }
      alert(`Saving PNG at ${size.label} requires the ${tierName} tier.`);
      return;
    }

    // ── Path A: Canvas-resolution export (fast, free tier default) ──────────
    // No re-render needed.  Watermark if free, then encode and download.
    if (size.isCanvas) {
      const mimeType = format === "jpeg" ? "image/jpeg" : "image/png";
      const quality = format === "jpeg" ? 0.92 : undefined;
      const d = new Date();
      const ts = d.getFullYear() + "-" +
        String(d.getMonth() + 1).padStart(2, "0") + "-" +
        String(d.getDate()).padStart(2, "0") + "-" +
        String(d.getHours()).padStart(2, "0") + "-" +
        String(d.getMinutes()).padStart(2, "0") + "-" +
        String(d.getSeconds()).padStart(2, "0");
      const filename = `fractal-${ts}.${format}`;

      const sourceCanvas = applyWatermarkIfFree(canvas);

      sourceCanvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.download = filename; a.href = url; a.click();
        URL.revokeObjectURL(url);
        setStatus(`Saved ${filename}`);
        voiceSpeak(`Saved as ${filename}.`, userLanguageRef.current);
      }, mimeType, quality);
      return;
    }

    // ── Path B: High-resolution PNG export ───────────────────────────────────
    // Re-render the fractal at the target resolution via renderFormulaFractal,
    // draw to an offscreen canvas, then encode and download.  This mirrors
    // the TIFF export path; the only difference is the encoder.
    setPngExporting(true);

    // Snapshot the current spec so it cannot change mid-render.
    const spec = {
      view: { ...view },
      maxIter,
      fractalType,
      juliaC: { ...juliaC },
      paletteName,
      palettes,
      usePaletteBank,
      fullColors,
    };

    const filename = buildPngFilename(spec, size);

    try {
      const w = size.width;
      const h = size.height;

      // Yield to the event loop so the "exporting" UI can paint.
      await new Promise((r) => setTimeout(r, 0));

      const result = renderFormulaFractal(
        w, h, spec.maxIter, spec.view, spec.fractalType,
        spec.juliaC, spec.paletteName, spec.palettes,
        spec.usePaletteBank, spec.fullColors
      );

      // Draw the RGB color buffer into an offscreen canvas.
      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const octx = off.getContext("2d");
      const imageData = octx.createImageData(w, h);
      const dst = imageData.data;
      const src = result.colors;
      // result.colors is Uint8ClampedArray of width*height*3 (RGB triplets);
      // imageData expects width*height*4 (RGBA).
      for (let i = 0, j = 0; i < src.length; i += 3, j += 4) {
        dst[j]     = src[i];
        dst[j + 1] = src[i + 1];
        dst[j + 2] = src[i + 2];
        dst[j + 3] = 255;
      }
      octx.putImageData(imageData, 0, 0);

      // Apply watermark if free (won't normally fire here since hi-res is
      // paid-tier, but keeps the rule consistent if tier somehow downgrades
      // mid-export).
      const finalCanvas = applyWatermarkIfFree(off);

      const mimeType = format === "jpeg" ? "image/jpeg" : "image/png";
      const quality = format === "jpeg" ? 0.92 : undefined;

      await new Promise((resolve, reject) => {
        finalCanvas.toBlob((blob) => {
          if (!blob) { reject(new Error("encode failed")); return; }
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.download = filename; a.href = url; a.click();
          URL.revokeObjectURL(url);
          resolve();
        }, mimeType, quality);
      });

      setStatus(`Saved ${filename}`);
      voiceSpeak(`Saved as ${filename}.`, userLanguageRef.current);
    } catch (err) {
      console.error("PNG export failed:", err);
      alert(`PNG export failed: ${err.message}`);
    } finally {
      setPngExporting(false);
    }
  }
  
  // eslint-disable-next-line
  function zoomView(factor, centreX, centreY) {
    // factor < 1 = zoom in, factor > 1 = zoom out
    const v = viewRef.current;
    const cx = v.xMin + (centreX / WIDTH) * (v.xMax - v.xMin);
    const cy = v.yMax - (centreY / HEIGHT) * (v.yMax - v.yMin);
    const newW = (v.xMax - v.xMin) * factor;
    const newH = (v.yMax - v.yMin) * factor;
    const fxRatio = centreX / WIDTH;
    const fyRatio = centreY / HEIGHT;
    const nv = {
      xMin: cx - fxRatio * newW,
      xMax: cx - fxRatio * newW + newW,
      yMin: cy + fyRatio * newH - newH,
      yMax: cy + fyRatio * newH,
    };
    stopPlayback();
    setPlayhead(null);
    setSelectedColumn(null);
    clearImportedRaster();
    setView(nv);
    setInputXMin(String(nv.xMin));
    setInputXMax(String(nv.xMax));
    setInputYMin(String(nv.yMin));
    setInputYMax(String(nv.yMax));
  }

  // Expand a complex-plane rectangle so its aspect ratio matches the canvas's
  // (WIDTH/HEIGHT = 1.5 for 750x500). Keeps the rectangle's center fixed and
  // grows it on whichever axis is too short. This prevents the rendered image
  // from being stretched/squashed when the source view's aspect doesn't equal
  // the canvas aspect — which can happen if the user manually edits xMin/xMax/
  // yMin/yMax to non-matching values, or if a preset has a slightly off aspect.
  function expandViewToCanvasAspect(xMin, xMax, yMin, yMax) {
    const canvasAspect = WIDTH / HEIGHT;
    const w = xMax - xMin;
    const h = yMax - yMin;
    const cx = (xMin + xMax) / 2;
    const cy = (yMin + yMax) / 2;
    let newW = w, newH = h;
    if (w / h > canvasAspect) {
      // Source rectangle is too wide for canvas — grow height to match.
      newH = w / canvasAspect;
    } else {
      // Source rectangle is too tall (or matches) — grow width to match.
      newW = h * canvasAspect;
    }
    return {
      xMin: cx - newW / 2,
      xMax: cx + newW / 2,
      yMin: cy - newH / 2,
      yMax: cy + newH / 2,
    };
  }

  // Zoom to one cell of the 15x10 overlay grid. Mirrors the math in
  // handleContextMenu's selection-zoom: convert canvas-pixel bounds of the
  // chosen cell into complex-plane bounds, then commit the new view.
  // Cells are numbered 1..150 left-to-right, top-to-bottom (col = (n-1)%15,
  // row = floor((n-1)/15)). 15x10 chosen so cells are exact integer pixels
  // (50x50 on a 750x500 canvas) AND match the canvas aspect ratio (3:2),
  // so zoomed views inherit the canvas aspect and don't appear squashed.
  function zoomToGridCell(cellNumber) {
    const GRID_COLS = 15;
    const GRID_ROWS = 10;
    if (cellNumber < 1 || cellNumber > GRID_COLS * GRID_ROWS) return;
    const idx = cellNumber - 1;
    const col = idx % GRID_COLS;
    const row = Math.floor(idx / GRID_COLS);
    const xLeft   = (col       / GRID_COLS) * WIDTH;
    const xRight  = ((col + 1) / GRID_COLS) * WIDTH;
    const yTop    = (row       / GRID_ROWS) * HEIGHT;
    const yBottom = ((row + 1) / GRID_ROWS) * HEIGHT;
    const v = viewRef.current;
    const rawXMin = v.xMin + (xLeft   / WIDTH)  * (v.xMax - v.xMin);
    const rawXMax = v.xMin + (xRight  / WIDTH)  * (v.xMax - v.xMin);
    const rawYMax = v.yMax - (yTop    / HEIGHT) * (v.yMax - v.yMin);
    const rawYMin = v.yMax - (yBottom / HEIGHT) * (v.yMax - v.yMin);
    // Expand to canvas aspect so the rendered image isn't squashed when the
    // source view doesn't match canvas aspect (e.g., a user-edited view).
    const newView = expandViewToCanvasAspect(rawXMin, rawXMax, rawYMin, rawYMax);
    pendingSelectionRef.current = null;
    clearImportedRaster();
    stopPlayback();
    setPlayhead(null);
    setSelectedColumn(null);
    setView(newView);
    setInputXMin(String(newView.xMin));
    setInputXMax(String(newView.xMax));
    setInputYMin(String(newView.yMin));
    setInputYMax(String(newView.yMax));
    setStatus(`Zoomed to grid cell ${cellNumber}`);
    speak(`Zoomed to grid cell ${cellNumber}.`);
    pushHistory(newView, fractalType, maxIter, juliaC);
  }

  // Handle activation (click or Enter) on a grid cell: always zoom to that
  // cell. zoomToGridCell sets playhead to null, so the next Play full scan
  // begins from column 0 of the new zoomed view — which matches the
  // expectation that pressing Enter on a cell takes you into that region
  // ready to play from the beginning. The earlier behavior of moving the
  // playhead in place (without zooming) when a scan had already begun was
  // removed because it surprised users who expected Enter to always zoom.
  // The "move playhead, don't zoom" gesture is still available via voice
  // ("jump to N percent") and via clicking on the canvas during scan mode.
  function handleCellActivate(cellNumber, col) {
    zoomToGridCell(cellNumber);
  }

  function handleContextMenu(e) {
    e.preventDefault();

    const pos = getCanvasCoordinates(e);
    // ── BAND MODE: right-click adds a region (its escape value / band) to the
    // sequence; Ctrl-right-click removes it. No zoom in band mode. ──
    // Defer to zoom only when the right-click lands INSIDE a pending selection
    // rectangle; otherwise a bare right-click adds a band region.
    const _psel = pendingSelectionRef.current;
    const _insideSel = !!_psel && pos.x >= _psel.left && pos.x <= _psel.right && pos.y >= _psel.top && pos.y <= _psel.bottom;
    // CONTOUR MODE: right-click adds the contour at the cursor to the sequence.
    if ((soundModeRef.current === "contour" || soundModeRef.current === "layered") && !_insideSel) {
      addContourAt(pos.x, pos.y);
      return;
    }
    if (soundModeRef.current === "band" && !_insideSel) {
      const d = fractalDataRef.current;
      if (!d?.rawIndex || !d?.interior) { speakLabel("Compute a fractal first."); return; }
      const px = Math.max(0, Math.min(WIDTH - 1, Math.floor(pos.x)));
      const py = Math.max(0, Math.min(HEIGHT - 1, Math.floor(pos.y)));
      const idx = py * WIDTH + px;
      if (d.interior[idx] === 1) { speakLabel("That point is inside the set — no band there."); return; }
      const escapeValue = d.rawIndex[idx];
      // Decide + speak ONCE here (outside the state updater), then update state
      // with a pure updater — React Strict Mode double-invokes updaters, so any
      // speech inside them would be spoken twice.
      const curSeq = bandSequenceRef.current;
      if (e.ctrlKey) {
        const exists = curSeq.some((r) => r.escapeValue === escapeValue);
        if (exists) {
          speakLabel(`Removed band ${escapeValue}.`);
          setBandSequence((seq) => seq.filter((r) => r.escapeValue !== escapeValue));
        }
      } else {
        const exists = curSeq.some((r) => r.escapeValue === escapeValue);
        if (exists) {
          speakLabel(`Band ${escapeValue} is already in the sequence.`);
        } else {
          speakLabel(`Added band ${escapeValue} as region ${curSeq.length + 1}.`);
          setBandSequence((seq) => [...seq, {
            id: Date.now() + Math.random(),
            escapeValue,
            instrument: "flute",
            scaleDegree: 0,
            octaveOffset: 0,
            columnSpeedMs: 150,
            clickX: px, clickY: py,
          }]);
        }
      }
      return;
    }
    const sel = pendingSelectionRef.current;

    // If there's a pending selection and the right-click is inside it, commit the zoom.
    // If there's a pending selection but the click is outside it, cancel it.
    // If there's no pending selection, do nothing (right-click-to-zoom-out removed).
    if (sel) {
      const insideX = pos.x >= sel.left && pos.x <= sel.right;
      const insideY = pos.y >= sel.top  && pos.y <= sel.bottom;

      if (insideX && insideY) {
        // Commit: convert screen coordinates to complex-plane coordinates.
        // Expand to canvas aspect so the rendered image isn't squashed when
        // the user drew a selection rectangle that doesn't match canvas
        // proportions.
        const rawXMin = viewRef.current.xMin + (sel.left  / WIDTH)  * (viewRef.current.xMax - viewRef.current.xMin);
        const rawXMax = viewRef.current.xMin + (sel.right / WIDTH)  * (viewRef.current.xMax - viewRef.current.xMin);
        const rawYMax = viewRef.current.yMax - (sel.top   / HEIGHT) * (viewRef.current.yMax - viewRef.current.yMin);
        const rawYMin = viewRef.current.yMax - (sel.bottom/ HEIGHT) * (viewRef.current.yMax - viewRef.current.yMin);
        const newView = expandViewToCanvasAspect(rawXMin, rawXMax, rawYMin, rawYMax);

        pendingSelectionRef.current = null;
        clearImportedRaster();
        stopPlayback();
        setPlayhead(null);
        setSelectedColumn(null);
        setView(newView);
        setInputXMin(String(newView.xMin));
        setInputXMax(String(newView.xMax));
        setInputYMin(String(newView.yMin));
        setInputYMax(String(newView.yMax));
        setStatus("Zoom complete");
        speak("Zoom complete.");
        pushHistory(newView, fractalType, maxIter, juliaC);
      } else {
        // Right-click outside selection cancels it
        pendingSelectionRef.current = null;
        if (canvasRef.current && fractalDataRef.current) {
          drawFractal(canvasRef.current, WIDTH, HEIGHT, fractalDataRef.current, playhead, orbitPath, scanOrientation, (showBoundariesRef.current ? movementsRef.current.map(m => m.startCol).filter(c => c > 0) : []));
        }
        setStatus("Selection cancelled.");
      }
    }
  }

  // Push the current view onto the history stack, truncating any forward history.
  // raster and imgMeta are optional — only set when pushing an imported .img entry.
  // Commit the four editable bound fields as a new view. Validates (all finite,
  // xMax>xMin, yMax>yMin); on success routes through the SAME path the wheel-zoom
  // handler uses (setView + sync inputs + pushHistory + stop playback + clear
  // playhead), so it recomputes and enters undo history like a zoom.
  function commitBoundsFromInputs() {
    // Tolerant parse: normalize Unicode minus/dashes to ASCII, strip spaces,
    // accept comma decimals. Copy-pasted coordinates often carry a U+2212
    // minus (which parseFloat rejects as NaN); this fixes that.
    const num = (str) => {
      if (typeof str !== "string") return NaN;
      let t = str.trim().replace(/[\u2212\u2013\u2014\u2010\u2011]/g, "-");
      // If there's a comma but no dot, treat comma as the decimal point.
      if (t.indexOf(",") !== -1 && t.indexOf(".") === -1) t = t.replace(",", ".");
      return parseFloat(t);
    };
    const xmin = num(inputXMin), xmax = num(inputXMax);
    const ymin = num(inputYMin), ymax = num(inputYMax);
    if (![xmin, xmax, ymin, ymax].every(Number.isFinite)) {
      speakLabel("Please enter four numeric coordinates."); return;
    }
    if (xmax <= xmin) { speakLabel("x maximum must be greater than x minimum."); return; }
    if (ymax <= ymin) { speakLabel("y maximum must be greater than y minimum."); return; }
    const nv = { xMin: xmin, xMax: xmax, yMin: ymin, yMax: ymax };
    stopPlaybackRef.current();
    setView(nv);
    setInputXMin(String(nv.xMin)); setInputXMax(String(nv.xMax));
    setInputYMin(String(nv.yMin)); setInputYMax(String(nv.yMax));
    pushHistory(nv, fractalType, maxIter, juliaC);
    setPlayhead(null); setSelectedColumn(null);
    const ratio = (nv.xMax - nv.xMin) / (nv.yMax - nv.yMin);
    const warn = Math.abs(ratio - 1.5) > 0.03 ? " Note: aspect ratio is not 3 to 2, so the image may look stretched." : "";
    speakLabel("View updated." + warn);
  }
  function pushHistory(viewSnapshot, fractalTypeSnapshot, maxIterSnapshot, juliaCSnapshot) {
    const entry = {
      view:        viewSnapshot,
      fractalType: fractalTypeSnapshot,
      maxIter:     maxIterSnapshot,
      juliaC:      juliaCSnapshot,
    };
    setNav(prev => {
      const truncated = prev.history.slice(0, Math.max(0, prev.historyIndex) + 1);
      const top = truncated[truncated.length - 1];
      if (top &&
          top.fractalType === entry.fractalType &&
          top.maxIter     === entry.maxIter &&
          top.juliaC.re   === entry.juliaC.re &&
          top.juliaC.im   === entry.juliaC.im &&
          top.view.xMin   === entry.view.xMin &&
          top.view.xMax   === entry.view.xMax &&
          top.view.yMin   === entry.view.yMin &&
          top.view.yMax   === entry.view.yMax) {
        return prev; // already at this view, don't duplicate
      }
      return {
        history:      [...truncated, entry],
        historyIndex: truncated.length,
      };
    });
  }

  function resetHistory(v, ft, mi, jc) {
    setNav({
      history:      [{ view: v, fractalType: ft, maxIter: mi, juliaC: jc }],
      historyIndex: 0,
    });
  }

  function navigateHistory(idx) {
    const entry = nav.history[idx];
    if (!entry || idx < 0 || idx >= nav.history.length) return;
    stopPlayback();
    setPlayhead(null);
    setSelectedColumn(null);
    setView(entry.view);
    setFractalType(entry.fractalType);
    setMaxIter(entry.maxIter);
    setJuliaC(entry.juliaC);
    setInputFractalType(entry.fractalType);
    setInputXMin(String(entry.view.xMin));
    setInputXMax(String(entry.view.xMax));
    setInputYMin(String(entry.view.yMin));
    setInputYMax(String(entry.view.yMax));
    setInputMaxIter(String(entry.maxIter));
    setInputJuliaRe(String(entry.juliaC.re));
    setInputJuliaIm(String(entry.juliaC.im));
    setImportedRaster(null);
    setImportedImgMeta(null);
    setNav(prev => ({ ...prev, historyIndex: idx }));
  }

  function applyFractalSpec() {
    clearImportedRaster();

    const newView = {
      xMin: parseNumber(inputXMin, view.xMin),
      xMax: parseNumber(inputXMax, view.xMax),
      yMin: parseNumber(inputYMin, view.yMin),
      yMax: parseNumber(inputYMax, view.yMax),
    };

    const newMaxIter = clamp(Math.floor(parseNumber(inputMaxIter, maxIter)), 10, MAXCOLRS - 1);

    const newJulia = {
      re: parseNumber(inputJuliaRe, juliaC.re),
      im: parseNumber(inputJuliaIm, juliaC.im),
    };

    stopPlayback();
    setPlayhead(null);
    setSelectedColumn(null);
    setFractalType(inputFractalType);
    setView(newView);
    setMaxIter(newMaxIter);
    setJuliaC(newJulia);
    setStatus("Fractal rendered");
    speak("Fractal rendered.");
    resetHistory(newView, inputFractalType, newMaxIter, newJulia);
  }

  function loadPreset() {
    clearImportedRaster();

    const preset = presets[presetKey];
    if (!preset) return;

    if (bandSequenceRef.current.length) { setBandSequence([]); setStatus("Regions cleared for the new image."); }
    setInputFractalType(preset.fractalType);
    setInputXMin(String(preset.view.xMin));
    setInputXMax(String(preset.view.xMax));
    setInputYMin(String(preset.view.yMin));
    setInputYMax(String(preset.view.yMax));
    setInputMaxIter(String(preset.maxIter));
    setInputJuliaRe(String(preset.juliaC.re));
    setInputJuliaIm(String(preset.juliaC.im));

    stopPlayback();
    setPlayhead(null);
    setSelectedColumn(null);
    setFractalType(preset.fractalType);
    setView(preset.view);
    setMaxIter(preset.maxIter);
    setJuliaC(preset.juliaC);
    setStatus(`Loaded preset: ${preset.label}`);
    speak("Preset loaded.");
    if (presetKey in DEFAULT_PRESETS) {
      resetHistory(preset.view, preset.fractalType, preset.maxIter, preset.juliaC);
    }
  }

  function resetToCurrentSpec() {
    stopPlayback();
    setPlayhead(null);
    setSelectedColumn(null);

    setInputFractalType(fractalType);
    setInputXMin(String(view.xMin));
    setInputXMax(String(view.xMax));
    setInputYMin(String(view.yMin));
    setInputYMax(String(view.yMax));
    setInputMaxIter(String(maxIter));
    setInputJuliaRe(String(juliaC.re));
    setInputJuliaIm(String(juliaC.im));

    setStatus("Form reset to current fractal");
  }

  const selectedStats = selectedColumn == null ? null : fractalData.columnStats[selectedColumn];

  const sectionStyle = {
    background: "#111827",
    border: "1px solid #1f2937",
    borderRadius: "16px",
    padding: "18px",
    boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
  };

  const buttonStyle = {
    padding: "10px 16px",
    borderRadius: "10px",
    border: "1px solid #374151",
    background: "#e5e7eb",
    color: "#111827",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: 600,
  };

  const darkButtonStyle = {
    ...buttonStyle,
    background: "#1f2937",
    color: "#f9fafb",
  };

  const accentButtonStyle = {
    ...buttonStyle,
    background: "#38bdf8",
    color: "#082f49",
    border: "1px solid #38bdf8",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: "8px",
    border: "1px solid #334155",
    background: "#020617",
    color: "#f8fafc",
    boxSizing: "border-box",
  };

  // ── Instrumental-only fields (Key, Scale, Harmony) ──────────────────────────
  // These have always been `disabled` when Sound is Simple sine waves, but the
  // dark palette renders a disabled <select> almost identically to an enabled
  // one — so the field simply refused to respond, with no visible reason. Dim
  // the control AND its label together, and state the reason in words. (The
  // spoken counterpart is on the Sound select's handlers, since a disabled
  // control cannot take focus and so can never announce itself.)
  const instrumentalOn = soundMode === "instrumental";
  // Modes whose pitches go through realize(), and therefore depend on Key and
  // Scale: the instrumental scan, layered mode's flute triplets, and drawn
  // paths. Harmony stays gated to the instrumental scan alone.
  const keyApplies = soundMode === "instrumental" || soundMode === "layered" || soundMode === "path";
  const musicFieldStyle = keyApplies ? inputStyle : {
    ...inputStyle,
    background: "#0b1120",
    border: "1px solid #1e293b",
    color: "#475569",
    cursor: "not-allowed",
  };
  const musicRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: keyApplies ? "#cbd5e1" : "#475569",
    fontSize: "14px",
  };

  // Used for view-coordinate displays (xMin/xMax/yMin/yMax) — visually softer
  // than inputStyle so they read as informational rather than editable. The
  // muted background, dotted underline-equivalent border, default cursor, and
  // caret-color: transparent collectively signal "this is a display, not a
  // field you forgot to fill in."
  const readOnlyInputStyle = {
    ...{
      width: "100%",
      padding: "8px 10px",
      borderRadius: "8px",
      boxSizing: "border-box",
    },
    border: "1px dashed #1e293b",
    background: "transparent",
    color: "#94a3b8",
    cursor: "default",
    caretColor: "transparent",
    fontVariantNumeric: "tabular-nums",
  };

  const labelStyle = {
    fontSize: "12px",
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    marginBottom: "6px",
    display: "block",
  };

  return (
    <div
      className={kioskMode ? "kiosk" : ""}
      style={{
        minHeight: "100vh",
        background: "#020617",
        color: "#f8fafc",
        fontFamily: "Arial, Helvetica, sans-serif",
        padding: "24px",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        button:focus, input:focus, select:focus, textarea:focus, [tabindex]:focus {
          outline: 3px solid #38bdf8;
          outline-offset: 2px;
        }
        /* ── Kiosk mode for the Ambient Player ────────────────────────────── */
        /* When the Toolkit is embedded in the Player, hide all UI except the */
        /* canvas. The Player provides its own overlay controls.              */
        .kiosk { padding: 0 !important; }
        .kiosk > div > *:not(.kiosk-canvas-section) { display: none !important; }
        .kiosk .kiosk-canvas-section {
          display: flex !important;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 0;
          margin: 0;
          background: #000 !important;
          border: none !important;
          box-shadow: none !important;
          border-radius: 0 !important;
        }
        /* Hide everything inside the canvas section EXCEPT the canvas's
           direct container (first child) — removes instruction text + buttons. */
        .kiosk .kiosk-canvas-section > *:not(:first-child) {
          display: none !important;
        }
        /* Canvas container: fill viewport, center the canvas inside. */
        .kiosk .kiosk-canvas-section > div:first-child {
          width: 100vw;
          height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        /* The Toolkit's canvas is 750×500 (3:2). aspect-ratio + max bounds
           letterbox the canvas to preserve its native ratio on any viewport. */
        .kiosk #fractal-canvas {
          width: auto !important;
          height: auto !important;
          max-width: 100vw;
          max-height: 100vh;
          aspect-ratio: 750 / 500;
          border-radius: 0 !important;
          border: none !important;
        }
      `}</style>
      {eyeCalibrating && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:99998 }}>
          <div style={{ position:'absolute', top:'5%', left:'50%', transform:'translateX(-50%)', color:'white', fontSize:'20px', textAlign:'center', maxWidth:'80%' }}>
            Look at each dot and click it 5 times to calibrate eye tracking
          </div>
          {[
            { x:'10%', y:'18%' }, { x:'50%', y:'18%' }, { x:'90%', y:'18%' },
            { x:'10%', y:'50%' }, { x:'50%', y:'50%' }, { x:'90%', y:'50%' },
            { x:'10%', y:'82%' }, { x:'50%', y:'82%' }, { x:'90%', y:'82%' },
          ].map((d, i) => {
            const c = eyeCalibClicks[i] || 0;
            const done = c >= 5;
            return (
              <button
                key={i}
                onClick={() => {
                  setEyeCalibClicks(prev => {
                    const next = { ...prev, [i]: (prev[i] || 0) + 1 };
                    if (Object.values(next).filter(v => v >= 5).length === 9) {
                      eyeCalibrationDoneRef.current?.();
                      eyeCalibrationDoneRef.current = null;
                    }
                    return next;
                  });
                }}
                aria-label={`Calibration dot ${i + 1}, ${c} of 5 clicks`}
                style={{
                  position:'absolute', left:d.x, top:d.y, transform:'translate(-50%,-50%)',
                  width:'48px', height:'48px', borderRadius:'50%', cursor:'pointer',
                  border:'2px solid white',
                  background: done ? '#22c55e' : `rgba(59,130,246,${0.25 + c * 0.15})`,
                  color:'white', fontSize:'14px', fontWeight:'bold',
                }}
              >
                {done ? '✓' : c}
              </button>
            );
          })}
        </div>
      )}
      {/* ── Import modal ─────────────────────────────────────────────────── */}
      {importModal && (
        <ImportModal
          type={importModal}
          cachedNames={cachedPaletteNames}
          onClose={() => setImportModal(null)}
            onSpeak={(name) => {
              window.speechSynthesis.cancel();
              if (!spokenLabelsOn()) return;
              const utt = new SpeechSynthesisUtterance(name);
              utt.lang = userLanguageRef.current || 'en-US';
              utt.rate = 0.95;
              window.speechSynthesis.speak(utt);
            }  
          }
          onLocalFile={() => {
            if (importModal === "palette") {
              document.getElementById("palette-import-input").click();
            } else {
              document.getElementById("img-import-input").click();
            }
          }}
          onImport={(buffer, filename) => {
            if (importModal === "palette") {
              importPaletteFromBuffer(buffer, filename);
            } else {
              importImgFromBuffer(buffer, filename);
            }
          }}
        />
      )}

      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {!accessibilitySetup.configured && (
          <div style={{
            background: "#0c4a6e", border: "1px solid #0369a1", borderRadius: "10px",
            padding: "12px 16px", marginBottom: "16px", color: "#e0f2fe",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px",
          }}>
            <div style={{ fontSize: "13px", lineHeight: 1.5 }}>
              Setting this up for a user who needs accessibility support?{" "}
              <button
                onClick={() => setShowSetup(true)}
                style={{ background: "transparent", border: "none", color: "#bae6fd", textDecoration: "underline", cursor: "pointer", padding: 0, font: "inherit" }}
              >
                Open accessibility setup
              </button>{" "}— or dismiss this for the regular experience.
            </div>
            <button
              onClick={() => setAccessibilitySetup(v => ({ ...v, configured: true }))}
              style={{ background: "#1e3a5f", border: "1px solid #1e40af", color: "#e0f2fe", padding: "4px 12px", borderRadius: "6px", cursor: "pointer", fontSize: "13px", whiteSpace: "nowrap" }}
              aria-label="Dismiss accessibility setup banner"
            >
              Dismiss
            </button>
          </div>
        )}
        {/* ── Auth widget (header right) ──────────────────────────────────── */}
        <AuthWidget />
        <UpgradePanel />

        {/* ── Title ──────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: "20px" }}>
          <h1 style={{ fontSize: "34px", margin: "0 0 10px 0", textAlign: "center" }}>
            The Fractals Artist Toolkit
          </h1>
          <p style={{ color: "#cbd5e1", fontSize: "15px", lineHeight: 1.5, margin: "0 auto", textAlign: "center", maxWidth: "860px" }}>
            The image below is the current fractal. Scroll down to access the controls.
          </p>
        </div>

        {/* ── Full-width canvas ──────────────────────────────────────────── */}
        <div className="kiosk-canvas-section" style={sectionStyle}>
          <div style={{ position: "relative" }}>
            {showBandLabels && soundMode === "band" && bandSequence.map((r, i) => (
              <div
                key={r.id}
                title={`Region ${i + 1}: band ${r.escapeValue}`}
                style={{
                  position: "absolute",
                  left: `${(r.clickX / WIDTH) * 100}%`,
                  top: `${(r.clickY / HEIGHT) * 100}%`,
                  transform: "translate(-50%, -50%)",
                  zIndex: 5,
                  background: "#0ea5e9",
                  color: "#082f49",
                  fontWeight: 700,
                  fontSize: "12px",
                  minWidth: "18px",
                  height: "18px",
                  lineHeight: "18px",
                  textAlign: "center",
                  borderRadius: "9px",
                  border: "1px solid #082f49",
                  padding: "0 4px",
                  pointerEvents: "none",
                }}
              >
                {i + 1}
              </div>
            ))}
            <canvas
              ref={canvasRef}
              id="fractal-canvas"
              aria-label="Fractal canvas"
              width={PHYS_WIDTH}
              height={PHYS_HEIGHT}
              tabIndex={0}
              onMouseDown={(e) => { canvasRef.current?.focus({ preventScroll: true }); handleMouseDown(e); }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onContextMenu={handleContextMenu}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                borderRadius: "12px",
                border: "1px solid #334155",
                cursor: "crosshair",
                background: "#000",
                outline: "none",
              }}
            />
            {showGrid && (
              <div
                aria-label="Grid overlay, 15 columns by 10 rows, 150 cells. Tab to navigate, Enter to zoom into a cell."
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  gridTemplateColumns: "repeat(15, 1fr)",
                  gridTemplateRows: "repeat(10, 1fr)",
                  // Container itself doesn't capture mouse so canvas drag still works;
                  // individual cells re-enable pointer events for keyboard focus.
                  pointerEvents: "none",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                {Array.from({ length: 150 }, (_, i) => {
                  const cellNumber = i + 1;
                  const row = Math.floor(i / 15) + 1;
                  const col = (i % 15) + 1;
                  return (
                    <div
                      key={cellNumber}
                      tabIndex={0}
                      role="button"
                      aria-label={`Grid cell ${cellNumber} of 150, row ${row} column ${col}. Press Enter to zoom, arrow keys to move.`}
                      onFocus={() => {
                        // Cell index is 0-based; cellNumber is 1-based.
                        const bearing = describeCellBearing(gridStats, cellNumber - 1, 15, 10, maxIter);
                        const positional = `Cell ${cellNumber}, row ${row} column ${col}`;
                        speakLabel(bearing ? `${positional}. ${bearing}` : positional);
                      }}
                      onKeyDown={(e) => {
                        const GRID_COLS = 15;
                        const GRID_ROWS = 10;
                        const TOTAL = GRID_COLS * GRID_ROWS;
                        let target = null;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleCellActivate(cellNumber, col);
                          return;
                        } else if (e.key === "ArrowRight") {
                          // Wrap: 150 -> 1
                          target = cellNumber === TOTAL ? 1 : cellNumber + 1;
                        } else if (e.key === "ArrowLeft") {
                          // Wrap: 1 -> 150
                          target = cellNumber === 1 ? TOTAL : cellNumber - 1;
                        } else if (e.key === "ArrowDown") {
                          // Wrap: bottom row -> top row, same column
                          target = row === GRID_ROWS ? col : cellNumber + GRID_COLS;
                        } else if (e.key === "ArrowUp") {
                          // Wrap: top row -> bottom row, same column
                          target = row === 1 ? (GRID_ROWS - 1) * GRID_COLS + col : cellNumber - GRID_COLS;
                        } else if (e.key === "Home") {
                          target = e.ctrlKey ? 1 : (row - 1) * GRID_COLS + 1;
                        } else if (e.key === "End") {
                          target = e.ctrlKey ? TOTAL : row * GRID_COLS;
                        } else {
                          return; // not a key we handle — let Tab/Esc/etc. bubble
                        }
                        if (target !== null) {
                          e.preventDefault();
                          // Siblings are in DOM order — index target-1 in parent.
                          const parent = e.currentTarget.parentElement;
                          const next = parent?.children?.[target - 1];
                          if (next) next.focus({ preventScroll: false });
                        }
                      }}
                      style={{
                        // Re-enable pointer events on cells so they can receive focus
                        // and click; the empty interior of the canvas still passes
                        // mouse events through because the cells are transparent
                        // overlays whose only job is to be focusable rectangles.
                        pointerEvents: "auto",
                        border: "1px solid rgba(148, 163, 184, 0.45)",
                        boxSizing: "border-box",
                        cursor: "pointer",
                        outline: "none",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(56, 189, 248, 0.18)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      onFocusCapture={(e) => {
                        e.currentTarget.style.background = "rgba(56, 189, 248, 0.32)";
                        e.currentTarget.style.borderColor = "rgba(56, 189, 248, 0.95)";
                      }}
                      onBlurCapture={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderColor = "rgba(148, 163, 184, 0.45)";
                      }}
                      onClick={() => handleCellActivate(cellNumber, col)}
                    />
                  );
                })}
              </div>
            )}
          </div>
          {soundMode === "band" && (
            <div style={{ ...sectionStyle, marginTop: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                <strong style={{ color: "#f8fafc", fontSize: "15px" }}>Regions — play order</strong>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ color: "#94a3b8", fontSize: "13px" }}>
                    Right-click a region on the image to add · Ctrl-right-click to remove
                  </span>
                  {bandSequence.length > 0 && (
                    <button
                      onClick={() => {
                        const n = bandSequenceRef.current.length;
                        if (window.confirm(`Clear all ${n} region${n === 1 ? "" : "s"}? This cannot be undone.`)) {
                          setBandSequence([]);
                          setStatus("All regions cleared.");
                          speak("All regions cleared.");
                        }
                      }}
                      onFocus={() => speakLabel("Clear all regions. Empties the region list so you can start a fresh composition.")}
                      style={{ background: "#7f1d1d", color: "#fecaca", border: "1px solid #991b1b", borderRadius: "8px", padding: "4px 10px", fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap" }}
                      data-description="Clear all regions. Empties the region list after a confirmation, so you can start a fresh band composition on a new image. Does not affect saved compositions."
                    >Clear all</button>
                  )}
                </div>
              </div>
              {bandSequence.length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: "13px", padding: "8px 0" }}>
                  No regions yet. Right-click a coloured region on the fractal to add it.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {bandSequence.map((r, i) => (
                    <div key={r.id} style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", background: "#0b1220", border: "1px solid #1f2937", borderRadius: "10px", padding: "8px 10px" }}>
                      <span style={{ background: "#0ea5e9", color: "#082f49", fontWeight: 700, minWidth: "20px", height: "20px", lineHeight: "20px", textAlign: "center", borderRadius: "10px", fontSize: "12px" }}>{i + 1}</span>
                      <span style={{ color: "#94a3b8", fontSize: "13px", minWidth: "70px" }}>band {r.escapeValue}</span>
                      <select
                        value={r.instrument}
                        onChange={(e) => setBandSequence((seq) => seq.map((x) => x.id === r.id ? { ...x, instrument: e.target.value } : x))}
                        style={{ ...inputStyle, width: "auto", minWidth: "150px", padding: "6px 8px", fontSize: "13px" }}
                        aria-label={`Region ${i + 1} instrument`}
                      >
                        {Object.entries(GM_INSTRUMENTS).map(([group, list]) => (
                          <optgroup key={group} label={group.replace(/_/g, " ")}>
                            {list.map((inst) => (
                              <option key={inst} value={inst}>{inst.replace(/_/g, " ")}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <label style={{ color: "#94a3b8", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                        deg
                        <select
                          value={r.scaleDegree}
                          onChange={(e) => setBandSequence((seq) => seq.map((x) => x.id === r.id ? { ...x, scaleDegree: parseInt(e.target.value, 10) } : x))}
                          style={{ ...inputStyle, width: "auto", padding: "6px 8px", fontSize: "13px" }}
                          aria-label={`Region ${i + 1} scale degree`}
                        >
                          {Array.from({ length: (SCALES[musicKey.scale] || SCALES.major).length }, (_, d) => (
                            <option key={d} value={d}>{d + 1}</option>
                          ))}
                        </select>
                      </label>
                      <label style={{ color: "#94a3b8", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                        oct
                        <select
                          value={r.octaveOffset}
                          onChange={(e) => setBandSequence((seq) => seq.map((x) => x.id === r.id ? { ...x, octaveOffset: parseInt(e.target.value, 10) } : x))}
                          style={{ ...inputStyle, width: "auto", padding: "6px 8px", fontSize: "13px" }}
                          aria-label={`Region ${i + 1} octave offset`}
                        >
                          {[-2, -1, 0, 1, 2].map((o) => (
                            <option key={o} value={o}>{o > 0 ? `+${o}` : o}</option>
                          ))}
                        </select>
                      </label>
                      <label style={{ color: "#94a3b8", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                        <input
                          type="number" min="0.01" max="10" step="0.01"
                          value={(r.columnSpeedMs / 1000).toFixed(2)}
                          onChange={(e) => { const sec = parseFloat(e.target.value); if (!isNaN(sec) && sec > 0) setBandSequence((seq) => seq.map((x) => x.id === r.id ? { ...x, columnSpeedMs: Math.round(sec * 1000) } : x)); }}
                          style={{ ...inputStyle, width: "60px", padding: "6px 8px", fontSize: "13px" }}
                          aria-label={`Region ${i + 1} seconds per column`}
                        />
                        s/col
                      </label>
                      <div style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
                        <button
                          onClick={() => setBandSequence((seq) => { if (i === 0) return seq; const n = seq.slice(); [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}
                          disabled={i === 0}
                          style={{ ...darkButtonStyle, padding: "4px 8px", fontSize: "13px", opacity: i === 0 ? 0.35 : 1 }}
                          aria-label={`Move region ${i + 1} earlier`}
                          title="Move earlier"
                        >↑</button>
                        <button
                          onClick={() => setBandSequence((seq) => { if (i >= seq.length - 1) return seq; const n = seq.slice(); [n[i + 1], n[i]] = [n[i], n[i + 1]]; return n; })}
                          disabled={i >= bandSequence.length - 1}
                          style={{ ...darkButtonStyle, padding: "4px 8px", fontSize: "13px", opacity: i >= bandSequence.length - 1 ? 0.35 : 1 }}
                          aria-label={`Move region ${i + 1} later`}
                          title="Move later"
                        >↓</button>
                        <button
                          onClick={() => setBandSequence((seq) => seq.filter((x) => x.id !== r.id))}
                          style={{ ...darkButtonStyle, padding: "4px 8px", fontSize: "13px", background: "#7f1d1d", color: "#fecaca", border: "1px solid #991b1b" }}
                          aria-label={`Delete region ${i + 1}`}
                          title="Delete"
                        >🗑</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {soundMode === "band" && (
            <div style={{ ...sectionStyle, marginTop: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                <strong style={{ color: "#f8fafc", fontSize: "15px" }}>Sections — instrument passes</strong>
                <button
                  onClick={() => setBandSections((secs) => [...secs, { id: Date.now() + Math.random(), instrument: (bandSequenceRef.current[0]?.instrument) || "flute", repeats: 3, octaveOffset: 0 }])}
                  onFocus={() => speakLabel("Add section. Adds an instrument pass that repeats the whole composition.")}
                  style={{ background: "#0e7490", color: "#ecfeff", border: "1px solid #155e75", borderRadius: "8px", padding: "4px 10px", fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap" }}
                  data-description="Add section. Adds an instrument pass that plays the whole composition a set number of times on a chosen instrument and octave. Sections play in order."
                >Add section</button>
              </div>
              {bandSections.length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: "13px", padding: "8px 0" }}>
                  No sections — the composition plays once as written. Add a section to repeat it across instruments.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {bandSections.map((sec, i) => (
                    <div key={sec.id} style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", background: "#0b1220", border: "1px solid #1f2937", borderRadius: "10px", padding: "8px 10px" }}>
                      <span style={{ background: "#22d3ee", color: "#083344", fontWeight: 700, minWidth: "20px", height: "20px", lineHeight: "20px", textAlign: "center", borderRadius: "10px", fontSize: "12px" }}>{i + 1}</span>
                      <select
                        value={sec.instrument}
                        onChange={(e) => setBandSections((secs) => secs.map((x) => x.id === sec.id ? { ...x, instrument: e.target.value } : x))}
                        style={{ ...inputStyle, width: "150px", padding: "6px 8px", fontSize: "13px" }}
                        aria-label={`Section ${i + 1} instrument`}
                      >
                        {Object.entries(GM_INSTRUMENTS).map(([group, list]) => (
                          <optgroup key={group} label={group}>
                            {list.map((inst) => <option key={inst} value={inst}>{inst}</option>)}
                          </optgroup>
                        ))}
                      </select>
                      <label style={{ color: "#94a3b8", fontSize: "13px", display: "flex", alignItems: "center", gap: "4px" }}>
                        <input
                          type="number" min="1" max="20" step="1"
                          value={sec.repeats}
                          onChange={(e) => { const n = parseInt(e.target.value, 10); if (!isNaN(n) && n >= 1) setBandSections((secs) => secs.map((x) => x.id === sec.id ? { ...x, repeats: n } : x)); }}
                          style={{ ...inputStyle, width: "50px", padding: "6px 8px", fontSize: "13px" }}
                          aria-label={`Section ${i + 1} repeats`}
                        />
                        ×
                      </label>
                      <label style={{ color: "#94a3b8", fontSize: "13px", display: "flex", alignItems: "center", gap: "4px" }}>
                        oct
                        <input
                          type="number" min="-3" max="3" step="1"
                          value={sec.octaveOffset}
                          onChange={(e) => { const n = parseInt(e.target.value, 10); if (!isNaN(n)) setBandSections((secs) => secs.map((x) => x.id === sec.id ? { ...x, octaveOffset: Math.max(-3, Math.min(3, n)) } : x)); }}
                          style={{ ...inputStyle, width: "50px", padding: "6px 8px", fontSize: "13px" }}
                          aria-label={`Section ${i + 1} octave offset`}
                        />
                      </label>
                      <div style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
                        <button
                          onClick={() => setBandSections((secs) => { if (i === 0) return secs; const n = secs.slice(); [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}
                          disabled={i === 0}
                          style={{ ...darkButtonStyle, padding: "4px 8px", fontSize: "13px", opacity: i === 0 ? 0.35 : 1 }}
                          aria-label={`Move section ${i + 1} earlier`}
                          title="Move earlier"
                        >↑</button>
                        <button
                          onClick={() => setBandSections((secs) => { if (i >= secs.length - 1) return secs; const n = secs.slice(); [n[i + 1], n[i]] = [n[i], n[i + 1]]; return n; })}
                          disabled={i >= bandSections.length - 1}
                          style={{ ...darkButtonStyle, padding: "4px 8px", fontSize: "13px", opacity: i >= bandSections.length - 1 ? 0.35 : 1 }}
                          aria-label={`Move section ${i + 1} later`}
                          title="Move later"
                        >↓</button>
                        <button
                          onClick={() => setBandSections((secs) => secs.filter((x) => x.id !== sec.id))}
                          style={{ ...darkButtonStyle, padding: "4px 8px", fontSize: "13px", background: "#7f1d1d", color: "#fecaca", border: "1px solid #991b1b" }}
                          aria-label={`Delete section ${i + 1}`}
                          title="Delete"
                        >🗑</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px", flexWrap: "wrap", gap: "10px" }}>
            <div style={{ color: "#94a3b8", fontSize: "13px", lineHeight: 1.5 }}>
              Drag to select region · Right-click inside selection to zoom · Escape to cancel selection ·
              Ctrl+scroll to zoom on cursor · Arrow keys to pan · Space to play/stop · Esc to stop
            </div>
            <div style={{ display: "flex", gap: "10px", flexShrink: 0 }}>
              <button
                style={{ ...darkButtonStyle, opacity: nav.historyIndex <= 0 ? 0.35 : 1 }}
                disabled={nav.historyIndex <= 0}
                onClick={() => navigateHistory(nav.historyIndex - 1)}
                onFocus={() => speakLabel("Back. Returns to the previous fractal view in your history.")}
                data-description="Back. Returns to the previous fractal view in your zoom and pan history."
              >
                ← Back
              </button>
              <button
                style={{ ...darkButtonStyle, opacity: nav.historyIndex >= nav.history.length - 1 ? 0.35 : 1 }}
                disabled={nav.historyIndex >= nav.history.length - 1}
                onClick={() => navigateHistory(nav.historyIndex + 1)}
                onFocus={() => speakLabel("Forward. Advances to the next fractal view in your history.")}
                data-description="Forward. Advances to the next fractal view in your history. Available only after using Back."
              >
                Forward →
              </button>
              {/* Save as… — unified export menu. Closed it reads "Save as…";
                  opened it lists PNG resolutions, TIFF print resolutions, then
                  (after a separator) the native IMG format. Option values are
                  prefixed "png:" / "tiff:" / "__saveImg__" so the onChange can
                  route to the right export path even though PNG and TIFF share
                  size labels. Picking an item exports in one action. */}
              <select
                value="__saveExport__"
                aria-label="Save as"
                disabled={pngExporting || tiffExporting}
                onChange={(e) => {
                  if (pngExporting || tiffExporting) return;
                  const v = e.target.value;
                  if (v === "__saveExport__") return;            // hidden label item
                  if (v === "__saveImg__") { saveImg(); return; }
                  if (v === "__saveComposition__") { saveComposition(); return; }
                  if (v.indexOf("png:") === 0) {
                    const lbl = v.slice(4);
                    const next = PNG_EXPORT_PRESETS.find((p) => p.label === lbl);
                    if (!next) return;
                    setPngExportSize(next);
                    saveImage("png", next);
                    return;
                  }
                  if (v.indexOf("tiff:") === 0) {
                    const lbl = v.slice(5);
                    const next = TIFF_EXPORT_PRESETS.find((p) => p.label === lbl);
                    if (!next) return;
                    setTiffExportSize(next);
                    handleTIFFExport(next);
                    return;
                  }
                }}
                onFocus={() => speakLabel("Save as. Open this menu to export the fractal as a PNG image, a print resolution TIFF, or the app's native IMG format.")}
                data-description="Save as. Opens a menu of export options. PNG screen size is free; PNG and TIFF at 2K and 4K require the Artist tier; 8K and 12K require Studio. TIFF is for high-resolution printing, and stores its fractal parameters inside the file so Open TIFF can restore the view. IMG is the app's native format that preserves all parameters for later reload."
                style={{
                  ...darkButtonStyle,
                  width: "130px",
                  cursor: (pngExporting || tiffExporting) ? "wait" : "pointer",
                  opacity: (pngExporting || tiffExporting) ? 0.5 : 1,
                }}
              >
                <option value="__saveExport__" hidden>
                  {pngExporting ? "Saving\u2026" : tiffExporting ? `Rendering\u2026 ${Math.round(tiffExportProgress * 100)}%` : "Save as\u2026"}
                </option>
                {PNG_EXPORT_PRESETS.map((preset) => {
                  const cap = capabilityForPngSize(preset);
                  const required = tierRequiredFor(cap);
                  const locked = !can(cap, userTier);
                  const tierLabel = required === TIERS.STUDIO ? "Studio"
                                  : required === TIERS.ARTIST ? "Artist"
                                                              : "free";
                  const desc = preset.isCanvas ? "screen size"
                             : preset.width <= 2000 ? "2K \u2014 web/share"
                             : preset.width <= 4000 ? "4K \u2014 medium print (~33\u00d722 cm)"
                             : preset.width <= 8000 ? "8K \u2014 gallery (~67\u00d744 cm)"
                                                    : "12K \u2014 large (~100\u00d767 cm)";
                  return (
                    <option
                      key={`png-${preset.label}`}
                      value={`png:${preset.label}`}
                      disabled={locked}
                      style={locked ? { color: "#64748b" } : undefined}
                    >
                      PNG, {desc} ({tierLabel}){locked ? " \u2014 locked" : ""}
                    </option>
                  );
                })}
                {TIFF_EXPORT_PRESETS.map((preset) => {
                  const cap = capabilityForSize(preset);
                  const required = tierRequiredFor(cap);
                  const locked = !can(cap, userTier);
                  const tierLabel = required === TIERS.STUDIO ? "Studio" : "Artist";
                  const desc = preset.width <= 2000 ? "2K \u2014 web/share"
                             : preset.width <= 4000 ? "4K \u2014 medium print (~33\u00d722 cm)"
                             : preset.width <= 8000 ? "8K \u2014 gallery (~67\u00d744 cm)"
                                                    : "12K \u2014 large (~100\u00d767 cm)";
                  return (
                    <option
                      key={`tiff-${preset.label}`}
                      value={`tiff:${preset.label}`}
                      disabled={locked}
                      style={locked ? { color: "#64748b" } : undefined}
                    >
                      TIFF, {desc} ({tierLabel}){locked ? " \u2014 locked" : ""}
                    </option>
                  );
                })}
                <option disabled>{"\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500"}</option>
                <option value="__saveImg__">IMG, native format</option>
                <option value="__saveComposition__">Composition (.fmc.json)</option>
              </select>
              <button id="color-editor-btn" style={darkButtonStyle} onClick={() => {
                if (colorEditorWindowRef.current && !colorEditorWindowRef.current.closed) {
                  colorEditorWindowRef.current.focus(); return;
                }
                const w = window.open('', 'color_editor', 'width=520,height=600,resizable=yes,scrollbars=yes');
                colorEditorWindowRef.current = w;
                const html = buildColorEditorHtml();
                w.document.write(html);
                w.document.close();
              }} onFocus={() => speakLabel("Color editor. Opens a separate window for editing palette colors.")} data-description="Color editor. Opens a separate window for editing palette colors. Lets you adjust red, green, and blue starting values, step sizes, and direction for each of 256 palette pages.">Color editor</button>
              <button id="sound-editor-btn" style={darkButtonStyle} onClick={openSoundEditor} onFocus={() => speakLabel("Sound editor. Opens a separate window for editing the key and scale.")} data-description="Sound editor. Opens a separate window to edit the global key and scale for instrumental fractal music.">Sound editor</button>
            </div>
            <button id="help-btn" style={darkButtonStyle} onClick={(e) => {
              if (e.shiftKey) { setShowSetup(true); return; }
              setShowHelp(true);
            }} onFocus={() => speakLabel("Help. Opens a dialog with information about every control.")} data-description="Help. Opens a dialog with detailed information about every control in the app. Shift-click opens the accessibility setup dialog for caregivers.">Help</button>
            {!accessibilitySetup.minimalToolbar && (<>
            <button
              id="eye-tracking-btn"
              style={{ ...darkButtonStyle, minWidth: '44px', background: eyeTrackingActive ? '#0ea5e9' : darkButtonStyle.background, color: eyeTrackingActive ? 'white' : darkButtonStyle.color }}
              onClick={() => {
                if (eyeTrackingActive) {
                  stopEyeTracking();
                } else {
                  startEyeTracking(userLanguageRef.current || 'en-US', () => {});
                }
              }}
              aria-label={eyeTrackingActive ? 'Eye tracking active, click to stop' : 'Eye tracking inactive, click to start'}
              onFocus={() => speakLabel(`Eye tracking button. ${eyeTrackingActive ? 'Eye tracking is active. Click to stop.' : 'Eye tracking is inactive. Click to start webcam-based gaze control.'}`)}
              data-description="Eye tracking button. Toggles webcam-based gaze control. When active, the button is highlighted and your gaze can click controls by dwelling on them for one second. Calibrate by looking at each of nine dots and clicking five times. Required for users who cannot speak but can control eye movement."
            >👁</button>
            {eyeTrackingActive && (
              <button
                id="eye-recalibrate-btn"
                style={{ ...darkButtonStyle, minWidth: '44px' }}
                onClick={() => {
                  if (gazePollRef.current) { clearInterval(gazePollRef.current); gazePollRef.current = null; }
                  dwellStateRef.current = { element: null, startTime: 0 };
                  if (dwellRingRef.current) dwellRingRef.current.style.display = 'none';
                  startEyeTracking(userLanguageRef.current || 'en-US', () => {}, { recalibrateOnly: true });
                }}
                aria-label="Recalibrate eye tracking"
                onFocus={() => speakLabel("Recalibrate button. Re-runs the eye-tracking calibration. Use this if gaze accuracy has drifted.")}
                data-description="Recalibrate button. Re-runs the 9-dot eye-tracking calibration. Use this if you notice gaze accuracy has drifted or if you've moved relative to the webcam."
                title="Recalibrate eye tracking"
              >↻</button>
            )}
            <button
              style={{ ...darkButtonStyle, background: isListening ? '#ef4444' : '#1e293b', color: isListening ? 'white' : '#cbd5e1', minWidth: '44px' }}
              aria-label={isListening ? 'Microphone, voice active' : 'Microphone, voice inactive'}
              onFocus={() => speakLabel(`Microphone button. ${isListening ? 'Voice is active. Click to stop.' : 'Voice is inactive. Click to start voice control.'}`)}
              data-description="Microphone button. Toggles voice control. When active, the button is red and the microphone is listening for commands. When inactive, click or press Enter to start voice control."
              onClick={() => {
                if (isListening) { stopListening(); return; }
                if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
                isVoiceActiveRef.current = true;
                // beginListening() guards on voiceStateRef === 'listening', so set
                // it here.  Without this, repeat clicks of the mic button after
                // a stopListening() (which sets state to 'idle') silently fail.
                voiceStateRef.current = 'listening';
                if (!onboardingDone) {
                  // Auto-detect language from browser, skip asking the user
                  const langMap = { en: 'en-US', fr: 'fr-FR', es: 'es-ES', de: 'de-DE', it: 'it-IT' };
                  const prefix = (navigator.language || 'en').split('-')[0];
                  const lang = langMap[prefix] || 'en-US';
                  setUserLanguage(lang); userLanguageRef.current = lang; localStorage.setItem('fractal_language', lang);
                  const nameQs = { 'en-US':'What shall I call you?', 'fr-FR':'Comment dois-je vous appeler?', 'es-ES':'¿Cómo debo llamarte?', 'de-DE':'Wie soll ich dich nennen?', 'it-IT':'Come devo chiamarti?' };
                  awaitingNameRef.current = true;
                  voiceSpeak(nameQs[lang]||nameQs['en-US'], lang, () => {
                    beginListening();
                    });
                } else {
                  beginListening();
                }
              }}
              title={isVoiceActiveRef.current ? 'Voice active — click to stop' : 'Click to start voice control'}
            >
              {isVoiceActiveRef.current ? '🎙' : '🎙'}
            </button>
            </>)}
          </div>
        </div>

        {voiceStatus ? (
          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px", paddingLeft: "4px" }}>{voiceStatus}</div>
        ) : null}

        {!voiceReady && (
          <button ref={autoActivateBtnRef} onClick={() => {}}
            style={{ display:"block",width:"100%",marginTop:"8px",padding:"10px",borderRadius:"8px",border:"1px solid #38bdf8",background:"transparent",color:"#38bdf8",fontSize:"14px",cursor:"pointer",textAlign:"center" }}
            aria-label="Activate voice control">
            👆 Press Enter or click to activate voice control
          </button>
        )}

        {/* ── Three-column control row ───────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px", marginTop: "20px" }}>

          {/* Column 1: Fractal Definition */}
          <div id="section-fractal" style={sectionStyle}>
            <div style={{ fontSize: "12px", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: "14px" }}>
              Fractal Definition
            </div>
            <div style={{ display: "grid", gap: "12px" }}>

              <div>
                <label style={labelStyle}>Preset</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <select id="preset-select" value={presetKey} onChange={(e) => { if (bandSequenceRef.current.length) { setBandSequence([]); setStatus("Regions cleared for the new image."); } setPresetKey(e.target.value); speakLabel(presets[e.target.value]?.label || e.target.value); }}
                    onFocus={() => speakLabel(`Preset selector. Currently ${presets[presetKey]?.label || presetKey}. Use arrow keys to browse, then press Enter to load.`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); loadPreset(); } }}
                    data-description="Preset selector. Choose from built-in fractal views like Mandelbrot Full, Seahorse Valley, Elephant Valley, and Julia presets, or any custom presets you have imported. Press Enter to load."
                    style={inputStyle}>
                    <optgroup label="Built-in">
                      {Object.entries(presets).filter(([k]) => k in DEFAULT_PRESETS).map(([key, preset]) => (
                        <option key={key} value={key}>{preset.label}</option>
                      ))}
                    </optgroup>
                    {Object.keys(presets).some(k => !(k in DEFAULT_PRESETS)) && (
                      <optgroup label="Imported">
                        {Object.entries(presets).filter(([k]) => !(k in DEFAULT_PRESETS)).map(([key, preset]) => (
                          <option key={key} value={key}>{preset.label}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <button style={buttonStyle} onClick={loadPreset} onFocus={() => speakLabel("Load preset. Loads the preset selected in the dropdown above.")} data-description="Load preset. Loads the currently selected preset from the dropdown above. You can also press Enter while focused on the preset dropdown.">Load</button>
                </div>
                {Object.keys(presets).some(k => !(k in DEFAULT_PRESETS)) && (
                  <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "2px" }}>Imported</div>
                    {Object.entries(presets).filter(([k]) => !(k in DEFAULT_PRESETS)).map(([key, preset]) => (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <button
                          onFocus={() => speakLabel(`${preset.label}, imported preset. Press Enter to select.`)}
                          onClick={() => setPresetKey(key)}
                          style={{
                            flex: 1, fontSize: "13px", color: presetKey === key ? "#f8fafc" : "#94a3b8",
                            cursor: "pointer", padding: "2px 4px", borderRadius: "4px",
                            background: presetKey === key ? "#1e293b" : "transparent",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            border: "none", textAlign: "left",
                          }}
                        >
                          {preset.label}
                        </button>
                        <button
                          onFocus={() => speakLabel(`Delete ${preset.label}`)}
                          onClick={() => {
                            const next = { ...presets };
                            delete next[key];
                            setPresets(next);
                            voiceSpeak(`${preset.label} removed.`, userLanguageRef.current);
                            if (presetKey === key) setPresetKey(Object.keys(DEFAULT_PRESETS)[0]);
                          }}
                          style={{ ...darkButtonStyle, padding: "2px 7px", fontSize: "12px", lineHeight: 1.4, flexShrink: 0 }}
                          aria-label={`Delete ${preset.label}`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {imgLibrary.length > 0 && (
                  <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "2px" }}>My Library</div>
                    {imgLibrary.map(fname => (
                      <div key={fname} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span
                          style={{ flex: 1, fontSize: "13px", color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={fname}
                        >
                          {fname.replace(/\.img$/i, "")}
                        </span>
                        <button
                          onFocus={() => speakLabel(`Load ${fname}`)}
                          onClick={() => idbGet(`imgLib:${fname}`).then(buf => { if (buf) importImgFromBuffer(buf, fname); })}
                          style={{ ...darkButtonStyle, padding: "2px 7px", fontSize: "12px", lineHeight: 1.4, flexShrink: 0 }}
                          aria-label={`Load ${fname}`}
                        >▶</button>
                        <button
                          onFocus={() => speakLabel(`Delete ${fname}`)}
                          onClick={() => { idbDelete(`imgLib:${fname}`);
                            setImgLibrary(prev => prev.filter(n => n !== fname));
                            speakLabel(`${fname} removed.`); 
                          }}
                          style={{ ...darkButtonStyle, padding: "2px 7px", fontSize: "12px", lineHeight: 1.4, flexShrink: 0 }}
                          aria-label={`Delete ${fname}`}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Fractal type</label>
                <select id="fractal-type-select" value={inputFractalType} onChange={(e) => { setInputFractalType(e.target.value); 
                speakLabel(e.target.value === 'julia' ? 'Julia' : 'Mandelbrot'); 
                }}
                  onFocus={() => speakLabel(`Fractal type. Currently ${inputFractalType}. Use arrow keys to change.`)}
                  data-description="Fractal type. Choose between Mandelbrot, the classic black bug shape, or Julia, which traces a fixed complex constant. Each type has different visual character and produces different musical scales."
                  style={inputStyle}>
                  <option value="mandelbrot">Mandelbrot</option>
                  <option value="julia">Julia</option>
                </select>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div><label style={labelStyle}>xMin</label><input style={inputStyle} id="input-xmin" value={inputXMin} onChange={(e) => setInputXMin(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commitBoundsFromInputs(); }} onFocus={() => speakLabel(`x minimum, currently ${inputXMin}. Editable; press Enter to go to the view.`)} data-description="X minimum. Editable; press Enter or click Go to view to recompute." /></div>
                <div><label style={labelStyle}>xMax</label><input style={inputStyle} id="input-xmax" value={inputXMax} onChange={(e) => setInputXMax(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commitBoundsFromInputs(); }} onFocus={() => speakLabel(`x maximum, currently ${inputXMax}. Editable; press Enter to go to the view.`)} data-description="X maximum. Editable; press Enter or click Go to view to recompute." /></div>
                <div><label style={labelStyle}>yMin</label><input style={inputStyle} id="input-ymin" value={inputYMin} onChange={(e) => setInputYMin(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commitBoundsFromInputs(); }} onFocus={() => speakLabel(`y minimum, currently ${inputYMin}. Editable; press Enter to go to the view.`)} data-description="Y minimum. Editable; press Enter or click Go to view to recompute." /></div>
                <div><label style={labelStyle}>yMax</label><input style={inputStyle} id="input-ymax" value={inputYMax} onChange={(e) => setInputYMax(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commitBoundsFromInputs(); }} onFocus={() => speakLabel(`y maximum, currently ${inputYMax}. Editable; press Enter to go to the view.`)} data-description="Y maximum. Editable; press Enter or click Go to view to recompute." /></div>
              </div>
              <div style={{ marginTop: "6px" }}>
                <button style={darkButtonStyle} onClick={commitBoundsFromInputs} onFocus={() => speakLabel("Go to view. Recomputes the fractal at the entered coordinates.")}>Go to view</button>
              </div>

              <div>
                <label style={labelStyle}>Max iterations</label>
                <input style={inputStyle} id="input-maxiter" value={inputMaxIter} onChange={(e) => setInputMaxIter(e.target.value)} onFocus={() => speakLabel(`Max iterations, currently ${inputMaxIter}`)} />
              </div>

              <div>
                <label style={labelStyle} htmlFor="input-aa">Anti-aliasing</label>
                <select
                  style={inputStyle}
                  id="input-aa"
                  value={aaFactor}
                  onChange={(e) => setAaFactor(Number(e.target.value))}
                  onFocus={() => speakLabel(`Anti-aliasing, currently ${aaFactor === 1 ? "off" : aaFactor + " times"}`)}
                  data-description="Anti-aliasing (supersampling). Off renders one sample per pixel. 2x and 4x render that many samples per axis and average them, which smooths the salt-and-pepper speckle along fractal boundaries at the cost of 4x or 16x render time. Applies to the on-screen image; high-resolution PNG and TIFF exports are already smoothed by their own resolution."
                >
                  <option value={1}>Off (1×)</option>
                  <option value={2}>2× (4 samples/pixel)</option>
                  <option value={4}>4× (16 samples/pixel)</option>
                </select>
              </div>

              {inputFractalType === "julia" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div><label style={labelStyle}>Julia c Re</label><input style={inputStyle} value={inputJuliaRe} onChange={(e) => setInputJuliaRe(e.target.value)} onFocus={() => speakLabel(`Julia c real part, currently ${inputJuliaRe}`)} /></div>
                  <div><label style={labelStyle}>Julia c Im</label><input style={inputStyle} value={inputJuliaIm} onChange={(e) => setInputJuliaIm(e.target.value)} onFocus={() => speakLabel(`Julia c imaginary part, currently ${inputJuliaIm}`)} /></div>
                </div>
              )}

              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                <button style={accentButtonStyle} onClick={applyFractalSpec} onFocus={() => speakLabel("Render fractal. Applies the current form values and computes a fresh image.")} data-description="Render fractal. Applies the current Fractal Definition values (preset, fractal type, x and y bounds, max iterations, and Julia constant if applicable) and computes a fresh fractal image. Replaces whatever's on the canvas with the result.">Render fractal</button>
                <button style={darkButtonStyle} onClick={resetToCurrentSpec} onFocus={() => speakLabel("Reset form. Reverts the form inputs to the current fractal's parameters.")} data-description="Reset form. Reverts the input fields in the Fractal Definition section back to the parameters of the currently displayed fractal. Useful if you've started typing changes but want to abandon them without rendering.">Reset form</button>
                <button style={darkButtonStyle} onClick={clearImportedRaster} onFocus={() => speakLabel("Clear raster. Removes any imported raster image overlay from the canvas.")} data-description="Clear raster. Removes any imported raster image overlay from the canvas. Has no effect if no raster has been imported.">Clear raster</button>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", color: "#cbd5e1", fontSize: "14px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    tabIndex={0}
                    aria-label="Show grid overlay on canvas"
                    checked={showGrid}
                    onChange={e => setShowGrid(e.target.checked)}
                    onFocus={() => speakLabel("Show grid. Toggles a 15 by 10 cell overlay on the canvas for keyboard navigation.")}
                    data-description="Show grid. Toggles a 15-by-10 grid overlay on the fractal canvas. Each of the 150 cells is keyboard-focusable. Pressing Enter on a cell zooms into that cell's region and stops any playback. Arrow keys move between cells, wrapping at the edges."
                  />
                  Show grid
                </label>
                <button
                  style={{ ...darkButtonStyle, opacity: showGrid ? 1 : 0.4 }}
                  disabled={!showGrid}
                  onClick={() => {
                    // Focus the first grid cell. Cells are rendered as divs
                    // with role="button" inside the canvas's wrapper div.
                    const c = canvasRef.current;
                    if (!c || !c.parentElement) return;
                    const firstCell = c.parentElement.querySelector('div[role="button"]');
                    if (firstCell) firstCell.focus({ preventScroll: false });
                  }}
                  onFocus={() => speakLabel("Focus first cell. Tab or arrow keys move between cells, Enter zooms into the cell.")}
                  data-description="Focus first grid cell. Moves keyboard focus to cell 1 of the grid overlay (top-left). From there, Tab and arrow keys both move between cells, with arrow keys wrapping at edges. Enter zooms into the focused cell and stops any playback. Disabled when the grid overlay is hidden."
                >
                  Focus first cell
                </button>
              </div>

            </div>
          </div>

          {/* Column 2: Palette & Colour Editor */}
          <div id="section-palette" style={sectionStyle}>
            <div style={{ fontSize: "12px", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: "14px" }}>
              Palette
            </div>
            <div style={{ display: "grid", gap: "12px" }}>

              <div>
                {/* Collapsible palette selector: Space/Enter on trigger to open/close,
                    Tab through items when open, Escape closes and returns focus. */}
                <button
                  id="palette-select"
                  ref={paletteListTriggerRef}
                  aria-haspopup="listbox"
                  aria-expanded={paletteListOpen}
                  data-description="Palette selector. Choose which color scheme renders the fractal. Includes built-in palettes like Classic, Fire, Ice, Forest, plus any custom palette banks you have imported. Press Space or Enter to open the list."
                  onFocus={() => speakLabel(`Palette: ${palettes[paletteName]?.label || paletteName}. Press Space or Enter to ${paletteListOpen ? "close" : "open"} the list.`)}
                  onKeyDown={(e) => { if (e.key === 'Tab' && e.shiftKey) { e.preventDefault(); document.getElementById('palette-color-affects-sound')?.focus(); } else if (e.key === 'Escape') { setPaletteListOpen(false); voiceSpeak('Palette list closed.', userLanguageRef.current); e.stopPropagation(); } }}
                  onClick={() => {
                    const next = !paletteListOpen;
                    setPaletteListOpen(next);
                    voiceSpeak(next ? "Palette list open. Tab to browse." : "Palette list closed.", userLanguageRef.current);
                  }}
                  style={{ ...inputStyle, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", width: "100%", textAlign: "left" }}
                >
                  <span>{palettes[paletteName]?.label || paletteName}</span>
                  <span style={{ fontSize: "10px", color: "#64748b" }}>{paletteListOpen ? "▲" : "▼"}</span>
                </button>
                {paletteListOpen && (
                  <div
                    role="listbox"
                    aria-label="Palette options"
                    style={{ display: "flex", flexDirection: "column", gap: "2px", maxHeight: "180px", overflowY: "auto", border: "1px solid #334155", borderRadius: "6px", padding: "4px", marginTop: "4px" }}
                  >
                    {Object.keys(palettes).map((key) => {
                      const pal = palettes[key];
                      const isSelected = paletteName === key;
                      return (
                        <button
                          key={key}
                          role="option"
                          aria-selected={isSelected}
                          tabIndex={0}
                          onFocus={() => speakLabel(pal.label)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setPaletteListOpen(false);
                              voiceSpeak("Palette list closed.", userLanguageRef.current);
                              paletteListTriggerRef.current?.focus();
                              e.stopPropagation();
                            }
                          }}
                          onClick={async () => {
                            setPaletteName(key);
                            if (pal.bank) {
                              // Reload this palette's bytes through the normal
                              // importer so its colours actually apply and any
                              // in-progress Color Editor edits are cleared,
                              // instead of keeping the previously-loaded bank.
                              let reloaded = false;
                              try {
                                const buf = await idbGet(`paletteBank:${key}`);
                                if (buf) { await importPaletteFromBuffer(buf, key); reloaded = true; }
                              } catch (_) {}
                              if (!reloaded) {
                                setUsePaletteBank(true); setPaletteLoaded(true); loadEditorPage(palettePage);
                                voiceSpeak(`${pal.label} selected.`, userLanguageRef.current);
                              }
                            } else {
                              setUsePaletteBank(false); setPaletteLoaded(false);
                              voiceSpeak(`${pal.label} selected.`, userLanguageRef.current);
                            }
                            setPaletteListOpen(false);
                            paletteListTriggerRef.current?.focus();
                          }}
                          style={{
                            background: isSelected ? "#1e293b" : "transparent",
                            border: isSelected ? "1px solid #38bdf8" : "1px solid transparent",
                            borderRadius: "4px",
                            color: isSelected ? "#f8fafc" : "#94a3b8",
                            fontSize: "13px",
                            padding: "5px 10px",
                            textAlign: "left",
                            cursor: "pointer",
                            width: "100%",
                          }}
                        >
                          {pal.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {Object.keys(palettes).some(k => !(k in DEFAULT_PALETTES)) && (
                  <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "2px" }}>Imported</div>
                    {Object.entries(palettes).filter(([k]) => !(k in DEFAULT_PALETTES)).map(([key, palette]) => (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <button
                          onFocus={() => speakLabel(`${palette.label}, imported palette. Press Enter to select.`)}
                          onClick={async () => {
                            setPaletteName(key);
                            // Prefer IDB cache (local-first); fall back to
                            // Firebase if not in IDB. After Stage 1, almost
                            // all dropdown clicks should hit IDB.
                            try {
                              const cached = await idbGet(`paletteBank:${key}`);
                              if (cached) {
                                await importPaletteFromBuffer(cached, key);
                                return;
                              }
                            } catch (err) {
                              console.warn('IDB lookup failed, falling back to Firebase:', err);
                            }
                            try {
                              const files = await fetchLibraryFiles();
                              const file = files.find(f => f.name === key || f.name === key + '.new');
                              if (file) {
                                const buf = await downloadLibraryFile(file.downloadUrl);
                                await importPaletteFromBuffer(buf, file.name);
                              }
                            } catch (err) {
                              console.error('Failed to load palette:', err);
                            }
                          }}
                          style={{
                            flex: 1, fontSize: "13px", color: paletteName === key ? "#f8fafc" : "#94a3b8",
                            cursor: "pointer", padding: "2px 4px", borderRadius: "4px",
                            background: paletteName === key ? "#1e293b" : "transparent",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            border: "none", textAlign: "left",
                          }}
                        >
                          {(palette.label || key).replace(/\.tmp$/i, '')}
                        </button>
                        <button
                          onFocus={() => speakLabel(`Delete ${palette.label}`)}
                          onClick={async () => {
                            const next = { ...palettes };
                            delete next[key];
                            setPalettes(next);
                            voiceSpeak(`${palette.label} removed.`, userLanguageRef.current);
                            // If the deleted palette was the active one, fall
                            // back to the first default palette and clear the
                            // in-memory bank so the fractal re-renders without
                            // it. The Firebase copy (if any) is untouched —
                            // this is a local-cache delete only.
                            if (paletteName === key) {
                              setPaletteName(Object.keys(DEFAULT_PALETTES)[0]);
                              setPaletteBank(null);
                              setUsePaletteBank(false);
                              setPaletteFilename("none loaded");
                            }
                            // Remove from IDB cache. Best-effort — if the
                            // palette wasn't in IDB (e.g. it's a built-in
                            // gradient added to the palettes map at startup),
                            // the delete is a no-op.
                            try {
                              await idbDelete(`paletteBank:${key}`);
                            } catch (err) {
                              console.warn(`Failed to delete cached palette ${key}:`, err);
                            }
                          }}
                          style={{ ...darkButtonStyle, padding: "2px 7px", fontSize: "12px", lineHeight: 1.4, flexShrink: 0 }}
                          aria-label={`Delete ${palette.label} from local cache`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button style={buttonStyle} onClick={() => setImportModal("palette")} onFocus={() => speakLabel("Import palette bank. Opens a dialog to load palette files from your library or local disk.")} data-description="Import palette bank. Opens a dialog where you can load custom palette files from a library or your local computer. Imported palettes appear in the palette selector.">
                  Import palette bank
                </button>
                <button
                  style={{ ...buttonStyle, ...(imgImportHighlight ? { background: "#f97316", color: "#fff", boxShadow: "0 0 12px #f97316", transition: "all 0.3s" } : {}) }}
                  onClick={() => setImportModal("img")}
                  id="import-img-btn"
                  aria-label="Import IMG file"
                  onFocus={() => speakLabel("Import IMG file. Opens a dialog to load a previously saved IMG file.")}
                  data-description="Import IMG file. Opens a dialog to load a previously saved IMG file. IMG files preserve the exact view, fractal type, max iterations, and palette of a saved fractal."
                >
                  Import IMG
                </button>
                <button
                  style={buttonStyle}
                  onClick={() => document.getElementById("tiff-import-input").click()}
                  id="import-tiff-btn"
                  aria-label="Open TIFF"
                  onFocus={() => speakLabel("Open TIFF. Opens a dialog to reopen a TIFF saved by the Toolkit and restore its view.")}
                  data-description="Open TIFF. Reopens a TIFF saved by the Toolkit and restores its fractal type, view, iterations, Julia parameter and palette from the parameters stored inside the file, then re-renders the image. TIFFs saved before September 11, 2026 carry no parameters and cannot be reopened."
                >
                  Open TIFF
                </button>
                <button
                  style={buttonStyle}
                  onClick={() => document.getElementById("composition-import-input").click()}
                  id="import-composition-btn"
                  aria-label="Open composition"
                  onFocus={() => speakLabel("Open composition. Opens a dialog to load a saved composition file.")}
                  data-description="Open composition. Opens a dialog to load a saved fractal-music composition file, restoring the fractal view, key, and loudness palette."
                >
                  Open composition
                </button>
                <select
                  id="example-composition-select"
                  style={inputStyle}
                  defaultValue=""
                  aria-label="Example compositions"
                  onFocus={() => { ensureExampleComps(); speakLabel("Example compositions. Choose one to load."); }}
                  onMouseDown={() => ensureExampleComps()}
                  onChange={(e) => {
                    const sel = exampleComps.find(c => c.name === e.target.value);
                    if (sel) loadExampleComposition(sel.downloadUrl, sel.name);
                    e.target.value = "";
                  }}
                >
                  <option value="">Example compositions...</option>
                  {exampleComps.map(c => <option key={c.name} value={c.name}>{c.name.replace(/\.fmc\.json$/i, "").replace(/\.json$/i, "")}</option>)}
                </select>
                <input id="composition-import-input" type="file" accept=".json" style={{ display: "none" }} onChange={handleImportComposition} />
                <input id="soundmap-import-input" type="file" accept=".json" style={{ display: "none" }} onChange={handleImportSoundMap} />
                <input id="palette-import-input" type="file" accept=".5,.6,.7,.8,.90,.pal,.map,*/*" style={{ display: "none" }} onChange={handleImportPalette} />
                <input id="img-import-input" type="file" accept=".img" style={{ display: "none" }} onChange={handleImportImg} />
                <input id="tiff-import-input" type="file" accept=".tif,.tiff,image/tiff" style={{ display: "none" }} onChange={handleImportTiff} />
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: "8px", color: "#cbd5e1", fontSize: "14px" }}>
              <input id="palette-color-affects-sound" type="checkbox" tabIndex={0} aria-label="Color influences sound" checked={colorAffectsSound}
onChange={(e) => { setColorAffectsSound(e.target.checked); 
                speakLabel(`Color influences sound ${e.target.checked ? "on" : "off"}`); }}
                onFocus={() => speakLabel(`Color influences sound. ${colorAffectsSound ? "On" : "Off"}. Toggles whether palette colors modulate the music's harmonic content.`)}
                onKeyDown={(e) => { if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); 
                document.getElementById('palette-select')?.focus(); 
                } }}
                data-description="Color influences sound. When checked, the colors in the palette modulate the harmonic content of the music. When unchecked, only iteration depth determines pitch and timbre. Try toggling it for different sonic textures." />
                Color influences sound
              </label>
              {openCompositionName && (
                <div style={{ color: "#93c5fd", fontSize: "14px", fontWeight: 600, marginTop: "4px" }}>
                  ♪ {openCompositionName.replace(/\.fmc\.json$/i, "").replace(/\.json$/i, "").replace(/\.tmp$/i, "").replace(/-\d{6,}$/, "")}
                </div>
              )}
            </div>
          </div>

          {/* Column 3: Transport & Audio */}
          <div id="section-transport" style={sectionStyle}>
            <div style={{ fontSize: "12px", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: "14px" }}>
              Transport &amp; Audio
            </div>
            <div style={{ display: "grid", gap: "14px" }}>
              <div style={{ marginTop: "6px", minHeight: "22px" }}>
                {playhead != null ? (
                  <span style={{ color: "#38bdf8", fontSize: "15px", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                    ● {scanOrientation === "horizontal" ? "Row" : "Column"} {playhead}
                  </span>
                ) : (
                  <span style={{ color: "#64748b", fontSize: "13px" }}>
                    {scanOrientation === "horizontal" ? "Row" : "Column"} position shows here while playing
                  </span>
                )}
              </div>
              <div style={{ marginTop: "6px" }}>
                <label style={labelStyle}>Movements (tempo map)</label>
                <div style={{ color: "#94a3b8", fontSize: "12px", marginBottom: "6px", lineHeight: 1.4 }}>
                  Divide the scan into sections, each with its own tempo and an optional
                  rest after it. Start/end are scan positions (watch the column readout
                  while playing to find where to place a boundary).
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ color: "#94a3b8", fontSize: "11px" }}>
                      <th style={{ textAlign: "left", padding: "2px 4px" }}>#</th>
                      <th style={{ padding: "2px 4px" }}>start</th>
                      <th style={{ padding: "2px 4px" }}>end</th>
                      <th style={{ padding: "2px 4px" }}>tempo</th>
                      <th style={{ padding: "2px 4px" }}>end rest</th>
                      <th style={{ padding: "2px 4px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((mv, i) => {
                      const isActive = i === activeMovementIndex;
                      return (
                      <tr key={i}
                        aria-current={isActive ? "true" : undefined}
                        style={isActive ? {
                          background: "rgba(245, 158, 11, 0.14)",
                          boxShadow: "inset 3px 0 0 0 #f59e0b",
                        } : undefined}>
                        <td style={{
                          color: isActive ? "#fbbf24" : "#64748b",
                          fontWeight: isActive ? 700 : 400,
                          padding: "2px 4px",
                          whiteSpace: "nowrap",
                        }}>{isActive ? "\u25B6 " : ""}{i + 1}</td>
                        <td style={{ padding: "2px 4px" }}>
                          <input type="number" value={mv.startCol} min={0} max={WIDTH - 1} step={1}
                            style={{ width: "62px", background: "#1e293b", border: "1px solid #334155", color: "#f8fafc", borderRadius: "5px", padding: "3px 5px" }}
                            onChange={(e) => {
                              const v = Math.max(0, Math.min(WIDTH - 1, Number(e.target.value) || 0));
                              setMovements(ms => ms.map((m, j) => j === i ? { ...m, startCol: v } : m));
                            }} />
                        </td>
                        <td style={{ padding: "2px 4px" }}>
                          <input type="number" value={mv.endCol} min={0} max={WIDTH - 1} step={1}
                            style={{ width: "62px", background: "#1e293b", border: "1px solid #334155", color: "#f8fafc", borderRadius: "5px", padding: "3px 5px" }}
                            onChange={(e) => {
                              const v = Math.max(0, Math.min(WIDTH - 1, Number(e.target.value) || 0));
                              setMovements(ms => ms.map((m, j) => j === i ? { ...m, endCol: v } : m));
                            }} />
                        </td>
                        <td style={{ padding: "2px 4px" }}>
                          <select value={mv.tempoMs}
                            style={{ background: "#1e293b", border: "1px solid #334155", color: "#f8fafc", borderRadius: "5px", padding: "3px 5px" }}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setMovements(ms => ms.map((m, j) => j === i ? { ...m, tempoMs: v } : m));
                            }}>
                            {TEMPO_OPTIONS.map(t => <option key={t.name} value={t.ms}>{t.name}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: "2px 4px", textAlign: "center" }}>
                          <input type="number" value={mv.endRestBeats} min={0} max={16} step={1}
                            style={{ width: "48px", background: "#1e293b", border: "1px solid #334155", color: "#f8fafc", borderRadius: "5px", padding: "3px 5px" }}
                            onChange={(e) => {
                              const v = Math.max(0, Math.min(16, Number(e.target.value) || 0));
                              setMovements(ms => ms.map((m, j) => j === i ? { ...m, endRestBeats: v } : m));
                            }} />
                        </td>
                        <td style={{ padding: "2px 4px", textAlign: "center" }}>
                          <button
                            style={{ background: "#243449", border: "1px solid #334155", color: "#fca5a5", borderRadius: "5px", padding: "2px 8px", cursor: "pointer" }}
                            disabled={movements.length <= 1}
                            onClick={() => setMovements(ms => ms.length > 1 ? ms.filter((_, j) => j !== i) : ms)}
                            data-description={`Delete movement ${i + 1}.`}
                          >×</button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                <button
                  style={{ ...buttonStyle, marginTop: "6px" }}
                  onClick={() => setMovements(ms => {
                    const last = ms[ms.length - 1];
                    const start = last ? Math.min(WIDTH - 1, last.endCol + 1) : 0;
                    return [...ms, { startCol: start, endCol: WIDTH - 1, tempoMs: 600, endRestBeats: 0 }];
                  })}
                  data-description="Add a movement. Appends a new section to the tempo map, starting after the last movement."
                >+ Add movement</button>
              </div>

              {/* ── Diagnostics (read-only) ─────────────────────────────────
                  Reports what the scan will ask of the audio engine. Changes
                  nothing: no note, no tempo, no palette. */}
              <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #1e293b" }}>
                <button
                  id="diagnostics-toggle"
                  aria-expanded={diagnosticsOpen}
                  aria-controls="diagnostics-body"
                  data-description="Diagnostics. Measures what the scan asks of the audio engine: how many voices sound at once, which columns are busiest, and why a stretch is silent. It only measures — it never plays or changes anything. Press Space or Enter to open."
                  onFocus={() => speakLabel(`Diagnostics. Press Space or Enter to ${diagnosticsOpen ? "close" : "open"}.`)}
                  onKeyDown={(e) => { if (e.key === 'Escape' && diagnosticsOpen) { setDiagnosticsOpen(false); voiceSpeak('Diagnostics closed.', userLanguageRef.current); e.stopPropagation(); } }}
                  onClick={() => {
                    const next = !diagnosticsOpen;
                    setDiagnosticsOpen(next);
                    voiceSpeak(next ? "Diagnostics open." : "Diagnostics closed.", userLanguageRef.current);
                  }}
                  style={{ ...inputStyle, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", width: "100%", textAlign: "left" }}
                >
                  <span>Diagnostics</span>
                  <span style={{ fontSize: "10px", color: "#64748b" }}>{diagnosticsOpen ? "\u25B2" : "\u25BC"}</span>
                </button>
              </div>

              {diagnosticsOpen && (
              <div id="diagnostics-body" style={{ marginTop: "8px" }}>
                <label style={labelStyle}>Voice load (read-only)</label>
                <div style={{ color: "#94a3b8", fontSize: "12px", marginBottom: "6px", lineHeight: 1.4 }}>
                  Each note keeps sounding for {SMPLR_RELEASE_SEC}s after its region ends, so
                  notes from neighbouring regions and columns overlap. Detailed columns
                  played fast stack up the most voices — that is what makes the sound
                  stutter. Column detail and tempo are the same lever.
                </div>

                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  <button
                    id="analyse-scan-button"
                    style={buttonStyle}
                    disabled={analyzing}
                    onClick={runScanAnalysis}
                    data-description="Analyse scan. Walks every column and reports the peak number of simultaneous voices, the hottest columns, and any silent stretches. Reads only: it does not play or change anything."
                  >{analyzing ? "Analysing…" : "Analyse scan"}</button>
                  <button
                    id="inspect-column-button"
                    style={darkButtonStyle}
                    onClick={() => runColumnInspection(playhead != null ? playhead : selectedColumn)}
                    data-description="Inspect column. Shows every region of the selected column: its size, whether it lies inside the set, its escape value and band, and exactly which instruments fire at what velocity. Explains why a column is silent."
                  >Inspect column</button>
                </div>

                {scanReport && (
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#cbd5e1" }}>
                    <div style={{ marginBottom: "4px" }}>
                      Peak <strong style={{ color: "#fbbf24" }}>{scanReport.peakVoices}</strong> simultaneous
                      voices, at column {scanReport.peakCol}. Cap is {VOICE_CAP} notes per region.
                      Total {scanReport.totalSec.toFixed(1)}s.
                    </div>
                    {scanReport.silentRuns.length > 0 && (
                      <div style={{ marginBottom: "4px", color: "#94a3b8" }}>
                        Silent: {scanReport.silentRuns.slice(0, 6).map((r) =>
                          r.from === r.to ? `${r.from}` : `${r.from}\u2013${r.to}`).join(", ")}
                        {scanReport.silentRuns.length > 6 ? ", \u2026" : ""}
                        {" \u2014 no instrument reaches a loudness above zero at those bands."}
                      </div>
                    )}
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <thead>
                        <tr style={{ color: "#94a3b8", fontSize: "11px" }}>
                          <th style={{ textAlign: "left", padding: "2px 4px" }}>hottest col</th>
                          <th style={{ padding: "2px 4px" }}>regions</th>
                          <th style={{ padding: "2px 4px" }}>notes</th>
                          <th style={{ padding: "2px 4px" }}>peak voices</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scanReport.hottest.map((c) => (
                          <tr key={c.col}>
                            <td style={{ padding: "2px 4px", color: "#f8fafc" }}>{c.col}</td>
                            <td style={{ padding: "2px 4px", textAlign: "center" }}>{c.regions}</td>
                            <td style={{ padding: "2px 4px", textAlign: "center" }}>{c.notes}</td>
                            <td style={{ padding: "2px 4px", textAlign: "center", color: "#fbbf24" }}>{c.peakVoices}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: "4px", color: "#64748b", fontSize: "11px", lineHeight: 1.4 }}>
                      Play the piece and note where it first stutters, then look up that
                      column here. That number is your machine's ceiling — slow the
                      movement, thin the palette, or move the boundary to stay under it.
                    </div>
                  </div>
                )}

                {columnReport && (
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#cbd5e1" }}>
                    <div style={{ marginBottom: "4px" }}>
                      Column {columnReport.col}: {columnReport.rows.length} regions,{" "}
                      {columnReport.colMs}ms, {columnReport.voices} voices.
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <thead>
                        <tr style={{ color: "#94a3b8", fontSize: "11px" }}>
                          <th style={{ textAlign: "left", padding: "2px 4px" }}>px</th>
                          <th style={{ padding: "2px 4px" }}>escape</th>
                          <th style={{ padding: "2px 4px" }}>band</th>
                          <th style={{ textAlign: "left", padding: "2px 4px" }}>fires</th>
                        </tr>
                      </thead>
                      <tbody>
                        {columnReport.rows.map((r, i) => (
                          <tr key={i} style={r.inSet ? { color: "#64748b" } : undefined}>
                            <td style={{ padding: "2px 4px" }}>{r.height}</td>
                            <td style={{ padding: "2px 4px", textAlign: "center" }}>
                              {r.inSet ? "in set" : r.escape}
                            </td>
                            <td style={{ padding: "2px 4px", textAlign: "center" }}>
                              {r.band == null ? "\u2014" : r.band}
                            </td>
                            <td style={{ padding: "2px 4px" }}>
                              {r.inSet
                                ? "rest"
                                : r.notes.length === 0
                                  ? <span style={{ color: "#fca5a5" }}>nothing (all instruments at zero)</span>
                                  : r.notes.map((n) => `${n.instrument} ${n.velocity}`).join(", ")}
                              {r.capped > 0 ? <span style={{ color: "#64748b" }}> (+{r.capped} capped)</span> : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              <div>
                <select id="mode-select" value={mode}
                    onChange={(e) => { setMode(e.target.value); 
                    speakLabel(e.target.value === "scan" ? "Scan mode" : "Orbit mode"); }}
                    onFocus={() => speakLabel(`Mode selector. Currently ${mode === "scan" ? "Scan mode" : "Orbit mode"}. Use arrow keys to change.`)}
                  onKeyDown={(e) => { if (e.key === 'Tab' && e.shiftKey) { e.preventDefault(); document.getElementById('transport-voice-volume')?.focus(); } }}
                  data-description="Mode selector. Switches between scan mode, which plays the fractal column by column, and orbit mode, which traces a Mandelbrot orbit path from a chosen point. Use arrow keys to switch."
                  style={inputStyle}>
                  <option value="scan">Scan Mode</option>
                  <option value="orbit">Orbit Mode</option>
                </select>
              </div>
              
              <label style={{ display: "flex", alignItems: "center", gap: "8px", color: "#cbd5e1", fontSize: "14px" }}>
                Sound:
                <select
                  id="sound-mode-select"
                  value={soundMode}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "instrumental" && !can("instrumentalMusic", userTier)) {
                      speakLabel("Instrumental fractal music requires the Artist tier.");
                      return;
                    }
                    if (v === "contour" && !can("contourGlide", userTier)) {
                      speakLabel("Contour glide requires the Studio tier.");
                      return;
                    }
                    if (v === "layered" && !can("contourGlide", userTier)) {
                      speakLabel("Layered mode requires the Studio tier.");
                      return;
                    }
                    if (v === "path" && !can("contourGlide", userTier)) {
                      speakLabel("Path mode requires the Studio tier.");
                      return;
                    }
                    if (v === "band" && !can("bandComposer", userTier)) {
                      speakLabel("The band composer requires the Artist tier.");
                      return;
                    }
                    setSoundMode(v);
                    // Say what the change enables or disables. A disabled <select>
                    // cannot take focus, so Key and Harmony can never announce
                    // themselves — this is the only place a listener finds out.
                    speakLabel(v === "instrumental"
                      ? "Instrumental fractal music. Key and Harmony are now available."
                      : "Simple sine waves. Key and Harmony do not apply in this mode.");
                  }}
                  onFocus={() => speakLabel(`Sound mode. Currently ${soundMode === "instrumental" ? "instrumental fractal music. Key and Harmony are available" : "simple sine waves. Key and Harmony do not apply"}.`)}
                  data-description="Sound mode. Simple sine waves, or instrumental fractal music which requires the Artist tier."
                  style={inputStyle}
                >
                  <option value="sine">Simple sine waves</option>
                  <option value="instrumental" disabled={!can("instrumentalMusic", userTier)}>
                    {can("instrumentalMusic", userTier) ? "Instrumental fractal music" : "Instrumental fractal music 🔒 Artist"}
                  </option>
                  <option value="band" disabled={!can("bandComposer", userTier)}>
                    {can("bandComposer", userTier) ? "Band composer" : "Band composer 🔒 Artist"}
                  </option>
                  <option value="contour" disabled={!can("contourGlide", userTier)}>
                    {can("contourGlide", userTier) ? "Contour glide (preview)" : "Contour glide (preview) 🔒 Studio"}
                  </option>
                  <option value="layered" disabled={!can("contourGlide", userTier)}>
                    {can("contourGlide", userTier) ? "Layered (envelope + triplets)" : "Layered (envelope + triplets) 🔒 Studio"}
                  </option>
                  <option value="path" disabled={!can("contourGlide", userTier)}>
                    {can("contourGlide", userTier) ? "Drawn path (vertices)" : "Drawn path (vertices) 🔒 Studio"}
                  </option>
                </select>
              </label>
              {soundMode === "path" && (
                <label style={musicRowStyle}>
                  Draw:
                  <select
                    value={pathDrawMode}
                    onChange={(e) => { setPathDrawMode(e.target.value); speakLabel(e.target.value === "freehand" ? "Freehand. Press and sweep to draw." : "Vertices. Click to place points."); }}
                    aria-label="Path draw mode"
                    style={{ ...inputStyle, width: "auto", minWidth: "140px", padding: "6px 8px", fontSize: "13px" }}
                  >
                    <option value="vertices">Vertices (click)</option>
                    <option value="freehand">Freehand (sweep)</option>
                  </select>
                </label>
              )}
              {soundMode === "path" && (
                <label style={musicRowStyle}>
                  Pitch:
                  <select
                    value={pathPitchFit}
                    onChange={(e) => { setPathPitchFit(e.target.value); speakLabel(e.target.value === "raw" ? "Raw escape value." : e.target.value === "shift" ? "Shift, exact escape steps." : e.target.value === "linear" ? "Linear, fitted to two octaves." : "Rank, fitted to two octaves."); }}
                    aria-label="Path pitch mapping"
                    style={{ ...inputStyle, width: "auto", minWidth: "160px", padding: "6px 8px", fontSize: "13px" }}
                  >
                    <option value="shift">Shift (exact escape steps)</option>
                    <option value="rank">Rank (even spread)</option>
                    <option value="linear">Linear (true intervals)</option>
                    <option value="raw">Raw escape value</option>
                  </select>
                </label>
              )}
              {soundMode === "path" && (
                <div style={{ ...musicRowStyle, flexWrap: "wrap", gap: "10px" }}>
                  <label htmlFor="path-voice">Voice:</label>
                  <select id="path-voice" value={pathActiveVoice}
                    onChange={(e) => {
                      const to = e.target.value;
                      if (to === pathActiveVoiceRef.current) return;
                      // Park what is being edited, bring the other one forward.
                      const parked = pathOtherRef.current || { verts: [], lengths: [], instrument: "flute", notesPerSeg: 1 };
                      const nowEditing = { verts: pathVerticesRef.current || [], lengths: pathLengthsRef.current || [],
                                           instrument: pathInstrumentRef.current, notesPerSeg: pathNotesPerSegRef.current };
                      setPathOther(nowEditing); pathOtherRef.current = nowEditing;
                      setPathVertices(parked.verts || []); pathVerticesRef.current = parked.verts || [];
                      setPathLengths(parked.lengths || []); pathLengthsRef.current = parked.lengths || [];
                      setPathInstrument(parked.instrument || "flute");
                      setPathNotesPerSeg(parked.notesPerSeg || 1);
                      setPathVertsText((parked.verts || []).map((v, i) => {
                        const L = (parked.lengths || [])[i];
                        const base = `${Math.round(v.x)},${Math.round(v.y)}`;
                        return (Number.isFinite(L) && L > 0 && L !== 1) ? `${base}:${L}` : base;
                      }).join(" "));
                      setPathActiveVoice(to); pathActiveVoiceRef.current = to;
                      drawPathOverlay(parked.verts || []);
                      speakLabel(`Editing voice ${to}.`);
                    }}
                    aria-label="Which voice the vertex controls edit"
                    style={{ ...inputStyle, width: "auto", padding: "6px 8px", fontSize: "13px" }}>
                    <option value="A">A</option>
                    <option value="B">B</option>
                  </select>
                  <label htmlFor="path-ratio">B speed x A:</label>
                  <input id="path-ratio" type="number" min="0.1" max="12" step="0.5"
                    value={pathRateRatio}
                    onChange={(e) => setPathRateRatio(Math.max(0.1, Math.min(12, parseFloat(e.target.value) || 1)))}
                    onFocus={() => speakLabel(`Voice B speed, ${pathRateRatio} times voice A.`)}
                    aria-label="Voice B speed as a multiple of voice A"
                    style={{ ...inputStyle, width: "80px" }} />
                </div>
              )}
              {soundMode === "path" && (
                <div style={{ ...musicRowStyle, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <label htmlFor="path-verts-text" style={{ minWidth: "80px" }}>Vertices:</label>
                  <textarea
                    id="path-verts-text"
                    value={pathVertsText}
                    onChange={(e) => setPathVertsText(e.target.value)}
                    placeholder="516,144 489,172 473,188"
                    rows={2}
                    aria-label="Path vertex coordinates, x comma y pairs separated by spaces"
                    style={{ ...inputStyle, flex: 1, minWidth: "220px", fontFamily: "monospace", fontSize: "12px" }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      // Accept pairs separated by whitespace, newlines or semicolons.
                      const pv = [], plen = [];
                      for (const tok of String(pathVertsText).split(/[\s;]+/)) {
                        if (!tok) continue;
                        // "x,y" or "x,y:length" — length multiplies the note slot.
                        const [pair, lenStr] = tok.split(":");
                        const bits = pair.split(",");
                        if (bits.length !== 2) continue;
                        const x = Number(bits[0]), y = Number(bits[1]);
                        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
                        const L = Number(lenStr);
                        pv.push({ x: Math.max(0, Math.min(WIDTH - 1, Math.round(x))), y: Math.max(0, Math.min(HEIGHT - 1, Math.round(y))) });
                        plen.push(Number.isFinite(L) && L > 0 ? Math.min(16, L) : 1);
                      }
                      if (pv.length < 2) { speakLabel("Need at least two coordinate pairs."); return; }
                      setPathVertices(pv);
                      pathVerticesRef.current = pv;
                      setPathLengths(plen);
                      pathLengthsRef.current = plen;
                      drawPathOverlay(pv);
                      speakLabel(`Placed ${pv.length} vertices.`);
                    }}
                    aria-label="Apply the typed coordinates as the drawn path"
                    style={{ ...inputStyle, width: "auto", cursor: "pointer", padding: "6px 12px" }}
                  >Apply</button>
                  <button
                    type="button"
                    onClick={() => {
                      const pv = pathVerticesRef.current || [];
                      const pl = pathLengthsRef.current || [];
                      setPathVertsText(pv.map((v, i) => {
                        const L = pl[i];
                        const base = `${Math.round(v.x)},${Math.round(v.y)}`;
                        return (Number.isFinite(L) && L !== 1) ? `${base}:${L}` : base;
                      }).join(" "));
                      speakLabel(`Read ${pv.length} vertices into the box.`);
                    }}
                    aria-label="Read the current drawn path into the coordinate box"
                    style={{ ...inputStyle, width: "auto", cursor: "pointer", padding: "6px 12px" }}
                  >Read</button>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={pathShowCoords}
                      onChange={(e) => {
                        setPathShowCoords(e.target.checked);
                        pathShowCoordsRef.current = e.target.checked;
                        drawPathOverlay(pathVerticesRef.current);
                        speakLabel(e.target.checked ? "Coordinates shown." : "Coordinates hidden.");
                      }}
                      aria-label="Show vertex numbers and coordinates on the image"
                    />
                    Show coordinates
                  </label>
                </div>
              )}
              {soundMode === "path" && (
                <div style={{ ...musicRowStyle, flexWrap: "wrap", gap: "10px" }}>
                  <label htmlFor="path-repeats">Repeats:</label>
                  <input id="path-repeats" type="number" min="1" max="16" step="1"
                    value={pathRepeats}
                    onChange={(e) => setPathRepeats(Math.max(1, Math.min(16, parseInt(e.target.value, 10) || 1)))}
                    onFocus={() => speakLabel(`Repeats, ${pathRepeats}.`)}
                    aria-label="Number of times to repeat the whole path"
                    style={{ ...inputStyle, width: "70px" }} />
                  <label htmlFor="path-transpose">Transpose:</label>
                  <input id="path-transpose" type="number" min="-24" max="24" step="1"
                    value={pathTranspose}
                    onChange={(e) => setPathTranspose(Math.max(-24, Math.min(24, parseInt(e.target.value, 10) || 0)))}
                    onFocus={() => speakLabel(`Transpose, ${pathTranspose} semitones.`)}
                    aria-label="Transpose the whole path in semitones"
                    style={{ ...inputStyle, width: "70px" }} />
                  <label htmlFor="path-transpose-step">per repeat:</label>
                  <input id="path-transpose-step" type="number" min="-12" max="12" step="1"
                    value={pathTransposeStep}
                    onChange={(e) => setPathTransposeStep(Math.max(-12, Math.min(12, parseInt(e.target.value, 10) || 0)))}
                    onFocus={() => speakLabel(`Transpose per repeat, ${pathTransposeStep} semitones.`)}
                    aria-label="Semitones added on each successive repetition"
                    style={{ ...inputStyle, width: "70px" }} />
                  <label htmlFor="path-pause">Pause:</label>
                  <input id="path-pause" type="number" min="0" max="8" step="0.25"
                    value={pathPause}
                    onChange={(e) => setPathPause(Math.max(0, Math.min(8, parseFloat(e.target.value) || 0)))}
                    onFocus={() => speakLabel(`Pause after each repetition, ${pathPause} seconds.`)}
                    aria-label="Seconds of silence after each repetition"
                    style={{ ...inputStyle, width: "80px" }} />
                </div>
              )}
              {soundMode === "path" && (
                <div style={{ ...sectionStyle, marginTop: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                    <strong style={{ color: "#f8fafc", fontSize: "14px" }}>Passes</strong>
                    <span style={{ display: "flex", gap: "8px" }}>
                      <button
                        type="button"
                        onClick={() => {
                          const row = { id: `p${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
                                        repeats: pathPasses.length ? 1 : Math.max(1, pathRepeats),
                                        transpose: pathPasses.length ? 0 : pathTranspose,
                                        pause: pathPasses.length ? 0 : pathPause,
                                        rate: 0, velocity: 0, instrument: "" };
                          setPathPasses([...pathPasses, row]);
                          speakLabel(`Pass ${pathPasses.length + 1} added.`);
                        }}
                        aria-label="Add a pass"
                        style={{ ...inputStyle, width: "auto", cursor: "pointer", padding: "5px 10px", fontSize: "12px" }}
                      >Add pass</button>
                      {pathPasses.length > 0 && (
                        <button
                          type="button"
                          onClick={() => { setPathPasses([]); speakLabel("Passes cleared. Using the single pass settings."); }}
                          aria-label="Clear all passes"
                          style={{ ...inputStyle, width: "auto", cursor: "pointer", padding: "5px 10px", fontSize: "12px" }}
                        >Clear all</button>
                      )}
                    </span>
                  </div>
                  {pathPasses.length === 0 ? (
                    <div style={{ color: "#94a3b8", fontSize: "12px" }}>
                      No passes — the Repeats, Transpose and Pause fields above are used. Add a pass to change transpose, speed, loudness or instrument at chosen points instead. Notes/sec or velocity of 0, and a blank instrument, inherit the settings above.
                    </div>
                  ) : pathPasses.map((p, i) => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", flexWrap: "wrap" }}>
                      <span style={{ color: "#94a3b8", fontSize: "12px", minWidth: "18px" }}>{i + 1}</span>
                      <label style={{ fontSize: "12px" }}>repeats
                        <input type="number" min="1" max="16" step="1" value={p.repeats}
                          onChange={(e) => { const v = Math.max(1, Math.min(16, parseInt(e.target.value, 10) || 1));
                            setPathPasses(pathPasses.map((q, j) => j === i ? { ...q, repeats: v } : q)); }}
                          aria-label={`Pass ${i + 1} repeats`}
                          style={{ ...inputStyle, width: "62px", marginLeft: "5px" }} />
                      </label>
                      <label style={{ fontSize: "12px" }}>transpose
                        <input type="number" min="-48" max="48" step="1" value={p.transpose}
                          onChange={(e) => { const v = Math.max(-48, Math.min(48, parseInt(e.target.value, 10) || 0));
                            setPathPasses(pathPasses.map((q, j) => j === i ? { ...q, transpose: v } : q)); }}
                          aria-label={`Pass ${i + 1} transpose in semitones`}
                          style={{ ...inputStyle, width: "62px", marginLeft: "5px" }} />
                      </label>
                      <label style={{ fontSize: "12px" }}>pause
                        <input type="number" min="0" max="8" step="0.25" value={p.pause}
                          onChange={(e) => { const v = Math.max(0, Math.min(8, parseFloat(e.target.value) || 0));
                            setPathPasses(pathPasses.map((q, j) => j === i ? { ...q, pause: v } : q)); }}
                          aria-label={`Pass ${i + 1} pause in seconds`}
                          style={{ ...inputStyle, width: "70px", marginLeft: "5px" }} />
                      </label>
                      <label style={{ fontSize: "12px" }}>notes/sec
                        <input type="number" min="0" max="16" step="0.5" value={p.rate ?? 0}
                          onChange={(e) => { const raw = parseFloat(e.target.value);
                            const v = (!Number.isFinite(raw) || raw <= 0) ? 0 : Math.max(0.5, Math.min(16, raw));
                            setPathPasses(pathPasses.map((q, j) => j === i ? { ...q, rate: v } : q)); }}
                          aria-label={`Pass ${i + 1} notes per second, zero to inherit the global rate`}
                          title="0 = inherit the global Notes/sec slider"
                          style={{ ...inputStyle, width: "70px", marginLeft: "5px" }} />
                      </label>
                      <label style={{ fontSize: "12px" }}>vel
                        <input type="number" min="0" max="127" step="1" value={p.velocity ?? 0}
                          onChange={(e) => { const raw = parseInt(e.target.value, 10);
                            const v = (!Number.isFinite(raw) || raw <= 0) ? 0 : Math.max(1, Math.min(127, raw));
                            setPathPasses(pathPasses.map((q, j) => j === i ? { ...q, velocity: v } : q)); }}
                          aria-label={`Pass ${i + 1} velocity, zero to inherit the default`}
                          title="0 = inherit the default velocity of 96"
                          style={{ ...inputStyle, width: "62px", marginLeft: "5px" }} />
                      </label>
                      <label style={{ fontSize: "12px" }}>instr
                        <select value={p.instrument || ""}
                          onChange={(e) => setPathPasses(pathPasses.map((q, j) => j === i ? { ...q, instrument: e.target.value } : q))}
                          aria-label={`Pass ${i + 1} instrument, blank to inherit the path instrument`}
                          title="Blank = inherit the path instrument"
                          style={{ ...inputStyle, width: "auto", maxWidth: "150px", marginLeft: "5px", padding: "5px 6px", fontSize: "12px" }}>
                          <option value="">(same as path)</option>
                          {Object.entries(GM_INSTRUMENTS).map(([group, list]) => (
                            <optgroup key={group} label={group}>
                              {list.map((nm) => <option key={nm} value={nm}>{nm}</option>)}
                            </optgroup>
                          ))}
                        </select>
                      </label>
                      <button type="button"
                        onClick={() => { if (i > 0) { const c = pathPasses.slice(); [c[i - 1], c[i]] = [c[i], c[i - 1]]; setPathPasses(c); } }}
                        aria-label={`Move pass ${i + 1} earlier`}
                        style={{ ...inputStyle, width: "auto", cursor: "pointer", padding: "4px 8px" }}>↑</button>
                      <button type="button"
                        onClick={() => { if (i < pathPasses.length - 1) { const c = pathPasses.slice(); [c[i], c[i + 1]] = [c[i + 1], c[i]]; setPathPasses(c); } }}
                        aria-label={`Move pass ${i + 1} later`}
                        style={{ ...inputStyle, width: "auto", cursor: "pointer", padding: "4px 8px" }}>↓</button>
                      <button type="button"
                        onClick={() => { setPathPasses(pathPasses.filter((_, j) => j !== i)); speakLabel(`Pass ${i + 1} removed.`); }}
                        aria-label={`Remove pass ${i + 1}`}
                        style={{ ...inputStyle, width: "auto", cursor: "pointer", padding: "4px 8px" }}>🗑</button>
                    </div>
                  ))}
                </div>
              )}
              {soundMode === "path" && pathDrawMode === "freehand" && (
                <label style={musicRowStyle}>
                  Resolution:
                  <input
                    type="range" min="4" max="40" step="2"
                    value={pathResolution}
                    onChange={(e) => { const v = parseInt(e.target.value, 10); setPathResolution(v); }}
                    onFocus={() => speakLabel(`Resolution ${pathResolution} pixels between notes. Lower is denser.`)}
                    aria-label="Freehand resolution, pixels between resampled notes"
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: "#94a3b8", fontSize: "12px", minWidth: "48px", textAlign: "right" }}>{pathResolution}px</span>
                </label>
              )}
              {soundMode === "path" && (
                <label style={musicRowStyle}>
                  Instrument:
                  <select
                    value={pathInstrument}
                    onChange={(e) => { setPathInstrument(e.target.value); speakLabel(`Path instrument ${e.target.value.replace(/_/g, " ")}.`); }}
                    aria-label="Drawn path instrument"
                    style={{ ...inputStyle, width: "auto", minWidth: "150px", padding: "6px 8px", fontSize: "13px" }}
                  >
                    {Object.entries(GM_INSTRUMENTS).map(([group, list]) => (
                      <optgroup key={group} label={group.replace(/_/g, " ")}>
                        {list.map((inst) => (
                          <option key={inst} value={inst}>{inst.replace(/_/g, " ")}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>
              )}
              {soundMode === "path" && pathDrawMode === "vertices" && (
                <label style={musicRowStyle}>
                  Notes per segment:
                  <input
                    type="range" min="1" max="12" step="1"
                    value={pathNotesPerSeg}
                    onChange={(e) => { const v = parseInt(e.target.value, 10); setPathNotesPerSeg(v); }}
                    onFocus={() => speakLabel(`Notes per segment ${pathNotesPerSeg}.`)}
                    aria-label="Notes read per straight segment"
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: "#94a3b8", fontSize: "12px", minWidth: "48px", textAlign: "right" }}>{pathNotesPerSeg}</span>
                </label>
              )}
              {soundMode === "path" && (
                <label style={musicRowStyle}>
                  Notes/sec:
                  <input
                    type="range" min="1" max="16" step="1"
                    value={pathRate}
                    onChange={(e) => { const v = parseInt(e.target.value, 10); setPathRate(v); }}
                    onFocus={() => speakLabel(`Path speed ${pathRate} notes per second.`)}
                    aria-label="Notes per second"
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: "#94a3b8", fontSize: "12px", minWidth: "48px", textAlign: "right" }}>{pathRate}</span>
                </label>
              )}
              {(soundMode === "contour" || soundMode === "layered") && (
                <label style={musicRowStyle}>
                  Contour speed:
                  <input
                    type="range" min="40" max="400" step="10"
                    value={contourSpeed}
                    onChange={(e) => { const v = parseInt(e.target.value, 10); setContourSpeed(v); }}
                    onFocus={() => speakLabel(`Contour glide speed. ${contourSpeed <= 90 ? "Slow" : contourSpeed >= 250 ? "Fast" : "Medium"}. Lower is slower.`)}
                    aria-label="Contour glide speed (lower is slower)"
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: "#94a3b8", fontSize: "12px", minWidth: "48px", textAlign: "right" }}>
                    {contourSpeed <= 90 ? "Slow" : contourSpeed >= 250 ? "Fast" : "Medium"}
                  </span>
                </label>
              )}
              {(soundMode === "contour" || soundMode === "layered") && (
                <label style={musicRowStyle}>
                  Contour gap:
                  <input
                    type="range" min="0" max="1200" step="20"
                    value={Math.round(contourGap * 1000)}
                    onChange={(e) => { const v = parseInt(e.target.value, 10) / 1000; setContourGap(v); }}
                    onFocus={() => speakLabel(`Gap between contours. ${contourGap < 0.01 ? "None" : contourGap.toFixed(2) + " seconds"}.`)}
                    aria-label="Gap between contours in a sequence"
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: "#94a3b8", fontSize: "12px", minWidth: "48px", textAlign: "right" }}>
                    {contourGap < 0.01 ? "None" : `${contourGap.toFixed(2)}s`}
                  </span>
                </label>
              )}
              {(soundMode === "contour" || soundMode === "layered") && (
                <label style={musicRowStyle}>
                  Pitch follows:
                  <select
                    value={contourPitchMode}
                    onChange={(e) => setContourPitchMode(e.target.value)}
                    onFocus={() => speakLabel(`Pitch source. Currently ${contourPitchMode === "archHeight" ? "arc height" : "distance from axis"}.`)}
                    aria-label="What the contour glide pitch follows"
                    style={musicFieldStyle}
                  >
                    <option value="axis">Distance from axis</option>
                    <option value="archHeight">Arc height</option>
                  </select>
                </label>
              )}
              {soundMode === "layered" && (
                <label style={musicRowStyle}>
                  Tempo:
                  <input
                    type="range" min="30" max="160" step="1"
                    value={tripletBpm}
                    onChange={(e) => setTripletBpm(parseInt(e.target.value, 10))}
                    onFocus={() => speakLabel(`Tempo, dotted quarter equals ${tripletBpm}.`)}
                    aria-label="Tempo in dotted quarter notes per minute, layered mode"
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: "#94a3b8", fontSize: "12px", minWidth: "48px", textAlign: "right" }}>♪. = {tripletBpm}</span>
                </label>
              )}
              {(soundMode === "contour" || soundMode === "layered") && (
                <div style={{ ...sectionStyle, marginTop: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                    <strong style={{ color: "#f8fafc", fontSize: "15px" }}>Contours — the sequence</strong>
                    {contourSequence.length > 0 && (
                      <button
                        onClick={() => { if (window.confirm("Clear all contours from the sequence?")) { setContourSequence([]); setStatus("Contour sequence cleared."); } }}
                        style={{ background: "#7f1d1d", color: "#fecaca", border: "1px solid #991b1b", borderRadius: "8px", padding: "4px 10px", fontSize: "12px", cursor: "pointer" }}
                        data-description="Clear all contours from the sequence."
                      >Clear all</button>
                    )}
                  </div>
                  {contourSequence.length === 0 ? (
                    <div style={{ color: "#94a3b8", fontSize: "13px", padding: "8px 0" }}>
                      No contours yet — right-click a ring to add it to the sequence. Left-click previews a single contour.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {contourSequence.map((c, i) => {
                        const _isNow = i === currentContourIndex;
                        return (
                        <div key={c.id} style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", background: _isNow ? "rgba(255,176,0,0.18)" : "#0b1220", border: _isNow ? "1px solid rgba(255,176,0,0.9)" : "1px solid #1f2937", borderRadius: "10px", padding: "8px 10px" }}>
                          <span style={{ background: _isNow ? "rgba(255,176,0,0.95)" : "#22d3ee", color: "#083344", fontWeight: 700, minWidth: "20px", height: "20px", lineHeight: "20px", textAlign: "center", borderRadius: "10px", fontSize: "12px" }}>{i + 1}</span>
                          <span style={{ color: "#e2e8f0", fontSize: "13px" }}>escape {c.level}</span>
                          <select
                            value={c.wave || "sine"}
                            onChange={(e) => { const w = e.target.value; setContourSequence((seq) => seq.map((x) => x.id === c.id ? { ...x, wave: w } : x)); }}
                            aria-label={`Waveform for contour ${i + 1}`}
                            title="Waveform"
                            style={{ background: "#0b1220", color: "#e2e8f0", border: "1px solid #1f2937", borderRadius: "6px", padding: "2px 4px", fontSize: "12px" }}
                          >
                            <option value="sine">sine</option>
                            <option value="triangle">triangle</option>
                            <option value="sawtooth">saw</option>
                            <option value="square">square</option>
                          </select>
                          <select
                            value={c.speed == null ? "global" : String(c.speed)}
                            onChange={(e) => { const v = e.target.value; const sp = v === "global" ? null : parseInt(v, 10); setContourSequence((seq) => seq.map((x) => x.id === c.id ? { ...x, speed: sp } : x)); }}
                            aria-label={`Speed for contour ${i + 1}`}
                            title="Speed (Global uses the slider above)"
                            style={{ background: "#0b1220", color: "#e2e8f0", border: "1px solid #1f2937", borderRadius: "6px", padding: "2px 4px", fontSize: "12px" }}
                          >
                            <option value="global">Global</option>
                            <option value="70">Slow</option>
                            <option value="130">Medium</option>
                            <option value="280">Fast</option>
                          </select>
                          <span style={{ color: "#94a3b8", fontSize: "12px" }}>{c.closed ? "closed loop" : "open arc"} · {c.points.length} pts</span>
                          <label style={{ color: "#94a3b8", fontSize: "12px", display: "flex", alignItems: "center", gap: "3px" }}>cols
                            <input type="number" min="0" max={WIDTH - 1} value={typeof c.colMin === "number" ? c.colMin : 0}
                              onChange={(e) => { const v = Math.max(0, Math.min(WIDTH - 1, parseInt(e.target.value, 10) || 0)); setContourSequence((seq) => seq.map((x) => x.id === c.id ? { ...x, colMin: v } : x)); }}
                              aria-label={`Start column for contour ${i + 1}`} title="Start column (0 = left edge)"
                              style={{ width: "52px", background: "#0b1220", color: "#e2e8f0", border: "1px solid #1f2937", borderRadius: "6px", padding: "2px 4px", fontSize: "12px" }} />
                            <span>–</span>
                            <input type="number" min="0" max={WIDTH - 1} value={typeof c.colMax === "number" ? c.colMax : (WIDTH - 1)}
                              onChange={(e) => { const v = Math.max(0, Math.min(WIDTH - 1, parseInt(e.target.value, 10) || 0)); setContourSequence((seq) => seq.map((x) => x.id === c.id ? { ...x, colMax: v } : x)); }}
                              aria-label={`End column for contour ${i + 1}`} title="End column (749 = right edge)"
                              style={{ width: "52px", background: "#0b1220", color: "#e2e8f0", border: "1px solid #1f2937", borderRadius: "6px", padding: "2px 4px", fontSize: "12px" }} />
                          </label>
                          <div style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
                            <button
                              onClick={() => { setColDragFor(colDragFor === c.id ? null : c.id); if (colDragFor !== c.id) speakLabel(`Drag left to right across the canvas to set columns for contour ${i + 1}. A tiny drag clears to full width.`); }}
                              style={{ ...darkButtonStyle, padding: "4px 8px", fontSize: "12px", background: colDragFor === c.id ? "#22d3ee" : undefined, color: colDragFor === c.id ? "#083344" : undefined }}
                              aria-label={`Set columns for contour ${i + 1} by dragging on the canvas`}
                              title="Set columns by dragging a vertical band on the canvas"
                            >{colDragFor === c.id ? "Drag on canvas…" : "Set cols"}</button>
                            <button
                              onClick={() => setContourSequence((seq) => { if (i === 0) return seq; const n = seq.slice(); [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}
                              disabled={i === 0}
                              style={{ ...darkButtonStyle, padding: "4px 8px", fontSize: "13px", opacity: i === 0 ? 0.35 : 1 }}
                              aria-label={`Move contour ${i + 1} earlier`}
                              title="Move earlier"
                            >↑</button>
                            <button
                              onClick={() => setContourSequence((seq) => { if (i >= seq.length - 1) return seq; const n = seq.slice(); [n[i + 1], n[i]] = [n[i], n[i + 1]]; return n; })}
                              disabled={i >= contourSequence.length - 1}
                              style={{ ...darkButtonStyle, padding: "4px 8px", fontSize: "13px", opacity: i >= contourSequence.length - 1 ? 0.35 : 1 }}
                              aria-label={`Move contour ${i + 1} later`}
                              title="Move later"
                            >↓</button>
                            <button
                              onClick={() => setContourSequence((seq) => seq.filter((x) => x.id !== c.id))}
                              style={{ ...darkButtonStyle, padding: "4px 8px", fontSize: "13px", background: "#7f1d1d", color: "#fecaca", border: "1px solid #991b1b" }}
                              aria-label={`Delete contour ${i + 1}`}
                              title="Delete"
                            >🗑</button>
                          </div>
                        </div>
                      );})}
                    </div>
                  )}
                </div>
              )}
              <label style={musicRowStyle}>
                Key:
                <select id="music-key-select" value={musicKey.root} onChange={(e) => { setMusicKey(k => ({ ...k, root: e.target.value })); speakLabel(`Key ${spokenRoot(e.target.value)}.`); }} onFocus={() => speakLabel(`Key. Currently ${spokenRoot(musicKey.root)}. Use arrow keys to change.`)} disabled={!keyApplies} style={musicFieldStyle}>
                  {["C","Db","D","Eb","E","F","Gb","G","Ab","A","Bb","B"].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <select id="music-scale-select" value={musicKey.scale} onChange={(e) => { setMusicKey(k => ({ ...k, scale: e.target.value })); speakLabel(`Scale ${spokenScale(e.target.value)}.`); }} onFocus={() => speakLabel(`Scale. Currently ${spokenScale(musicKey.scale)}. Use arrow keys to change.`)} disabled={!keyApplies} style={musicFieldStyle}>
                  {["major","minor","pentatonic","blues","wholetone","chromatic"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label style={musicRowStyle}>
                Harmony:
                <select id="music-harmony-select" value={harmonyMode} onChange={(e) => { setHarmonyMode(e.target.value); speakLabel(e.target.value === "chordal" ? "Harmony chordal. Notes snap to a chord progression." : "Harmony fractal. Pure sonification."); }} onFocus={() => speakLabel(`Harmony. Currently ${harmonyMode === "chordal" ? "chordal, notes snap to a chord progression" : "fractal, pure sonification"}. Use arrow keys to change.`)} disabled={!instrumentalOn} style={musicFieldStyle}>
                  <option value="fractal">Fractal</option>
                  <option value="chordal">Chordal</option>
                </select>
              </label>
              {!keyApplies && (
                <div style={{ fontSize: "12px", color: "#64748b", lineHeight: 1.4, marginTop: "-2px" }}>
                  Key and Scale apply to Instrumental fractal music, Layered and Drawn path. Harmony applies to Instrumental fractal music only.
                </div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                <button id="fractal-play-button" style={buttonStyle} onClick={() => startPlaybackDelayed(0)} data-description="Play full scan. Starts playing the fractal as music from the leftmost column to the rightmost. Each column produces a chord from the colors in that vertical line." >Play full scan</button>
                <button id="fractal-resume-button" style={accentButtonStyle} onClick={() => startPlaybackDelayed(playhead ?? 0)} data-description="Resume here. Continues playback from where the playhead currently sits, instead of restarting from the beginning." >Resume here</button>
                <button id="fractal-pause-button" style={darkButtonStyle} onClick={stopPlayback} data-description="Pause. Stops the music and freezes the playhead at its current position. Resume here will continue from this point." >Pause</button>
                <button id="fractal-boundaries-button" style={darkButtonStyle} onClick={() => setShowBoundaries(v => !v)} data-description="Show or hide movement boundaries. Toggles the triangle-and-line markers that show where movements begin and end. Hidden by default so listeners see a clean image; this does not change the music or the movements themselves. Placing a mark automatically shows the boundaries." >{showBoundaries ? "Hide boundaries" : "Show boundaries"}</button>
                <label style={{ display: "flex", alignItems: "center", gap: "6px", color: "#cbd5e1", fontSize: "14px" }}>
                  Delay
                  <input
                    id="play-delay-input"
                    type="number"
                    min={0}
                    max={120}
                    step={1}
                    value={playDelay}
                    aria-label="Play delay in seconds"
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(120, parseInt(e.target.value, 10) || 0));
                      setPlayDelay(v);
                    }}
                    onFocus={() => speakLabel(`Play delay. Currently ${playDelay} seconds. Playback waits this long after you press Play.`)}
                    data-description="Play delay. Number of seconds to wait after pressing Play before playback begins, so you can switch windows before a recording starts. Zero starts immediately. Escape cancels a countdown in progress."
                    style={{ width: "60px", padding: "4px 6px", background: "#1e293b", color: "#e2e8f0", border: "1px solid #475569", borderRadius: "4px" }}
                  />
                  s
                </label>
              </div>
              {delayLeft != null && (
                <div role="status" aria-live="polite" style={{ color: "#fbbf24", fontSize: "16px", fontWeight: 600 }}>
                  Playing in {delayLeft}…
                </div>
              )}

              <div style={{ color: "#cbd5e1", fontSize: "14px" }}>
                {isPlaying ? "▶ Playback active" : "■ Playback idle"}
              </div>
              <div>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", color: "#cbd5e1", fontSize: "14px" }}>
                  <input
                    id="scan-orientation-horizontal"
                    type="checkbox"
                    tabIndex={0}
                    aria-label="Horizontal scan orientation"
                    checked={scanOrientation === "horizontal"}
                    onChange={(e) => {
                      const next = e.target.checked ? "horizontal" : "vertical";
                      setScanOrientation(next);
                      speakLabel(
                        next === "horizontal"
                          ? "Horizontal scan. Takes effect on next play."
                          : "Vertical scan. Takes effect on next play."
                      );
                    }}
                    onFocus={() =>
                      speakLabel(
                        `Scan orientation. Currently ${scanOrientation}. Checked is horizontal, scanning top to bottom. Unchecked is vertical, scanning left to right.`
                      )
                    }
                    data-description="Scan orientation. When unchecked, a vertical line sweeps left to right and each column is sonified top to bottom. When checked, a horizontal line sweeps top to bottom and each row is sonified left to right. The change applies the next time you play."
                  />
                  Horizontal scan
                </label>
              </div>
              <div>
                <label style={labelStyle}>Column speed: {(lineDrawMs / 1000).toFixed(2)} s</label>
                <input id="input-column-speed" type="range" min="250" max="4000" step="50" value={lineDrawMs}
                  onChange={(e) => setLineDrawMs(Number(e.target.value))}
                  onFocus={() => speakLabel(`Column speed, currently ${(lineDrawMs / 1000).toFixed(2)} seconds. Use arrow keys to adjust.`)}
                  onKeyUp={(e) => { clearTimeout(window._sliderAnnounceTimer); 
                  window._sliderAnnounceTimer = setTimeout(() => speakLabel(`Column speed ${(Number(e.target.value) / 1000).toFixed(2)} seconds`), 300); 
                  }}
                  data-description="Column speed. Sets how long each column of the fractal plays before moving to the next, in seconds. Lower is faster. Range: 0.25 to 4 seconds."
                  style={{ width: "100%" }} />
              </div>

              <div>
                <label style={labelStyle}>Stereo width: {stereoWidth.toFixed(2)}</label>
                <input id="input-stereo-width" type="range" min="0" max="1" step="0.05" value={stereoWidth}
                  onChange={(e) => setStereoWidth(Number(e.target.value))}
                  onFocus={() => speakLabel(`Stereo width, currently ${stereoWidth.toFixed(2)}. Use arrow keys to adjust.`)}
onKeyUp={(e) => { clearTimeout(window._sliderAnnounceTimer); window._sliderAnnounceTimer = setTimeout(() => speakLabel(`Stereo width ${Number(e.target.value).toFixed(2)}`), 300); }}
                  data-description="Stereo width. Controls how wide the stereo image is when playing. Zero is mono, all sound centered. One is full stereo separation, with low rows on the left and high rows on the right. Use to taste."
                  style={{ width: "100%" }} />
              </div>

              <div>
                <label style={labelStyle}>Music volume: {musicVolume}</label>
                <input id="input-music-volume" type="range" min="0" max="100" step="1" value={musicVolume}
                  onChange={(e) => setMusicVolume(Number(e.target.value))}
                  onFocus={() => speakLabel(`Music volume, currently ${musicVolume}. Use arrow keys to adjust.`)}
onKeyUp={(e) => { clearTimeout(window._sliderAnnounceTimer); window._sliderAnnounceTimer = setTimeout(() => speakLabel(`Music volume ${e.target.value}`), 300); }}
                  data-description="Music volume. Adjusts the loudness of the fractal sonification only. Does not affect voice announcements. Range: 0 silent to 100 maximum."
                  style={{ width: "100%" }} />
              </div>

              <div>
                <label style={labelStyle}>Voice volume: {voiceVolume}</label>
                <input id="transport-voice-volume" type="range" min="0" max="100" step="1" value={voiceVolume}
                  onChange={(e) => setVoiceVolume(Number(e.target.value))}
                  onFocus={() => speakLabel(`Voice volume, currently ${voiceVolume}. Use arrow keys to adjust.`)}
                  onKeyUp={(e) => { clearTimeout(window._sliderAnnounceTimer); window._sliderAnnounceTimer = setTimeout(() => speakLabel(`Voice volume ${e.target.value}`), 300); }}
                  onKeyDown={(e) => { if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); document.getElementById('mode-select')?.focus(); } }}
                  data-description="Voice volume. Adjusts the loudness of the app's spoken announcements only. Does not affect music. Range: 0 silent to 100 maximum."
                  style={{ width: "100%" }} />
                <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", cursor: "pointer" }}>
                  <input
                    id="transport-spoken-labels"
                    type="checkbox"
                    checked={spokenLabels}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setSpokenLabels(on);
                      spokenLabelsRef.current = on;
                      try { window.localStorage.setItem("fmSpokenLabels", on ? "on" : "off"); } catch (_) {}
                      // Announce the change only when switching ON — switching off
                      // should be silent, which is the point.
                      if (on) speakLabel("Spoken labels on.");
                    }}
                    aria-label="Spoken labels. Turn off if you use a screen reader, to avoid hearing every control announced twice."
                    data-description="Spoken labels. The app announces each control as you reach it. Turn this off if you use a screen reader, which announces the same controls itself. The controls stay fully labelled either way."
                  />
                  <span style={labelStyle}>Spoken labels</span>
                </label>
                <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px", lineHeight: 1.4 }}>
                  Turn off if you use a screen reader — it announces these controls itself.
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ── Footer row: column data + fractal status ───────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginTop: "20px" }}>

          {/* Selected column data */}
          <div style={sectionStyle}>
            <div style={{ fontSize: "12px", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: "12px" }}>
              Selected column data
            </div>
            {selectedStats ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "10px" }}>
                <MetricCard label="Column" value={String(selectedColumn)} />
                <MetricCard label="Mean value" value={selectedStats.mean.toFixed(2)} />
                <MetricCard label="Std. deviation" value={selectedStats.stdDev.toFixed(2)} />
                <MetricCard label="Edge crossings" value={String(selectedStats.edgeCrossings)} />
                <MetricCard label="Inside ratio" value={selectedStats.insideRatio.toFixed(2)} />
              </div>
            ) : (
              <div style={{ color: "#94a3b8", fontSize: "14px" }}>
                Click the image or start playback to inspect a column.
              </div>
            )}
          </div>

          {/* Current fractal status */}
          <div style={sectionStyle}>
            <div style={{ fontSize: "12px", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: "12px" }}>
              Current fractal · <span style={{ color: "#f8fafc", fontSize: "14px", textTransform: "none", letterSpacing: "normal" }}>{status}</span>
            </div>
            <div style={{ fontSize: "13px", lineHeight: 1.7, color: "#cbd5e1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
              <div>Type: {fractalType === "mandelbrot" ? "Mandelbrot" : "Julia"}</div>
              <div>Palette: {palettes[paletteName]?.label || paletteName}</div>
              <div>x: [{view.xMin.toFixed(6)}, {view.xMax.toFixed(6)}]</div>
              <div>y: [{view.yMin.toFixed(6)}, {view.yMax.toFixed(6)}]</div>
              <div>Max iterations: {maxIter}</div>
              <div>Full scan: {formatDuration(totalScanMs)}</div>
              <div>Mode: {mode === "scan" ? "Scan" : "Orbit"}</div>
              <div>Using palette bank: {usePaletteBank ? "Yes" : "No"}</div>
              {fractalType === "julia" && <div>Julia c: {juliaC.re.toFixed(5)} {juliaC.im >= 0 ? "+" : "-"} {Math.abs(juliaC.im).toFixed(5)}i</div>}
              {paletteLoaded && paletteBank && (
                <div>Editing page: {palettePage}{editedFullColors ? " (modified)" : ""}</div>
              )}
              {importedImgMeta && (
                <>
                  <div>IMG: {importedImgMeta.fileName}</div>
                  <div>Size: {importedImgMeta.sourceWidth}×{importedImgMeta.sourceHeight}</div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {showHelp && (
        <HelpDialog onClose={() => {
          setShowHelp(false);
          // Return focus to the Help button
          setTimeout(() => document.getElementById('help-btn')?.focus(), 0);
        }} />
      )}
      {showSetup && (
        <SetupDialog
          initialValue={accessibilitySetup}
          onSave={(v) => {
            setAccessibilitySetup(v);
            setShowSetup(false);
            // Strip ?setup=1 so reloading the page doesn't reopen it.
            try {
              const u = new URL(window.location.href);
              u.searchParams.delete('setup');
              window.history.replaceState({}, '', u.toString());
            } catch (_) {}
          }}
          onCancel={() => {
            setShowSetup(false);
            try {
              const u = new URL(window.location.href);
              u.searchParams.delete('setup');
              window.history.replaceState({}, '', u.toString());
            } catch (_) {}
          }}
        />
      )}
      </div>
    );
  }
